import { domain } from "zod/v4/core/regexes";
import { isMaintenanceActive } from "../utils/maintenance";
import { handleMaintenanceResponse, handleNetworkError } from "../utils/maintenanceGuard";
import {
  HIST_QUOTE_ODOO_FIELD,
  OPP_EXPECTED_ODOO_FIELD,
  PIPELINE_MULTICURRENCY_CODES,
  emptyOpportunityRevenueByCurrency,
  type OpportunityRevenueByCurrency,
} from "../utils/crmOpportunityMultiCurrency";
import { opportunityStageIsTerminalAcceptedOrDeclined } from "../utils/offersFromCrm";
import {
  buildArtListVisibilityOrParts,
  mapProfileRoleToArtListRole,
  normalizeOdooCompanyIds,
} from "../utils/artListVisibility";

interface OdooCredentials {
  sessionId: string;
  userId?: number;
  login?: string;
  password?: string;
}

interface OdooUser {
  id: number;
  name: string;
  email: string;
  login: string;
  partner_id: number;
  is_admin: boolean;
  company_id: number;
}

export interface ArtListDocumentAttachment {
  id: number;
  name: string;
  mimetype: string;
  create_date?: string;
}

export interface ArtworkListMembership {
  id: number;
  code: string;
  name: string;
  type: string;
  color: string;
  startDate: string;
  country: string;
  accessible: boolean;
  previewImages?: string[];
}

export interface DashboardNotification {
  id?: number;
  active?: boolean;
  message: string;
  start_date: string;
  end_date: string;
}

export type DashboardNotificationInput = Pick<
  DashboardNotification,
  "message" | "start_date" | "end_date"
>;

const CACHE_CLEAR_BUTTON_PARAM_KEY = "artgallery.dashboard.cache_clear_enabled";

function parseConfigFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

function parseDashboardNotificationPayload(res: Record<string, unknown> | null | undefined): DashboardNotification {
  const row = res || {};
  return {
    id: Number(row.id || 0) || undefined,
    active: Boolean(row.active),
    message: String(row.message || ""),
    start_date: String(row.start_date || ""),
    end_date: String(row.end_date || ""),
  };
}

/** Odoo JSON-RPC often returns many2one as `[id, display_name]`; `Number([id, name])` is NaN. */
function normalizeOdooMany2oneId(raw: unknown): number | null {
  if (raw === false || raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  if (Array.isArray(raw) && raw.length > 0) {
    const id0 = Number(raw[0]);
    if (Number.isFinite(id0) && id0 > 0) return id0;
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;
  return null;
}

interface OdooArtwork {
  id: number;
  name: string; // Artwork name
  res_artist_id: [number, string]; // Artist name
  IdName: string; // Artwork ID
  SizeH: number; // sizeH cm
  Medium: string; // Medium
  control_gallery_combined: string; // Gallery
  artwork_price_combined: number; // Price
  ApprouvedDiscount: number; // Max. Discount
  Category: string; // Category
  Type: string; // Type
  CRMTypeLayout?: 'master' | 'second_market' | 'contemporary' | false;
  Status: string; // Status
  ConsPurchase?: string | false;
  OwnerShip?: string | false;
  ConsignmentCustomer?: string | false;
  Region: string; // Region
  Location: string;

  SubLocation: string;

  artwork_year: number;
  create_date: string;
  entry_date?: string;
  SizeL: number; // sizeL cm
  SizeP: number; // sizeP cm
  WeightKg: number; // weight kg
  WeightPounds: number;
  SizeIncL: number; // sizeL inch
  SizeIncH: number; // sizeH inch
  SizeIncP: number; // sizeP inch
  LtdNumber: string; // Limited number
  NewMainLR: string | false; // Manual low-res main image
  NewMainHR: string | false; // Manual high-res main image
  main_super_picture_hd: string | false; // Main Super HD Pictures
  signatures_pictures: string | false; // Signatures
  main_picture_hd: string | false; // Main HD Pictures
  detail_1_url: string | false; // Det Pictures 1
  main_web_picture: string | false; // Main Web Pictures
  detail_2_url: string | false; // Det Pictures 2
  main_a6_picture: string | false; // Main A6 picture
  perspective_url: string | false; // Perspective Picture
  main_hd_web_picture: string | false; // Main HD Web
  back_pictures: string | false; // Back Pictures
  frame_picture: string | false; // Framed
  other_url: string | false; // Others url
  image_1920: string | false; // Base64 image data for preview
  CertificateWording: string | false;
  ProvenanceWordingCrm: string | false;
  CertificateCertAddress: string | false;
  artwork_exhibited: string | false;
  artwork_signed: string | false;
  LiteratureEn: string | false;
  certificateid: number;
  certificateurl: string | false;
  VideoPresentationUrl1: string | false;
  VideoPresentationUrl2: string | false;
  VideoDetailUrl1: string | false;
  VideoDetailUrl2: string | false;
  VideoInstallationUrl1: string | false;
  VideoInstallationUrl2: string | false;
  VideoTimelapseUrl1: string | false;
  VideoTimelapseUrl2: string | false;
  VideoDocumentaryUrl1: string | false;
  VideoDocumentaryUrl2: string | false;
  VideoInterviewUrl1: string | false;
  VideoInterviewUrl2: string | false;
  CatalogueUrl1: string | false;
  CatalogueUrl2: string | false;
  CertificateUrl1: string | false;
  CertificateUrl2: string | false;
  PublicationUrl1: string | false;
  PublicationUrl2: string | false;
  OtherDocumentUrl1: string | false;
  OtherDocumentUrl2: string | false;

  is_shared_property?: string;

}

interface OdooContact {
  id: number;
  name: string;
  email: string;
  phone: string;
  city: string;
  country_id: [number, string] | string;
  is_company: boolean;
  customer_rank: number;
  total_sales_order: number;
  create_date: string;
  write_date: string;
  is_partner_lead: boolean;
  is_partner_client: boolean;

  // Contact Detail fields
  client_classifications: string;
  title: string | false;
  gallery_order_count: number;
  control_galleries_ids: boolean | false;

  // Additional emails and phones
  Email1: string | false;
  Email2: string | false;
  Phone1: string | false;
  Phone2: string | false;

  // Address fields
  street: string;
  street2: string | false;
  main_street3?: string | false;
  state_id: [number, string] | false;
  zip: string;

  // Shipping address
  ShippingAddress: string | false;
  Shipping_stree2?: string | false;
  Shipping_stree3?: string | false;
  ShippingAddress2?: string | false;
  Country_Shipping_Address: string;
  State_Shipping_Address: string;
  City_Shipping_Address: string;
  Zip_Shipping_Address: string;

  // Billing address
  BillingAddress: string | false;
  Country_Billing_Address: string;
  State_Billing_Address: string;
  City_Billing_Address: string;
  Zip_Billing_Address: string;

  // Personal info
  BirthDate?: string | false;
  

  // Additional fields
  website?: string | false;
  function?: string | false; // Job position
  lang?: string | false; // Language
  mobile?: string | false; // Mobile phone

  // Artist interests
  interest_artist_ids?: any[];


  _model?: 'crm.prospect' | 'res.partner';
}

// ✨ Activity System Interfaces (based on Odoo mail.activity model)
interface OdooActivityType {
  id: number;
  name: string;
  category: string; // 'meeting', 'phonecall', 'email', 'todo', etc.
  delay_count: number; // Default delay
  icon: string;
  decoration_type: string;
}

interface OdooActivity {
  id: number;
  res_model: string; // Model the activity is linked to (e.g. res.partner, product.template)
  res_id: number; // Record ID on that model
  activity_type_id: [number, string]; // Type of activity - from mail.activity.type
  summary: string; // Optional title or notes
  date_deadline: string; // Due date (YYYY-MM-DD format)
  user_id: [number, string]; // Assigned to (many2one to res.users)
  note: string; // Rich text note
  state: 'planned' | 'today' | 'overdue' | 'done' | 'cancel'; // Activity state
  create_date: string;
  write_date: string;
  create_uid: [number, string]; // Created by
  calendar_event_id?: number | false; // Linked calendar event
  res_name?: string; // Name of the linked record
}

// Frontend Activity interface (transformed from Odoo)
export interface Activity {
  id: number;
  resModel: string;
  resId: number;
  activityType: {
    id: number;
    name: string;
    category: string;
    icon: string;
  };
  summary: string;
  dueDate: string;
  assignedTo: {
    id: number;
    name: string;
  };
  note: string;
  state: 'planned' | 'today' | 'overdue' | 'done' | 'cancel';
  createdAt: string;
  updatedAt: string;
  feedback?: string; // Feedback when marking activity as done
  createdBy: {
    id: number;
    name: string;
  };
  resName?: string;
  artistName?: string;
}



const normalizeLocation = (val?: string): 'par' | 'lon' | 'hou' | null => {
  if (!val) return null;
  const v = val.toLowerCase().replace(/\s+/g, '');
  if (v.includes('par')) return 'par';
  if (v.includes('lon')) return 'lon';
  if (v.includes('hou')) return 'hou';
  return null;
};

/** Trimmed non-empty string, or empty (this CRM often uses Email1/Phone1 instead of email/phone). */
const odooStrTrim = (v: unknown): string => {
  if (v == null || v === false) return "";
  const s = String(v).trim();
  return s;
};

const coalesceEmailFromOdooRow = (
  row: Record<string, unknown> | null | undefined,
): string | false => {
  if (!row) return false;
  for (const k of ["email", "Email1", "Email2", "email_pro"] as const) {
    const s = odooStrTrim(row[k]);
    if (s) return s;
  }
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
 
/** Fields fetched for every contact regardless of model or tab. */
const COMMON_CONTACT_FIELDS = [
  'id', 'name', 'email', 'phone', 'city', 'country_id',
  'is_company', 'customer_rank', 'total_sales_order',
  'create_date', 'write_date', 'is_partner_lead', 'is_partner_client',
  'client_classifications', 'gallery_order_count', 'control_galleries_ids',
  'Email1', 'Email2', 'Phone1', 'Phone2',
  'street', 'street2', 'state_id', 'zip',
  'ShippingAddress', 'Country_Shipping_Address', 'State_Shipping_Address',
  'City_Shipping_Address', 'Zip_Shipping_Address',
  'BillingAddress', 'Country_Billing_Address', 'State_Billing_Address',
  'City_Billing_Address', 'Zip_Billing_Address',
  'BirthDate', 'interest_artist_ids',
  'calculated_status_ids', 'category_crm', 'category_crm_ids',
  'Vip', 'last_purchase', 'countries_Country',
  'gallery_order_count_filemaker', 'number_of_invoices',
] as const;
 
/** Extra fields only present on res.partner. */
const PARTNER_EXTRA_FIELDS = [
  'title', 'last_purchase_invoices', 'Id', 'IdEntity', 'Id_with_c', 'show_personal_info',
] as const;
 
/** Extra fields only present on crm.prospect. */
const PROSPECT_EXTRA_FIELDS = [
  'title_ids', 'Id', 'IdEntity', 'Id_with_c', 'show_personal_info',
] as const;
 

const ALL_CONTACTS_FIELDS = [
  'id', 'name', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
  'street', 'street2', 'city', 'zip', 'country_id', 'state_id',
  'BirthDate', 'client_classifications', 'is_partner_client', 'is_partner_lead',
  'Country_Shipping_Address', 'State_Shipping_Address', 'City_Shipping_Address', 'Zip_Shipping_Address',
  'Country_Billing_Address',  'State_Billing_Address',  'City_Billing_Address',  'Zip_Billing_Address',
  'create_date', 'write_date',
] as const;
 
const ACTIVE_ONLY_DOMAIN = [['active', '=', true]];

const PARTNER_FIELDS = [...COMMON_CONTACT_FIELDS, ...PARTNER_EXTRA_FIELDS];
const PROSPECT_FIELDS = [...COMMON_CONTACT_FIELDS, ...PROSPECT_EXTRA_FIELDS];
 
// ─────────────────────────────────────────────────────────────────────────────
// SHARED HELPERS
// ─────────────────────────────────────────────────────────────────────────────
 
/** Tag each record with its originating model and merge both arrays. */
function mergeAndMark(
  prospects: any[],
  partners: any[],
): any[] {
  const marked = [
    ...(Array.isArray(prospects) ? prospects : []).map((c) => ({ ...c, _model: 'crm.prospect' })),
    ...(Array.isArray(partners)  ? partners  : []).map((c) => ({ ...c, _model: 'res.partner'  })),
  ];
  return marked;
}
 
/**
 * Fetch paginated contacts from both crm.prospect and res.partner in parallel,
 * merge, sort, and slice to the requested page.
 *
 * We intentionally over-fetch (limit + offset) from each model so that after
 * merging we always have enough records to fill a full page regardless of how
 * the data is distributed between the two models.
 */
async function fetchMergedContacts(
  this: any,
  partnerDomain: any[],
  prospectDomain: any[],
  partnerFields: string[],
  prospectFields: string[],
  limit: number,
  offset: number,
  order?: string,
  label = 'fetchMergedContacts',
): Promise<any[]> {
  console.log(`📋 [${label}] Querying both models – limit:`, limit, 'offset:', offset, 'order:', order);
 
  const fetchOpts = (fields: string[]) => ({
    fields,
    limit: limit + offset,
    offset: 0,
    ...(order ? { order } : {}),
  });
 
  const [prospects, partners] = await Promise.all([
    this.executeCall('crm.prospect', 'search_read', [prospectDomain], fetchOpts(prospectFields))
      .catch((e: unknown) => { console.error(`❌ [${label}] crm.prospect error:`, e); return []; }),
    this.executeCall('res.partner',  'search_read', [partnerDomain],  fetchOpts(partnerFields))
      .catch((e: unknown) => { console.error(`❌ [${label}] res.partner error:`,  e); return []; }),
  ]);
 
  console.log(`📋 [${label}] Raw counts – prospects:`, prospects?.length ?? 0, 'partners:', partners?.length ?? 0);
 
  const merged   = this.sortContactsForDisplay(mergeAndMark(prospects, partners), order);
  const paginated = merged.slice(offset, offset + limit);
 
  console.log(`📋 [${label}] Final paginated count:`, paginated.length);
  return paginated;
}
 
/**
 * Count records from both models in parallel and return their sum.
 */
async function countMerged(
  this: any,
  partnerDomain: any[],
  prospectDomain: any[],
  label = 'countMerged',
): Promise<number> {
  console.log(`📋 [${label}] Counting both models`);
 
  const [prospectCount, partnerCount] = await Promise.all([
    this.executeCall('crm.prospect', 'search_count', [prospectDomain]).catch(() => 0),
    this.executeCall('res.partner',  'search_count', [partnerDomain ]).catch(() => 0),
  ]);
 
  const total =
    (typeof prospectCount === 'number' ? prospectCount : 0) +
    (typeof partnerCount  === 'number' ? partnerCount  : 0);
 
  console.log(`📋 [${label}] prospects:`, prospectCount, 'partners:', partnerCount, 'total:', total);
  return total;
}


const coalescePhoneFromOdooRow = (
  row: Record<string, unknown> | null | undefined,
): string | false => {
  if (!row) return false;
  for (const k of ["phone", "mobile", "Phone1", "Phone2", "phone_pro"] as const) {
    const s = odooStrTrim(row[k]);
    if (s) return s;
  }
  return false;
};

/** National part already E.164 or user pasted full number — do not prefix again. */
const combineIntlPhone = (countryCode: string, national: string): string => {
  let c = odooStrTrim(countryCode);
  const n = odooStrTrim(national);
  if (!n) return "";
  if (n.startsWith("+")) return n;
  if (c) {
    if (!c.startsWith("+")) c = `+${c.replace(/^\+/, "")}`;
    return `${c} ${n}`.trim();
  }
  return n;
};

/**
 * Full phone for `crm.lead` / `phone` field: pairs Opera CRM intl. code fields with national columns
 * (same idea as ContactDetail `formatPhoneDisplay`).
 */
const buildFullPhoneForCrmLead = (
  row: Record<string, unknown> | null | undefined,
): string | false => {
  if (!row) return false;
  const pairs: Array<[string, string]> = [
    ["phone", "international_code_phone_pro"],
    ["mobile", "mobile_country_code"],
    ["Phone1", "international_code_phone1"],
    ["Phone2", "international_code_phone2"],
    ["phone_pro", "international_code_phone_pro"],
  ];
  for (const [numKey, codeKey] of pairs) {
    const national = odooStrTrim(row[numKey]);
    if (!national) continue;
    const full = combineIntlPhone(odooStrTrim(row[codeKey]), national);
    if (full) return full;
  }
  return coalescePhoneFromOdooRow(row);
};


class OdooClient {
  // 🖼️ Standard image fields for ALL artwork requests
  // ✅ These 12 image URL fields are now included in EVERY artwork request (added detail_2_url)
  private readonly ARTWORK_IMAGE_FIELDS = [
    'NewMainLR',               // Main Low-Res Picture
    'NewMainHR',               // Main High-Res Picture
    'main_super_picture_hd',    // Main Super HD Picture (highest priority)
    'main_picture_hd',          // Main HD Pictures
    'main_web_picture',         // Main Web Pictures
    'detail_1_url',             // Detail Pictures 1
    'detail_2_url',             // Detail Pictures 2 (added)
    'perspective_url',          // Perspective Picture
    'back_pictures',            // Back Pictures
    'frame_picture',            // Frame Picture
    'other_url',                // Other url
    'signatures_pictures',      // Signatures Pictures
    'main_a6_picture',          // Main A6 picture
    'main_hd_web_picture',      // Main HD Web Picture
    'CatalogueUrl1',
    'CatalogueUrl2',
    'CertificateUrl1',
    'CertificateUrl2',
    'PublicationUrl1',
    'PublicationUrl2',
    'OtherDocumentUrl1',
    'OtherDocumentUrl2',
  ];

  // 📋 Standard artwork fields (basic info + all images)
  // 🚀 PERFORMANCE: Removed image_1920 from list queries (too slow)
  private readonly STANDARD_ARTWORK_FIELDS = [
    // Basic artwork info

    'id', 'name', 'res_artist_id', 'IdName', 'Category', 'Medium',
    'artwork_price_combined', 'PriceRef', 'CurrencyRef', 'Status', 'artwork_year', 'Type', 'CRMTypeLayout', 'create_date', 'entry_date',
    'control_gallery_combined', 'SizeH', 'SizeL', 'SizeIncH', 'SizeIncL',
    'WeightKg', 'WeightPounds', 'ApprouvedDiscount', 'Location', 'SubLocation', 'Region',
    "ConsPurchase",
    "OwnerShip",
    "ConsignmentCustomer",
    'PriceCHFEnt', 'PriceEUREnt', 'PriceGBPEnt', 'PriceHKDEnt', 'PriceKRWEnt', 'PriceAEDEnt', 'PriceUSDEnt', 'PriceSGDEnt', 'x_auction_house_date', "VideoPresentationUrl1",
      "VideoPresentationUrl2",
      "VideoDetailUrl1",
      "VideoDetailUrl2",
      "VideoInstallationUrl1",
      "VideoInstallationUrl2",
      "VideoTimelapseUrl1",
      "VideoTimelapseUrl2",
      "VideoDocumentaryUrl1",
      "VideoDocumentaryUrl2",
      "VideoInterviewUrl1",
      "VideoInterviewUrl2",
      "CatalogueUrl1",
      "CatalogueUrl2",
      "CertificateUrl1",
      "CertificateUrl2",
      "PublicationUrl1",
      "PublicationUrl2",
      "OtherDocumentUrl1",
      "OtherDocumentUrl2",
    // All image URL fields (fast)
    
    ...this.ARTWORK_IMAGE_FIELDS
  ];

  private baseURL = (() => {
    const { odooBaseUrl } = require('../config/env');
    return odooBaseUrl || (process.env.NODE_ENV === 'production' ? 'https://operacrm.com' : '');
  })();
    private backendURL = (() => {

      const config = require('../config/env');
      const { odooBackendUrl } = config;
  
      // Auto-detect backend URL based on current domain
      const currentHost = window.location.hostname;
      const currentProtocol = window.location.protocol;
      const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
  
  
      if (odooBackendUrl && !odooBackendUrl.includes('backend:')) {
        return odooBackendUrl;
      }
  
  
      if (isLocalhost) {
        return 'http://localhost:5000';
      } else {
  
        return `${currentProtocol}//${currentHost}`;
      }
    })();

  private credentials: OdooCredentials | null = null;
  private user: OdooUser | null = null;
  private csrfToken: string | null = null;
  private cachedIsSeller: boolean | null = null;
  private cachedHideNoImage: boolean | null = null;

  // 🔐 Permissions safety net — prevents the dead-session request storm.
  // Cache + single-flight + circuit breaker make it physically impossible
  // for getUserPermissions() to flood the backend, regardless of HTTP status.
  private _permsCache: { uid: number; perms: string[]; ts: number } | null = null;
  private _permsInFlight: Promise<string[]> | null = null;
  private _permFailures = 0;
  private _permsCooldownUntil = 0;
  private _sessionInvalidHandled = false;
  private readonly _permsTTL = 60_000;          // cache permissions for 60s
  private readonly _permsMaxFailures = 3;       // open the circuit after 3 consecutive failures
  private readonly _permsCooldownMs = 30_000;   // ...and stay open for 30s

  private dashboardStatsCache: Map<string, { timestamp: number; data: any }> = new Map();
  private dashboardStatsInflight: Map<string, Promise<any>> = new Map();

  private artworksListCache: Map<string, { timestamp: number; data: any[] }> = new Map();
  private artworksListInflight: Map<string, Promise<any[]>> = new Map();
  private artworksCountCache: Map<string, { timestamp: number; data: number }> = new Map();
  private artworksCountInflight: Map<string, Promise<number>> = new Map();
  private locationPartnerIdsCache: Map<string, number[]> = new Map();
  private partnerAddressesCache: Map<number, any[]> = new Map();
  private addressDetailsCache: Map<number, any> = new Map();

  private normalizeContactSortValue(record: any, field: string): string | number {
    const text = (value: any): string => {
      if (Array.isArray(value)) return String(value[1] ?? value[0] ?? "").trim();
      if (value === false || value === null || value === undefined) return "";
      return String(value).trim();
    };

    switch (field) {
      case "email":
        return (
          text(record.email) ||
          text(record.Email1) ||
          text(record.Email2)
        ).toLowerCase();
      case "phone":
        return (
          text(record.Phone1) ||
          text(record.phone) ||
          text(record.Phone2)
        ).toLowerCase();
      case "country_id":
        return (
          text(record.countries_Country) ||
          text(record.country_id) ||
          text(record.Country_Shipping_Address) ||
          text(record.Country_Billing_Address)
        ).toLowerCase();
      case "category_crm":
        return (
          text(record.category) ||
          text(record.Category) ||
          text(record.category_crm) ||
          text(record.category_crm_ids)
        ).toLowerCase();
      case "client_classifications":
        return text(record.client_classifications).toLowerCase();
      case "total_sales_order":
        return Number(record.total_sales_order ?? record.total_purchase_amount ?? 0);
      default:
        return text(record[field]).toLowerCase();
    }
  }

  private sortContactsForDisplay<T extends Record<string, any>>(records: T[], order?: string): T[] {
    if (!order) return records;

    const [field = "", direction = "asc"] = order.trim().split(/\s+/);
    if (!field) return records;

    const sortDir = direction.toLowerCase() === "desc" ? -1 : 1;

    return [...records].sort((a, b) => {
      const aVal = this.normalizeContactSortValue(a, field);
      const bVal = this.normalizeContactSortValue(b, field);

      if (typeof aVal === "number" || typeof bVal === "number") {
        return ((Number(aVal) || 0) - (Number(bVal) || 0)) * sortDir;
      }

      const aText = String(aVal);
      const bText = String(bVal);
      if (!aText && bText) return 1;
      if (aText && !bText) return -1;

      const comparison = aText.localeCompare(bText, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return comparison * sortDir;
    });
  }

  constructor() {
    // Restore session from localStorage
    const stored = localStorage.getItem('odoo_session');
    if (stored) {
      try {
        const c = JSON.parse(stored);
        // Keep as-is: we allow older formats; ensure types
        this.credentials = {
          sessionId: c.sessionId || '',
          userId: Number(c.userId || 0),
          login: c.login || '',
          password: c.password || ''
        };
        // this.credentials = JSON.parse(stored);

      } catch (e) {
        localStorage.removeItem('odoo_session');
      }
    }

      const storedUser = localStorage.getItem("odoo_user");
    if (storedUser) {
      try {
        const u = JSON.parse(storedUser) as Record<string, unknown>;
        const uid = Number(u.id);
        if (Number.isFinite(uid) && uid > 0) {
          this.user = {
            id: uid,
            name: String(u.name ?? ""),
            email: String(u.email ?? ""),
            login: String(u.login ?? ""),
            partner_id: normalizeOdooMany2oneId(u.partner_id) ?? 0,
            is_admin: Boolean(u.is_admin),
            company_id: normalizeOdooMany2oneId(u.company_id) ?? 1,
          };
        }
      } catch {
        /* ignore */
      }
    }
  
  }
  private persistCredentials() {

    if (this.credentials) {
      localStorage.setItem('odoo_session', JSON.stringify(this.credentials));
    } else {
      localStorage.removeItem('odoo_session');
    }
  }



  public restoreCredentialsAfter2FA() {

    const uid = Number(localStorage.getItem('pending_2fa_uid') || '0');

    this.credentials = {
      sessionId: localStorage.getItem("pending_2fa_session") || "",
      // userId: Number(localStorage.getItem("pending_2fa_uid")),
      userId: uid,
      login: localStorage.getItem("pending_2fa_login") || "",
      // password: localStorage.getItem("pending_2fa_pass") || "",
      password: ''
    };
    this.persistCredentials();
    // localStorage.setItem("odoo_session", JSON.stringify(this.credentials));
  }

  // 🧪 Test basic connectivity to Odoo
  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/web/database/get_list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'call',
          params: {},
          id: Date.now()
        })
      });


      const data = await response.json();

      return response.ok && !data.error;
    } catch (error) {
      console.error('🧪 Connection test failed:', error);
      return false;
    }
  }

  // async authenticate(login: string, password: string): Promise<{ user: OdooUser; sessionId: string }> {

  //   try {
  //     const response = await fetch(`${this.baseURL}/web/session/authenticate`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       credentials: 'include', // Important for session cookies
  //       body: JSON.stringify({
  //         jsonrpc: '2.0',
  //         method: 'call',
  //         params: {
  //           db: 'odoo_15',
  //           login,
  //           password
  //         },
  //         id: Date.now()
  //       })
  //     });

  //     const data = await response.json();

  //     console.log("ankush", data)

  //     if (data.error) {
  //       throw new Error(`Authentication failed: ${data.error.data?.message || 'Invalid credentials'}`);
  //     }

  //     if (!data.result || !data.result.uid) {
  //       throw new Error('Authentication failed: Invalid response');
  //     }

  //     const sessionId =
  //       data.result.session_id ||
  //       response.headers.get('set-cookie')?.match(/session_id=([^;]+)/)?.[1] ||
  //       document.cookie.match(/session_id=([^;]+)/)?.[1] ||
  //       '';
  //       console.log("sessionId",sessionId)
  //     this.credentials = {
  //       sessionId,
  //       // sessionId: data.result.session_id || '',
  //       userId: data.result.uid,
  //       login,
  //       password
  //     };

  //     this.user = {
  //       id: data.result.uid,
  //       name: data.result.name || login,
  //       email: data.result.email || '',
  //       login,
  //       partner_id: data.result.partner_id || 0,
  //       is_admin: data.result.is_admin || false,
  //       company_id: data.result.company_id || 1
  //     };

  //     // Store session in localStorage
  //     localStorage.setItem('odoo_session', JSON.stringify(this.credentials));
  //     localStorage.setItem('odoo_user', JSON.stringify(this.user));

  //     // Refresh CSRF/session info post-auth
  //     try { await this.refreshSessionInfo(); } catch { }
  //     return { user: this.user, sessionId: this.credentials.sessionId };
  //   } catch (error) {
  //     console.error('❌ Authentication error:', error);
  //     throw error;
  //   }
  // }





  // 🚪 LOGOUT


  // async authenticate(login: string, password: string): Promise<{
  //   user: OdooUser | null;
  //   sessionId: string;
  //   twoFARequired: boolean;
  // }> {
  //   try {
  //     const response = await fetch(`${this.baseURL}/web/session/authenticate`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       credentials: "include",
  //       body: JSON.stringify({
  //         jsonrpc: "2.0",
  //         method: "call",
  //         params: { db: "odoo_15", login, password },
  //         id: Date.now(),
  //       }),
  //     });

  //     const data = await response.json();
  //     console.log("🔥 AUTH DATA:", data);

  //     if (data.error) {
  //       throw new Error(data.error.data?.message || "Invalid credentials");
  //     }

  //     // ⭐ wait a bit so browser commits set-cookie header
  //     await new Promise((resolve) => setTimeout(resolve, 50));

  //     // ⭐ correct extraction of session_id
  //     const sessionId =
  //       data.result.session_id ||
  //       document.cookie.match(/session_id=([^;]+)/)?.[1] ||
  //       "";

  //     console.log("SESSION ID CAPTURED:", sessionId);

  //     // ⭐ CASE: 2FA Required
  //     if (!data.result.uid) {
  //       if (!sessionId) {
  //         console.warn("⚠️ 2FA required but sessionId is empty — retry recommended");
  //       }

  //       localStorage.setItem("pending_2fa_login", login);
  //       localStorage.setItem("pending_2fa_pass", password);
  //       localStorage.setItem("pending_2fa_session", sessionId);

  //       return {
  //         user: null,
  //         sessionId,
  //         twoFARequired: true,
  //       };
  //     }

  //     // ⭐ Normal login
  //     const user: OdooUser = {
  //       id: data.result.uid,
  //       name: data.result.name || login,
  //       email: data.result.email || "",
  //       login,
  //       partner_id: data.result.partner_id || 0,
  //       is_admin: data.result.is_admin || false,
  //       company_id: data.result.company_id || 1,
  //     };

  //     this.credentials = {
  //       sessionId,
  //       userId: user.id,
  //       login,
  //       password,
  //     };

  //     this.user = user;

  //     localStorage.setItem("odoo_session", JSON.stringify(this.credentials));
  //     localStorage.setItem("odoo_user", JSON.stringify(user));

  //     try {
  //       await this.refreshSessionInfo();
  //     } catch {}

  //     return {
  //       user,
  //       sessionId,
  //       twoFARequired: false,
  //     };
  //   } catch (error) {
  //     console.error("❌ Authentication error:", error);
  //     throw error;
  //   }
  // }







  // ✅ GET FULL SESSION INFO — used after 2FA verification


  // ✅ FINAL — Get full Odoo session info (works after 2FA also)
  // async getSessionInfo(): Promise<any> {
  //   try {
  //     const resp = await fetch(`${this.baseURL}/web/session/get_session_info`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       credentials: "include",   // 🔥 required for session_id cookie
  //       body: JSON.stringify({
  //         jsonrpc: "2.0",
  //         method: "call",
  //         params: {},
  //         id: Date.now(),
  //       }),
  //     });

  //     const data = await resp.json();
  //     console.log("📌 getSessionInfo:", data);

  //     // 🔥 Normalize response so frontend always receives:
  //     // { uid, name, username/email, is_admin }
  //     const r = data?.result || data?.data?.result || null;

  //     if (!r) return null;

  //     return {
  //       result: {
  //         uid: r.uid,
  //         name: r.name,
  //         username: r.username || r.email,
  //         email: r.email || r.username,
  //         is_admin: r.is_admin,
  //       }
  //     };

  //   } catch (err) {
  //     console.error("❌ getSessionInfo error:", err);
  //     return null;
  //   }
  // }




  async rpc(path: string, payload: any) {
    if (isMaintenanceActive()) {
      window.location.href = "/maintenance";
      return;
    }
    const url = `${this.baseURL}${path}`;
    // const resp = await fetch(url, {
    //   method: "POST",
    //   credentials: "include",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify(payload),
    // });
    // return resp.json();
    try {
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (await handleMaintenanceResponse(resp)) return;
      return resp.json();
    } catch (err) {
      handleNetworkError();
      throw err;
    }
  }

  // get_session_info wrapper (returns normalized result or null)
  async getSessionInfo(): Promise<any | null> {
    try {
      const res = await this.rpc("/web/session/get_session_info", {
        jsonrpc: "2.0",
        method: "call",
        params: {},
        id: Date.now(),
      });

      // many Odoo endpoints return { result: {...} }
      const payload = res?.result ?? res?.data?.result ?? null;
      return payload ? { result: payload } : null;
    } catch (err) {
      console.error("getSessionInfo error:", err);
      return null;
    }
  }

  // Poll get_session_info until uid appears or timeout
  private async pollForSessionInfo(maxAttempts = 6, delayMs = 150) {
    for (let i = 0; i < maxAttempts; i++) {
      const info = await this.getSessionInfo();
      if (info && info.result && info.result.uid) return info;
      // small delay
      await new Promise((r) => setTimeout(r, delayMs));
    }
    return null;
  }

  /**
   * Authenticate (handles 2FA detection)
   * Returns: { user: OdooUser|null, sessionId: string, twoFARequired: boolean }
   */
  async authenticate(login: string, password: string) {
    if (isMaintenanceActive()) {
      window.location.href = "/maintenance";
      return;
    }
    try {
      const response = await fetch(`${this.baseURL}/web/session/authenticate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          method: "call",
          params: { db: require('../config/env').odooDb, login, password },
          id: Date.now(),
        }),
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.data?.message || "Invalid credentials");
      }

      // CASE 1: Login successful (no 2FA)
      if (data.result.uid) {
        const user: OdooUser = {

          id: data.result.uid,
          name: data.result.name || login,
          email: data.result.email || "",
          login,
          partner_id: normalizeOdooMany2oneId(data.result.partner_id) ?? 0,
          is_admin: data.result.is_admin || false,
          company_id: normalizeOdooMany2oneId(data.result.company_id) ?? 1,
        };

        this.credentials = {
          sessionId: "", // cannot read HttpOnly cookie
          userId: user.id,
          login,
          password,
        };

        this.user = user;
        this.persistCredentials();
        // localStorage.setItem("odoo_session", JSON.stringify(this.credentials));
        localStorage.setItem("odoo_user", JSON.stringify(user));

        return { user, twoFARequired: false };
      }

      // CASE 2: 2FA REQUIRED (uid === null)
      if (
        data?.result?.uid === null &&
        data?.result?.name === false &&
        data?.result?.username === false
      ) {
        console.warn("🔥 2FA REQUIRED");

        // Store pending login
        localStorage.setItem("pending_2fa_login", login);
        localStorage.setItem("pending_2fa_pass", password);

        // Extract session_id cookie created by Odoo
        document.cookie.split("; ").forEach(c => {
          if (c.startsWith("session_id=")) {
            const sid = c.split("=")[1];
            localStorage.setItem("pending_2fa_session", sid);
          }
        });

        return { user: null, twoFARequired: true };
      }

    } catch (err: any) {
      console.error("Authentication error:", err);
      throw err;
    }
  }


  // After OTP verification, call this to refresh session info
  async refreshSessionInfo() {
    const info = await this.getSessionInfo();
    if (!info || !info.result || !info.result.uid) {
      return null;
    }
    const u = info.result;
    const user: OdooUser = {
      id: u.uid,
      name: u.name || "",
      email: u.username || u.email || "",
      login: this.credentials?.login || localStorage.getItem("pending_2fa_login") || "",
      partner_id: normalizeOdooMany2oneId(u.partner_id) ?? 0,
      is_admin: !!u.is_admin,
      company_id: normalizeOdooMany2oneId(u.company_id) ?? 1,
    };
    this.user = user;

    // localStorage.setItem("pending_2fa_uid", String(user.id));
    localStorage.setItem("odoo_user", JSON.stringify(user));
    this.credentials = {
      sessionId: localStorage.getItem("pending_2fa_session") || "",
      userId: user.id,
      login: localStorage.getItem("pending_2fa_login") || "",
      password: "" // 🔥 PASSWORD MUST BE EMPTY FOR 2FA USERS
    };
    this.persistCredentials();
    // localStorage.setItem("odoo_session", JSON.stringify(this.credentials));
    // this.credentials = { sessionId: "", userId: user.id };
    return { user };
  }

  /**
   * Synchronously returns the `res.partner` id of the currently authenticated
   * user — the same id Odoo stamps as `author_id` on every `mail.message` this
   * session posts. Used by the Contact History UI to decide whether the
   * viewer is the note's author (and thus should see action-specific
   * hyperlinks like the web-view / PDF-link share URL).
   *
   * Returns `null` if the partner id is not yet loaded (e.g. right after a
   * hard refresh before `getSessionInfo` has resolved).
   */
  getCurrentUserPartnerId(): number | null {
    const pid = this.user?.partner_id;
    return typeof pid === "number" && pid > 0 ? pid : null;
  }

  /**
   * Async variant that guarantees a best-effort round-trip to
   * `/web/session/get_session_info` when the cached partner id is missing
   * (e.g. after persistence restores `credentials` but not the full `user`).
   * The freshly-fetched id is written back into `this.user` so subsequent
   * sync calls return it instantly.
   */
  async fetchCurrentUserPartnerId(): Promise<number | null> {
    const cached = this.getCurrentUserPartnerId();
    if (cached) return cached;
    try {
      const info = await this.getSessionInfo();
      const pid = normalizeOdooMany2oneId(info?.result?.partner_id);
      if (pid && pid > 0) {
        if (this.user) {
          this.user = { ...this.user, partner_id: pid };
        }
        return pid;
      }
      return null;
    } catch {
      return null;
    }
  }

  async logout() {
    try {
      await this.rpc("/web/session/destroy", {
        jsonrpc: "2.0",
        method: "call",

        params: {},

        id: Date.now(),
      });
    } catch (e) {
      console.warn("logout error:", e);
    } finally {
      this.credentials = null;
      this.user = null;
      this.cachedIsSeller = null;
      this.cachedHideNoImage = null;
      this._permsCache = null;
      this._permFailures = 0;
      this._permsCooldownUntil = 0;
      localStorage.removeItem("odoo_session");
      localStorage.removeItem("odoo_user");
      localStorage.removeItem("pending_2fa_login");
      localStorage.removeItem("pending_2fa_pass");
      localStorage.removeItem("pending_2fa_session");
    }
  }


  async verifyOTP(code: string) {
    const sessionId = localStorage.getItem("pending_2fa_session");

    const resp = await fetch(`${this.baseURL}/api/v1/user/verify_2fa`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        session_id: sessionId,
      }),
    });

    return await resp.json();
  }


  // ✅ PUBLIC WRAPPER for exporting selected artworks
  public async fetchArtworksByIds(ids: (string | number)[]): Promise<any[]> {
    const numericIds = ids
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));

    return await this.getArtworksByIds(numericIds);
  }




  // async logout(): Promise<void> {
  //   try {
  //     await fetch(`${this.baseURL}/web/session/destroy`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       credentials: 'include',
  //       body: JSON.stringify({
  //         jsonrpc: '2.0',
  //         method: 'call',
  //         params: {},
  //         id: Date.now()
  //       })
  //     });
  //   } catch (error) {
  //     console.error('Logout error:', error);
  //   } finally {
  //     // Clear local storage
  //     localStorage.removeItem('odoo_session');
  //     localStorage.removeItem('odoo_user');
  //     this.credentials = null;
  //     this.user = null;
  //     this.csrfToken = null;
  //   }
  // }

  // 👤 Get current user's groups (controller first, then RPC fallback)





  async getUserPermissions(): Promise<string[]> {
    const uid = this.user?.id || this.credentials?.userId || null;

    // 1) Serve from the short-lived cache — kills repeated identical calls.
    if (uid && this._permsCache && this._permsCache.uid === uid &&
        Date.now() - this._permsCache.ts < this._permsTTL) {
      return this._permsCache.perms;
    }

    // 2) Circuit breaker — after repeated failures, stop hitting the backend
    //    for a cooldown window. This is the hard guarantee against a storm.
    if (Date.now() < this._permsCooldownUntil) {
      return this._permsCache?.perms ?? [];
    }

    // 3) Single-flight — never run two permission resolutions concurrently.
    if (this._permsInFlight) return this._permsInFlight;

    this._permsInFlight = (async (): Promise<string[]> => {
      try {
        const perms = await this._resolveUserPermissions();
        this._permFailures = 0;
        if (uid) this._permsCache = { uid, perms, ts: Date.now() };
        return perms;
      } catch (e) {
        this._permFailures++;
        if (this._permFailures >= this._permsMaxFailures) {
          this._permsCooldownUntil = Date.now() + this._permsCooldownMs;
          console.warn('[perms] circuit opened after repeated failures — cooling down');
        }
        return this._permsCache?.perms ?? [];
      } finally {
        this._permsInFlight = null;
      }
    })();

    return this._permsInFlight;
  }

  // 🔐 Called once when the backend reports the session is invalid (401/403).
  // Avoids importing the auth store (no circular dependency): it clears the
  // persisted auth state directly and redirects to /login, breaking any loop.
  private _onSessionInvalid(): void {
    if (this._sessionInvalidHandled) return;
    this._sessionInvalidHandled = true;
    try {
      this.credentials = null;
      this.user = null;
      this._permsCache = null;
      localStorage.removeItem('auth-storage');   // zustand persist key (see authStore)
      localStorage.removeItem('odoo_user');
      localStorage.removeItem('odoo_session');
    } catch { /* noop */ }
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }

  // Actual controller-first / RPC-fallback resolution (unchanged behaviour,
  // except an unauthenticated 401/403 now stops cold instead of falling through).
  private async _resolveUserPermissions(): Promise<string[]> {
    const tryController = async (): Promise<string[] | null> => {
      try {
        const response = await fetch(`${this.baseURL}/api/v1/user/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params: {}, id: 'user_permissions' })
        });

        // 🛑 Unauthenticated — DO NOT fall through to the RPC calls.
        if (response.status === 401 || response.status === 403) {
          this._onSessionInvalid();
          return [];
        }

        const data = await response.json();

        // Some Odoo deployments signal an expired session inside a 200 body.
        if (data?.error?.code === 100 || /session\s*expired/i.test(data?.error?.message || '')) {
          this._onSessionInvalid();
          return [];
        }

        if (!response.ok || data?.error) {
          console.warn('Failed to fetch user permissions:', data?.error || response.statusText);
          return null;
        }
        const groups = data?.result?.groups;
        return Array.isArray(groups) ? groups : [];
      } catch (e) {
        console.warn('getUserPermissions controller error:', e);
        return null;
      }
    };

    const tryRPC = async (): Promise<string[]> => {
      try {
        const uid = this.user?.id || this.credentials?.userId;
        if (!uid) {
          if (this.credentials?.login && this.credentials?.password) {
            await this.authenticate(this.credentials.login, this.credentials.password);
          }
        }
        const userId = this.user?.id || this.credentials?.userId;
        if (!userId) return [];

        const usersRead = await this.executeCall('res.users', 'read', [[userId], ['groups_id']]);
        const groupsIds: number[] = Array.isArray(usersRead) && usersRead[0]?.groups_id ? usersRead[0].groups_id : [];
        if (!Array.isArray(groupsIds) || groupsIds.length === 0) return [];

        const externalMap = await this.executeCall('res.groups', 'get_external_id', [groupsIds]);

        const values = externalMap ? Object.values(externalMap) as string[] : [];
        return values.filter(Boolean);
      } catch (e) {
        console.error('getUserPermissions RPC error:', e);
        return [];
      }
    };

    const controllerResult = await tryController();
    if (controllerResult && controllerResult.length > 0) return controllerResult;
    return await tryRPC();
  }

  // 🛒 CART HELPERS
  private normalizeCartItems(items: any[]): any[] {
    if (!Array.isArray(items)) return [];
    return items.map((it: any) => {
      const artworkId = (it?.artwork_id && Array.isArray(it.artwork_id)) ? it.artwork_id[0]
        : (it?.product_template_id && Array.isArray(it.product_template_id)) ? it.product_template_id[0]

          : (it?.product_tmpl_id && Array.isArray(it.product_tmpl_id)) ? it.product_tmpl_id[0]
            : (it?.artwork_id || it?.product_template_id || it?.product_tmpl_id || it?.product_id || it?.id);

      const artistName = Array.isArray(it?.artist_id) ? it.artist_id[1] : (it?.artist || it?.artist_name);
      const ptName = Array.isArray(it?.product_template_id) ? it.product_template_id[1] : undefined;
      const title = it?.title || ptName || it?.name || it?.artwork_name || 'Untitled';
      const price = typeof it?.price === 'number' ? it.price : Number(it?.price || 0);
      const imageUrl = it?.NewMainLR || it?.NewMainHR || it?.image_url || it?.image || it?.main_web_picture || it?.main_picture_hd || null;
      return {
        id: String(artworkId ?? ''),
        IdName: it?.IdName || it?.id_name || undefined,
        title,
        artist: artistName || 'Unknown Artist',
        price: Number.isFinite(price) ? price : 0,
        currency: it?.currency || 'EUR',
        image_url: imageUrl || undefined,
        status: it?.status || it?.state || undefined,
      };
    }).filter((x: any) => x.id);
  }

  // Choose best image URL for an artwork record
  private getBestArtworkImageUrl(artwork: any): string | undefined {
    const url =
      artwork?.NewMainLR ||
      artwork?.NewMainHR ||
      artwork?.main_super_picture_hd ||
      artwork?.main_picture_hd ||
      artwork?.main_web_picture ||
      artwork?.main_a6_picture ||
      artwork?.detail_1_url ||
      artwork?.detail_2_url ||
      artwork?.perspective_url ||
      artwork?.back_pictures ||
      artwork?.frame_picture ||
      artwork?.other_url ||
      artwork?.signatures_pictures;
    if (url && url !== 'false' && url !== false) return url as string;
    if (artwork?.image_1920 && artwork.image_1920 !== 'false' && artwork.image_1920 !== false) {
      return `data:image/png;base64,${artwork.image_1920}`;
    }
    return undefined;
  }

  // Batch fetch artworks by ids for enrichment
  private async getArtworksByIds(ids: number[]): Promise<any[]> {
    if (!ids || ids.length === 0) return [];
    const uniqueIds = Array.from(new Set(ids.filter((n) => Number.isFinite(n))));
    if (uniqueIds.length === 0) return [];
    const tmplFields = [
      'id', 'name', 'res_artist_id', 'IdName', 'artwork_price_combined', 'list_price', 'Status', 'CRMTypeLayout',
      'NewMainLR', 'NewMainHR', 'main_super_picture_hd', 'main_picture_hd', 'main_web_picture', 'main_a6_picture',
      'detail_1_url', 'detail_2_url', 'perspective_url', 'back_pictures', 'frame_picture',
      'other_url', 'signatures_pictures', 'image_1920',
      'CatalogueUrl1', 'CatalogueUrl2', 'CertificateUrl1', 'CertificateUrl2',
      'PublicationUrl1', 'PublicationUrl2', 'OtherDocumentUrl1', 'OtherDocumentUrl2',

      'SizeH', 'SizeL', 'SizeIncH', 'SizeIncL', 'SizeP', 'SizeIncP',

      'Medium', 'Category', 'Type', 'CRMTypeLayout', 'artwork_year', 'entry_date', 'CurrencyRef', 'PriceRef',
      'Location', 'SubLocation', 'Region', 'ConsPurchase', "OwnerShip",
      "ConsignmentCustomer",
      'WeightKg', 'WeightPounds', 'ApprouvedDiscount', 'control_gallery_combined',
      'PriceCHFEnt', 'PriceEUREnt', 'PriceGBPEnt', 'PriceHKDEnt', 'PriceKRWEnt', 'PriceAEDEnt', 'PriceUSDEnt', 'PriceSGDEnt',
    ];
    // First, try reading as product.template ids
    let tmplResults: any[] = await this.executeCall('product.template', 'read', [uniqueIds], { fields: tmplFields }).catch(() => [] as any[]);
    tmplResults = Array.isArray(tmplResults) ? tmplResults : [];
    const foundTmplIds = new Set<number>(tmplResults.map((r: any) => r.id));
    const missingIds = uniqueIds.filter((id) => !foundTmplIds.has(id));
    if (missingIds.length === 0) return tmplResults;

    // Some ids might be product.product variant ids; resolve to template ids
    const prodResults: any[] = await this.executeCall('product.product', 'read', [missingIds], { fields: ['id', 'product_tmpl_id'] }).catch(() => [] as any[]);
    const templateIdsFromVariants: number[] = [];
    for (const p of Array.isArray(prodResults) ? prodResults : []) {
      const tmplId = Array.isArray(p.product_tmpl_id) ? p.product_tmpl_id[0] : p.product_tmpl_id;
      if (Number.isFinite(tmplId)) templateIdsFromVariants.push(tmplId);
    }
    const uniqueTemplateIds = Array.from(new Set(templateIdsFromVariants.filter((n) => Number.isFinite(n))));
    if (uniqueTemplateIds.length === 0) return tmplResults;
    const moreTmpl = await this.executeCall('product.template', 'read', [uniqueTemplateIds], { fields: tmplFields }).catch(() => [] as any[]);
    return tmplResults.concat(Array.isArray(moreTmpl) ? moreTmpl : []);
  }

  //cart items with product details 
  private async enrichCartItems(items: any[]): Promise<any[]> {
    const needIds: number[] = [];
    for (const it of items) {
      const idNum = parseInt(String(it.id), 10);
      const missingTitle = !it.title || it.title === 'Untitled';
      const missingArtist = !it.artist || it.artist === 'Unknown Artist';
      const missingPrice = !it.price || it.price === 0;
      const missingImage = !it.image_url;
      if (Number.isFinite(idNum) && (missingTitle || missingArtist || missingPrice || missingImage)) {
        needIds.push(idNum);
      }
    }
    if (needIds.length === 0) return items;
    const details = await this.getArtworksByIds(needIds);
    const byId: Record<number, any> = {};
    for (const d of details) byId[d.id] = d;
    return items.map((it) => {
      const idNum = parseInt(String(it.id), 10);
      const d = byId[idNum];
      if (!d) return it;
      return {
        ...it,
        IdName: it.IdName || d.IdName || it.id,
        title: it.title && it.title !== 'Untitled' ? it.title : (d.name || it.title),
        artist: it.artist && it.artist !== 'Unknown Artist'
          ? it.artist
          : (Array.isArray(d.res_artist_id) ? (d.res_artist_id[1] || 'Unknown Artist') : (d.res_artist_id || it.artist || 'Unknown Artist')),
        price: it.price && it.price > 0 ? it.price : (Number(d.artwork_price_combined || d.list_price || 0)),
        image_url: it.image_url || this.getBestArtworkImageUrl(d),
        status: it.status || d.Status || it.status,
      };
    });
  }

  // Get current user's cart - optimized to reduce API calls
  async getMyCart(): Promise<any[]> {
    try {
      // Go directly to RPC call since REST endpoints are returning 404
      const items = await this.executeCall('og.cart', 'get_my_cart', []);
      const normalized = this.normalizeCartItems(items);
      return await this.enrichCartItems(normalized);
    } catch (e) {
      console.warn('getMyCart RPC error:', e);
      return [];
    }
  }

  // 🛒 Add item to cart - optimized to reduce API calls
  async addCartItem(artworkId: string | number): Promise<any[]> {
    const idNum = typeof artworkId === 'string' ? parseInt(artworkId, 10) : artworkId;


    try {
      // Go directly to RPC call since REST endpoints are returning 404
      const res = await this.executeCall('og.cart', 'add_item', [idNum]);

      // If addition was successful, get updated cart
      if (res === true) {
        return await this.getMyCart();
      }


      // If we got cart items back, normalize and return them
      if (Array.isArray(res)) {
        const normalized = this.normalizeCartItems(res);
        return await this.enrichCartItems(normalized);
      }


      // Fallback: return empty array if no valid response
      return [];
    } catch (e) {
      console.warn('addCartItem RPC error:', e);
      return [];
    }
  }

  // 🗑️ Remove item from cart - optimized to reduce API calls
  async removeCartItem(artworkId: string | number): Promise<any[]> {
    const idNum = typeof artworkId === 'string' ? parseInt(artworkId, 10) : artworkId;


    try {
      // Go directly to RPC call since REST endpoints are returning 404
      const res = await this.executeCall('og.cart', 'remove_item', [idNum]);


      // If removal was successful, get updated cart
      if (res === true) {
        return await this.getMyCart();
      }


      // If we got cart items back, normalize and return them
      if (Array.isArray(res)) {
        const normalized = this.normalizeCartItems(res);
        return await this.enrichCartItems(normalized);
      }


      // Fallback: return empty array if no valid response
      return [];
    } catch (e) {
      console.warn('removeCartItem RPC error:', e);
      throw e; // Re-throw to be handled by the store
    }
  }

  // 🧹 Clear cart
  async clearCartItems(): Promise<boolean> {
    const controllerPaths = [
      '/api/v1/cart/clear',
      '/api/v1/cart/clear_cart',
    ];
    for (const path of controllerPaths) {
      try {
        const resp = await fetch(`${this.baseURL}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params: {}, id: Date.now() })
        });
        const data = await resp.json();
        if (resp.ok && !data?.error) return true;
      } catch (_) { }
    }
    try {
      const res = await this.executeCall('og.cart', 'clear_cart', []);
      return !!res || res === undefined;
    } catch (e) {
      console.warn('clearCartItems RPC error:', e);
      return false;
    }
  }

  // 🔄 Refresh session info 
  // async refreshSessionInfo(): Promise<void> {
  //   const resp = await fetch(`${this.baseURL}/web/session/get_session_info`, {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     credentials: 'include',
  //     body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params: {}, id: Date.now() })
  //   });
  //   const data = await resp.json();
  //   if (data?.result) {
  //     this.csrfToken = data.result.csrf_token || null;
  //     if (this.credentials) {
  //       this.credentials.sessionId = data.result.session_id || this.credentials.sessionId;
  //       try { localStorage.setItem('odoo_session', JSON.stringify(this.credentials)); } catch { }
  //     }
  //   }
  // }

  // 🔐 CHANGE PASSWORD using Odoo built-in controller 
  async changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Promise<boolean> {
    if (!this.credentials) {
      throw new Error('Not authenticated. Please login first.');
    }
    try {
      const makeRequest = async () => {
        const response = await fetch(`${this.baseURL}/web/session/change_password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            jsonrpc: '2.0',
            params: {
              old_password: oldPassword,
              new_password: newPassword,
              confirm_password: confirmPassword,
            },
            id: Date.now()
          })
        });
        const data = await response.json();
        return { response, data };
      };

      let { response, data } = await makeRequest();

      if (!response.ok || data?.error) {
        const message = data?.error?.data?.message || data?.error?.message || '';
        const isSessionExpired = /expired|invalid session/i.test(message) || response.status === 401 || response.status === 403;
        if (isSessionExpired && this.credentials?.login) {
          // Try to silently re-authenticate with stored password, else fallback to provided oldPassword
          let reauthed = false;
          try {
            if (this.credentials?.password) {
              await this.authenticate(this.credentials.login, this.credentials.password);
              reauthed = true;
            }
          } catch { }
          if (!reauthed) {
            try {
              await this.authenticate(this.credentials.login, oldPassword);
              reauthed = true;
            } catch { }
          }
          if (reauthed) {
            try { await this.refreshSessionInfo(); } catch { }
            ({ response, data } = await makeRequest());
          } else {
            throw new Error('Session expired. Please login again.');
          }
        }
      }

      if (!response.ok || data?.error) {
        const controllerError = new Error(data?.error?.data?.message || data?.error?.message || 'Password change failed');
        // Fallback: try direct RPC without relying on cookies
        try {
          if (this.credentials?.login) {
            // Ensure credentials reflect the old password
            await this.authenticate(this.credentials.login, oldPassword);
          }
          // Attempt with 3 args
          try {
            const rpcRes = await this.executeCall('res.users', 'change_password', [oldPassword, newPassword, confirmPassword]);
            if (rpcRes || rpcRes === undefined) {
              if (this.credentials) {
                this.credentials.password = newPassword;
                try { localStorage.setItem('odoo_session', JSON.stringify(this.credentials)); } catch { }
              }
              return true;
            }
          } catch (_) {
            // Attempt with 2 args (old, new) for older Odoo variants
            const rpcRes2 = await this.executeCall('res.users', 'change_password', [oldPassword, newPassword]);
            if (rpcRes2 || rpcRes2 === undefined) {
              if (this.credentials) {
                this.credentials.password = newPassword;
                try { localStorage.setItem('odoo_session', JSON.stringify(this.credentials)); } catch { }
              }
              return true;
            }
          }
          // Admin fallback: write password directly (requires admin rights)
          const users = await this.executeCall('res.users', 'search_read', [[['login', '=', this.credentials?.login]], ['id']], {});
          const userId = Array.isArray(users) && users[0]?.id;
          if (userId) {
            const wrote = await this.executeCall('res.users', 'write', [[userId], { password: newPassword }], {});
            if (wrote) {
              if (this.credentials) {
                this.credentials.password = newPassword;
                try { localStorage.setItem('odoo_session', JSON.stringify(this.credentials)); } catch { }
              }
              return true;
            }
          }
        } catch (fallbackErr) {
          console.error('❌ RPC fallback change_password failed:', fallbackErr);
        }
        throw controllerError;
      }

      // Update stored password to the new one for future re-auths
      if (this.credentials) {
        this.credentials.password = newPassword;
        try { localStorage.setItem('odoo_session', JSON.stringify(this.credentials)); } catch { }
      }

      return data.result === true || !!data.result;
    } catch (error) {
      console.error('❌ changePassword error:', error);
      throw error;
    }
  }

  // 🔧 EXECUTE ODOO MODEL CALLS via Direct Odoo API
  // private async executeCall(model: string, method: string, args: any[] = [], kwargs: any = {}, retryCount = 0): Promise<any> {
  //   if (!this.credentials) {
  //     console.error('❌ Not authenticated! Please login first.');
  //     throw new Error('Not authenticated. Please login first.');
  //   }

  //   const isTwoFA = !!localStorage.getItem("pending_2fa_session");
  //   const realPassword = isTwoFA ? "" : (this.credentials.password || "");

  //   // Prevent infinite retry loops
  //   if (retryCount > 1) {
  //     console.error('❌ Too many retry attempts, stopping to prevent infinite loop');
  //     throw new Error('Session authentication failed after multiple attempts');
  //   }

  //   try {

  //     const requestBody = {
  //       jsonrpc: '2.0',
  //       method: 'call',
  //       params: {
  //         service: 'object',
  //         method: 'execute_kw',
  //         args: [
  //           'odoo_15', // database
  //           this.credentials.userId, // user_id
  //           // this.credentials.password, // password (required for stateless auth)
  //           realPassword,
  //           model, // model
  //           method, // method
  //           args, // arguments
  //           kwargs // keyword arguments
  //         ]
  //       },
  //       id: Date.now()
  //     };

  //     const response = await fetch(`${this.baseURL}/jsonrpc`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       credentials: 'include', // Keep for any potential cookie support
  //       body: JSON.stringify(requestBody)
  //     });

  //     const data = await response.json();

  //     if (data.error) {
  //       // Session expired, try to re-authenticate ONCE
  //       if (data.error.code === 100 && retryCount === 0) {
  //         const isTwoFA = !!localStorage.getItem("pending_2fa_session");
  //           if (isTwoFA) {
  //             console.warn("⛔ Session expired during 2FA mode — cannot re-auth. Forcing logout.");
  //             throw new Error("Session expired, please login again");
  //           }
  //         if (this.credentials.login && this.credentials.password) {
  //           await this.authenticate(this.credentials.login, this.credentials.password);
  //           return this.executeCall(model, method, args, kwargs, retryCount + 1);
  //         }
  //       }
  //       throw new Error(`Odoo API error: ${JSON.stringify(data.error)}`);
  //     }

  //     return data.result;
  //   } catch (error) {
  //     throw error;
  //   }
  // }
  private async executeCall(

    model: string,
    method: string,
    args: any[] = [],
    kwargs: any = {},
    retryCount = 0
  ): Promise<any> {
    if (!this.credentials) {
      throw new Error("Not authenticated. Please login first.");
    }

    const password = this.credentials.password || "";
    const isTwoFA = password === "" || !!localStorage.getItem("pending_2fa_session");

    if (retryCount > 1) {
      throw new Error("Too many retries");
    }

    try {
      // ===========================
      // COOKIE-SESSION MODE (2FA)
      // ===========================
      if (isTwoFA) {
        const payload = {
          jsonrpc: "2.0",
          method: "call",
          params: { model, method, args, kwargs },
          id: Date.now(),
        };

        const resp = await fetch(`${this.baseURL}/web/dataset/call_kw`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await resp.json();
        if (data.error) {
          throw new Error(`Odoo session RPC error: ${JSON.stringify(data.error)}`);
        }
        return data.result;
      }

      // ===========================
      // NORMAL PASSWORD MODE
      // ===========================
      const rpcBody = {
        jsonrpc: "2.0",
        method: "call",
        params: {
          service: "object",
          method: "execute_kw",
          args: [
            require('../config/env').odooDb,
            this.credentials.userId,
            password, // real password
            model,
            method,
            args,
            kwargs,
          ],
        },
        id: Date.now(),
      };

      const resp = await fetch(`${this.baseURL}/jsonrpc`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rpcBody),
      });

      const data = await resp.json();

      if (data.error) {
        const errMsg = data.error?.message || "";
        const isExpired = data.error.code === 100 || /session/i.test(errMsg);

        if (isExpired && retryCount === 0 && this.credentials.password) {
          await this.authenticate(this.credentials.login!, this.credentials.password);
          return this.executeCall(model, method, args, kwargs, retryCount + 1);
        }

        throw new Error(`Odoo RPC error: ${JSON.stringify(data.error)}`);
      }

      return data.result;
    } catch (err) {
      throw err;

    }
  }


  // 🎨 GET ARTWORKS (FAST) - WITH ALL IMAGE URLS
  async getArtworks(limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = this.STANDARD_ARTWORK_FIELDS;
    const domain = [
      "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];


    // Determine if current user should hide artworks without images (sellers and directors)
    const shouldHide = await this.shouldHideArtworksWithoutImage();

    // Use optimized server wrapper that enforces has_image for sellers and directors

    const result = await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, offset, 'id desc', shouldHide ? true : undefined]
    );
    return result;
  }

  // 🔢 GET ARTWORKS COUNT
  async getArtworksCount(): Promise<number> {
    const baseDomain: any[] = [
      "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const domain = shouldHide
      ? ["&", ...baseDomain, ["has_image_artwork", "=", true]]
      : baseDomain;

    const result = await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );

    return result;
  }


  async getArtworksNoImageLowPrice(limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = this.STANDARD_ARTWORK_FIELDS;
    const domain = [
      "&",
      "&",
      ["active", "=", true],
      ["artwork_price_combined", "<", 1],
      ["has_image_artwork", "=", false]
    ];

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      {
        fields,
        limit,
        offset,
        order: 'id desc'
      }
    );
  }


  async getArtworksNoImageLowPriceCount(): Promise<number> {
    const domain = [
      "&",
      "&",
      ["active", "=", true],
      ["artwork_price_combined", "<", 1],
      ["has_image_artwork", "=", false]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }


  async getArtworksNoImage(limit = 50, offset = 0, order = 'id desc'): Promise<OdooArtwork[]> {
    const fields = this.STANDARD_ARTWORK_FIELDS;
    const domain = [
      "&", "&", "&",
      ["active", "=", true],
      ["has_image_artwork", "=", false],
      ["Status", "ilike", "in stock"],
      ["artwork_price_combined", ">", 1]
    ];

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      {
        fields,
        limit,
        offset,
        order
      }
    );
  }

  async getArtworksNoImageCount(): Promise<number> {
    const domain = [
      "&", "&", "&",
      ["active", "=", true],
      ["has_image_artwork", "=", false],
      ["Status", "ilike", "in stock"],
      ["artwork_price_combined", ">", 1]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }


  async getAllArtworksNoImageForExport(order: 'entry_date asc' | 'entry_date desc' | 'id desc' = 'id desc'): Promise<OdooArtwork[]> {

    const fields = [
      'id', 'name', 'res_artist_id', 'IdName', 'Category', 'Medium',
      'artwork_price_combined', 'PriceRef', 'CurrencyRef', 'Status', 'artwork_year', 'Type', 'entry_date',
      'control_gallery_combined', 'SizeH', 'SizeL', 'SizeIncH', 'SizeIncL',
      'WeightKg', 'WeightPounds', 'ApprouvedDiscount', 'Location', 'SubLocation', 'Region'
    ];

    const domain = [
      "&", "&", "&",
      ["active", "=", true],
      ["has_image_artwork", "=", false],
      ["Status", "ilike", "in stock"],
      ["artwork_price_combined", ">", 1]
    ];

    const BATCH_SIZE = 2000;
    let offset = 0;
    let allRecords: OdooArtwork[] = [];
    let hasMore = true;

    while (hasMore) {
      const batch = await this.executeCall(
        'product.template',
        'search_read',
        [domain],
        {
          fields,
          limit: BATCH_SIZE,
          offset: offset,
          order: order === 'entry_date asc' ? 'entry_date asc, id asc' :
            order === 'entry_date desc' ? 'entry_date desc, id desc' : 'id desc'
        }
      );

      if (batch.length > 0) {
        allRecords = [...allRecords, ...batch];
        offset += BATCH_SIZE;
      } else {
        hasMore = false;
      }


      if (batch.length < BATCH_SIZE) {
        hasMore = false;
      }
    }

    return allRecords;
  }


  async getArtworksLowPrice(limit = 50, offset = 0, order = 'id desc'): Promise<OdooArtwork[]> {
    const fields = this.STANDARD_ARTWORK_FIELDS;
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", "<", 1],
      ["Status", "ilike", "in stock"]
    ];

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      {
        fields,
        limit,
        offset,
        order
      }
    );
  }

  async getArtworksLowPriceCount(): Promise<number> {
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", "<", 1],
      ["Status", "ilike", "in stock"]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }


  async getContactsNoPhoneOrEmail(limit = 50, offset = 0, order = 'id desc'): Promise<any[]> {
    const fields = [
      'id', 'name', 'FirstName', 'LastName', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
      'Id_with_c', 'status_lead_cust_crm', 'customer_rank', 'total_sales_order', 'country_id', 'countries_Country', 'client_classifications'
    ];

    const domain = [
      ["active", "=", true],
      ["is_user", "=", false],
      ["is_company", "=", false],
      ["status_lead_cust_crm", "ilike", "Customer"],

      ["email", "in", [false, ""]],
      ["Email1", "in", [false, ""]],
      ["Email2", "in", [false, ""]],

      ["phone", "in", [false, ""]],
      ["Phone1", "in", [false, ""]],
      ["Phone2", "in", [false, ""]]
    ];

    return await this.executeCall(
      'res.partner',
      'search_read',
      [domain],
      {
        fields,
        limit,
        offset,
        order: order
      }
    );
  }

  async getContactsNoPhoneOrEmailCount(): Promise<number> {
    const domain = [
      ["active", "=", true],
      ["is_user", "=", false],
      ["is_company", "=", false],
      ["status_lead_cust_crm", "ilike", "Customer"],

      ["email", "in", [false, ""]],
      ["Email1", "in", [false, ""]],
      ["Email2", "in", [false, ""]],

      ["phone", "in", [false, ""]],
      ["Phone1", "in", [false, ""]],
      ["Phone2", "in", [false, ""]]
    ];

    return await this.executeCall(
      'res.partner',
      'search_count',
      [domain]
    );
  }

  async getAllContactsNoPhoneOrEmailForExport(order = 'id desc'): Promise<any[]> {
    const fields = [
      'id', 'name', 'FirstName', 'LastName', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
      'Id_with_c', 'status_lead_cust_crm', 'customer_rank', 'total_sales_order', 'country_id', 'countries_Country', 'client_classifications'
    ];

    const domain = [
      ["active", "=", true],
      ["is_user", "=", false],
      ["is_company", "=", false],
      ["status_lead_cust_crm", "ilike", "Customer"],

      ["email", "in", [false, ""]],
      ["Email1", "in", [false, ""]],
      ["Email2", "in", [false, ""]],

      ["phone", "in", [false, ""]],
      ["Phone1", "in", [false, ""]],
      ["Phone2", "in", [false, ""]]
    ];

    const BATCH_SIZE = 2000;
    let offset = 0;
    let allRecords: any[] = [];
    let hasMore = true;

    while (hasMore) {
      const batch = await this.executeCall(
        'res.partner',
        'search_read',
        [domain],
        {
          fields,
          limit: BATCH_SIZE,
          offset: offset,
          order
        }
      );

      if (batch.length > 0) {
        allRecords = [...allRecords, ...batch];
        offset += BATCH_SIZE;
      } else {
        hasMore = false;
      }

      if (batch.length < BATCH_SIZE) {
        hasMore = false;
      }
    }

    return allRecords;
  }

  // 🔐 Cached seller detection based on groups
  private async isSellerUser(): Promise<boolean> {
    if (this.cachedIsSeller !== null) return this.cachedIsSeller;
    try {
      const groups = await this.getUserPermissions();
      const isSeller = groups.includes('artgallery_custom_fields.group_artgallery_seller')
        || (groups.includes('sales_team.group_sale_salesman') && !groups.includes('sales_team.group_sale_manager'));
      this.cachedIsSeller = isSeller;
      return isSeller;
    } catch {
      this.cachedIsSeller = false;
      return false;
    }
  }


  private async shouldHideArtworksWithoutImage(): Promise<boolean> {
    if (this.cachedHideNoImage !== null) return this.cachedHideNoImage;
    try {
      const groups = await this.getUserPermissions();
      const isSuperAdmin = groups.includes('artgallery_custom_fields.group_artgallery_superuser')
        || groups.includes('artgallery_custom_fields.group_artgallery_administrator');
      if (isSuperAdmin) {
        this.cachedHideNoImage = false;
        return false;
      }
      const isSeller = groups.includes('artgallery_custom_fields.group_artgallery_seller')
        || (groups.includes('sales_team.group_sale_salesman') && !groups.includes('sales_team.group_sale_manager'));
      const isDirector = groups.includes('artgallery_custom_fields.group_artgallery_director');

      const isCommunicationService = groups.includes('artgallery_custom_fields.group_artgallery_communication_service');
      this.cachedHideNoImage = isSeller || isDirector || isCommunicationService;
      return this.cachedHideNoImage;

    } catch {
      return false;
    }
  }

  // 🔍 SEARCH ARTWORKS
  private async buildArtworkSearchDomain(query: string): Promise<any[]> {
    const hideWithoutImage = await this.shouldHideArtworksWithoutImage();
    console.log("hideWithoutImage", hideWithoutImage);

    const trimmedQuery = String(query || "").trim();
    const isNumeric = /^\d+$/.test(trimmedQuery);

    let domain: any[] = isNumeric
      ? [
          "&",
          ["artwork_price_combined", ">", 1],
          "|",
          "|",
          ["id", "=", Number(trimmedQuery)],
          ["Id", "=", Number(trimmedQuery)],
          ["certificateid", "=", Number(trimmedQuery)],
        ]
      : [
          "&",
          ["artwork_price_combined", ">", 1],
          "|",
          "|",
          "|",
          "|",
          ["name", "ilike", trimmedQuery],
          ["res_artist_id", "ilike", trimmedQuery],
          ["ArtistName", "ilike", trimmedQuery],
          ["IdName", "ilike", trimmedQuery],
          ["Category", "ilike", trimmedQuery],
        ];

    if (hideWithoutImage) {
      domain = [
        "&",
        ["has_image_artwork", "=", true],
        ...domain,
      ];
    }

    return domain;
  }

  async searchArtworks(
    query: string,
    limit = 100,
    includeBase64Image = true,
    statuses: string[] = [],
  ): Promise<OdooArtwork[]> {
    let domain = await this.buildArtworkSearchDomain(query);
    const validStatuses = statuses.filter((status) => status.trim() !== "");
    if (validStatuses.length > 0) {
      domain = ["&", ["Status", "in", validStatuses], ...domain];
      const hasOnlyNormalStatuses = validStatuses.every((status) => {
        const normalizedStatus = status.trim().toLowerCase().replace(/\s+/g, "_");
        return normalizedStatus !== "sold" && normalizedStatus !== "on_order";
      });
      if (hasOnlyNormalStatuses) {
        domain = ["&", ["entry_date", "!=", false], ...domain];
      }
    }

    const fields = [
      "id",
      "name", // Artwork name
      "res_artist_id", //artist name
      "IdName", // Artwork ID
      "SizeH", // sizeH cm
      "Medium", // Medium
      "control_gallery_combined",// Gallery
      "artwork_price_combined", // Price (legacy)
      "PriceRef", // Price reference
      "CurrencyRef", // Currency reference
      "ApprouvedDiscount", //Max. Discount
      "Category", // Category
      "Type",   // Type
      "CRMTypeLayout",
      "Status", // Status
      "ConsPurchase",
      "OwnerShip",
      "ConsignmentCustomer",
      "Region", // Region
      "SizeL", // sizeL cm
      "SizeP", // sizeP cm
      "Location",
      "SubLocation",
      "artwork_year",
      "entry_date",
      "SizeIncL",
      "SizeIncH",
      "WeightKg",
      "WeightPounds",

      "PriceCHFEnt", "PriceEUREnt", "PriceGBPEnt", "PriceHKDEnt", "PriceKRWEnt", "PriceAEDEnt", "PriceUSDEnt", "PriceSGDEnt",
      "NewMainLR",
      "NewMainHR",
      "main_super_picture_hd",// Main Super HD Pictures
      "main_picture_hd", // Main HD Pictures
      "main_web_picture",
      "main_a6_picture",
    ];

    if (includeBase64Image) {
      fields.push("image_1920");
    }

    const result = await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      { fields, limit, order: 'id desc' }
    );
    return result;
  }

  async searchAllArtworks(query: string): Promise<OdooArtwork[]> {
    const domain = await this.buildArtworkSearchDomain(query);
    const totalMatches = await this.executeCall(
      "product.template",
      "search_count",
      [domain]
    );

    if (!totalMatches || totalMatches <= 0) {
      return [];
    }

    return await this.executeCall("product.template", "search_read", [domain], {
      fields: [...this.STANDARD_ARTWORK_FIELDS, "image_1920"],
      limit: totalMatches,
    });
  }

  // 🎨 GET ARTWORK BY ID
  async searchArtworksGlobally(query: string, limit = 5000): Promise<OdooArtwork[]> {
    const trimmedQuery = String(query || "").trim();
    if (!trimmedQuery || trimmedQuery.length < 2) {
      return [];
    }

    const isNumeric = /^\d+$/.test(trimmedQuery);
    const domain = isNumeric
      ? [
          "&",
          ["active", "=", true],
          "|",
          "|",
          ["id", "=", Number(trimmedQuery)],
          ["Id", "=", Number(trimmedQuery)],
          ["certificateid", "=", Number(trimmedQuery)],
        ]
      : [
          "&",
          ["active", "=", true],
          "|",
          "|",
          "|",
          "|",
          ["name", "ilike", trimmedQuery],
          ["ArtistName", "ilike", trimmedQuery],
          ["IdName", "ilike", trimmedQuery],
          ["default_code", "ilike", trimmedQuery],
          ["Medium", "ilike", trimmedQuery],
        ];

    return await this.executeCall("product.template", "search_read", [domain], {
      fields: [...this.STANDARD_ARTWORK_FIELDS, "image_1920", "default_code"],
      limit,
    });
  }

  async getArtworkById(id: number): Promise<OdooArtwork | null> {
    const fields = [
      "id",
      "name", // Artwork name
      "res_artist_id", //artist name
      "IdName", // Artwork ID
      "SizeH", // sizeH cm
      "Medium", // Medium
      "control_gallery_combined",// Gallery
      "artwork_price_combined", // Price (legacy)
      "PriceRef", // Price reference
      "CurrencyRef", // Currency reference
      "ApprouvedDiscount", //Max. Discount
      "Category", // Category
      "Type",   // Type
      "Status", // Status
      "Region", // Region
      "SizeL", // sizeL cm
      "SizeP", // sizeP cm
      "WeightKg", //weight kg
      "WeightPounds",
      "SizeIncL", // sizeL inch
      "SizeIncH", // sizeH inch
      "SizeIncP", // sizeP inch
      "artwork_year",
      "create_date",
      "entry_date",
      "Location",

      "SubLocation",
      "LtdNumber", // 

      "PriceCHFEnt", "PriceEUREnt", "PriceGBPEnt", "PriceHKDEnt", "PriceKRWEnt", "PriceAEDEnt", "PriceUSDEnt", "PriceSGDEnt",
      "NewMainLR",
      "NewMainHR",
      "main_super_picture_hd",// Main Super HD Pictures
      "signatures_pictures", // Signatures
      "main_picture_hd", // Main HD Pictures
      "detail_1_url", // Det Pictures 1
      "main_web_picture", // Main Web Pictures
      "detail_2_url", // Det Pictures 2
      "main_a6_picture", // Main A6 picture
      "perspective_url", // Perspective Picture
      "main_hd_web_picture", // Main HD Web
      "back_pictures", // Back Pictures
      "frame_picture", // Framed
      "other_url",  // Others url
      "CertificateWording",
      "ProvenanceWordingCrm",
      "CertificateCertAddress",
      "artwork_exhibited",
      "artwork_signed",
      "LiteratureEn",
      "certificateid",
      "certificateurl",
      "image_1920",
      "artist_bio",
      "is_shared_property",
      "artwork_reserved",
      "artwork_reserved_gallery",
      "artwork_reserved_date",
      "artwork_reserved_user",
      "x_auction_house_date",
      "ConsPurchase",
       "OwnerShip",
      "ConsignmentCustomer",
      "VideoPresentationUrl1",
      "VideoPresentationUrl2",
      "VideoDetailUrl1",
      "VideoDetailUrl2",
      "VideoInstallationUrl1",
      "VideoInstallationUrl2",
      "VideoTimelapseUrl1",
      "VideoTimelapseUrl2",
      "VideoDocumentaryUrl1",
      "VideoDocumentaryUrl2",
      "VideoInterviewUrl1",
      "VideoInterviewUrl2",
      "CatalogueUrl1",
      "CatalogueUrl2",
      "CertificateUrl1",
      "CertificateUrl2",
      "PublicationUrl1",
      "PublicationUrl2",
      "OtherDocumentUrl1",
      "OtherDocumentUrl2",
      "CRMTypeLayout",
      "description_sale",
      "artwork_provenance",
    ];

    const result = await this.executeCall(
      'product.template',
      'read',
      [[id]],
      { fields }
    );

    if (result && result.length > 0) {
      const artwork = result[0];
      return artwork;
    } else {
      return null;
    }
  }


  async getArtworkMedias(productId: number) {
    const fields = [
      "id",
      "media_type",
      "media_url",
      "media_name",
      "media_display_name",
      "is_uploaded"
    ];

    const domain = [["product_id", "=", productId]];
    console.log("[media] getArtworkMedias start", { productId });

    try {
      // Preferred Odoo signature: search_read(domain, fields in kwargs)
      const res = await this.executeCall(
        "product.media.bunny",
        "search_read",
        [domain],
        { fields }
      );
      console.log("[media] getArtworkMedias success (kwargs)", {
        productId,
        count: Array.isArray(res) ? res.length : 0,
      });
      return res;
    } catch (errKwargs) {
      console.warn("[media] getArtworkMedias kwargs failed, trying positional fields", errKwargs);
    }

    // Fallback for servers expecting positional fields argument
    const resFallback = await this.executeCall(
      "product.media.bunny",
      "search_read",
      [domain, fields],
      {}
    );
    console.log("[media] getArtworkMedias success (positional)", {
      productId,
      count: Array.isArray(resFallback) ? resFallback.length : 0,
    });
    return resFallback;
  }


  

  async getContacts(limit = 50, offset = 0, order?: string): Promise<OdooContact[]> {

    const commonFields = [
      'id', 'name', 'FirstName', 'LastName', 'email', 'phone', 'city', 'country_id',
      'is_company', 'customer_rank', 'total_sales_order',
      'create_date', 'write_date', 'is_partner_lead', 'is_partner_client',
      'client_classifications',
      'gallery_order_count',
      'control_galleries_ids',
      'Email1', 'Email2',
      'Phone1', 'Phone2',
      'street', 'street2', 'state_id', 'zip',
      'ShippingAddress',
      'Shipping_stree2',
      'Shipping_stree3',
      'ShippingAddress2',
      'Country_Shipping_Address',

      'State_Shipping_Address',

      'City_Shipping_Address',
      'Zip_Shipping_Address',
      'BillingAddress',
      'Country_Billing_Address',
      'State_Billing_Address',

      'City_Billing_Address',

      'Zip_Billing_Address',
      'BirthDate',
      'website', 'function', 'lang', 'mobile', 'company_type', 'parent_id', 'Company', 'seller_ids',
      'international_code_phone1', 'international_code_phone_pro', 'international_code_phone2',
      'birth_date', 'birth_date_partner', 'partner_name', 'email_pro', 'phone_pro', 'social_link',
      'calculated_status_ids',
      'interest_artist_ids',
      'category_crm',
      'category_crm_ids',
      'Vip', 'last_purchase', 'number_of_invoices', 'countries_Country', 'gallery_order_count_filemaker', 'total_sales_order',
      'filemaker_id'
    ];


    const partnerFields = [...commonFields, 'title', 'last_purchase_invoices', 'Id', 'IdEntity', 'Id_with_c', 'show_personal_info'];


    const prospectFields = [...commonFields, 'title_ids', 'Id', 'IdEntity', 'Id_with_c', 'show_personal_info'];

    const domain = [
      ["active", "=", true],
      ["is_user", "=", false],
      ["is_company", "=", false]
    ];


    const prospectDomain = [
      ["active", "=", true],
      ["is_company", "=", false]
    ];



    const [prospects, partners] = await Promise.all([

      this.executeCall('crm.prospect', 'search_read', [prospectDomain], { fields: prospectFields, limit: limit + offset, offset: 0, ...(order ? { order } : {}) }).catch((e) => {
        console.error('❌ [getContacts] Error querying crm.prospect:', e);
        return [];
      }),

      this.executeCall('res.partner', 'search_read', [domain], { fields: partnerFields, limit: limit + offset, offset: 0, ...(order ? { order } : {}) }).catch((e) => {
        console.error('❌ [getContacts] Error querying res.partner:', e);
        return [];
      })
    ]);



    const markedProspects = (Array.isArray(prospects) ? prospects : []).map((c: any) => ({ ...c, _model: 'crm.prospect' }));
    const markedPartners = (Array.isArray(partners) ? partners : []).map((c: any) => ({ ...c, _model: 'res.partner' }));


    let merged = [...markedProspects, ...markedPartners];


    merged = this.sortContactsForDisplay(merged, order);

    const paginated = merged.slice(offset, offset + limit);
    return paginated;
  }

  // 🔢 GET CONTACTS COUNT
  // Note: Only counts contacts that are either leads OR clients (matching the filtered count logic)
  async getContactsCount(): Promise<number> {
    // For res.partner: active, not user, not company, AND (is_partner_lead OR is_partner_client)
    const domain = [
      "&", "&", "&",
      ["active", "=", true],
      ["is_user", "=", false],
      ["is_company", "=", false],
      "|",
      ["is_partner_lead", "=", true],
      ["is_partner_client", "=", true]
    ];

    // For crm.prospect: active, not company, AND (is_partner_lead OR is_partner_client)
    const prospectDomain = [
      "&",
      ["active", "=", true],
      ["is_company", "=", false],
      "|",
      ["is_partner_lead", "=", true],
      ["is_partner_client", "=", true]
    ];

    const [prospectCount, partnerCount] = await Promise.all([
      this.executeCall('crm.prospect', 'search_count', [prospectDomain]).catch(() => 0),
      this.executeCall('res.partner', 'search_count', [domain]).catch(() => 0)
    ]);

    const total = (typeof prospectCount === 'number' ? prospectCount : 0) + (typeof partnerCount === 'number' ? partnerCount : 0);
    return total;
  }


  async getContactById(id: number): Promise<any> {

    const commonFields = [
      'id', 'name', 'FirstName', 'LastName', 'email', 'phone', 'mobile', 'street', 'street2', 'main_street3',
      'city', 'state_id', 'zip', 'country_id', 'website',
      'is_company', 'customer_rank',
      'create_date', 'write_date',

      'client_classifications',
      'gallery_order_count', 'control_galleries_ids',
      'Email1', 'Email2',
      'Phone1', 'Phone2', 'phone_country_code', 'mobile_country_code',
      'filemaker_id',
      'title_ids',
      'ShippingAddress', 'Country_Shipping_Address', 'State_Shipping_Address',
      'City_Shipping_Address', 'Zip_Shipping_Address',
      'BillingAddress', 'Country_Billing_Address', 'State_Billing_Address',
      'City_Billing_Address', 'Zip_Billing_Address',
      'BirthDate',
      'function', 'lang', 'mobile', 'company_type', 'parent_id', 'Company', 'seller_ids',
      'international_code_phone1', 'international_code_phone_pro', 'international_code_phone2',
      'birth_date', 'birth_date_partner', 'partner_name', 'email_pro', 'phone_pro', 'social_link',
      'calculated_status_ids',
      'interest_artist_ids',
      'Vip',
      'last_purchase',
      'number_of_invoices',
      'category_crm',
      'category_crm_ids',
      'countries_Country', 'gallery_order_count_filemaker', 'total_sales_order',
      'is_partner_lead', 'is_partner_client'
    ];


    const partnerFields = [
      ...commonFields,
      'phone_number_full',
      'supplier_rank',
      'address_ids',
      'category_id',
      'image_1920',
      'comment',
      'Id',
      'IdEntity',
      'Id_with_c',
      'seller_fm',
      'last_purchase_invoices',
      'show_personal_info'
    ];


    const prospectFields = [...commonFields, 'Id_with_c', 'IdEntity', 'Id', 'show_personal_info'];


    console.log(`🔍 [getContactById] Querying crm.prospect for ID ${id} with fields:`, prospectFields);
    let contact = await this.executeCall('crm.prospect', 'search_read', [
      [['id', '=', id]],
      prospectFields
    ]).catch((e: any) => {
      console.error(`❌ [getContactById] Error querying crm.prospect for ID ${id}:`, e);
      if (e?.data?.message) {
        console.error(`❌ [getContactById] Error message:`, e.data.message);
      }
      return null;
    });

    if (contact && contact.length > 0) {
      console.log(`✅ Contact found in crm.prospect with ID: ${id}`, contact[0]);

      return { ...contact[0], _model: 'crm.prospect' };
    } else {
      console.log(`ℹ️ [getContactById] No contact found in crm.prospect for ID ${id}, trying res.partner...`);
    }


    contact = await this.executeCall('res.partner', 'search_read', [
      [['id', '=', id]],
      partnerFields
    ]).catch((e) => {
      console.error(`❌ [getContactById] Error querying res.partner for ID ${id}:`, e);
      return null;
    });

    if (contact && contact.length > 0) {
      console.log(`✅ Contact found in res.partner with ID: ${id}`);

      return { ...contact[0], _model: 'res.partner' };
    }

    console.warn(`⚠️ Contact not found in either crm.prospect or res.partner with ID: ${id}`);
    return null;
  }

  // Returns true only when an *active* res.partner row exists for `id`.
  // Used before writing a partner_id FK so the UI can surface a clear
  // "client no longer exists" message instead of letting Odoo throw a
  // misleading wrapper error on FK violation.
  async verifyPartnerActive(partnerId: number): Promise<boolean> {
    if (!Number.isFinite(partnerId) || partnerId <= 0) return false;
    try {
      const count = await this.executeCall(
        'res.partner',
        'search_count',
        [[['id', '=', partnerId], ['active', '=', true]]],
      );
      return Number(count) > 0;
    } catch (e) {
      console.error('❌ [verifyPartnerActive] error checking partner', partnerId, e);
      return false;
    }
  }

  async getPartnerAddressIds(partnerId: number): Promise<number[]> {
    try {
      const res = await this.executeCall('res.partner', 'read', [[partnerId], ['address_ids']]);
      const ids = Array.isArray(res) && res[0]?.address_ids ? res[0].address_ids : [];
      return Array.isArray(ids) ? ids.map((n: any) => Number(n)).filter((n: any) => Number.isFinite(n)) : [];
    } catch (e) {
      console.warn('[OdooClient] Failed to get address_ids for partner', partnerId, e);
      return [];
    }
  }


  async getAddressesByIds(addressIds: number[]): Promise<any[]> {
    if (!Array.isArray(addressIds) || addressIds.length === 0) return [];
    const domain = [['id', 'in', addressIds]];
    const fields = [
      'id', 'LEntity', 'Category', 'SuffixeAddress', 'AddrName', 'Country', 'Email', 'Email1', 'Title', 'FirstName',
      'LastName', 'Company', 'Street1', 'Street2', 'Street3', 'Phone1', 'Phone2', 'BuinessPhone', 'Region', 'City',
      'State', 'ZipCode', 'ContactPerson', 'is_main'
    ];
    try {
      const res = await this.executeCall('res.partner.addresses', 'search_read', [domain, fields], {});
      return Array.isArray(res) ? res : [];
    } catch (e) {
      console.error('[OdooClient] Failed to read addresses by ids', addressIds, e);
      return [];
    }
  }


  async getAddressesForPartner(partnerId: number): Promise<any[]> {
    const ids = await this.getPartnerAddressIds(partnerId);
    if (!ids || ids.length === 0) return [];
    return await this.getAddressesByIds(ids);
  }


  async getAddressesForMultiplePartners(partnerIds: number[]): Promise<Map<number, any[]>> {
    const result = new Map<number, any[]>();
    if (!partnerIds || partnerIds.length === 0) return result;

    const normalizedPartnerIds = Array.from(
      new Set(
        partnerIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );
    if (normalizedPartnerIds.length === 0) return result;

    const missingPartnerIds = normalizedPartnerIds.filter(
      (partnerId) => !this.partnerAddressesCache.has(partnerId),
    );

    if (missingPartnerIds.length === 0) {
      for (const partnerId of normalizedPartnerIds) {
        result.set(partnerId, this.partnerAddressesCache.get(partnerId) || []);
      }
      return result;
    }

    const startTime = Date.now();

    try {

      const PARTNER_BATCH_SIZE = 500;
      const partnerBatches: number[][] = [];
      for (let i = 0; i < missingPartnerIds.length; i += PARTNER_BATCH_SIZE) {
        partnerBatches.push(missingPartnerIds.slice(i, i + PARTNER_BATCH_SIZE));
      }


      const partnerDataResults: any[][] = [];
      for (const batch of partnerBatches) {
        try {
          const result = await this.executeCall('res.partner', 'read', [batch, ['id', 'address_ids']]);
          partnerDataResults.push(Array.isArray(result) ? result : []);
        } catch (error) {
          console.warn('Failed to fetch partner batch, retrying once...');
          await new Promise(resolve => setTimeout(resolve, 500));
          try {
            const retryResult = await this.executeCall('res.partner', 'read', [batch, ['id', 'address_ids']]);
            partnerDataResults.push(Array.isArray(retryResult) ? retryResult : []);
          } catch {
            partnerDataResults.push([]);
          }
        }
      }

      const allAddressIds: number[] = [];
      const partnerToAddressIds = new Map<number, number[]>();
      const addAddressIdsForPartner = (partnerId: number, addressIds: number[]) => {
        if (!Number.isFinite(partnerId) || partnerId <= 0 || addressIds.length === 0) return;
        const existing = partnerToAddressIds.get(partnerId) || [];
        partnerToAddressIds.set(
          partnerId,
          Array.from(new Set([...existing, ...addressIds])),
        );
        allAddressIds.push(...addressIds);
      };

      for (const partnersData of partnerDataResults) {
        if (Array.isArray(partnersData)) {
          for (const p of partnersData) {
            const addressIds = Array.isArray(p.address_ids) ? p.address_ids : [];
            const numericIds = addressIds.map((n: any) => Number(n)).filter((n: any) => Number.isFinite(n));
            addAddressIdsForPartner(Number(p.id), numericIds);
          }
        }
      }

      for (const batch of partnerBatches) {
        const linkedRows = await Promise.all([
          this.executeCall(
            'res.partner.addresses',
            'search_read',
            [[['customer_id', 'in', batch]]],
            { fields: ['id', 'customer_id'], limit: 10000 },
          ).catch(() => []),
          this.executeCall(
            'res.partner.addresses',
            'search_read',
            [[['prospect_id', 'in', batch]]],
            { fields: ['id', 'prospect_id'], limit: 10000 },
          ).catch(() => []),
        ]);

        for (const rows of linkedRows) {
          if (!Array.isArray(rows)) continue;
          for (const row of rows) {
            const addressId = Number(row?.id);
            if (!Number.isFinite(addressId) || addressId <= 0) continue;

            const customerId = Array.isArray(row?.customer_id)
              ? Number(row.customer_id[0])
              : Number(row?.customer_id);
            const prospectId = Array.isArray(row?.prospect_id)
              ? Number(row.prospect_id[0])
              : Number(row?.prospect_id);

            if (Number.isFinite(customerId) && customerId > 0) {
              addAddressIdsForPartner(customerId, [addressId]);
            }
            if (Number.isFinite(prospectId) && prospectId > 0) {
              addAddressIdsForPartner(prospectId, [addressId]);
            }
          }
        }
      }

      console.log('getAddressesForMultiplePartners - Found', allAddressIds.length, 'address IDs in', Date.now() - startTime, 'ms');

      if (allAddressIds.length === 0) return result;


      const uniqueAddressIds = [...new Set(allAddressIds)];
      const missingAddressIds = uniqueAddressIds.filter((id) => !this.addressDetailsCache.has(id));
      const ADDRESS_BATCH_SIZE = 500;
      const addressBatches: number[][] = [];
      for (let i = 0; i < missingAddressIds.length; i += ADDRESS_BATCH_SIZE) {
        addressBatches.push(missingAddressIds.slice(i, i + ADDRESS_BATCH_SIZE));
      }

      const addressResults: any[][] = [];
      for (const batch of addressBatches) {
        try {
          const result = await this.getAddressesByIds(batch);
          addressResults.push(result);
        } catch (error) {
          console.warn('📍 Failed to fetch address batch, retrying once...');
          await new Promise(resolve => setTimeout(resolve, 500));
          try {
            const retryResult = await this.getAddressesByIds(batch);
            addressResults.push(retryResult);
          } catch {
            addressResults.push([]);
          }
        }
      }

      const addressMap = new Map<number, any>();
      for (const addresses of addressResults) {
        for (const addr of addresses) {
          this.addressDetailsCache.set(addr.id, addr);
        }
      }

      for (const addressId of uniqueAddressIds) {
        const address = this.addressDetailsCache.get(addressId);
        if (address) {
          addressMap.set(addressId, address);
        }
      }

      for (const partnerId of missingPartnerIds) {
        const addrIds = partnerToAddressIds.get(partnerId) || [];
        const addresses = addrIds.map(id => addressMap.get(id)).filter(Boolean);
        this.partnerAddressesCache.set(partnerId, addresses);
      }

      for (const partnerId of normalizedPartnerIds) {
        result.set(partnerId, this.partnerAddressesCache.get(partnerId) || []);
      }

      console.log('getAddressesForMultiplePartners - Completed in', Date.now() - startTime, 'ms');
      return result;
    } catch (e) {
      console.error('[OdooClient] Failed to batch fetch addresses', e);
      return result;
    }
  }


  async updateAddress(addressId: number, values: Record<string, any>): Promise<boolean> {
    try {

      const cleaned = Object.entries(values).reduce((acc, [k, v]) => {
        if (typeof v === 'string') {
          const t = v.trim();
          acc[k] = t === '' ? false : t;
        } else {
          acc[k] = v;
        }
        return acc;
      }, {} as Record<string, any>);

      const res = await this.executeCall('res.partner.addresses', 'write', [[addressId], cleaned]);
      return !!res;
    } catch (e) {
      console.error('[OdooClient] Failed to update address', addressId, e);
      return false;
    }
  }

  // ✏️ Update multiple address records (sequential writes)
  async updateAddressesBulk(changes: Array<{ id: number; values: Record<string, any> }>): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;
    for (const c of changes) {
      const ok = await this.updateAddress(c.id, c.values);
      if (ok) success++; else failed++;
    }
    return { success, failed };
  }


  async createAddress(customerId: number, values: Record<string, any>, idWithC?: string): Promise<number | null> {
    try {
      const payload = { ...values, customer_id: customerId } as Record<string, any>;


      if (idWithC && !payload.LEntity) {
        payload.LEntity = idWithC;
      }

      Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (typeof v === 'string') {
          const t = v.trim();
          payload[k] = t === '' ? false : t;
        }
      });
      const res = await this.executeCall('res.partner.addresses', 'create', [payload]);
      const id = typeof res === 'number' ? res : (Array.isArray(res) ? res[0] : null);
      return Number.isFinite(id) ? Number(id) : null;
    } catch (e) {
      console.error('[OdooClient] Failed to create address for customer', customerId, e);
      return null;
    }
  }


  async createAddressesBulk(customerId: number, items: Array<Record<string, any>>): Promise<{ created: number[]; failed: number }> {
    const created: number[] = [];
    let failed = 0;


    let idWithC: string | undefined;
    try {
      const [contact] = await this.executeCall('res.partner', 'read', [[customerId], ['Id_with_c']]);
      if (contact?.Id_with_c && contact.Id_with_c !== false) {
        idWithC = String(contact.Id_with_c);
      }
    } catch (e) {
      console.warn('[OdooClient] Could not fetch Id_with_c for customer', customerId);
    }

    for (const vals of items) {
      const id = await this.createAddress(customerId, vals, idWithC);
      if (id) created.push(id); else failed++;
    }
    return { created, failed };
  }

  // ==================== PROSPECT ADDRESS METHODS ====================


  async getAddressesForProspect(prospectId: number): Promise<any[]> {
    try {
      const domain = [['prospect_id', '=', prospectId]];
      const fields = [
        'id', 'LEntity', 'Category', 'SuffixeAddress', 'AddrName', 'Country', 'Email', 'Email1', 'Title', 'FirstName',
        'LastName', 'Company', 'Street1', 'Street2', 'Street3', 'Phone1', 'Phone2', 'BuinessPhone', 'Region', 'City',
        'State', 'ZipCode', 'ContactPerson', 'is_main'
      ];
      const res = await this.executeCall('res.partner.addresses', 'search_read', [domain, fields], {});
      return Array.isArray(res) ? res : [];
    } catch (e) {
      console.error('[OdooClient] Failed to get addresses for prospect', prospectId, e);
      return [];
    }
  }


  async createAddressForProspect(prospectId: number, values: Record<string, any>): Promise<number | null> {
    try {
      const payload = { ...values, prospect_id: prospectId } as Record<string, any>;

      Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (typeof v === 'string') {
          const t = v.trim();
          payload[k] = t === '' ? false : t;
        }
      });
      const res = await this.executeCall('res.partner.addresses', 'create', [payload]);
      const id = typeof res === 'number' ? res : (Array.isArray(res) ? res[0] : null);
      return Number.isFinite(id) ? Number(id) : null;
    } catch (e) {
      console.error('[OdooClient] Failed to create address for prospect', prospectId, e);
      return null;
    }
  }


  async createAddressesBulkForProspect(prospectId: number, items: Array<Record<string, any>>): Promise<{ created: number[]; failed: number }> {
    const created: number[] = [];
    let failed = 0;
    for (const vals of items) {
      const id = await this.createAddressForProspect(prospectId, vals);
      if (id) created.push(id); else failed++;
    }
    return { created, failed };
  }



  async getContactTitleIds(contactId: number): Promise<number[] | any[]> {
    try {
      const res = await this.executeCall('res.partner', 'search_read', [
        [['id', '=', contactId], ['is_user', '=', false], ['is_company', '=', false]],
        ['title_ids']
      ]);
      if (Array.isArray(res) && res.length > 0) {
        return res[0]?.title_ids || [];
      }
      return [];
    } catch (e) {
      console.error('❌ Failed to fetch title_ids for contact', contactId, e);
      return [];
    }
  }


  async getDashboardStats(): Promise<{ totalContacts: number; totalArtworks: number; totalSales: number }> {
    try {
      const [contactsCount, artworksCount] = await Promise.all([
        this.getContactsCount(),
        this.getArtworksCount()
      ]);


      const totalSales = 0;

      return {
        totalContacts: contactsCount,
        totalArtworks: artworksCount,
        totalSales
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      return { totalContacts: 0, totalArtworks: 0, totalSales: 0 };
    }
  }


  async getDashboardStatsByPeriod(period: 'month' | 'quarter' | 'semester' | 'year'): Promise<{
    newArtworks: number;
    artworksSold: number;
    salesRevenue: number;
    newProspects: number;
    newClients: number;
    totalArtworksInStock: number;
  }> {
    try {
      // Calculate date range based on period
      const now = new Date();


      let startDate: Date;


      switch (period) {
        case 'month':
          // Last 30 days (instead of calendar month)
          startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
          break;
        case 'quarter':
          // Last 90 days (instead of calendar quarter)
          startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
          break;
        case 'semester':
          // Last 180 days (instead of calendar semester)
          startDate = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
          break;
        case 'year':
          // Last 365 days (instead of calendar year)
          startDate = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
          break;
      }


      const startDateStr = startDate.toISOString().split('T')[0];

      // Get artworks in stock count
      const totalArtworksInStock = await this.getArtworksInStockCount();

      // Get new artworks added in this period
      const newArtworks = await this.getNewArtworksCount(startDateStr);


      // Get new contacts in this period
      const [newProspects, newClients] = await Promise.all([
        this.getNewProspectsCount(startDateStr),
        this.getNewClientsCount(startDateStr)
      ]);

      // Get real sales revenue for this period
      const salesRevenue = await this.getSalesRevenue(startDateStr);

      // Real artworks sold count based on SoldDate
      const artworksSold = await this.getSalesCountSince(startDateStr);

      return {
        newArtworks,
        artworksSold,
        salesRevenue,
        newProspects,
        newClients,
        totalArtworksInStock
      };
    } catch (error) {
      console.error('Error getting dashboard stats by period:', error);
      return {
        newArtworks: 0,
        artworksSold: 0,
        salesRevenue: 0,
        newProspects: 0,
        newClients: 0,
        totalArtworksInStock: 0
      };
    }
  }


  async getDashboardStatsFast(period: 'month' | 'quarter' | 'semester' | 'year'): Promise<{
    newArtworks: number;
    artworksSold: number;
    salesRevenue: number;
    newProspects: number;
    newClients: number;
    recentArtworks: Array<{ id: number; title: string; date: string; artist: string; image?: string | false; available?: boolean }>;
  }> {
    // Simple TTL cache (30s) + inflight dedupe per period
    const cacheKey = `dashFast:${period}`;
    const nowMs = Date.now();
    const TTL_MS = 30_000;
    const cached = this.dashboardStatsCache.get(cacheKey);
    if (cached && nowMs - cached.timestamp < TTL_MS) {
      return cached.data;
    }
    const inflight = this.dashboardStatsInflight.get(cacheKey);
    if (inflight) {
      return inflight as Promise<any>;
    }

    const now = new Date();
    const to = now.toISOString().slice(0, 10);
    const from = (() => {
      const d = new Date(now);
      if (period === 'month') d.setDate(d.getDate() - 30);
      else if (period === 'quarter') d.setMonth(d.getMonth() - 3);
      else if (period === 'semester') d.setMonth(d.getMonth() - 6);
      else d.setFullYear(d.getFullYear() - 1);
      return d.toISOString().slice(0, 10);
    })();

    const promise = (async () => {

      try {
        const [res, inventoryArrivals, localNewArtworks] = await Promise.all([
          this.executeCall(
            'product.template',
            'dashboard_stats_fast',
            [],
            { date_from: from, date_to: to, limit_recent: 4 }
          ),
          // The inventory card must use entry_date + current stock status.
          this.getNewArtworksInStock(from, 4).catch(() => [] as OdooArtwork[]),
          this.getNewArtworksCount(from).catch(() => null as number | null),
        ]);

        const arrivals = Array.isArray(inventoryArrivals) ? inventoryArrivals : [];


        const data = {
          newArtworks: localNewArtworks === null
            ? Number(res?.newArtworks || 0)
            : localNewArtworks,
          artworksSold: Number(res?.artworksSold || 0),
          salesRevenue: Number(res?.salesRevenue || 0),
          newProspects: Number(res?.newProspects || 0),
          newClients: Number(res?.newClients || 0),
          recentArtworks: arrivals.length > 0
            ? arrivals.map((artwork: any) => ({
                id: Number(artwork.id),
                title: artwork.name || artwork.title || 'Untitled',
                date: artwork.entry_date || '',
                artist: Array.isArray(artwork.res_artist_id)
                  ? artwork.res_artist_id[1]
                  : (artwork.res_artist_id || artwork.artist || 'Unknown Artist'),
                image: this.getBestArtworkImageUrl(artwork),
                available: true,
              }))
            : (Array.isArray(res?.recentArtworks) ? res.recentArtworks : []),
        };
        this.dashboardStatsCache.set(cacheKey, { timestamp: Date.now(), data });
        return data;
      } catch (error: any) {

        console.warn('Dashboard stats error, falling back to empty counts:', error);

        const fallbackData = {
          newArtworks: 0,
          artworksSold: 0,
          salesRevenue: 0,
          newProspects: 0,
          newClients: 0,
          recentArtworks: [],
        };

        this.dashboardStatsCache.set(cacheKey, { timestamp: Date.now(), data: fallbackData });
        return fallbackData;
      }

    })();

    this.dashboardStatsInflight.set(cacheKey, promise);
    try {
      const result = await promise;
      return result;
    } finally {
      this.dashboardStatsInflight.delete(cacheKey);
    }
  }


  // GET DASHBOARD 
  async getDashboardRecords(
    type: 'newArtworks' | 'newClients' | 'newProspects',
    period: 'month' | 'quarter' | 'semester' | 'year',
    limit: number = 20,
    offset: number = 0
  ): Promise<{ records: Array<any>; totalCount: number }> {
    const now = new Date();
    const to = now.toISOString().slice(0, 10);
    const from = (() => {
      const d = new Date(now);
      if (period === 'month') d.setDate(d.getDate() - 30);
      else if (period === 'quarter') d.setMonth(d.getMonth() - 3);
      else if (period === 'semester') d.setMonth(d.getMonth() - 6);
      else d.setFullYear(d.getFullYear() - 1);
      return d.toISOString().slice(0, 10);
    })();


    const recordTypeMap: Record<string, string> = {
      'newArtworks': 'artworks',
      'newClients': 'clients',
      'newProspects': 'prospects',
    };
    const record_type = recordTypeMap[type] || type;

    try {
      const res = await this.executeCall(
        'product.template',
        'dashboard_stats_records',
        [],
        {
          record_type,
          date_from: from,
          date_to: to,
          limit,
          offset
        }
      );

      return {
        records: Array.isArray(res?.records) ? res.records : [],
        totalCount: Number(res?.total || 0),
      };
    } catch (error: any) {
      console.warn(`Failed to load dashboard records for ${type}:`, error);
      return {
        records: [],
        totalCount: 0,
      };
    }
  }


  // 🧾 GET SALES COUNT SINCE DATE (ARTWORKS SOLD)
  async getSalesCountSince(startDate: string): Promise<number> {
    const domain = [
      '&',
      ['SoldDate', '!=', false],
      ['SoldDate', '>=', startDate]
    ];
    return await this.executeCall(
      'gallery.sales.lines.items',
      'search_count',
      [domain]
    );
  }

  // 🧾 GET SALES LINES SINCE DATE (DETAILS, WITH PAGINATION)
  async getSalesLinesSince(startDate: string, limit = 20, offset = 0): Promise<Array<{ customer_id: any; res_artist_id: any; product_template_id: any; SoldDate: string; SoldPriceGlobalEur: number }>> {
    const fields = ['customer_id', 'res_artist_id', 'product_template_id', 'SoldDate', 'SoldPriceGlobalEur'];
    const domain = [
      '&',
      ['SoldDate', '!=', false],
      ['SoldDate', '>=', startDate]
    ];
    return await this.executeCall(
      'gallery.sales.lines.items',
      'search_read',
      [domain],
      { fields, order: 'SoldDate desc', limit, offset }
    );
  }

  // 🧾 GET SALES LINES WITH TOTAL COUNT (FOR PAGINATION UI)
  async getSalesLinesSincePaginated(startDate: string, limit = 20, offset = 0, order: string = 'SoldDate desc'): Promise<{ lines: Array<{ customer_id: any; res_artist_id: any; product_template_id: any; SoldDate: string; SoldPriceGlobalEur: number }>; totalCount: number }> {
    const fields = ['customer_id', 'res_artist_id', 'product_template_id', 'SoldDate', 'SoldPriceGlobalEur'];
    const domain = [
      '&',
      ['SoldDate', '!=', false],
      ['SoldDate', '>=', startDate]
    ];
    const [lines, totalCount] = await Promise.all([
      this.executeCall('gallery.sales.lines.items', 'search_read', [domain], { fields, order, limit, offset }),
      this.executeCall('gallery.sales.lines.items', 'search_count', [domain])
    ]);
    return { lines, totalCount };
  }

  // 🧾 GET ALL SALES LINES (NO DATE FILTER) WITH PAGINATION AND ORDER
  async getAllSalesLinesPaginated(limit = 20, offset = 0, order: string = 'SoldDate desc'): Promise<{ lines: Array<{ customer_id: any; res_artist_id: any; product_template_id: any; SoldDate: string; SoldPriceGlobalEur: number }>; totalCount: number }> {
    const fields = ['customer_id', 'res_artist_id', 'product_template_id', 'SoldDate', 'SoldPriceGlobalEur'];
    const domain = [
      ['SoldDate', '!=', false]
    ];
    const [lines, totalCount] = await Promise.all([
      this.executeCall('gallery.sales.lines.items', 'search_read', [domain], { fields, order, limit, offset }),
      this.executeCall('gallery.sales.lines.items', 'search_count', [domain])
    ]);
    return { lines, totalCount };
  }

  // 🧾 SOLD LINES BY INVOICE DATE (exclude fee/share), WITH PAGINATION
  async getSoldLinesByInvoiceDatePaginated(
    fromDate?: string,
    toDate?: string,
    limit: number = 20,
    offset: number = 0,
    order: string = 'DateInvoice desc'
  ): Promise<{ lines: Array<{ customer_id: any; res_artist_id: any; product_template_id: any; DateInvoice?: string }>; totalCount: number }> {
    // Prefer stored DateInvoice on lines; fetch opera_order_id to enrich if missing
    const fields = ['customer_id', 'res_artist_id', 'product_template_id', 'DateInvoice', 'opera_order_id'];
    // DateInvoice filters live on the related opera_order_id
    const domain: any[] = [
      '&', ['opera_order_id.DateInvoice', '!=', false],
      '&', ['opera_order_id.OrderType', 'not ilike', 'fee'], ['opera_order_id.OrderType', 'not ilike', 'share']
    ];
    if (fromDate) {
      domain.push('&');
      domain.push(['opera_order_id.DateInvoice', '>=', fromDate]);
      domain.push(['opera_order_id.DateInvoice', '!=', false]);
    }
    if (toDate) {
      domain.push('&');
      domain.push(['opera_order_id.DateInvoice', '<=', toDate]);
      domain.push(['opera_order_id.DateInvoice', '!=', false]);
    }

    const [rawLines, totalCount] = await Promise.all([
      this.executeCall('gallery.sales.lines.items', 'search_read', [domain], { fields, order, limit, offset }),
      this.executeCall('gallery.sales.lines.items', 'search_count', [domain])
    ]);

    // Resolve DateInvoice from related opera.order in one batch (fallback)
    let lines = Array.isArray(rawLines) ? rawLines : [];
    const orderIds: number[] = Array.from(new Set<number>(
      lines
        .map((l: any) => {
          const v = l?.opera_order_id;
          if (Array.isArray(v) && typeof v[0] === 'number') return v[0];
          if (typeof v === 'number') return v;
          return null as number | null;
        })
        .filter((x: number | null): x is number => typeof x === 'number')
    ));

    if (orderIds.length > 0 && !lines.some((l: any) => l && l.DateInvoice)) {
      try {
        const orders = await this.executeCall(
          'opera.order',
          'search_read',
          [[['id', 'in', orderIds]]],
          { fields: ['id', 'DateInvoice'], limit: orderIds.length }
        );
        const idToInvoice: Record<number, string | undefined> = {};
        if (Array.isArray(orders)) {
          for (const o of orders) {
            if (typeof o?.id === 'number') idToInvoice[o.id] = o?.DateInvoice as string | undefined;
          }
        }
        lines = lines.map((l: any) => {
          const v = l?.opera_order_id;
          const oid = Array.isArray(v) && typeof v[0] === 'number' ? v[0] : (typeof v === 'number' ? v : undefined);
          const DateInvoice = l?.DateInvoice || (oid && idToInvoice[oid]) || undefined;
          return { ...l, DateInvoice };
        });
      } catch (_) {
        // If lookup fails, continue without DateInvoice enrichment
      }
    }

    return { lines, totalCount };
  }

  // ===================== CONTACTS: DEDICATED FILTERED SEARCH =====================
  // Resolve artist ids from a mixed array of inputs: numbers, "id:name", or names
  private async resolveArtistIds(inputs?: Array<string | number>): Promise<number[]> {
    if (!inputs || inputs.length === 0) return [];
    const ids: number[] = [];
    const names: string[] = [];
    for (const item of inputs) {
      if (typeof item === 'number') { ids.push(item); continue; }
      const str = String(item);
      if (/^\d+$/.test(str)) { ids.push(parseInt(str, 10)); continue; }
      if (str.includes(':')) {
        const [idPart] = str.split(':', 2);
        if (/^\d+$/.test(idPart)) { ids.push(parseInt(idPart, 10)); continue; }
      }
      names.push(str);
    }
    if (names.length > 0) {
      // Fallback lookup by names
      try {
        const result = await this.executeCall(
          'res.artist',
          'search_read',
          [[['name', 'in', names]]],
          { fields: ['id', 'name'], limit: names.length }
        );
        if (Array.isArray(result)) {
          for (const r of result) if (typeof r.id === 'number') ids.push(r.id);
        }
      } catch (_) { /* ignore */ }
    }
    // Deduplicate
    return Array.from(new Set(ids));
  }

  private async resolveCountryIds(inputs?: Array<string | number>): Promise<number[]> {
    if (!inputs || inputs.length === 0) return [];
    const ids: number[] = [];
    const names: string[] = [];
    for (const item of inputs) {
      if (typeof item === 'number') { ids.push(item); continue; }
      const str = String(item);
      if (/^\d+$/.test(str)) { ids.push(parseInt(str, 10)); continue; }
      names.push(str);
    }
    if (names.length > 0) {
      try {
        const result = await this.executeCall(
          'res.country',
          'search_read',
          [[['name', 'in', names]]],
          { fields: ['id', 'name'], limit: names.length }
        );
        if (Array.isArray(result)) {
          for (const r of result) if (typeof r.id === 'number') ids.push(r.id);
        }
      } catch (_) { /* ignore */ }
    }
    return Array.from(new Set(ids));
  }

  private async resolveStateIds(inputs?: Array<string | number>): Promise<number[]> {
    if (!inputs || inputs.length === 0) return [];
    const ids: number[] = [];
    const names: string[] = [];
    for (const item of inputs) {
      if (typeof item === 'number') { ids.push(item); continue; }
      const str = String(item);
      if (/^\d+$/.test(str)) { ids.push(parseInt(str, 10)); continue; }
      names.push(str);
    }
    if (names.length > 0) {
      try {
        const result = await this.executeCall(
          'res.country.state',
          'search_read',
          [[['name', 'in', names]]],
          { fields: ['id', 'name'], limit: names.length }
        );
        if (Array.isArray(result)) {
          for (const r of result) if (typeof r.id === 'number') ids.push(r.id);
        }
      } catch (_) { /* ignore */ }
    }
    return Array.from(new Set(ids));
  }

  private normalizeTextValues(inputs?: Array<string | number>): string[] {
    return Array.from(
      new Set(
        (inputs || [])
          .map((v) => String(v || "").trim())
          .filter((v) => v && !/^\d+$/.test(v)),
      ),
    );
  }

  private getLocationFilterCacheKey(payload: {
    countries?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    regions?: Array<string | number>;
  }): string {
    return JSON.stringify({
      countries: this.normalizeTextValues(payload.countries).sort(),
      states: this.normalizeTextValues(payload.states).sort(),
      cities: this.normalizeTextValues(payload.cities).sort(),
      regions: this.normalizeTextValues(payload.regions).sort(),
    });
  }

  // Build stable location partner IDs so city/state filters work with both main partner and address tables.
  private async resolvePartnerIdsForLocationFilters(payload: {
    countries?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    regions?: Array<string | number>;
  }): Promise<number[] | null> {
    const cityNames = this.normalizeTextValues(payload.cities);
    const citySet = new Set(cityNames.map((c) => c.toLowerCase()));
    const stateNamesInput = this.normalizeTextValues(payload.states);
    const regionNamesInput = this.normalizeTextValues(payload.regions);
    const countryNamesInput = this.normalizeTextValues(payload.countries);
    const hasLocationFilters =
      countryNamesInput.length > 0 ||
      cityNames.length > 0 ||
      stateNamesInput.length > 0 ||
      regionNamesInput.length > 0;
    if (!hasLocationFilters) return null;

    const cacheKey = this.getLocationFilterCacheKey(payload);
    const cachedIds = this.locationPartnerIdsCache.get(cacheKey);
    if (cachedIds) {
      return cachedIds;
    }

    const [countryIds, stateIds] = await Promise.all([
      this.resolveCountryIds(payload.countries),
      
      this.resolveStateIds(payload.states),
    ]);

    let countryNames = [...countryNamesInput];
    if (countryIds.length > 0) {
      try {
        const rows = await this.executeCall(
          "res.country",
          "search_read",
          [[["id", "in", countryIds]]],
          { fields: ["name"], limit: Math.max(countryIds.length, 20) },
        );
        if (Array.isArray(rows)) {
          countryNames = Array.from(
            new Set([
              ...countryNames,
              ...rows
                .map((r: any) => (typeof r?.name === "string" ? r.name.trim() : ""))
                .filter(Boolean),
            ]),
          );
        }
      } catch (_) {
        // keep input names only
      }
    }

    let stateNames = [...stateNamesInput];
    if (stateIds.length > 0) {
      try {
        const rows = await this.executeCall(
          "res.country.state",
          "search_read",
          [[["id", "in", stateIds]]],
          { fields: ["name"], limit: Math.max(stateIds.length, 20) },
        );
        if (Array.isArray(rows)) {
          stateNames = Array.from(
            new Set([
              ...stateNames,
              ...rows
                .map((r: any) => (typeof r?.name === "string" ? r.name.trim() : ""))
                .filter(Boolean),
            ]),
          );
        }
      } catch (_) {
        // keep input names only
      }
    }

    let directPartnerRows: any[] = [];
    const canUseDirectPartnerRows = regionNamesInput.length === 0;
    if (canUseDirectPartnerRows) {
      try {
        const directDomain: any[] = [
          ["active", "=", true],
          ["is_user", "=", false],
          ["is_company", "=", false],
        ];
        if (countryIds.length > 0) directDomain.push(["country_id", "in", countryIds]);
        if (stateIds.length > 0) directDomain.push(["state_id", "in", stateIds]);

        const rows = await this.executeCall(
          "res.partner",
          "search_read",
          [directDomain],
          { fields: ["id", "city"], limit: 10000 },
        );
        directPartnerRows = Array.isArray(rows)
          ? rows.filter((r: any) => {
            if (citySet.size === 0) return true;
            const city = typeof r?.city === "string" ? r.city.trim().toLowerCase() : "";
            return citySet.has(city);
          })
          : [];
      } catch (_) {
        directPartnerRows = [];
      }
    }

    let addressRows: any[] = [];
    try {
      const addressDomain: any[] = [];
      if (countryNames.length > 0) addressDomain.push(["Country", "in", countryNames]);
      if (stateNames.length > 0) addressDomain.push(["State", "in", stateNames]);
      if (regionNamesInput.length > 0) {
        addressDomain.push(["Region", "in", regionNamesInput]);
      }
      if (citySet.size > 0) {
        addressDomain.push(["City", "!=", false]);
      }

      const rows = await this.executeCall(
        "res.partner.addresses",
        "search_read",
        [addressDomain],
        { fields: ["id", "customer_id", "prospect_id", "City"], limit: 10000 },
      );
      addressRows = Array.isArray(rows)
        ? rows.filter((r: any) => {
          if (citySet.size === 0) return true;
          const city = typeof r?.City === "string" ? r.City.trim().toLowerCase() : "";
          return citySet.has(city);
        })
        : [];
    } catch (_) {
      addressRows = [];
    }

    const addressPartnerIds = addressRows
      .map((r: any) => {
        const customerId = Array.isArray(r?.customer_id) ? r.customer_id[0] : r?.customer_id;
        const prospectId = Array.isArray(r?.prospect_id) ? r.prospect_id[0] : r?.prospect_id;
        return [Number(customerId), Number(prospectId)];
      })
      .flat()
      .filter((id: number) => Number.isFinite(id) && id > 0);

    const ids = Array.from(
      new Set(
        [
          ...directPartnerRows.map((r: any) => Number(r?.id)),
          ...addressPartnerIds,
        ]
          .filter((id: number) => Number.isFinite(id)),
      ),
    );
    console.log("[resolvePartnerIdsForLocationFilters]", {
      countries: payload.countries || [],
      states: payload.states || [],
      cities: payload.cities || [],
      regions: payload.regions || [],
      directPartnerCount: directPartnerRows.length,
      addressPartnerCount: addressPartnerIds.length,
      resolvedCount: ids.length,
    });
    this.locationPartnerIdsCache.set(cacheKey, ids);
    return ids;
  }

  // Normalize ranges from labels or custom min/max to [[min,max], ...]
  private buildTotalPurchaseRanges(labels?: string[], min?: string, max?: string): Array<[number, number | null]> {
    const ranges: Array<[number, number | null]> = [];
    const add = (a?: number, b?: number | null) => {
      if (typeof a === 'number') ranges.push([a, typeof b === 'number' ? b : null]);
    };

    // Robust amount parser: supports K/M suffix and ignores commas/spaces/€
    const parseAmount = (token?: string): number | undefined => {
      if (!token) return undefined;
      const s = token
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^0-9km.-]/g, ''); // keep digits, k/m, dot, minus
      if (!s) return undefined;
      // Extract numeric and optional suffix at end
      const m = s.match(/^(-?\d+(?:\.\d+)?)([km])?$/i);
      if (m) {
        const num = parseFloat(m[1]);
        const suffix = (m[2] || '').toLowerCase();
        if (suffix === 'k') return num * 1_000;
        if (suffix === 'm') return num * 1_000_000;
        return num;
      }
      // If it's a range-like segment (e.g., "100k-500k"), caller handles split
      const n = s.match(/\d+(?:\.\d+)?/);
      return n ? parseFloat(n[0]) : undefined;
    };

    // Parse label presets like '> 1M', '< 100K', '100K - 500K'
    (labels || []).forEach(label => {
      const raw = (label || '').toLowerCase().replace(/[\s,€]/g, '');
      if (!raw) return;
      if (raw.startsWith('>')) {
        const n = parseAmount(raw.slice(1));
        if (typeof n === 'number') add(n, null); // open-ended: min only
        return;
      }
      if (raw.startsWith('<')) {
        const n = parseAmount(raw.slice(1));
        if (typeof n === 'number') add(0, n);
        return;
      }
      if (raw.includes('-')) {
        const [aStr, bStr] = raw.split('-', 2);
        const a = parseAmount(aStr);
        const b = parseAmount(bStr);
        if (typeof a === 'number' && typeof b === 'number') add(a, b);
        return;
      }
      // Single token (e.g., '100k') – treat as upper bound
      const n = parseAmount(raw);
      if (typeof n === 'number') add(0, n);
    });

    // Custom min/max override (also supports K/M)
    const minNum = parseAmount(min);
    const maxNum = parseAmount(max);
    if (typeof minNum === 'number' && typeof maxNum === 'number') {
      add(minNum, maxNum);
    } else if (typeof minNum === 'number') {
      add(minNum, null);
    } else if (typeof maxNum === 'number') {
      add(0, maxNum);
    }

    return ranges;
  }

  private buildLastPurchaseRange(lastPurchaseRanges?: string[]): { years: number; operator: '>' | '<' } | undefined {
    if (!lastPurchaseRanges || lastPurchaseRanges.length === 0) return undefined;
    const token = lastPurchaseRanges[0]; // take first selected
    const m = token.match(/([<>])\s*(\d+)y/i);
    if (m) {
      const op = m[1] as '>' | '<';
      const years = parseInt(m[2], 10);
      return { years, operator: op };
    }
    // Fallback known labels
    const map: Record<string, { years: number; operator: '>' | '<' }> = {
      '< 1y': { years: 1, operator: '<' },
      '< 5y': { years: 5, operator: '<' },
      '< 10y': { years: 10, operator: '<' },
      '> 10y': { years: 10, operator: '>' },
    };
    return map[token as keyof typeof map];
  }

  /** When spent-at gallery is set, purchase ranges apply to opera.order.From, not global total_sales_order. */
  private applySpentAtAndPurchaseRanges(
    requestPayload: Record<string, unknown>,
    payload: {
      spentAtGalleries?: string[];
      purchaseRanges?: string[];
      minPrice?: string;
      maxPrice?: string;
    },
  ): void {
    const spentAtCodes = (payload.spentAtGalleries || [])
      .map((code) => String(code || '').trim())
      .filter(Boolean);
    const purchaseRanges = this.buildTotalPurchaseRanges(
      payload.purchaseRanges,
      payload.minPrice,
      payload.maxPrice,
    );

    if (spentAtCodes.length > 0) {
      requestPayload.spent_at_gallery_codes = spentAtCodes;
      if (purchaseRanges.length > 0) {
        requestPayload.spent_at_purchase_ranges = purchaseRanges;
      }
      delete requestPayload.total_purchase_ranges;
      return;
    }

    if (purchaseRanges.length > 0) {
      requestPayload.total_purchase_ranges = purchaseRanges;
    }
  }

  async searchPartnersWithFilters(payload: {
    purchaseArtists?: Array<string | number>;
    interestArtists?: Array<string | number>;
    interestDateFrom?: string;
    interestDateTo?: string;
    clientTypes?: string[];
    countries?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    regions?: Array<string | number>;
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    lastPurchaseRanges?: string[];
    artworkPurchaseRanges?: string[];
    galleries?: string[];
    galleryIds?: Array<string | number>;
    spentAtGalleries?: string[];
    type?: 'all' | 'clients' | 'prospects';
    scope?: 'my_contact' | 'my_gallery' | 'worldwide';
    searchTerm?: string; // Added: support search + filters together
  }, limit = 20, offset = 0, order?: string): Promise<{ contacts: any[]; totalCount: number }> {
    // Transform UI payload to backend contract
    const purchase_artist_ids = await this.resolveArtistIds(payload.purchaseArtists);
    const interest_artist_ids = await this.resolveArtistIds(payload.interestArtists);
    const country_ids = await this.resolveCountryIds(payload.countries);
    const state_ids = await this.resolveStateIds(payload.states);
    const client_types = (payload.clientTypes || [])
      .map((type) => String(type || "").trim())
      .filter(Boolean);
    const state_names = this.normalizeTextValues(payload.states);
    const city_names = (payload.cities || [])
      .map((c) => String(c || "").trim())
      .filter(Boolean);
    const last_purchase_range = this.buildLastPurchaseRange(payload.lastPurchaseRanges);

    const requestPayload: any = {
      base_domain: [['active', '=', true], ['is_user', '=', false], ['is_company', '=', false]],
      purchase_artist_ids,
      interest_artist_ids,
      last_purchase_range,
      country_ids,
      state_ids,
      state_names,
      city_names,
      client_types,
      limit,
      offset,
      type: payload.type,
    };
    this.applySpentAtAndPurchaseRanges(requestPayload, payload);

    if (payload.interestDateFrom) requestPayload.interest_date_from = payload.interestDateFrom;
    if (payload.interestDateTo) requestPayload.interest_date_to = payload.interestDateTo;

    const hasStrictLocationFilters =
      this.normalizeTextValues(payload.regions).length > 0 ||
      this.normalizeTextValues(payload.cities).length > 0 ||
      this.normalizeTextValues(payload.states).length > 0;
    const locationPartnerIds = await this.resolvePartnerIdsForLocationFilters(payload);
    if (hasStrictLocationFilters && Array.isArray(locationPartnerIds) && locationPartnerIds.length > 0) {
      requestPayload.base_domain = [
        ...requestPayload.base_domain,
        ['id', 'in', locationPartnerIds],
      ];
      // Keep country_ids when selected so country + region/state/city combinations
      // cannot widen back to the whole dataset if the resolved ID set is noisy.
      // Keep state_ids/city_names so backend can still apply strict location filtering.
      // city_names retained intentionally.
    }

    if (order) {
      requestPayload.order = order;
      console.log('🔀 [searchPartnersWithFilters] Adding order to payload:', order);
    } else {
      console.log('🔀 [searchPartnersWithFilters] No order provided');
    }

    if (payload.scope) {
      requestPayload.scope = payload.scope;
    }


    if (payload.searchTerm && payload.searchTerm.trim().length >= 2) {
      requestPayload.search_term = payload.searchTerm.trim();
      console.log('[searchPartnersWithFilters] Adding search_term:', requestPayload.search_term);
    }

    // Map artwork purchase price bands directly; backend normalizes labels
    if (Array.isArray(payload.artworkPurchaseRanges) && payload.artworkPurchaseRanges.length > 0) {
      requestPayload.artwork_purchase_ranges = payload.artworkPurchaseRanges;
    }

    // Map gallery filters if provided (prefer ids if present)
    if (Array.isArray(payload.galleryIds) && payload.galleryIds.length > 0) {
      requestPayload.gallery_ids = payload.galleryIds;
    } else if (Array.isArray(payload.galleries) && payload.galleries.length > 0) {
      requestPayload.gallery_names = payload.galleries;
    }

    // Safety: remove empty arrays to avoid backend domain builder adding invalid leaves
    if (!purchase_artist_ids || purchase_artist_ids.length === 0) delete requestPayload.purchase_artist_ids;
    if (!interest_artist_ids || interest_artist_ids.length === 0) delete requestPayload.interest_artist_ids;
    if (!country_ids || country_ids.length === 0) delete requestPayload.country_ids;
    if (!state_ids || state_ids.length === 0) delete requestPayload.state_ids;
    if (!state_names || state_names.length === 0) delete requestPayload.state_names;
    if (!city_names || city_names.length === 0) delete requestPayload.city_names;
    if (!client_types || client_types.length === 0) delete requestPayload.client_types;
    if (!requestPayload.total_purchase_ranges || (requestPayload.total_purchase_ranges as unknown[]).length === 0) {
      delete requestPayload.total_purchase_ranges;
    }
    if (!requestPayload.spent_at_purchase_ranges || (requestPayload.spent_at_purchase_ranges as unknown[]).length === 0) {
      delete requestPayload.spent_at_purchase_ranges;
    }
    if (!requestPayload.spent_at_gallery_codes || (requestPayload.spent_at_gallery_codes as unknown[]).length === 0) {
      delete requestPayload.spent_at_gallery_codes;
    }
    if (!last_purchase_range) delete requestPayload.last_purchase_range;
    if (!requestPayload.gallery_ids && !requestPayload.gallery_names) {
      delete requestPayload.gallery_ids;
      delete requestPayload.gallery_names;
    }
    if (!requestPayload.artwork_purchase_ranges || requestPayload.artwork_purchase_ranges.length === 0) {
      delete requestPayload.artwork_purchase_ranges;
    }

    // First, get the combined count
    let totalCount = await this.executeCall('res.partner', 'search_partners_with_filters_count', [requestPayload]);
    let effectivePayload = requestPayload;

    // Fallback behavior: if combined returns 0 and both filters are present,
    // fall back to the non-empty side (prefer purchases, then interests)
    // Make sure to preserve the order parameter in fallback payloads
    if (
      totalCount === 0 &&
      Array.isArray(purchase_artist_ids) && purchase_artist_ids.length > 0 &&
      Array.isArray(interest_artist_ids) && interest_artist_ids.length > 0
    ) {
      const purchaseOnly = { ...requestPayload, interest_artist_ids: [] };
      // Preserve order if it exists
      if (order) {
        purchaseOnly.order = order;
      }
      const purchaseOnlyCount = await this.executeCall('res.partner', 'search_partners_with_filters_count', [purchaseOnly]);
      if (typeof purchaseOnlyCount === 'number' && purchaseOnlyCount > 0) {
        effectivePayload = purchaseOnly;
        totalCount = purchaseOnlyCount;
      } else {
        const interestOnly = { ...requestPayload, purchase_artist_ids: [] };
        // Preserve order if it exists
        if (order) {
          interestOnly.order = order;
        }
        const interestOnlyCount = await this.executeCall('res.partner', 'search_partners_with_filters_count', [interestOnly]);
        if (typeof interestOnlyCount === 'number' && interestOnlyCount > 0) {
          effectivePayload = interestOnly;
          totalCount = interestOnlyCount;
        }
      }
    }
    let contacts = await this.executeCall('res.partner', 'search_partners_with_filters', [effectivePayload, offset, limit]);

    console.log("contacts xxx",contacts);
    if (Array.isArray(contacts) && effectivePayload.order) {
      contacts = this.sortContactsForDisplay(contacts, effectivePayload.order);
    }
    console.log('🔀 [searchPartnersWithFilters] Received', contacts?.length || 0, 'contacts');
    if (contacts && contacts.length > 0 && effectivePayload.order) {
      console.log('🔀 [searchPartnersWithFilters] First contact total_sales_order:', contacts[0]?.total_sales_order);
      console.log('🔀 [searchPartnersWithFilters] Last contact total_sales_order:', contacts[contacts.length - 1]?.total_sales_order);
    }

    return { contacts: Array.isArray(contacts) ? contacts : [], totalCount: typeof totalCount === 'number' ? totalCount : 0 };
  }


  private async prepareExportPayload(payload: {
    purchaseArtists?: Array<string | number>;
    interestArtists?: Array<string | number>;
    interestDateFrom?: string;
    interestDateTo?: string;
    clientTypes?: string[];
    countries?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    regions?: Array<string | number>;
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    lastPurchaseRanges?: string[];
    artworkPurchaseRanges?: string[];
    galleries?: string[];
    galleryIds?: Array<string | number>;
    spentAtGalleries?: string[];
    type?: 'all' | 'clients' | 'prospects';
    scope?: 'my_contact' | 'my_gallery' | 'worldwide';
    searchTerm?: string;
    seller_ids?: Array<string | number>;
  }, order?: string): Promise<any> {
    const purchase_artist_ids = await this.resolveArtistIds(payload.purchaseArtists);
    const interest_artist_ids = await this.resolveArtistIds(payload.interestArtists);
    const country_ids = await this.resolveCountryIds(payload.countries);
    const state_ids = await this.resolveStateIds(payload.states);
    const client_types = (payload.clientTypes || [])
      .map((type) => String(type || "").trim())
      .filter(Boolean);
    const state_names = this.normalizeTextValues(payload.states);
    const city_names = (payload.cities || [])
      .map((c) => String(c || "").trim())
      .filter(Boolean);
    const last_purchase_range = this.buildLastPurchaseRange(payload.lastPurchaseRanges);

    const requestPayload: any = {
      base_domain: [['active', '=', true], ['is_user', '=', false], ['is_company', '=', false]],
      purchase_artist_ids,
      interest_artist_ids,
      last_purchase_range,
      country_ids,
      state_ids,
      state_names,
      city_names,
      client_types,
      type: payload.type,
    };
    this.applySpentAtAndPurchaseRanges(requestPayload, payload);

    if (payload.interestDateFrom) requestPayload.interest_date_from = payload.interestDateFrom;
    if (payload.interestDateTo) requestPayload.interest_date_to = payload.interestDateTo;

    const hasStrictLocationFilters =
      this.normalizeTextValues(payload.regions).length > 0 ||
      this.normalizeTextValues(payload.cities).length > 0 ||
      this.normalizeTextValues(payload.states).length > 0;
    const locationPartnerIds = await this.resolvePartnerIdsForLocationFilters(payload);
    if (hasStrictLocationFilters && Array.isArray(locationPartnerIds) && locationPartnerIds.length > 0) {
      requestPayload.base_domain = [
        ...requestPayload.base_domain,
        ['id', 'in', locationPartnerIds],
      ];
      // Keep state_ids/city_names so backend can still apply strict location filtering.
      // city_names retained intentionally.
    }

    if (order) requestPayload.order = order;
    if (payload.scope) requestPayload.scope = payload.scope;


    if (payload.searchTerm && payload.searchTerm.trim().length >= 2) {
      requestPayload.search_term = payload.searchTerm.trim();
    }

    if (Array.isArray(payload.artworkPurchaseRanges) && payload.artworkPurchaseRanges.length > 0) {
      requestPayload.artwork_purchase_ranges = payload.artworkPurchaseRanges;
    }

    if (Array.isArray(payload.galleryIds) && payload.galleryIds.length > 0) {
      requestPayload.gallery_ids = payload.galleryIds;
    } else if (Array.isArray(payload.galleries) && payload.galleries.length > 0) {
      requestPayload.gallery_names = payload.galleries;
    }

    if (Array.isArray(payload.seller_ids) && payload.seller_ids.length > 0) {
      requestPayload.seller_ids = payload.seller_ids;
    }

    if (!purchase_artist_ids || purchase_artist_ids.length === 0) delete requestPayload.purchase_artist_ids;
    if (!interest_artist_ids || interest_artist_ids.length === 0) delete requestPayload.interest_artist_ids;
    if (!country_ids || country_ids.length === 0) delete requestPayload.country_ids;
    if (!state_ids || state_ids.length === 0) delete requestPayload.state_ids;
    if (!state_names || state_names.length === 0) delete requestPayload.state_names;
    if (!city_names || city_names.length === 0) delete requestPayload.city_names;
    if (!client_types || client_types.length === 0) delete requestPayload.client_types;
    if (!requestPayload.total_purchase_ranges || requestPayload.total_purchase_ranges.length === 0) {
      delete requestPayload.total_purchase_ranges;
    }
    if (!requestPayload.spent_at_purchase_ranges || requestPayload.spent_at_purchase_ranges.length === 0) {
      delete requestPayload.spent_at_purchase_ranges;
    }
    if (!requestPayload.spent_at_gallery_codes || requestPayload.spent_at_gallery_codes.length === 0) {
      delete requestPayload.spent_at_gallery_codes;
    }
    if (!last_purchase_range) delete requestPayload.last_purchase_range;
    if (!requestPayload.gallery_ids && !requestPayload.gallery_names) {
      delete requestPayload.gallery_ids;
      delete requestPayload.gallery_names;
    }
    if (!requestPayload.artwork_purchase_ranges || requestPayload.artwork_purchase_ranges.length === 0) {
      delete requestPayload.artwork_purchase_ranges;
    }

    return requestPayload;
  }


  async exportContactsFast(
    payload: {
      purchaseArtists?: Array<string | number>;
      interestArtists?: Array<string | number>;
      interestDateFrom?: string;
      interestDateTo?: string;
      clientTypes?: string[];
      countries?: Array<string | number>;
      states?: Array<string | number>;
      cities?: Array<string | number>;
      regions?: Array<string | number>;
      purchaseRanges?: string[];
      minPrice?: string;
      maxPrice?: string;
      lastPurchaseRanges?: string[];
      artworkPurchaseRanges?: string[];
      galleries?: string[];
      galleryIds?: Array<string | number>;
      type?: 'all' | 'clients' | 'prospects';
      scope?: 'my_contact' | 'my_gallery' | 'worldwide';
      searchTerm?: string;
      seller_ids?: Array<string | number>;
    },
    order?: string,
    onProgress?: (loaded: number, total: number, message: string) => void
  ): Promise<any[]> {


    const BATCH_SIZE = 1000;
    const PARALLEL_BATCHES = 1;
    const exportType = payload.type || 'all';

    const startTime = Date.now();
    console.log('exportContactsFast - Type:', exportType, 'SearchTerm:', payload.searchTerm);

    onProgress?.(0, 0, "Preparing export...");
    const basePayload = await this.prepareExportPayload(payload, order);


    onProgress?.(0, 0, "Counting...");
    const totalCount = await this.executeCall('res.partner', 'search_partners_with_filters_count', [basePayload]);

    console.log('exportContactsFast - Backend count returned:', totalCount, 'in', Date.now() - startTime, 'ms');

    if (!totalCount || totalCount === 0) {
      return [];
    }

    onProgress?.(0, totalCount, `Fetching ${totalCount.toLocaleString()} contacts...`);

    const totalBatches = Math.ceil(totalCount / BATCH_SIZE);
    const allContacts: any[] = [];
    let completedBatches = 0;


    const fetchBatchWithRetry = async (offset: number, retries = 2): Promise<any[]> => {
      const batchPayload = { ...basePayload, limit: BATCH_SIZE, offset };
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const contacts = await this.executeCall('res.partner', 'search_partners_with_filters', [batchPayload, offset, BATCH_SIZE]);

          console.log("contactsXXXX", contacts);

          return Array.isArray(contacts) ? contacts : [];
        } catch (error) {
          console.warn(`Batch at offset ${offset} failed (attempt ${attempt}/${retries})`);
          if (attempt < retries) {

            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      console.error(`Batch at offset ${offset} failed after ${retries} retries`);
      return [];
    };

    for (let waveStart = 0; waveStart < totalBatches; waveStart += PARALLEL_BATCHES) {
      const waveEnd = Math.min(waveStart + PARALLEL_BATCHES, totalBatches);
      const batchPromises: Promise<any[]>[] = [];

      for (let batchIndex = waveStart; batchIndex < waveEnd; batchIndex++) {
        const offset = batchIndex * BATCH_SIZE;
        batchPromises.push(fetchBatchWithRetry(offset));
      }

      const batchResults = await Promise.all(batchPromises);

      for (const contacts of batchResults) {
        allContacts.push(...contacts);
        completedBatches++;
      }

      const loadedCount = Math.min(completedBatches * BATCH_SIZE, totalCount);
      onProgress?.(loadedCount, totalCount, `Loaded ${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()}`);

    }

    console.log('exportContactsFast - Fetch completed in', Date.now() - startTime, 'ms');


    const partnerModelCount = allContacts.filter(c => (c.model_type || c.source_model || '').includes('partner')).length;
    const prospectModelCount = allContacts.filter(c => (c.model_type || c.source_model || '').includes('prospect')).length;

    const isLeadCount = allContacts.filter(c => c.is_partner_lead === true).length;
    const isClientCount = allContacts.filter(c => c.is_partner_client === true).length;

    console.log('exportContactsFast - Raw contacts fetched:', allContacts.length);
    console.log('exportContactsFast - By model: res.partner=', partnerModelCount, ', crm.prospect=', prospectModelCount);
    console.log('exportContactsFast - By flag: is_partner_lead=', isLeadCount, ', is_partner_client=', isClientCount);


    let contactsToProcess = allContacts;
    if (exportType === 'prospects') {

      contactsToProcess = allContacts.filter(c => c.is_partner_lead === true);
      console.log('exportContactsFast - After prospect filter (is_partner_lead=True):', contactsToProcess.length);
    } else if (exportType === 'clients') {
      contactsToProcess = allContacts.filter(c => c.is_partner_client === true);
      console.log('exportContactsFast - After client filter (is_partner_client=True):', contactsToProcess.length);
    }

    const sortedContacts = [...contactsToProcess].sort((a, b) => {
      const aModel = a.model_type || a.source_model || '';
      const bModel = b.model_type || b.source_model || '';

      if (aModel.includes('partner') && !bModel.includes('partner')) return -1;
      if (!aModel.includes('partner') && bModel.includes('partner')) return 1;
      return 0;
    });


    const seenIdWithC = new Set<string>();
    const seenNameEmail = new Set<string>();
    const seenNameOnly = new Set<string>();

    const uniqueContacts = sortedContacts.filter(contact => {

      const idWithC = contact.Id_with_c;
      if (idWithC) {
        if (seenIdWithC.has(idWithC)) {
          return false;
        }
        seenIdWithC.add(idWithC);
      }


      const name = (contact.name || '').toLowerCase().trim();
      const email = (contact.email || contact.Email1 || contact.Email2 || '').toLowerCase().trim();

      if (name && email) {
        const nameEmailKey = `${name}|||${email}`;
        if (seenNameEmail.has(nameEmailKey)) {
          return false;
        }
        seenNameEmail.add(nameEmailKey);

        seenNameOnly.add(name);
        return true;
      }


      if (name) {
        if (seenNameOnly.has(name)) {

          if (exportType === 'all') {
            const modelType = contact.model_type || contact.source_model || '';
            if (modelType.includes('prospect')) {
              return false;
            }
          }

          if (exportType !== 'all') {

          }
        }
        seenNameOnly.add(name);
        return true;
      }


      return true;
    });

    const removedCount = contactsToProcess.length - uniqueContacts.length;
    console.log('exportContactsFast - Duplicates removed:', removedCount);
    console.log('exportContactsFast - Final unique count:', uniqueContacts.length);


    onProgress?.(uniqueContacts.length, uniqueContacts.length, `${uniqueContacts.length.toLocaleString()} unique contacts`);

    return uniqueContacts;
  }


  async searchPartnersForExport(payload: {
    purchaseArtists?: Array<string | number>;
    interestArtists?: Array<string | number>;
    interestDateFrom?: string;
    interestDateTo?: string;
    clientTypes?: string[];
    countries?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    lastPurchaseRanges?: string[];
    artworkPurchaseRanges?: string[];
    galleries?: string[];
    galleryIds?: Array<string | number>;
    type?: 'all' | 'clients' | 'prospects';
    scope?: 'my_contact' | 'my_gallery' | 'worldwide';
  }, limit = 2000, offset = 0, order?: string): Promise<any[]> {
    const basePayload = await this.prepareExportPayload(payload, order);
    const requestPayload = { ...basePayload, limit, offset };
    const contacts = await this.executeCall('res.partner', 'search_partners_with_filters', [requestPayload, offset, limit]);
    return Array.isArray(contacts) ? contacts : [];
  }

  // 🧮 Count breakdown for filters (all/client/lead)
  async getContactsCountBreakdownWithFilters(payload: {
    purchaseArtists?: Array<string | number>;
    interestArtists?: Array<string | number>;
    interestDateFrom?: string;
    interestDateTo?: string;
    clientTypes?: string[];
    countries?: Array<string | number>;
    states?: Array<string | number>;
    cities?: Array<string | number>;
    regions?: Array<string | number>;
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    lastPurchaseRanges?: string[];
    artworkPurchaseRanges?: string[];
    galleries?: string[];
    galleryIds?: Array<string | number>;
    spentAtGalleries?: string[];
    scope?: 'my_contact' | 'my_gallery' | 'worldwide';
    searchTerm?: string;
  }): Promise<{ total: number; client: number; lead: number }> {
    const purchase_artist_ids = await this.resolveArtistIds(payload.purchaseArtists);
    const interest_artist_ids = await this.resolveArtistIds(payload.interestArtists);
    const country_ids = await this.resolveCountryIds(payload.countries);
    const state_ids = await this.resolveStateIds(payload.states);
    const client_types = (payload.clientTypes || [])
      .map((type) => String(type || "").trim())
      .filter(Boolean);
    const state_names = this.normalizeTextValues(payload.states);
    const city_names = (payload.cities || [])
      .map((c) => String(c || "").trim())
      .filter(Boolean);
    const last_purchase_range = this.buildLastPurchaseRange(payload.lastPurchaseRanges);

    const requestPayload: any = {
      base_domain: [['active', '=', true], ['is_user', '=', false], ['is_company', '=', false]],
      purchase_artist_ids,
      interest_artist_ids,
      last_purchase_range,
      country_ids,
      state_ids,
      state_names,
      city_names,
      client_types
    };
    this.applySpentAtAndPurchaseRanges(requestPayload, payload);

    if (payload.interestDateFrom) requestPayload.interest_date_from = payload.interestDateFrom;
    if (payload.interestDateTo) requestPayload.interest_date_to = payload.interestDateTo;

    const hasStrictLocationFilters =
      this.normalizeTextValues(payload.regions).length > 0 ||
      this.normalizeTextValues(payload.cities).length > 0 ||
      this.normalizeTextValues(payload.states).length > 0;
    const locationPartnerIds = await this.resolvePartnerIdsForLocationFilters(payload);
    if (hasStrictLocationFilters && Array.isArray(locationPartnerIds) && locationPartnerIds.length > 0) {
      requestPayload.base_domain = [
        ...requestPayload.base_domain,
        ['id', 'in', locationPartnerIds],
      ];
      // Keep state_ids/city_names so backend can still apply strict location filtering.
      // city_names retained intentionally.
    }

    if (payload.scope) {
      requestPayload.scope = payload.scope;
    }


    if (payload.searchTerm && payload.searchTerm.trim().length >= 2) {
      requestPayload.search_term = payload.searchTerm.trim();
    }

    if (Array.isArray(payload.artworkPurchaseRanges) && payload.artworkPurchaseRanges.length > 0) {
      requestPayload.artwork_purchase_ranges = payload.artworkPurchaseRanges;
    }

    if (!purchase_artist_ids || purchase_artist_ids.length === 0) delete requestPayload.purchase_artist_ids;
    if (!interest_artist_ids || interest_artist_ids.length === 0) delete requestPayload.interest_artist_ids;
    if (!country_ids || country_ids.length === 0) delete requestPayload.country_ids;
    if (!state_ids || state_ids.length === 0) delete requestPayload.state_ids;
    if (!state_names || state_names.length === 0) delete requestPayload.state_names;
    if (!city_names || city_names.length === 0) delete requestPayload.city_names;
    if (!client_types || client_types.length === 0) delete requestPayload.client_types;
    if (!requestPayload.total_purchase_ranges || requestPayload.total_purchase_ranges.length === 0) {
      delete requestPayload.total_purchase_ranges;
    }
    if (!requestPayload.spent_at_purchase_ranges || requestPayload.spent_at_purchase_ranges.length === 0) {
      delete requestPayload.spent_at_purchase_ranges;
    }
    if (!requestPayload.spent_at_gallery_codes || requestPayload.spent_at_gallery_codes.length === 0) {
      delete requestPayload.spent_at_gallery_codes;
    }
    if (!last_purchase_range) delete requestPayload.last_purchase_range;
    if (!requestPayload.artwork_purchase_ranges || requestPayload.artwork_purchase_ranges.length === 0) {
      delete requestPayload.artwork_purchase_ranges;
    }

    // Galleries for count breakdown
    if (Array.isArray(payload.galleryIds) && payload.galleryIds.length > 0) {
      requestPayload.gallery_ids = payload.galleryIds;
    } else if (Array.isArray(payload.galleries) && payload.galleries.length > 0) {
      requestPayload.gallery_names = payload.galleries;
    }

    const res = await this.executeCall('res.partner', 'search_partners_with_filters_count_breakdown', [requestPayload]);
    const total = Number((res as any)?.total) || 0;
    const client = Number((res as any)?.client) || 0;
    const lead = Number((res as any)?.lead) || 0;
    return { total, client, lead };
  }

  
  // 🎨 GET ARTWORKS IN STOCK COUNT
  async getArtworksInStockCount(): Promise<number> {
    const baseDomain: any[] = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["Status", "ilike", "in stock"]
    ];
    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const domain = shouldHide ? ["&", ...baseDomain, ["has_image_artwork", "=", true]] : baseDomain;
    return await this.executeCall('product.template', 'search_count', [domain]);
  }

  // 🎨 GET ARTWORKS IN STOCK (FOR DISPLAY) - WITH ALL IMAGE URLS
  async getArtworksInStock(limit = 4): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'entry_date'];
    const domain = ["&", "&", ["active", "=", true], ["artwork_price_combined", ">", 1], ["Status", "ilike", "in stock"]];
    const shouldHide = await this.shouldHideArtworksWithoutImage();
    return await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, 0, 'entry_date desc', shouldHide ? true : undefined],
      {}
    );
  }

  // 🆕 GET NEW ARTWORKS COUNT (IN PERIOD)
  async getNewArtworksCount(startDate: string): Promise<number> {
    const baseDomain: any[] = [
      "&", "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["Status", "ilike", "in stock"],
      ["entry_date", ">=", startDate]
    ];
    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const domain = shouldHide ? ["&", ...baseDomain, ["has_image_artwork", "=", true]] : baseDomain;
    return await this.executeCall('product.template', 'search_count', [domain]);
  }

  // 🎨 GET NEW ARTWORKS CURRENTLY IN STOCK (ENTRY DATE BASED)
  // The dashboard card is about inventory arrivals, so use entry_date rather
  // than create_date and keep the same in-stock/image rules as the stock list.
  async getNewArtworksInStock(startDate: string, limit = 4): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'entry_date'];
    const baseDomain: any[] = [
      "&", "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["Status", "ilike", "in stock"],
      ["entry_date", ">=", startDate]
    ];
    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const domain = shouldHide ? ["&", ...baseDomain, ["has_image_artwork", "=", true]] : baseDomain;

    return await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, 0, 'entry_date desc, id desc', shouldHide ? true : undefined],
      {}
    );
  }

  // 📊 GET NEW PROSPECTS COUNT (create_date based)
  async getNewProspectsCount(startDate: string): Promise<number> {
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["is_partner_lead", "=", true],
      ["create_date", ">=", startDate]
    ];

    return await this.executeCall(
      'res.partner',
      'search_count',
      [domain]
    );
  }

  // 📊 GET NEW CLIENTS COUNT (write_date based)
  async getNewClientsCount(startDate: string): Promise<number> {
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["is_partner_client", "=", true],
      ["write_date", ">=", startDate]
    ];

    return await this.executeCall(
      'res.partner',
      'search_count',
      [domain]
    );
  }

  // 📧 SEND EMAIL VIA ODOO
  async sendEmail(params: {
    template?: string;
    to: string;
    subject: string;
    body: string;
    attachments?: File[];
  }): Promise<boolean> {
    try {
      // Create mail message
      const mailData = {
        subject: params.subject,
        body_html: params.body,
        email_to: params.to,
        email_from: this.user?.email || 'noreply@operagallery.com'
      };

      await this.executeCall(
        'mail.mail',
        'create',
        [mailData]
      );

      return true;
    } catch (error) {
      console.error('Email sending failed:', error);
      return false;
    }
  }

  // 🔍 SEARCH CONTACTS WITH FILTERS
  async searchContactsWithFilters(filters: {
    searchTerm?: string;
    countries?: string[];
    classifications?: string[];
    purchaseRanges?: string[];
    minPrice?: string;
    maxPrice?: string;
    interestArtists?: string[];
    purchaseArtists?: string[];
    type?: 'all' | 'clients' | 'prospects';
  }, limit = 20, offset = 0): Promise<{ contacts: OdooContact[], totalCount: number }> {
    const fields = [
      'id', 'Id', 'name', 'email', 'phone', 'city', 'country_id',
      'is_company', 'customer_rank', 'total_sales_order',
      'create_date', 'write_date', 'is_partner_lead', 'is_partner_client',
      'client_classifications', 'gallery_order_count',
      'Email1', 'Email2', 'Phone1', 'Phone2',
      'street', 'street2', 'state_id', 'zip', 'interest_artist_ids',
      'Vip', 'last_purchase', 'number_of_invoices',
      // New fields
      'IdEntity', 'Id_with_c', 'title_ids', 'countries_Country', 'gallery_order_count_filemaker', 'number_of_invoices', 'total_sales_order',
      'ShippingAddress', 'Shipping_stree2', 'Shipping_stree3', 'ShippingAddress2',
      'Country_Shipping_Address', 'City_Shipping_Address', 'Zip_Shipping_Address', 'State_Shipping_Address',
      'show_personal_info'
    ];

    let domain: any[] = [["active", "=", true], ["is_user", "=", false], ["is_company", "=", false]];

    console.log("filters called XXXX");
    if (filters.purchaseArtists && filters.purchaseArtists.length > 0) {
      const salesLines = await this.executeCall(
        'gallery.sales.lines.items',
        'search_read',
        [[['res_artist_id.name', 'in', filters.purchaseArtists]]],
        { fields: ['customer_id'] }
      );

      if (salesLines && salesLines.length > 0) {
        const customerIds = [...new Set(salesLines.map((line: any) => line.customer_id[0]))];
        domain.push(['id', 'in', customerIds]);
      } else {
        domain.push(['id', '=', -1]);
      }
    }

    if (filters.interestArtists && filters.interestArtists.length > 0) {
      domain.push(['interest_artist_ids.name', 'in', filters.interestArtists]);
    }

    const priceConditions: any[] = [];
    if (filters.purchaseRanges && filters.purchaseRanges.length > 0) {
      filters.purchaseRanges.forEach(range => {
        switch (range) {
          case '< 100K':
            priceConditions.push(['total_sales_order', '<', 100000]);
            break;
          case '100K - 500K':
            priceConditions.push(['&', ['total_sales_order', '>=', 100000], ['total_sales_order', '<', 500000]]);
            break;
          case '500K - 1M':
            priceConditions.push(['&', ['total_sales_order', '>=', 500000], ['total_sales_order', '<', 1000000]]);
            break;
          case '> 1M':
            priceConditions.push(['total_sales_order', '>=', 1000000]);
            break;
        }
      });
    }

    if (filters.minPrice || filters.maxPrice) {
      const min = parseFloat(filters.minPrice || '0');
      const max = parseFloat(filters.maxPrice || 'Infinity');
      if (min > 0) {
        priceConditions.push(['total_sales_order', '>=', min]);
      }
      if (max < Infinity) {
        priceConditions.push(['total_sales_order', '<=', max]);
      }
    }

    if (priceConditions.length > 0) {
      let finalPriceDomain = [];
      if (priceConditions.length > 1) {
        finalPriceDomain.push(...Array(priceConditions.length - 1).fill('|'));
        finalPriceDomain.push(...priceConditions.flat());
      } else {
        finalPriceDomain = priceConditions[0];
      }
      domain.push(finalPriceDomain);
    }

    if (filters.countries && filters.countries.length > 0) {
      const allCountriesResponse = await this.executeCall(
        'res.country',
        'search_read',
        [[['name', 'in', filters.countries]]],
        { fields: ['id', 'name'] }
      );

      if (allCountriesResponse && Array.isArray(allCountriesResponse)) {
        const countryIds = allCountriesResponse.map(country => country.id);
        if (countryIds.length > 0) {
          domain.push(['country_id', 'in', countryIds]);
        }
      }
    }

    const [contacts, totalCount] = await Promise.all([
      this.executeCall(
        'res.partner',
        'search_read',
        [domain],
        { fields, limit, offset, order: 'write_date DESC' }
      ),
      this.executeCall(
        'res.partner',
        'search_count',
        [domain]
      )
    ]);

    return { contacts, totalCount };
  }

  // 🎨 SEARCH ARTWORKS WITH FILTERS  
  async searchArtworksWithFilters(filters: {
    searchTerm?: string;
    artists?: string[];
    priceRanges?: string[];
    categories?: string[];
    statuses?: string[];
    dateInStock?: string;
  }, limit = 20, offset = 0): Promise<any[]> {
    const fields = [

      'id', 'name', 'res_artist_id', 'IdName', 'Category', 'Medium',

      'artwork_price_combined', 'Status',
      'control_gallery_combined', 'SizeH', 'SizeL',
      ...this.ARTWORK_IMAGE_FIELDS
    ];

    // ALWAYS start with mandatory artwork filters
    let domain: any[] = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    // Search term filter
    if (filters.searchTerm && filters.searchTerm.length >= 2) {
      domain.push(
        "|", "|", "|",
        ['name', 'ilike', filters.searchTerm],
        ['res_artist_id', 'ilike', filters.searchTerm],

        ['IdName', 'ilike', filters.searchTerm],

        ['Category', 'ilike', filters.searchTerm]
      );
    }

    // Artist filter
    if (filters.artists && filters.artists.length > 0) {
      domain.push(["res_artist_id", "in", filters.artists]);
    }

    // Category filter
    if (filters.categories && filters.categories.length > 0) {
      domain.push(["category", "in", filters.categories]);
    }

    // Status filter - case insensitive
    if (filters.statuses && filters.statuses.length > 0) {
      if (filters.statuses.length === 1) {
        // Single status - use ilike for case insensitive
        domain.push(["Status", "ilike", filters.statuses[0]]);
      } else {
        // Multiple statuses - use OR with ilike for each
        const statusConditions = filters.statuses.map(status => ["Status", "ilike", status]);
        const orOperators = Array(statusConditions.length - 1).fill("|");
        domain.push(...orOperators, ...statusConditions);
      }
    }

    // Price range filter - simplified for now
    if (filters.priceRanges && filters.priceRanges.length > 0 && !filters.priceRanges.includes('All')) {
      // For now, just add a basic price filter - can be enhanced later
      domain.push(["artwork_price_combined", ">", 0]);
    }

    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const result = await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, offset, 'id desc', shouldHide ? true : undefined]
    );
    return result;
  }

  async getAllCountries(): Promise<string[]> {
    try {
      // Get all countries from res.country model (master data)
      const response = await this.executeCall(
        'res.country',
        'search_read',
        [
          [], // No domain filter - get all countries
          ['name'] // Only get the name field
        ]
      );

      if (response && Array.isArray(response)) {
        return response
          .map(country => country.name)
          .filter(name => name && typeof name === 'string')
          .sort();
      }

      return [];
    } catch (error) {
      console.error('❌ Error fetching countries:', error);
      return [];
    }
  }

  // 🏛️ GET ALL STATES FROM ODOO MASTER DATA
  async getAllStates(): Promise<string[]> {
    try {
      // Get all states from res.country.state model (master data)
      const response = await this.executeCall(
        'res.country.state',
        'search_read',
        [
          [], // No domain filter - get all states
          ['name'] // Only get the name field
        ]
      );

      if (response && Array.isArray(response)) {
        return response
          .map(state => state.name)
          .filter(name => name && typeof name === 'string')
          .sort();
      }

      return [];
    } catch (error) {
      console.error('❌ Error fetching states:', error);
      return [];
    }
  }

  // 🏷️ GET ALL TITLES FROM title.options 
  async getAllTitles(): Promise<any[]> {
    try {
      const response = await this.executeCall(
        'title.options',
        'search_read',
        [

          [],
          ['id', 'name']
        ]
      );


      if (response && Array.isArray(response)) {
        return response
          .filter(title => title.id && title.name)
          .sort((a, b) => a.name.localeCompare(b.name));
      }

      return [];
    } catch (error) {
      console.error('❌ Error fetching titles:', error);
      return [];
    }
  }

  // 🌍 GET ALL COUNTRIES WITH IDs (for Many2One fields)
  async getAllCountriesWithIds(): Promise<any[]> {
    try {
      // Get all countries from res.country model with both ID and name
      const response = await this.executeCall(
        'res.country',
        'search_read',
        [
          [], // No domain filter - get all countries
          ['id', 'name', 'phone_code'] // Include dialing code for phone auto-prefix
        ]
      );

      if (response && Array.isArray(response)) {
        return response
          .filter(country => country.id && country.name)
          .sort((a, b) => a.name.localeCompare(b.name));
      }

      return [];
    } catch (error) {
      console.error('❌ Error fetching countries with IDs:', error);
      return [];
    }
  }

  // 👥 GET ALL USERS (for seller selection)
  async getAllUsers(): Promise<
    Array<{ id: number; name: string; galleryNames?: string[] }>
  > {
    try {
      const users = await this.executeCall(
        'res.users',
        'search_read',
        [[['active', '=', true]]],
        { fields: ['id', 'name', 'company_ids', 'default_gallery_id'], order: 'name' }
      );

      const galleryMap: Record<string, string> = {
        par: 'Paris',
        lon: 'London',
        hou: 'Houston',
        sin: 'Singapore',
        nyc: 'New York',
        mad: 'Madrid',
        dxb: 'Dubai',
        mon: 'Monaco',
        all: 'Exploitation',
        mia: 'Miami',
        ogbh: 'Bal Harbour',
        oggl: 'Art Gallery Group',
        asp: 'Aspen',
        gva: 'Geneve',
        dxa: 'Dubai Atlantis',
        bei: 'Beirut',
        hkg: 'Hong Kong',
        seo: 'Seoul',
        etymia: 'Eternity Miami',
        etypar: 'Eternity Paris',
        etygva: 'Eternity Geneve',
      };

      const companyIds = Array.from(
        new Set(
          (Array.isArray(users) ? users : [])
            .flatMap((user: any) => {
              const ids: number[] = Array.isArray(user?.company_ids)
                ? user.company_ids
                    .map((id: any) => Number(id))
                    .filter((id: number) => Number.isFinite(id) && id > 0)
                : [];
              const defaultId = Array.isArray(user?.default_gallery_id)
                ? Number(user.default_gallery_id[0])
                : Number(user?.default_gallery_id);
              if (Number.isFinite(defaultId) && defaultId > 0) ids.push(defaultId);
              return ids;
            }),
        ),
      );

      const companyById = new Map<number, { name: string; code: string }>();
      if (companyIds.length > 0) {
        const companies = await this.executeCall(
          'res.company',
          'read',
          [companyIds, ['name', 'control_gallery']],
        ).catch(() => []);

        (Array.isArray(companies) ? companies : []).forEach((company: any) => {
          const id = Number(company?.id);
          if (!Number.isFinite(id) || id <= 0) return;
          const code = String(company?.control_gallery || '').trim().toLowerCase();
          const fallbackName = String(company?.name || '').trim();
          companyById.set(id, {
            code,
            name: galleryMap[code] || fallbackName,
          });
        });
      }

      return (Array.isArray(users) ? users : []).map((user: any) => {
        const ids: number[] = Array.isArray(user?.company_ids)
          ? user.company_ids
              .map((id: any) => Number(id))
              .filter((id: number) => Number.isFinite(id) && id > 0)
          : [];
        const defaultId = Array.isArray(user?.default_gallery_id)
          ? Number(user.default_gallery_id[0])
          : Number(user?.default_gallery_id);
        if (Number.isFinite(defaultId) && defaultId > 0) ids.push(defaultId);

        const galleryNames = Array.from(
          new Set(
            ids
              .map((id) => companyById.get(id)?.name || '')
              .map((name) => name.trim())
              .filter(Boolean),
          ),
        );

        return {
          id: user.id,
          name: user.name,
          galleryNames,
        };
      });
    } catch (error) {
      console.error('❌ Failed to fetch users:', error);
      return [];
    }
  }

  // Get current user's profile details for email signatures
  async getCurrentUserProfile(): Promise<{
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    website: string;
    address: string;
    title: string;
    phonePrimary: string;
    phoneSecondary: string;
    galleryName: string | null;
    galleryPhone: string;
  } | null> {
    try {
      const userId = this.user?.id || this.credentials?.userId;
      if (!userId) return null;

      const [user] = await this.executeCall(
        'res.users',
        'read',
        [[userId], [
          'name',
          'first_name',
          'last_name',
          'title_user',
          'user_email_address',
          'user_gallery_address',
          'user_phone_number',
          'phone_pro',
          'login',
          'email',
          'phone',
          'mobile',
          'website',
          'default_gallery_id'
        ]]
      );


      const galleryMap: Record<string, string> = {
        'par': 'Paris',
        'lon': 'London',
        'hou': 'Houston',
        'sin': 'Singapore',
        'nyc': 'New York',
        'mad': 'Madrid',
        'dxb': 'Dubai',
        'mon': 'Monaco',
        'all': 'Exploitation',
        'mia': 'Miami',
        'ogbh': 'Bal Harbour',
        'oggl': 'Art Gallery Group',
        'asp': 'Aspen',
        'gva': 'Geneve',
        'dxa': 'Dubai Atlantis',
        'bei': 'Beirut',
        'hkg': 'Hong Kong',
        'seo': 'Seoul',
        'etymia': 'Eternity Miami',
        'etypar': 'Eternity Paris',
        'etygva': 'Eternity Geneve',
      };

      const firstName = user?.first_name || (user?.name ? String(user.name).split(' ')[0] : '') || '';
      const lastName = user?.last_name || (user?.name ? String(user.name).split(' ').slice(1).join(' ') : '') || '';
      // Prefer personal mailbox for proposal/shared views.
      // `user_email_address` can be gallery inbox (e.g. miami@...), while `login/email` is user mailbox.
      const email = (user?.login || user?.email || user?.user_email_address || '').toString();

      // Personal phone first, fallback to user-level gallery number. Final gallery fallback happens in UI via galleryPhone.
      const personalPhone = (user?.phone || user?.mobile || user?.phone_pro || '').toString();
      const phonePrimary = personalPhone;
      const phoneSecondary = (user?.user_phone_number || '').toString();
      const phone = (phonePrimary || '').toString();
      const address = (user?.user_gallery_address || '').toString();
      const website = (user?.website || '').toString();
      const title = (user?.title_user || '').toString();


      let galleryName: string | null = null;
      let galleryPhone = "";
      if (user?.default_gallery_id && Array.isArray(user.default_gallery_id)) {
        const defaultGalleryId = user.default_gallery_id[0];

        if (defaultGalleryId) {
          try {
            const [company] = await this.executeCall('res.company', 'read', [[defaultGalleryId], ['control_gallery', 'name', 'phone', 'phone_gallery']]);
            const code = company?.control_gallery || '';
            galleryName = galleryMap[code] || null;
            galleryPhone = (company?.phone_gallery || company?.phone || '').toString();

            if (!galleryName && company?.name) {
              galleryName = String(company.name)
                .replace(/opera\s*/gi, '')
                .replace(/\s*gallery\s*/gi, ' ')
                .trim() || null;
            }
          } catch {

            const rawName = user.default_gallery_id[1] || '';
            galleryName = String(rawName)
              .replace(/opera\s*/gi, '')
              .replace(/\s*gallery\s*/gi, ' ')
              .trim() || null;
          }
        }
      }

      return {
        id: Number(userId),
        name: (user?.name || `${firstName} ${lastName}`).toString().trim(),
        firstName,
        lastName,
        email,
        phone,
        website,
        address,
        title,
        phonePrimary,
        phoneSecondary,
        galleryName,
        galleryPhone,
      };
    } catch (error) {
      console.error('❌ Failed to fetch current user profile for signature:', error);
      return null;
    }
  }



  async updateUserProfile(data: {
    firstName: string;
    lastName: string;
    phone: string;
    title: string;
    defaultCurrency: string;
    defaultGalleryId?: number | null;
  }): Promise<boolean> {
    try {
      const userId = this.user?.id || this.credentials?.userId;
      if (!userId) return false;

      await this.executeCall(
        'res.users',
        'write',
        [[userId], {
          first_name: (data.firstName || '').trim(),
          last_name: (data.lastName || '').trim(),
          phone_pro: (data.phone || '').trim(),
          title_user: (data.title || '').trim(),
          default_currency: (data.defaultCurrency || '').trim(),
          default_gallery_id: data.defaultGalleryId || false,
        }],
        {}
      );

      return true;
    } catch (error) {
      console.error('❌ Failed to update user profile:', error);
      throw error;
    }
  }




  private _profileCache: {
    data: any;
    timestamp: number;
    userId: number | string;
  } | null = null;
  private _profileCacheTTL = 60000;

  async getCompleteUserProfile(forceRefresh = false): Promise<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    companies: Array<{ id: number; name: string; code: string }>;
    role: string;
    title: string;
    defaultCurrency: string;
    defaultGalleryId: number | null;
    defaultGalleryName: string | null;
  } | null> {
    try {
      const userId = this.user?.id || this.credentials?.userId;
      if (!userId) return null;


      if (!forceRefresh && this._profileCache &&
        this._profileCache.userId === userId &&
        Date.now() - this._profileCache.timestamp < this._profileCacheTTL) {
        return this._profileCache.data;
      }

      const [user] = await this.executeCall(
        'res.users',
        'read',
        [[userId], [
          'name',
          'first_name',
          'last_name',
          'login',
          'phone_pro',
          'company_ids',
          'title_user',
          'default_currency',
          'default_gallery_id',
        ]]
      );


      const galleryMap: Record<string, string> = {
        'par': 'Paris',
        'lon': 'London',
        'hou': 'Houston',
        'sin': 'Singapore',
        'nyc': 'New York',
        'mad': 'Madrid',
        'dxb': 'Dubai',
        'mon': 'Monaco',
        'all': 'Exploitation',
        'mia': 'Miami',
        'ogbh': 'Bal Harbour',
        'oggl': 'Art Gallery Group',
        'asp': 'Aspen',
        'gva': 'Geneve',
        'dxa': 'Dubai Atlantis',
        'bei': 'Beirut',
        'hkg': 'Hong Kong',
        'seo': 'Seoul',
        'etymia': 'Eternity Miami',
        'etypar': 'Eternity Paris',
        'etygva': 'Eternity Geneve',
      };

      const firstName = user?.first_name || (user?.name ? String(user.name).split(' ')[0] : '') || '';
      const lastName = user?.last_name || (user?.name ? String(user.name).split(' ').slice(1).join(' ') : '') || '';
      const email = (user?.login || '').toString();
      const phone = (user?.phone_pro || '').toString();
      const title = (user?.title_user || '').toString();
      const defaultCurrency = (user?.default_currency || '').toString();


      const [companiesData, permissions] = await Promise.all([

        (user?.company_ids && Array.isArray(user.company_ids) && user.company_ids.length > 0)
          ? this.executeCall('res.company', 'read', [user.company_ids, ['name', 'control_gallery']]).catch(() => [])
          : Promise.resolve([]),

        this.getUserPermissions().catch(() => [])
      ]);


      const companies: Array<{ id: number; name: string; code: string }> = [];
      companiesData.forEach((company: any) => {
        const code = company?.control_gallery || '';
        const name = galleryMap[code] || company?.name || code;
        if (name) {
          companies.push({ id: company.id, name, code });
        }
      });


      let defaultGalleryId: number | null = null;
      let defaultGalleryName: string | null = null;
      if (user?.default_gallery_id) {

        if (Array.isArray(user.default_gallery_id)) {
          defaultGalleryId = user.default_gallery_id[0];
        } else if (typeof user.default_gallery_id === 'number') {
          defaultGalleryId = user.default_gallery_id;
        }

        if (defaultGalleryId) {
          const matchingCompany = companies.find(c => c.id === defaultGalleryId);
          defaultGalleryName = matchingCompany?.name || null;
        }
      }


      let role = 'user';
      const groups = permissions.map((g: any) => String(g || '').toLowerCase());
       if (groups.includes('artgallery_custom_fields.group_artgallery_director')) {
         role = 'Director';
          } else if (groups.includes('artgallery_custom_fields.group_artgallery_superuser') ||
        groups.includes('artgallery_custom_fields.group_artgallery_administrator')) {
        role = 'Admin';
      } 
      // else if (groups.includes('artgallery_custom_fields.group_artgallery_director')) {
      //   role = 'Director';
      // }
      else if (groups.includes('artgallery_custom_fields.group_artgallery_seller')) {
        role = 'Sales';
      }

      const result = {
        id: Number(userId),
        firstName,
        lastName,
        email,
        phone,
        companies,
        role,
        title,
        defaultCurrency,
        defaultGalleryId,
        defaultGalleryName,
      };


      this._profileCache = {
        data: result,
        timestamp: Date.now(),
        userId,
      };

      return result;
    } catch (error) {
      console.error('❌ Failed to fetch complete user profile:', error);
      return null;
    }
  }


  clearProfileCache(): void {
    this._profileCache = null;
  }

  clearClientCaches(): void {
    this.dashboardStatsCache.clear();
    this.dashboardStatsInflight.clear();
    this.artworksListCache.clear();
    this.artworksListInflight.clear();
    this.artworksCountCache.clear();
    this.artworksCountInflight.clear();
    this.locationPartnerIdsCache.clear();
    this.partnerAddressesCache.clear();
    this.addressDetailsCache.clear();
    this.cachedIsSeller = null;
    this.clearProfileCache();
  }



  // async getCurrentUserProfile(): Promise<{
  //   id: number;
  //   name: string;
  //   firstName: string;
  //   lastName: string;
  //   email: string;
  //   phone: string;
  //   website: string;
  //   galleryName?: string;
  //   galleryAddress?: string;
  //   zipCity?: string;
  //   galleryPhone?: string;
  //   currency?: string;
  //   codeGallery?: string;
  // } | null> {
  //   try {
  //     const userId = this.user?.id || this.credentials?.userId;
  //     if (!userId) return null;

  //     // 1️⃣ Get user info
  //     const [user] = await this.executeCall(
  //       "res.users",
  //       "read",
  //       [[userId], [
  //         "name",
  //         "first_name",
  //         "last_name",
  //         "login",
  //         "email",
  //         "phone",
  //         "mobile",
  //         "phone_pro",
  //         "website",
  //         "ControlGallery"
  //         // <-- custom field in Odoo mapped from FileMaker
  //       ]]
  //     );

  //     // "code_gallery",

  //     if (!user) return null;

  //     // 2️⃣ Extract user base info
  //     const firstName =
  //       user?.first_name ||
  //       (user?.name ? String(user.name).split(" ")[0] : "") ||
  //       "";
  //     const lastName =
  //       user?.last_name ||
  //       (user?.name ? String(user.name).split(" ").slice(1).join(" ") : "") ||
  //       "";
  //     const email = (user?.login || user?.email || "").toString();
  //     const phone =
  //       (user?.phone_pro || user?.phone || user?.mobile || "").toString();
  //     const website = (user?.website || "https://www.operagallery.com/").toString();
  //     const codeGallery = "Dubai";
  //       console.log("ControlGallery",user?.ControlGallery)
  //     // 3️⃣ Fetch gallery info if CodeGallery present
  //     let galleryData = {};
  //     if (codeGallery) {
  //       const [gallery] = await this.executeCall(
  //         "crm.gallery", // adjust if model name differs in Odoo
  //         "search_read",
  //         [[["CodeGallery", "=", codeGallery]], [
  //           "CodeGallery",
  //           "CityCode",
  //           "Country_invoice",
  //           "Currency",
  //           "OmitCrm",
  //           "display_name"
  //         ]]
  //       );

  //       if (gallery) {
  //         galleryData = {
  //           galleryName: gallery.gallery_name,
  //           galleryAddress: gallery.address_gallery_crm,
  //           zipCity: gallery.zip_city,
  //           galleryPhone: gallery.phone_gallery,
  //           currency: gallery.currency,
  //           codeGallery: codeGallery,
  //         };
  //       }
  //       console.log("galleryData", galleryData)
  //     }

  //     // 4️⃣ Merge user + gallery info
  //     return {
  //       id: Number(userId),
  //       name: (user?.name || `${firstName} ${lastName}`).trim(),
  //       firstName,
  //       lastName,
  //       email,
  //       phone,
  //       website,
  //       ...galleryData,
  //     };
  //   } catch (error) {
  //     console.error("❌ Failed to fetch current user profile for signature:", error);
  //     return null;
  //   }
  // }



  async getCurrentUserGallery(): Promise<number | false> {
    try {
      const profile = await this.getCompleteUserProfile();
      if (!profile) {
        return false;
      }


      if (profile.defaultGalleryId) {

        const allGalleries = await this.getAllGalleries();

        const matchingCompany = profile.companies.find(c => c.id === profile.defaultGalleryId);
        if (matchingCompany && matchingCompany.code) {

          const matchingGallery = allGalleries.find(
            (g) => g.name.toLowerCase() === matchingCompany.code.toLowerCase()
          );
          if (matchingGallery) {
            return matchingGallery.id;
          }
        }
      }


      if (!profile.companies || profile.companies.length === 0) {
        return false;
      }

      const galleryCode = profile.companies[0].code;
      if (!galleryCode) {
        return false;
      }

      const allGalleries = await this.getAllGalleries();
      const matchingGallery = allGalleries.find(
        (g) => g.name.toLowerCase() === galleryCode.toLowerCase()
      );

      return matchingGallery ? matchingGallery.id : false;
    } catch (error) {
      console.error('❌ Failed to fetch current user gallery:', error);
      return false;
    }
  }

  // 🏛️ GET ALL GALLERIES (for gallery selection)
  async getAllGalleries(): Promise<Array<{ id: number, name: string }>> {
    try {
      const galleries = await this.executeCall(
        'control.galleries',
        'search_read',
        [[]],
        { fields: ['id', 'name'], order: 'name' }
      );

      return galleries.map((gallery: any) => ({
        id: gallery.id,
        name: gallery.name
      }));
    } catch (error) {
      console.error('❌ Failed to fetch galleries:', error);
      return [];
    }
  }

  // 🔍 SEARCH GALLERIES (for autocomplete)
  async searchGalleries(searchTerm: string, limit = 10): Promise<string[]> {
    try {
      const all = await this.getAllGalleries();
      const q = (searchTerm || '').toLowerCase();
      return all
        .map(g => g.name)
        .filter(name => typeof name === 'string' && name.toLowerCase().includes(q))
        .slice(0, limit);
    } catch (error) {
      console.error('❌ Error searching galleries:', error);
      return [];
    }
  }

  // 🏷️ GET ALL STATUSES (status.options) for calculated_status_ids
  async getAllStatuses(): Promise<Array<{ id: number, name: string }>> {
    try {
      const statuses = await this.executeCall(
        'status.options',
        'search_read',
        [[]],
        { fields: ['id', 'name'], order: 'name' }
      );
      return Array.isArray(statuses)
        ? statuses.map((s: any) => ({ id: s.id, name: s.name }))
        : [];
    } catch (error) {
      console.error('❌ Failed to fetch statuses:', error);
      return [];
    }
  }

  // 🏷️ GET ALL CATEGORIES (category.crm) for category_crm_ids
  async getAllCategories(): Promise<Array<{ id: number, name: string }>> {
    try {
      const categories = await this.executeCall(
        'category.crm',
        'search_read',
        [[]],
        { fields: ['id', 'name'], order: 'name' }
      );
      let normalized = Array.isArray(categories)
        ? categories.map((c: any) => ({ id: c.id, name: c.name }))
        : [];

      const hasDesignArt = normalized.some(
        (c) => String(c?.name || '').trim().toLowerCase() === 'design art'
      );

      if (!hasDesignArt) {
        try {
          const createdId = await this.executeCall(
            'category.crm',
            'create',
            [{ name: 'Design Art' }]
          );
          if (typeof createdId === 'number') {
            normalized = [...normalized, { id: createdId, name: 'Design Art' }];
          }
        } catch (createErr) {
          console.warn('Could not auto-create "Design Art" category:', createErr);
        }
      }

      return normalized;
    } catch (error) {
      console.error('❌ Failed to fetch categories:', error);
      return [];
    }
  }

  // 🔍 SEARCH COUNTRIES (for autocomplete)
  async searchCountries(searchTerm: string, limit = 10): Promise<string[]> {
    try {
      const allCountries = await this.getAllCountries();
      return allCountries
        .filter(country => country.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, limit);
    } catch (error) {
      console.error('❌ Error searching countries:', error);
      return [];
    }
  }

  // 🔍 SEARCH STATES (for autocomplete)
  async searchRegions(searchTerm: string, limit = 10): Promise<string[]> {
    try {
      const term = (searchTerm || "").trim();
      if (term.length < 2) return [];

      const rows = await this.executeCall(
        "res.partner.addresses",
        "search_read",
        [[["Region", "ilike", term]]],
        { fields: ["Region"], order: "Region asc", limit: Math.max(limit * 5, 30) },
      );

      if (!Array.isArray(rows)) return [];

      const unique = Array.from(
        new Set(
          rows
            .map((r: any) => (typeof r?.Region === "string" ? r.Region.trim() : ""))
            .filter(Boolean),
        ),
      );

      return unique
        .filter((region) => region.toLowerCase().includes(term.toLowerCase()))
        .slice(0, limit);
    } catch (error) {
      console.error("Error searching regions:", error);
      return [];
    }
  }

  async searchStates(
    searchTerm: string,
    limit = 10,
    countries?: Array<string | number>,
  ): Promise<string[]> {
    try {
      const term = (searchTerm || "").trim();
      if (term.length < 2) return [];

      const countryIds = await this.resolveCountryIds(countries);
      const selectedCountryNames = Array.from(
        new Set(
          (countries || [])
            .map((c) => String(c || "").trim())
            .filter((name) => name && !/^\d+$/.test(name)),
        ),
      );
      let resolvedCountryNames: string[] = [];
      if (Array.isArray(countryIds) && countryIds.length > 0) {
        try {
          const countryRows = await this.executeCall(
            "res.country",
            "search_read",
            [[["id", "in", countryIds]]],
            { fields: ["name"], limit: Math.max(countryIds.length, 20) },
          );
          if (Array.isArray(countryRows)) {
            resolvedCountryNames = countryRows
              .map((r: any) => (typeof r?.name === "string" ? r.name.trim() : ""))
              .filter(Boolean);
          }
        } catch (_) {
          resolvedCountryNames = [];
        }
      }

      const countryNames = Array.from(new Set([...selectedCountryNames, ...resolvedCountryNames]));
      const addressDomain: any[] = [["State", "!=", false], ["State", "ilike", term]];
      if (countryNames.length > 0) {
        addressDomain.push(["Country", "in", countryNames]);
      }

      const rows = await this.executeCall(
        "res.partner.addresses",
        "search_read",
        [addressDomain],
        { fields: ["State"], order: "State asc", limit: Math.max(limit * 5, 30) },
      );

      if (!Array.isArray(rows)) return [];

      const unique = Array.from(
        new Set(
          rows
            .map((r: any) => (typeof r?.State === "string" ? r.State.trim() : ""))
            .filter(Boolean),
        ),
      );

      return unique
        .filter((state) => state.toLowerCase().includes(term.toLowerCase()))
        .slice(0, limit);
    } catch (error) {
      console.error("Error searching states:", error);
      return [];
    }
  }

  // SEARCH CITIES (for autocomplete)
  async searchCities(
    searchTerm: string,
    limit = 10,
    countries?: Array<string | number>,
    states?: Array<string | number>,
  ): Promise<string[]> {
    try {
      const term = (searchTerm || "").trim();
      const hasLocationScope =
        (Array.isArray(countries) && countries.length > 0) ||
        (Array.isArray(states) && states.length > 0);
      if (term.length < 2 && !hasLocationScope) return [];

      const countryIds = await this.resolveCountryIds(countries);
      const stateIds = await this.resolveStateIds(states);
      const queryLimit = term.length > 0 ? Math.max(limit * 5, 30) : Math.max(limit * 10, 80);

      let stateNameRows: any[] = [];
      if (Array.isArray(stateIds) && stateIds.length > 0) {
        try {
          const rows = await this.executeCall(
            "res.country.state",
            "search_read",
            [[["id", "in", stateIds]]],
            { fields: ["name"], limit: Math.max(stateIds.length, 20) },
          );
          stateNameRows = Array.isArray(rows) ? rows : [];
        } catch (_) {
          stateNameRows = [];
        }
      }

      const selectedCountryNames = Array.from(
        new Set(
          (countries || [])
            .map((c) => String(c || "").trim())
            .filter((name) => name && !/^\d+$/.test(name)),
        ),
      );

      let resolvedCountryNames: string[] = [];
      if (Array.isArray(countryIds) && countryIds.length > 0) {
        try {
          const countryRows = await this.executeCall(
            "res.country",
            "search_read",
            [[["id", "in", countryIds]]],
            { fields: ["name"], limit: Math.max(countryIds.length, 20) },
          );
          if (Array.isArray(countryRows)) {
            resolvedCountryNames = countryRows
              .map((r: any) => (typeof r?.name === "string" ? r.name.trim() : ""))
              .filter(Boolean);
          }
        } catch (_) {
          resolvedCountryNames = [];
        }
      }

      const countryNames = Array.from(new Set([...selectedCountryNames, ...resolvedCountryNames]));
      const selectedStateNames = Array.from(
        new Set(
          (states || [])
            .map((s) => String(s || "").trim())
            .filter((name) => name && !/^\d+$/.test(name)),
        ),
      );
      const resolvedStateNames = Array.isArray(stateNameRows)
        ? stateNameRows
          .map((r: any) => (typeof r?.name === "string" ? r.name.trim() : ""))
          .filter(Boolean)
        : [];
      const stateNames = Array.from(new Set([...selectedStateNames, ...resolvedStateNames]));

      const addressDomain: any[] = [["City", "!=", false]];
      if (term.length > 0) {
        addressDomain.push(["City", "ilike", term]);
      }
      if (countryNames.length > 0) {
        addressDomain.push(["Country", "in", countryNames]);
      }
      if (stateNames.length > 0) {
        addressDomain.push(["State", "in", stateNames]);
      }

      let addressRows: any[] = [];
      try {
        const rows = await this.executeCall(
          "res.partner.addresses",
          "search_read",
          [addressDomain],
          { fields: ["City"], limit: queryLimit, order: "City asc" },
        );
        addressRows = Array.isArray(rows) ? rows : [];
      } catch (_) {
        addressRows = [];
      }

      const addressCities = addressRows
        .map((r: any) => (typeof r?.City === "string" ? r.City.trim() : ""))
        .filter(Boolean);
      const unique = Array.from(new Set(addressCities));

      return unique
        .filter((city) => city.toLowerCase().includes(term.toLowerCase()))
        .slice(0, limit);
    } catch (error) {
      console.error("Error searching cities:", error);
      return [];
    }
  }

  async resolveStateIdByName(name: string): Promise<number | null> {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    try {
      const rows = await this.executeCall(
        'res.country.state',
        'search_read',
        [[['name', 'ilike', trimmed]]],
        { fields: ['id', 'name'], limit: 1, order: 'name asc' }
      );
      if (Array.isArray(rows) && rows.length > 0 && rows[0]?.id) {
        return Number(rows[0].id);
      }
      return null;
    } catch (error) {
      // console.error('❌ Error resolving state ID by name:', error);
      return null;
    }
  }

  // 🔍 GLOBAL SEARCH (Contacts + Artworks)
  async globalSearch(query: string): Promise<Array<{
    id: number;
    name: string;
    type: 'contact' | 'artwork' | 'prospect';
    displayName: string;
  }>> {
    const trimmedQuery = query?.trim(); // Only trim for search, preserve original input
    console.log('🔍 odooClient.globalSearch called with:', { query, trimmedQuery });

    if (!trimmedQuery || trimmedQuery.length < 2) {
      console.log('🔍 Global search query too short, returning empty array');
      return [];
    }

    const isNumeric = /^\d+$/.test(trimmedQuery);

    try {
      // Search each source independently so one model/permission failure does not break the whole dashboard search.
      const [contacts, prospects, artworks] = await Promise.all([
        // Search contacts broadly across key fields, exclude internal users only
        this.executeCall('res.partner', 'search_read', [
          [
            "&", "&",
            ["active", "=", true],
            ["is_user", "=", false],
            // Big OR across common fields: name, company, emails, phones, IDs
            "|", "|", "|", "|", "|", "|", "|", "|", "|", "|", "|", "|",
            ["name", "ilike", trimmedQuery],
            ["Company", "ilike", trimmedQuery],
            ["company_name", "ilike", trimmedQuery],
            ["commercial_company_name", "ilike", trimmedQuery],
            ["email", "ilike", trimmedQuery],
            ["Email1", "ilike", trimmedQuery],
            ["Email2", "ilike", trimmedQuery],
            ["phone", "ilike", trimmedQuery],
            ["Phone1", "ilike", trimmedQuery],
            ["Phone2", "ilike", trimmedQuery],
            ["Id", "ilike", trimmedQuery],
            ["IdEntity", "ilike", trimmedQuery],
            ["Id_with_c", "ilike", trimmedQuery]
          ],
          ["id", "name", "client_classifications"],
          0,
          10
        ]).catch((error: any) => {
          console.warn('globalSearch: res.partner query failed', error);
          return [];
        }),

        // Search prospects with the same safe fields/pattern used by contact search
        this.executeCall('crm.prospect', 'search_read', [
          [
            "&", "&",
            ["active", "=", true],
            ["is_company", "=", false],
            ["is_partner_lead", "=", true],
            "|", "|", "|", "|", "|", "|",
            ["name", "ilike", `%${trimmedQuery}%`],
            ["name", "=", trimmedQuery],
            ["email", "ilike", `%${trimmedQuery}%`],
            ["Email1", "ilike", `%${trimmedQuery}%`],
            ["Email2", "ilike", `%${trimmedQuery}%`],
            ["phone", "ilike", `%${trimmedQuery}%`],
            ["city", "ilike", `%${trimmedQuery}%`]
          ],
          ["id", "name", "client_classifications"],
          0,
          10
        ]).catch((error: any) => {
          console.warn('globalSearch: crm.prospect query failed', error);
          return [];
        }),

        // Search artworks by name
        // this.executeCall('product.template', 'search_read', [
        //   [
        //     "&", "&",
        //     ["active", "=", true],
        //     ["artwork_price_combined", ">", 1],
        //     "|",
        //     ["name", "ilike", trimmedQuery],
        //     ["name", "=", trimmedQuery],   // Exact match for full names
        //     ["default_code", "ilike", trimmedQuery],   // ✅ artwork internal reference / ID
        //     ["id", "=", Number(trimmedQuery) || -1] 
        //   ],
        //   ["id", "name","default_code"],  // Only fetch id and name  
        //   0,               // offset
        //   20                // limit to 5 results
        // ])
        this.executeCall("product.template", "search_read", [
          isNumeric
            ? [
              "&",
              ["active", "=", true],
              "|", "|",
              ["id", "=", Number(trimmedQuery)],
              ["Id", "=", Number(trimmedQuery)],
              ["certificateid", "=", Number(trimmedQuery)]
            ]
            : [
              "&",
              ["active", "=", true],
              "|", "|", "|", "|",
              ["name", "ilike", trimmedQuery],
              ["ArtistName", "ilike", trimmedQuery],
              ["IdName", "ilike", trimmedQuery],
              ["default_code", "ilike", trimmedQuery],
              ["Medium", "ilike", trimmedQuery]
            ],
          [
            "id",
            "name",
            "ArtistName",
            "artwork_year",
            "Medium",
            "image_1920",
            "NewMainLR",
            "NewMainHR",
            "main_super_picture_hd",
            "main_picture_hd",
            "main_web_picture"
          ],
          0,
          0
        ]).catch((error: any) => {
          console.warn('globalSearch: product.template query failed', error);
          return [];
        })

      ]);

      // Format results with type labels
      const results: Array<{
        id: number;
        name: string;
        type: 'contact' | 'artwork' | 'prospect';
        displayName: string;
      }> = [];

      // Add contacts with (contact) label and classification
      if (contacts && Array.isArray(contacts)) {
        contacts.forEach((contact: any) => {
          const classification = contact.client_classifications ? ` - ${contact.client_classifications}` : '';
          results.push({
            id: contact.id,
            name: contact.name,
            type: 'contact' as const,
            displayName: `${contact.name}${classification} (contact)`
          });
        });
      }

      // 🆕 ADD THIS BLOCK (NEW – SAFE)
      if (prospects && Array.isArray(prospects)) {
        prospects.forEach((prospect: any) => {
          results.push({
            id: prospect.id,
            name: prospect.name,
            type: 'prospect' as const,
            displayName: `${prospect.name} (prospect)`
          });
        });
      }

      // Add artworks with (artwork) label  
      if (artworks && Array.isArray(artworks)) {
        artworks.forEach((artwork: any) => {
          results.push({
            id: artwork.id,
            name: artwork.name,
            type: 'artwork' as const,

            // displayName: `${artwork.name} (artwork)`
            displayName: `${artwork.name} [${artwork.default_code || artwork.id}] (artwork)`

          });
        });
      }

      console.log('🔍 globalSearch final results:', { count: results.length, results: results.slice(0, 3) });
      return results;

    } catch (error) {
      console.error('🔍 Global search error:', error);
      return [];
    }
  }

  // 👥 GET CLIENTS (SPECIFIC API)
  // async getClients(limit = 50, offset = 0, order?: string): Promise<any[]> {

  //   const commonFields = [
  //     'id', 'name', 'email', 'phone', 'city', 'country_id',
  //     'is_company', 'customer_rank', 'total_sales_order',
  //     'create_date', 'write_date', 'is_partner_lead', 'is_partner_client',
  //     'client_classifications', 'gallery_order_count', 'control_galleries_ids',
  //     'Email1', 'Email2', 'Phone1', 'Phone2',
  //     'street', 'street2', 'state_id', 'zip',

  //     'ShippingAddress', 'Country_Shipping_Address', 'State_Shipping_Address',

  //     'City_Shipping_Address', 'Zip_Shipping_Address',
  //     'BillingAddress', 'Country_Billing_Address', 'State_Billing_Address',
  //     'City_Billing_Address', 'Zip_Billing_Address',
  //     'BirthDate', 'interest_artist_ids',
  //     'calculated_status_ids',
  //     'category_crm',
  //     'category_crm_ids',
  //     'Vip', 'last_purchase', 'countries_Country', 'gallery_order_count_filemaker', 'number_of_invoices', 'total_sales_order'
  //   ];


  //   const partnerFields = [...commonFields, 'title', 'last_purchase_invoices', 'Id', 'IdEntity', 'Id_with_c', 'show_personal_info'];


  //   const prospectFields = [...commonFields, 'title_ids', 'Id', 'IdEntity', 'Id_with_c', 'show_personal_info'];

  //   const domain = [
  //     ["active", "=", true],
  //     ["is_user", "=", false],
  //     ["is_company", "=", false],
  //     ["is_partner_client", "=", true]
  //   ];


  //   const prospectDomain = [
  //     ["active", "=", true],
  //     ["is_company", "=", false],
  //     ["is_partner_client", "=", true]
  //   ];

  //   console.log('📋 [getClients] Querying both models - limit:', limit, 'offset:', offset, 'order:', order);


  //   const [prospects, partners] = await Promise.all([

  //     this.executeCall('crm.prospect', 'search_read', [prospectDomain], { fields: prospectFields, limit: limit + offset, offset: 0, ...(order ? { order } : {}) }).catch((e) => {
  //       console.error('❌ [getClients] Error querying crm.prospect:', e);
  //       return [];
  //     }),

  //     this.executeCall('res.partner', 'search_read', [domain], { fields: partnerFields, limit: limit + offset, offset: 0, ...(order ? { order } : {}) }).catch((e) => {
  //       console.error('❌ [getClients] Error querying res.partner:', e);
  //       return [];
  //     })
  //   ]);
  //   console.log('📋 [getClients] Results - prospects:', prospects?.length || 0, 'partners:', partners?.length || 0);


  //   const markedProspects = (Array.isArray(prospects) ? prospects : []).map((c: any) => ({ ...c, _model: 'crm.prospect' }));
  //   const markedPartners = (Array.isArray(partners) ? partners : []).map((c: any) => ({ ...c, _model: 'res.partner' }));


  //   let merged = [...markedProspects, ...markedPartners];


  //   merged = this.sortContactsForDisplay(merged, order);


  //   const paginated = merged.slice(offset, offset + limit);
  //   console.log('📋 [getClients] Final merged and paginated results:', paginated.length, 'contacts');
  //   return paginated;
  // }

  // // 🔢 GET CLIENTS COUNT
  // async getClientsCount(): Promise<number> {
  //   const domain = [
  //     ["active", "=", true],
  //     ["is_user", "=", false],
  //     ["is_company", "=", false],
  //     ["is_partner_client", "=", true]
  //   ];


  //   const prospectDomain = [
  //     ["active", "=", true],
  //     ["is_company", "=", false],
  //     ["is_partner_client", "=", true]
  //   ];

  //   console.log('📋 [getClientsCount] Counting both models');


  //   const [prospectCount, partnerCount] = await Promise.all([
  //     this.executeCall('crm.prospect', 'search_count', [prospectDomain]).catch(() => 0),
  //     this.executeCall('res.partner', 'search_count', [domain]).catch(() => 0)
  //   ]);

  //   const total = (typeof prospectCount === 'number' ? prospectCount : 0) + (typeof partnerCount === 'number' ? partnerCount : 0);
  //   console.log('📋 [getClientsCount] Total count - prospects:', prospectCount, 'partners:', partnerCount, 'total:', total);
  //   return total;
  // }

  // 👥 GET CLIENTS
async getClients(limit = 50, offset = 0, order?: string): Promise<any[]> {
  const partnerDomain = [
    ['active',           '=', true ],
    ['is_user',          '=', false],
    ['is_company',       '=', false],
    ['is_partner_client','=', true ],
  ];
  const prospectDomain = [
    ['active',           '=', true ],
    ['is_company',       '=', false],
    ['is_partner_client','=', true ],
  ];
 
  return fetchMergedContacts.call(
    this,
    partnerDomain, prospectDomain,
    PARTNER_FIELDS, PROSPECT_FIELDS,
    limit, offset, order,
    'getClients',
  );
}
 
// 🔢 GET CLIENTS COUNT
async getClientsCount(): Promise<number> {
  return countMerged.call(
    this,
    [
      ['active',           '=', true ],
      ['is_user',          '=', false],
      ['is_company',       '=', false],
      ['is_partner_client','=', true ],
    ],
    [
      ['active',           '=', true ],
      ['is_company',       '=', false],
      ['is_partner_client','=', true ],
    ],
    'getClientsCount',
  );
}


  // // 👥 GET PROSPECTS (SPECIFIC API)
  // async getProspects(limit = 50, offset = 0, order?: string): Promise<any[]> {

  //   const commonFields = [
  //     'id', 'name', 'email', 'phone', 'city', 'country_id',
  //     'is_company', 'customer_rank', 'total_sales_order',
  //     'create_date', 'write_date', 'is_partner_lead', 'is_partner_client',
  //     'client_classifications', 'gallery_order_count', 'control_galleries_ids',
  //     'Email1', 'Email2', 'Phone1', 'Phone2',
  //     'street', 'street2', 'state_id', 'zip',

  //     'ShippingAddress', 'Country_Shipping_Address', 'State_Shipping_Address',

  //     'City_Shipping_Address', 'Zip_Shipping_Address',
  //     'BillingAddress', 'Country_Billing_Address', 'State_Billing_Address',
  //     'City_Billing_Address', 'Zip_Billing_Address',
  //     'BirthDate', 'interest_artist_ids',
  //     'calculated_status_ids',
  //     'category_crm',
  //     'category_crm_ids',
  //     'Vip', 'last_purchase', 'countries_Country', 'gallery_order_count_filemaker', 'number_of_invoices', 'total_sales_order'
  //   ];


  //   const partnerFields = [...commonFields, 'title', 'last_purchase_invoices'];


  //   const prospectFields = [...commonFields, 'title_ids', 'show_personal_info'];

  //   const domain = [
  //     ["active", "=", true],
  //     ["is_user", "=", false],
  //     ["is_company", "=", false],
  //     ["is_partner_lead", "=", true]
  //   ];


  //   const prospectDomain = [
  //     ["active", "=", true],
  //     ["is_company", "=", false],
  //     ["is_partner_lead", "=", true]
  //   ];

  //   console.log('📋 [getProspects] Querying both models - limit:', limit, 'offset:', offset, 'order:', order);


  //   const [prospects, partners] = await Promise.all([

  //     this.executeCall('crm.prospect', 'search_read', [prospectDomain], { fields: prospectFields, limit: limit + offset, offset: 0, ...(order ? { order } : {}) }).catch((e) => {
  //       console.error('❌ [getProspects] Error querying crm.prospect:', e);
  //       return [];
  //     }),

  //     this.executeCall('res.partner', 'search_read', [domain], { fields: partnerFields, limit: limit + offset, offset: 0, ...(order ? { order } : {}) }).catch((e) => {
  //       console.error('❌ [getProspects] Error querying res.partner:', e);
  //       return [];
  //     })
  //   ]);

  //   console.log('📋 [getProspects] Results - prospects:', prospects?.length || 0, 'partners:', partners?.length || 0);


  //   const markedProspects = (Array.isArray(prospects) ? prospects : []).map((c: any) => ({ ...c, _model: 'crm.prospect' }));
  //   const markedPartners = (Array.isArray(partners) ? partners : []).map((c: any) => ({ ...c, _model: 'res.partner' }));


  //   let merged = [...markedProspects, ...markedPartners];


  //   merged = this.sortContactsForDisplay(merged, order);


  //   const paginated = merged.slice(offset, offset + limit);
  //   console.log('📋 [getProspects] Final merged and paginated results:', paginated.length, 'contacts');
  //   return paginated;
  // }

  // // 🔢 GET PROSPECTS COUNT
  // async getProspectsCount(): Promise<number> {
  //   const domain = [
  //     ["active", "=", true],
  //     ["is_user", "=", false],
  //     ["is_company", "=", false],
  //     ["is_partner_lead", "=", true]
  //   ];


  //   const prospectDomain = [
  //     ["active", "=", true],
  //     ["is_company", "=", false],
  //     ["is_partner_lead", "=", true]
  //   ];

  //   console.log('📋 [getProspectsCount] Counting both models');


  //   const [prospectCount, partnerCount] = await Promise.all([
  //     this.executeCall('crm.prospect', 'search_count', [prospectDomain]).catch(() => 0),
  //     this.executeCall('res.partner', 'search_count', [domain]).catch(() => 0)
  //   ]);

  //   const total = (typeof prospectCount === 'number' ? prospectCount : 0) + (typeof partnerCount === 'number' ? partnerCount : 0);
  //   console.log('📋 [getProspectsCount] Total count - prospects:', prospectCount, 'partners:', partnerCount, 'total:', total);
  //   return total;
  // }
  

  // 👥 GET PROSPECTS
async getProspects(limit = 50, offset = 0, order?: string): Promise<any[]> {
  // Prospects use a narrower field list (no Id/IdEntity/Id_with_c/show_personal_info
  // for the simplified prospect variant used outside getClients).
  const prospectOnlyFields = [
    ...COMMON_CONTACT_FIELDS,
    'title_ids',
    'show_personal_info',
  ];
  const partnerOnlyFields = [
    ...COMMON_CONTACT_FIELDS,
    'title',
    'last_purchase_invoices',
  ];
 
  const partnerDomain = [
    ['active',          '=', true ],
    ['is_user',         '=', false],
    ['is_company',      '=', false],
    ['is_partner_lead', '=', true ],
  ];
  const prospectDomain = [
    ['active',          '=', true ],
    ['is_company',      '=', false],
    ['is_partner_lead', '=', true ],
  ];
 
  return fetchMergedContacts.call(
    this,
    partnerDomain, prospectDomain,
    partnerOnlyFields, prospectOnlyFields,
    limit, offset, order,
    'getProspects',
  );
}
 
// 🔢 GET PROSPECTS COUNT
async getProspectsCount(): Promise<number> {
  return countMerged.call(
    this,
    [
      ['active',          '=', true ],
      ['is_user',         '=', false],
      ['is_company',      '=', false],
      ['is_partner_lead', '=', true ],
    ],
    [
      ['active',          '=', true ],
      ['is_company',      '=', false],
      ['is_partner_lead', '=', true ],
    ],
    'getProspectsCount',
  );
}


  // // 🔍 SEARCH CONTACTS
  // async searchContacts(query: string, limit = 20, type: 'all' | 'clients' | 'prospects' = 'all'): Promise<any[]> {
  //   const trimmedQuery = query?.trim(); // Only trim for search, don't modify original
  //   console.log('🔍 odooClient.searchContacts called with:', { query, trimmedQuery, limit, type });

  //   if (!trimmedQuery || trimmedQuery.length < 2) {
  //     console.log('🔍 Query too short, returning empty array');
  //     return [];
  //   }

  //   // Build a strict domain similar to other filtered endpoints:
  //   // AND(active, not user, not company, typeFilter) AND (OR on name/email/phone/city)
  //   const andConditions: any[] = [
  //     ["active", "=", true],
  //     ["is_user", "=", false],
  //     ["is_company", "=", false]
  //   ];
  //   if (type === 'clients') {
  //     andConditions.push(["is_partner_client", "=", true]);
  //   } else if (type === 'prospects') {
  //     andConditions.push(["is_partner_lead", "=", true]);
  //   }


  //   const prospectAndConditions: any[] = [
  //     ["active", "=", true],
  //     ["is_company", "=", false]
  //   ];
  //   if (type === 'clients') {
  //     prospectAndConditions.push(["is_partner_client", "=", true]);
  //   } else if (type === 'prospects') {
  //     prospectAndConditions.push(["is_partner_lead", "=", true]);
  //   }

  //   // Prepend the correct number of '&' operators for AND chaining
  //   const andOperators = Array(andConditions.length - 1).fill("&");
  //   const prospectAndOperators = Array(prospectAndConditions.length - 1).fill("&");



  //   const partnerOrGroup: any[] = [
  //     "|", "|", "|", "|", "|", "|", "|", "|", "|", "|", "|", "|",
  //     ['name', 'ilike', `%${trimmedQuery}%`],
  //     ['name', '=', trimmedQuery],
  //     ['email', 'ilike', `%${trimmedQuery}%`],
  //     ['Email1', 'ilike', `%${trimmedQuery}%`],
  //     ['Email2', 'ilike', `%${trimmedQuery}%`],
  //     ['phone', 'ilike', `%${trimmedQuery}%`],
  //     ['city', 'ilike', `%${trimmedQuery}%`],
  //     ['Id_with_c', '=', trimmedQuery],
  //     ['Id_with_c', 'ilike', `%${trimmedQuery}%`],
  //     ['Id_with_c', 'ilike', trimmedQuery],
  //     ['IdEntity', '=', trimmedQuery],
  //     ['IdEntity', 'ilike', `%${trimmedQuery}%`],
  //     ['IdEntity', 'ilike', trimmedQuery]
  //   ];


  //   const prospectOrGroup: any[] = [
  //     "|", "|", "|", "|", "|", "|",
  //     ['name', 'ilike', `%${trimmedQuery}%`],
  //     ['name', '=', trimmedQuery],
  //     ['email', 'ilike', `%${trimmedQuery}%`],
  //     ['Email1', 'ilike', `%${trimmedQuery}%`],
  //     ['Email2', 'ilike', `%${trimmedQuery}%`],
  //     ['phone', 'ilike', `%${trimmedQuery}%`],
  //     ['city', 'ilike', `%${trimmedQuery}%`]
  //   ];

  //   const domain = [
  //     ...andOperators,
  //     ...andConditions,
  //     ...partnerOrGroup
  //   ];

  //   const prospectDomain = [
  //     ...prospectAndOperators,
  //     ...prospectAndConditions,
  //     ...prospectOrGroup
  //   ];

  //   console.log('🔍 Search domain (res.partner):', domain);
  //   console.log('🔍 Search domain (crm.prospect):', prospectDomain);


  //   const commonSearchFields = [
  //     'id', 'name', 'email', 'phone', 'city',
  //     'is_partner_lead', 'is_partner_client',
  //     'client_classifications', 'show_personal_info', 'countries_Country'
  //   ];


  //   const partnerSearchFields = [...commonSearchFields, 'Id_with_c', 'IdEntity'];


  //   const prospectSearchFields = [...commonSearchFields, 'Email1', 'Email2'];


  //   const [prospectResults, partnerResults] = await Promise.all([
  //     this.executeCall('crm.prospect', 'search_read', [prospectDomain], { fields: prospectSearchFields, limit }).catch((e: any) => {
  //       console.error('❌ [searchContacts] Error querying crm.prospect:', e);
  //       console.error('❌ [searchContacts] Prospect search fields:', prospectSearchFields);
  //       console.error('❌ [searchContacts] Prospect domain:', prospectDomain);
  //       // Return empty array to allow partner results to still be returned
  //       return [];
  //     }),
  //     this.executeCall('res.partner', 'search_read', [domain], { fields: partnerSearchFields, limit }).catch((e: any) => {
  //       console.error('❌ [searchContacts] Error querying res.partner:', e);
  //       return [];
  //     })
  //   ]);

  //   console.log('🔍 [searchContacts] Results - prospects:', prospectResults?.length || 0, 'partners:', partnerResults?.length || 0);



  //   const markedProspects = (Array.isArray(prospectResults) ? prospectResults : []).map((c: any) => ({
  //     ...c,
  //     _model: 'crm.prospect',
  //     is_partner_lead: true,
  //     is_partner_client: c.is_partner_client || false
  //   }));


  //   const markedPartners = (Array.isArray(partnerResults) ? partnerResults : []).map((c: any) => ({
  //     ...c,
  //     _model: 'res.partner',
  //     is_partner_lead: c.is_partner_lead || false,
  //     is_partner_client: c.is_partner_client || false
  //   }));


  //   const merged = [...markedProspects, ...markedPartners].slice(0, limit);

  //   console.log('🔍 searchContacts result:', { count: merged.length, first: merged[0]?.name });
  //   return merged;
  // }

  // 🔍 SEARCH CONTACTS
// async searchContacts(
//   query: string,
//   limit = 20,
//   type: 'all' | 'clients' | 'prospects' = 'all',
// ): Promise<any[]> {
//   const trimmedQuery = query?.trim();
//   console.log('🔍 odooClient.searchContacts called with:', { query, trimmedQuery, limit, type });
 
//   if (!trimmedQuery || trimmedQuery.length < 2) {
//     console.log('🔍 Query too short, returning empty array');
//     return [];
//   }
 
//   // ── Domain helpers ────────────────────────────────────────────────────────
 
//   const basePartnerConditions: any[] = [
//     ['active',    '=', true ],
//     ['is_user',   '=', false],
//     ['is_company','=', false],
//   ];
//   const baseProspectConditions: any[] = [
//     ['active',    '=', true ],
//     ['is_company','=', false],
//   ];
 
//   if (type === 'clients') {
//     basePartnerConditions.push( ['is_partner_client', '=', true]);
//     baseProspectConditions.push(['is_partner_client', '=', true]);
//   } else if (type === 'prospects') {
//     basePartnerConditions.push( ['is_partner_lead', '=', true]);
//     baseProspectConditions.push(['is_partner_lead', '=', true]);
//   }
 
//   // Build AND-prefix operators for Odoo's polish-notation domain syntax.
//   const andOps = (n: number) => Array(n - 1).fill('&');
 
//   // OR clauses for free-text search – partner has Id_with_c / IdEntity, prospect does not.
//   const pct = `%${trimmedQuery}%`; // "percent-contains" shorthand
 
//   const partnerOrGroup: any[] = [
//     '|','|','|','|','|','|','|','|','|','|','|','|',
//     ['name',       'ilike', pct          ],
//     ['name',       '=',     trimmedQuery ],
//     ['email',      'ilike', pct          ],
//     ['Email1',     'ilike', pct          ],
//     ['Email2',     'ilike', pct          ],
//     ['phone',      'ilike', pct          ],
//     ['city',       'ilike', pct          ],
//     ['Id_with_c',  '=',     trimmedQuery ],
//     ['Id_with_c',  'ilike', pct          ],
//     ['Id_with_c',  'ilike', trimmedQuery ],
//     ['IdEntity',   '=',     trimmedQuery ],
//     ['IdEntity',   'ilike', pct          ],
//     ['IdEntity',   'ilike', trimmedQuery ],
//   ];
 
//   const prospectOrGroup: any[] = [
//     '|','|','|','|','|',
//     ['name',   'ilike', pct          ],
//     ['name',   '=',     trimmedQuery ],
//     ['email',  'ilike', pct          ],
//     ['Email1', 'ilike', pct          ],
//     ['Email2', 'ilike', pct          ],
//     ['phone',  'ilike', pct          ],
//     ['city',   'ilike', pct          ],
//   ];
 
//   const domain         = [...andOps(basePartnerConditions.length),  ...basePartnerConditions,  ...partnerOrGroup ];
//   const prospectDomain = [...andOps(baseProspectConditions.length), ...baseProspectConditions, ...prospectOrGroup];
 
//   console.log('🔍 Search domain (res.partner):',   domain);
//   console.log('🔍 Search domain (crm.prospect):', prospectDomain);
 
//   // ── Fields ────────────────────────────────────────────────────────────────
 
//   const commonSearchFields = [
//     'id', 'name', 'email', 'phone', 'city',
//     'is_partner_lead', 'is_partner_client',
//     'client_classifications', 'show_personal_info', 'countries_Country',
//   ];
//   const partnerSearchFields  = [...commonSearchFields, 'Id_with_c', 'IdEntity'];
//   const prospectSearchFields = [...commonSearchFields, 'Email1', 'Email2'     ];
 
//   // ── Parallel fetch ────────────────────────────────────────────────────────
 
//   const [prospectResults, partnerResults] = await Promise.all([
//     this.executeCall('crm.prospect', 'search_read', [prospectDomain], { fields: prospectSearchFields, limit })
//       .catch((e: any) => {
//         console.error('❌ [searchContacts] crm.prospect error:', e,
//           '\n  fields:', prospectSearchFields, '\n  domain:', prospectDomain);
//         return [];
//       }),
//     this.executeCall('res.partner', 'search_read', [domain], { fields: partnerSearchFields, limit })
//       .catch((e: any) => {
//         console.error('❌ [searchContacts] res.partner error:', e);
//         return [];
//       }),
//   ]);
 
//   console.log('🔍 [searchContacts] Results – prospects:', prospectResults?.length ?? 0, 'partners:', partnerResults?.length ?? 0);
 
//   const merged = [
//     ...(Array.isArray(prospectResults) ? prospectResults : []).map((c: any) => ({
//       ...c,
//       _model:           'crm.prospect',
//       is_partner_lead:  true,
//       is_partner_client: c.is_partner_client ?? false,
//     })),
//     ...(Array.isArray(partnerResults) ? partnerResults : []).map((c: any) => ({
//       ...c,
//       _model:           'res.partner',
//       is_partner_lead:  c.is_partner_lead  ?? false,
//       is_partner_client: c.is_partner_client ?? false,
//     })),
//   ].slice(0, limit);
 
//   console.log('🔍 searchContacts result:', { count: merged.length, first: merged[0]?.name });
//   return merged;
// }
 

// 🔍 SEARCH CONTACTS
async searchContacts(
  query: string,
  limit = 20,
  type: 'all' | 'clients' | 'prospects' = 'all',
): Promise<any[]> {
  const trimmedQuery = query?.trim();
  console.log('🔍 odooClient.searchContacts called with:', { query, trimmedQuery, limit, type });
 
  if (!trimmedQuery || trimmedQuery.length < 2) {
    console.log('🔍 Query too short, returning empty array');
    return [];
  }
 
  // ── Domain helpers ────────────────────────────────────────────────────────
 
  const basePartnerConditions: any[] = [
    ['active',    '=', true ],
    ['is_user',   '=', false],
    ['is_company','=', false],
  ];
  const baseProspectConditions: any[] = [
    ['active',    '=', true ],
    ['is_company','=', false],
  ];
 
  if (type === 'clients') {
    basePartnerConditions.push( ['is_partner_client', '=', true]);
    baseProspectConditions.push(['is_partner_client', '=', true]);
  } else if (type === 'prospects') {
    basePartnerConditions.push( ['is_partner_lead', '=', true]);
    baseProspectConditions.push(['is_partner_lead', '=', true]);
  }
 
  // Build AND-prefix operators for Odoo's polish-notation domain syntax.
  const andOps = (n: number) => Array(n - 1).fill('&');
  const orOps = (n: number) => Array(Math.max(n - 1, 0)).fill('|');
 
  // OR clauses for free-text search – partner has Id_with_c / IdEntity, prospect does not.
  const pct = `%${trimmedQuery}%`; // "percent-contains" shorthand
 
  const partnerOrClauses: any[] = [
    ['name',       'ilike', pct          ],
    ['name',       '=',     trimmedQuery ],
    ['FirstName',  'ilike', pct          ],
    ['FirstName',  '=',     trimmedQuery ],
    ['LastName',   'ilike', pct          ],
    ['LastName',   '=',     trimmedQuery ],
    ['email',      'ilike', pct          ],
    ['Email1',     'ilike', pct          ],
    ['Email2',     'ilike', pct          ],
    ['phone',      'ilike', pct          ],
    ['city',       'ilike', pct          ],
    ['Id_with_c',  '=',     trimmedQuery ],
    ['Id_with_c',  'ilike', pct          ],
    ['Id_with_c',  'ilike', trimmedQuery ],
    ['IdEntity',   '=',     trimmedQuery ],
    ['IdEntity',   'ilike', pct          ],
    ['IdEntity',   'ilike', trimmedQuery ],
  ];
 
  const prospectOrClauses: any[] = [
    ['name',   'ilike', pct          ],
    ['name',   '=',     trimmedQuery ],
    ['FirstName', 'ilike', pct       ],
    ['FirstName', '=',     trimmedQuery ],
    ['LastName',  'ilike', pct       ],
    ['LastName',   '=',     trimmedQuery ],
    ['email',  'ilike', pct          ],
    ['Email1', 'ilike', pct          ],
    ['Email2', 'ilike', pct          ],
    ['phone',  'ilike', pct          ],
    ['city',   'ilike', pct          ],
  ];
 
  const partnerOrGroup: any[] = [...orOps(partnerOrClauses.length), ...partnerOrClauses];
  const prospectOrGroup: any[] = [...orOps(prospectOrClauses.length), ...prospectOrClauses];
 
  const domain         = [...andOps(basePartnerConditions.length),  ...basePartnerConditions,  ...partnerOrGroup ];
  const prospectDomain = [...andOps(baseProspectConditions.length), ...baseProspectConditions, ...prospectOrGroup];
 
  console.log('🔍 Search domain (res.partner):',   domain);
  console.log('🔍 Search domain (crm.prospect):', prospectDomain);
 
  // ── Fields ────────────────────────────────────────────────────────────────
 
  const commonSearchFields = [
    'id', 'name', 'FirstName', 'LastName', 'email', 'phone', 'city',
    'is_partner_lead', 'is_partner_client',
    'client_classifications', 'show_personal_info', 'countries_Country',
  ];
  const partnerSearchFields  = [...commonSearchFields, 'Id_with_c', 'IdEntity'];
  const prospectSearchFields = [...commonSearchFields, 'Email1', 'Email2'     ];
 
  // ── Parallel fetch ────────────────────────────────────────────────────────
 
  const [prospectResults, partnerResults] = await Promise.all([
    this.executeCall('crm.prospect', 'search_read', [prospectDomain], { fields: prospectSearchFields, limit })
      .catch((e: any) => {
        console.error('❌ [searchContacts] crm.prospect error:', e,
          '\n  fields:', prospectSearchFields, '\n  domain:', prospectDomain);
        return [];
      }),
    this.executeCall('res.partner', 'search_read', [domain], { fields: partnerSearchFields, limit })
      .catch((e: any) => {
        console.error('❌ [searchContacts] res.partner error:', e);
        return [];
      }),
  ]);
 
  console.log('🔍 [searchContacts] Results – prospects:', prospectResults?.length ?? 0, 'partners:', partnerResults?.length ?? 0);
 
  const merged = [
    ...(Array.isArray(prospectResults) ? prospectResults : []).map((c: any) => ({
      ...c,
      _model:           'crm.prospect',
      is_partner_lead:  true,
      is_partner_client: c.is_partner_client ?? false,
    })),
    ...(Array.isArray(partnerResults) ? partnerResults : []).map((c: any) => ({
      ...c,
      _model:           'res.partner',
      is_partner_lead:  c.is_partner_lead  ?? false,
      is_partner_client: c.is_partner_client ?? false,
    })),
  ].slice(0, limit);
 
  console.log('🔍 searchContacts result:', { count: merged.length, first: merged[0]?.name });
  return merged;
}
 




  // ✏️ EDIT CONTACT
  async updateContact(contactId: number, updateData: Partial<OdooContact>): Promise<boolean> {
    try {
      console.log(`🔍 updateContact called - ID: ${contactId}, Data:`, updateData);

      let modelName = 'res.partner';


      if ((updateData as any)._model) {
        modelName = (updateData as any)._model;
        console.log(`📋 Using provided _model: ${modelName} for contact ${contactId}`);
      } else {

        try {
          const prospectCheck = await this.executeCall('crm.prospect', 'search_count', [
            [['id', '=', contactId]]
          ]);
          if (typeof prospectCheck === 'number' && prospectCheck > 0) {
            modelName = 'crm.prospect';
            console.log(`📋 Contact ${contactId} found in crm.prospect, using that model for update`);
          }
        } catch (e) {
          console.log(`📋 Contact ${contactId} not found in crm.prospect, using res.partner for update`);
        }
      }

      const sanitized: any = { ...updateData };
      if ('title' in sanitized) delete sanitized.title;
      if ('_model' in sanitized) delete sanitized._model; // Remove internal _model field
      // Read-only / display-only fields that can exist in frontend state but
      // must never be sent back in a write payload.
      [
        'interest_date',
        'interest_added_date',
        'partner_interest_date',
        'date_added',
        'relation_create_date',
        'gallery_order_count',
        'interest_order_count',
        'artwork_order_count',
        'total_purchase_amount',
        'total_sales_order',
        'total_sold_price_global_eur',
        'number_of_invoices',
        'show_personal_info',
        'is_partner_lead',
        'is_partner_client',
        'create_date',
        'write_date',
        'seller_fm',
      ].forEach((field) => {
        if (field in sanitized) delete sanitized[field];
      });
      // Normalize legacy category keys used by UI to real Odoo fields
      if ('Category' in sanitized && !('category_crm_ids' in sanitized) && !('category_crm' in sanitized)) {
        const legacyCategory = (sanitized as any).Category;
        if (typeof legacyCategory === 'number') {
          (sanitized as any).category_crm_ids = legacyCategory;
        } else if (Array.isArray(legacyCategory) && typeof legacyCategory[0] === 'number') {
          (sanitized as any).category_crm_ids = legacyCategory[0];
        } else if (typeof legacyCategory === 'string' && legacyCategory.trim()) {
          (sanitized as any).category_crm = legacyCategory.trim();
        }
      }
      if ('category' in sanitized && !('category_crm_ids' in sanitized) && !('category_crm' in sanitized)) {
        const legacyCategory = (sanitized as any).category;
        if (typeof legacyCategory === 'number') {
          (sanitized as any).category_crm_ids = legacyCategory;
        } else if (Array.isArray(legacyCategory) && typeof legacyCategory[0] === 'number') {
          (sanitized as any).category_crm_ids = legacyCategory[0];
        } else if (typeof legacyCategory === 'string' && legacyCategory.trim()) {
          (sanitized as any).category_crm = legacyCategory.trim();
        }
      }
      if ('Category' in sanitized) delete (sanitized as any).Category;
      if ('category' in sanitized) delete (sanitized as any).category;
      if ('category_id' in sanitized) delete (sanitized as any).category_id;
      if (Array.isArray((sanitized as any).title_ids)) {
        const arr = (sanitized as any).title_ids as any[];
        (sanitized as any).title_ids = arr.length > 0 && typeof arr[0] === 'number' ? arr[0] : false;
      }
      // Ensure phone codes pass through on update if present
      if ((sanitized as any).phone_country_code === '') delete (sanitized as any).phone_country_code;
      if ((sanitized as any).mobile_country_code === '') delete (sanitized as any).mobile_country_code;

      if ((sanitized as any).Id_with_c !== undefined) {
        if ((sanitized as any).Id_with_c === '' || (sanitized as any).Id_with_c === null) {
          (sanitized as any).Id_with_c = false;
        }
      }

      // Normalize Many2One fields: convert 0/''/null to false
      const normalizeM2O = (field: string) => {
        if (field in sanitized) {
          const v = (sanitized as any)[field];
          if (v === 0 || v === '0' || v === '' || v === null) {
            (sanitized as any)[field] = false;
          }
        }
      };


      if (Array.isArray((sanitized as any).seller_ids)) {
        const flat = (sanitized as any).seller_ids as any[];


        const looksLikeCommand = flat.length > 0 && Array.isArray(flat[0]) && typeof flat[0][0] === 'number';
        if (!looksLikeCommand) {
          const ids = flat
            .map((x) => (typeof x === 'string' ? parseInt(x, 10) : x))
            .filter((n) => Number.isFinite(n)) as number[];
          (sanitized as any).seller_ids = [[6, 0, ids]];
        }
      }
      normalizeM2O('control_galleries_ids');
      normalizeM2O('country_id');
      normalizeM2O('parent_id');

      const response = await this.executeCall(
        modelName,
        
        'write',
        [
          [contactId], // Array with the contact ID
          sanitized   // Fields to update
        ]
      );

      console.log(`🔍 updateContact response from ${modelName}:`, response);
      return response === true;
    } catch (error) {
      console.error(`❌ Error updating contact ${contactId}:`, error);
      console.error(`❌ Update data was:`, updateData);
      console.error(`❌ Error details:`, {
        message: error instanceof Error ? error.message : String(error),
        type: typeof error,
        constructor: error?.constructor?.name,
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      throw error;
    }
  }




  async checkDuplicateContact(firstName: string, lastName: string): Promise<any | null> {
    try {
      const trimmedFirstName = firstName?.trim() || '';
      const trimmedLastName = lastName?.trim() || '';


      if (!trimmedFirstName && !trimmedLastName) {
        console.log('[checkDuplicateContact] Missing both names:', { firstName, lastName });
        return null;
      }

      console.log('[checkDuplicateContact] Checking for duplicate:', {
        trimmedFirstName: trimmedFirstName || '(empty)',
        trimmedLastName: trimmedLastName || '(empty)'
      });


      let prospectDomain: any[];

      if (trimmedFirstName && trimmedLastName) {

        prospectDomain = [
          '&',
          '&',
          ['FirstName', 'ilike', trimmedFirstName],
          ['LastName', 'ilike', trimmedLastName],
          ['active', '=', true]
        ];
      } else if (trimmedFirstName && !trimmedLastName) {
        prospectDomain = [
          '&',
          '&',
          ['FirstName', 'ilike', trimmedFirstName],
          ['LastName', '=', false],
          ['active', '=', true]
        ];
      } else if (!trimmedFirstName && trimmedLastName) {
        prospectDomain = [
          '&',
          '&',
          ['FirstName', '=', false],
          ['LastName', 'ilike', trimmedLastName],
          ['active', '=', true]
        ];
      } else {
        return null;
      }

      console.log('[checkDuplicateContact] Domain:', prospectDomain);

      const prospects = await this.executeCall('crm.prospect', 'search_read', [
        prospectDomain
      ], {
        fields: ['id', 'name', 'FirstName', 'LastName', 'email'],
        limit: 10
      });

      console.log('[checkDuplicateContact] Search result:', prospects);

      if (prospects && Array.isArray(prospects) && prospects.length > 0) {

        const exactMatch = prospects.find((prospect: any) => {
          const prospectFirstName = (prospect.FirstName || '').trim().toLowerCase();
          const prospectLastName = (prospect.LastName || '').trim().toLowerCase();
          const inputFirstName = trimmedFirstName.toLowerCase();
          const inputLastName = trimmedLastName.toLowerCase();

          if (trimmedFirstName && trimmedLastName) {
            return prospectFirstName === inputFirstName && prospectLastName === inputLastName;
          } else if (trimmedFirstName && !trimmedLastName) {
            return prospectFirstName === inputFirstName && !prospectLastName;
          } else if (!trimmedFirstName && trimmedLastName) {
            return !prospectFirstName && prospectLastName === inputLastName;
          }
          return false;
        });

        if (exactMatch) {
          // console.log('[checkDuplicateContact] Exact duplicate found:', exactMatch);
          return exactMatch;
        }
      }

      console.log('[checkDuplicateContact] No duplicate found');
      return null;
    } catch (error) {
      console.error('[checkDuplicateContact] Error checking for duplicate contact:', error);

      return null;
    }
  }


  async checkDuplicateContactInProspect(firstName: string, lastName: string): Promise<any | null> {
    try {
      const trimmedFirstName = firstName?.trim() || '';
      const trimmedLastName = lastName?.trim() || '';


      if (!trimmedFirstName && !trimmedLastName) {
        console.log('[checkDuplicateContactInProspect] Missing both names:', { firstName, lastName });
        return null;
      }

      console.log('[checkDuplicateContactInProspect] Checking for duplicate:', {
        trimmedFirstName: trimmedFirstName || '(empty)',
        trimmedLastName: trimmedLastName || '(empty)'
      });

      // First, search with case-insensitive matching to get potential matches
      let prospectDomain: any[];

      if (trimmedFirstName && trimmedLastName) {
        // Use ilike for case-insensitive search, but we'll filter for exact matches
        prospectDomain = [
          '&',
          '&',
          ['FirstName', 'ilike', trimmedFirstName],
          ['LastName', 'ilike', trimmedLastName],
          ['active', '=', true]
        ];
      } else if (trimmedFirstName && !trimmedLastName) {
        prospectDomain = [
          '&',
          ['FirstName', 'ilike', trimmedFirstName],
          ['active', '=', true]
        ];
      } else if (!trimmedFirstName && trimmedLastName) {
        prospectDomain = [
          '&',
          ['LastName', 'ilike', trimmedLastName],
          ['active', '=', true]
        ];
      } else {
        return null;
      }

      console.log('[checkDuplicateContactInProspect] Domain:', prospectDomain);

      const prospects = await this.executeCall('crm.prospect', 'search_read', [
        prospectDomain
      ], {
        fields: ['id', 'name', 'FirstName', 'LastName', 'email'],
        limit: 10 // Get more results to filter for exact matches
      });

      console.log('[checkDuplicateContactInProspect] Search result:', prospects);

      if (prospects && Array.isArray(prospects) && prospects.length > 0) {
        // Filter for exact case-insensitive matches
        const exactMatch = prospects.find((prospect: any) => {
          const prospectFirstName = (prospect.FirstName || '').trim().toLowerCase();
          const prospectLastName = (prospect.LastName || '').trim().toLowerCase();
          const inputFirstName = trimmedFirstName.toLowerCase();
          const inputLastName = trimmedLastName.toLowerCase();

          if (trimmedFirstName && trimmedLastName) {
            return prospectFirstName === inputFirstName && prospectLastName === inputLastName;
          } else if (trimmedFirstName && !trimmedLastName) {
            return prospectFirstName === inputFirstName && !prospectLastName;
          } else if (!trimmedFirstName && trimmedLastName) {
            return !prospectFirstName && prospectLastName === inputLastName;
          }
          return false;
        });

        if (exactMatch) {
          console.log('[checkDuplicateContactInProspect] Exact duplicate found:', exactMatch);
          return exactMatch;
        }
      }

      console.log('[checkDuplicateContactInProspect] No duplicate found');
      return null;
    } catch (error) {
      console.error('[checkDuplicateContactInProspect] Error checking for duplicate contact in prospect:', error);

      return null;
    }
  }


  async checkDuplicateContactByEmail(email: string, excludeContactId?: number): Promise<any | null> {
    try {
      const trimmedEmail = email?.trim().toLowerCase() || '';

      if (!trimmedEmail) {
        // console.log('[checkDuplicateContactByEmail] Missing email');
        return null;
      }

      // console.log('[checkDuplicateContactByEmail] Checking for duplicate email:', trimmedEmail, excludeContactId ? `(excluding contact ID: ${excludeContactId})` : '');


      let emailDomain: any[];

      if (excludeContactId && excludeContactId > 0) {

        emailDomain = [
          '&', '&',
          ['active', '=', true],
          ['id', '!=', excludeContactId],
          '|', '|', '|',
          ['email', 'ilike', trimmedEmail],
          ['Email1', 'ilike', trimmedEmail],
          ['Email2', 'ilike', trimmedEmail],
          ['email_pro', 'ilike', trimmedEmail]
        ];
      } else {

        emailDomain = [
          '&',
          ['active', '=', true],
          '|', '|', '|',
          ['email', 'ilike', trimmedEmail],
          ['Email1', 'ilike', trimmedEmail],
          ['Email2', 'ilike', trimmedEmail],
          ['email_pro', 'ilike', trimmedEmail]
        ];
      }

      // console.log('[checkDuplicateContactByEmail] Domain:', emailDomain);


      const prospects = await this.executeCall('crm.prospect', 'search_read', [
        emailDomain
      ], {
        fields: ['id', 'name', 'FirstName', 'LastName', 'email', 'Email1', 'Email2', 'email_pro'],
        limit: 10
      });

      // console.log('[checkDuplicateContactByEmail] Prospects search result:', prospects);

      if (prospects && Array.isArray(prospects) && prospects.length > 0) {

        const exactMatch = prospects.find((prospect: any) => {
          const prospectEmail = (prospect.email || '').trim().toLowerCase();
          const prospectEmail1 = (prospect.Email1 || '').trim().toLowerCase();
          const prospectEmail2 = (prospect.Email2 || '').trim().toLowerCase();
          const prospectEmailPro = (prospect.email_pro || '').trim().toLowerCase();

          return prospectEmail === trimmedEmail ||
            prospectEmail1 === trimmedEmail ||
            prospectEmail2 === trimmedEmail ||
            prospectEmailPro === trimmedEmail;
        });

        if (exactMatch) {
          // console.log('[checkDuplicateContactByEmail] Exact duplicate found in prospects:', exactMatch);
          return exactMatch;
        }
      }


      const partners = await this.executeCall('res.partner', 'search_read', [
        emailDomain
      ], {
        fields: ['id', 'name', 'FirstName', 'LastName', 'email', 'Email1', 'Email2', 'email_pro'],
        limit: 10
      });

      // console.log('[checkDuplicateContactByEmail] Partners search result:', partners);

      if (partners && Array.isArray(partners) && partners.length > 0) {
        const exactMatch = partners.find((partner: any) => {
          const partnerEmail = (partner.email || '').trim().toLowerCase();
          const partnerEmail1 = (partner.Email1 || '').trim().toLowerCase();
          const partnerEmail2 = (partner.Email2 || '').trim().toLowerCase();
          const partnerEmailPro = (partner.email_pro || '').trim().toLowerCase();

          return partnerEmail === trimmedEmail ||
            partnerEmail1 === trimmedEmail ||
            partnerEmail2 === trimmedEmail ||
            partnerEmailPro === trimmedEmail;
        });

        if (exactMatch) {
          // console.log('[checkDuplicateContactByEmail] Exact duplicate found in partners:', exactMatch);
          return exactMatch;
        }
      }

      // console.log('[checkDuplicateContactByEmail] No duplicate found');
      return null;
    } catch (error) {
      // console.error('[checkDuplicateContactByEmail] Error checking for duplicate contact by email:', error);
      return null;
    }
  }

  async checkDuplicateContactByPhone(phone: string, excludeContactId?: number): Promise<any | null> {
    try {

      const normalizePhone = (phoneStr: string): string => {
        return phoneStr?.replace(/[\s\-\(\)\+]/g, '').trim() || '';
      };

      const normalizedPhone = normalizePhone(phone);

      if (!normalizedPhone) {
        // console.log('[checkDuplicateContactByPhone] Missing phone');
        return null;
      }

      // console.log('[checkDuplicateContactByPhone] Checking for duplicate phone:', normalizedPhone, excludeContactId ? `(excluding contact ID: ${excludeContactId})` : '');

      // Build domain - exclude current contact if provided
      let phoneDomain: any[];

      if (excludeContactId && excludeContactId > 0) {
        // Exclude current contact: active AND id != excludeId AND (phone matches)
        phoneDomain = [
          '&', '&',
          ['active', '=', true],
          ['id', '!=', excludeContactId],
          '|', '|',
          ['phone', 'ilike', normalizedPhone],
          ['Phone1', 'ilike', normalizedPhone],
          ['Phone2', 'ilike', normalizedPhone]
        ];
      } else {
        // No exclusion needed
        phoneDomain = [
          '&',
          ['active', '=', true],
          '|', '|',
          ['phone', 'ilike', normalizedPhone],
          ['Phone1', 'ilike', normalizedPhone],
          ['Phone2', 'ilike', normalizedPhone]
        ];
      }

      // console.log('[checkDuplicateContactByPhone] Domain:', phoneDomain);

      // Check in prospects first
      const prospects = await this.executeCall('crm.prospect', 'search_read', [
        phoneDomain
      ], {
        fields: ['id', 'name', 'FirstName', 'LastName', 'phone', 'Phone1', 'Phone2'],
        limit: 10
      });

      // console.log('[checkDuplicateContactByPhone] Prospects search result:', prospects);

      if (prospects && Array.isArray(prospects) && prospects.length > 0) {
        // Filter for exact case-insensitive phone matches (normalized)
        const exactMatch = prospects.find((prospect: any) => {
          const prospectPhone = normalizePhone(prospect.phone || '');
          const prospectPhone1 = normalizePhone(prospect.Phone1 || '');
          const prospectPhone2 = normalizePhone(prospect.Phone2 || '');

          return prospectPhone === normalizedPhone ||
            prospectPhone1 === normalizedPhone ||
            prospectPhone2 === normalizedPhone;
        });

        if (exactMatch) {
          // console.log('[checkDuplicateContactByPhone] Exact duplicate found in prospects:', exactMatch);
          return exactMatch;
        }
      }

      // Also check in res.partner
      const partners = await this.executeCall('res.partner', 'search_read', [
        phoneDomain
      ], {
        fields: ['id', 'name', 'FirstName', 'LastName', 'phone', 'Phone1', 'Phone2'],
        limit: 10
      });

      // console.log('[checkDuplicateContactByPhone] Partners search result:', partners);

      if (partners && Array.isArray(partners) && partners.length > 0) {
        const exactMatch = partners.find((partner: any) => {
          const partnerPhone = normalizePhone(partner.phone || '');
          const partnerPhone1 = normalizePhone(partner.Phone1 || '');
          const partnerPhone2 = normalizePhone(partner.Phone2 || '');

          return partnerPhone === normalizedPhone ||
            partnerPhone1 === normalizedPhone ||
            partnerPhone2 === normalizedPhone;
        });

        if (exactMatch) {
          // console.log('[checkDuplicateContactByPhone] Exact duplicate found in partners:', exactMatch);
          return exactMatch;
        }
      }

      // console.log('[checkDuplicateContactByPhone] No duplicate found');
      return null;
    } catch (error) {
      // console.error('[checkDuplicateContactByPhone] Error checking for duplicate contact by phone:', error);
      return null;
    }
  }

  // ➕ CREATE CONTACT
  async createContact(contactData: any): Promise<number | null> {
    try {
      // Helper function to safely trim string fields
      const safeString = (value: any): string | false => {
        if (value === null || value === undefined || value === false) return false;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed === '' ? false : trimmed;
        }
        return false;
      };

      // Ensure seller-created prospects are attached to the current seller user.
      let effectiveSellerIds: number[] = [];
      if (Array.isArray((contactData as any).seller_ids) && (contactData as any).seller_ids.length > 0) {
        effectiveSellerIds = (contactData as any).seller_ids
          .map((x: any) => (typeof x === 'string' ? parseInt(x, 10) : x))
          .filter((n: any) => Number.isFinite(n));
      } else {
        const isSeller = await this.isSellerUser();
        const currentUserId = Number(this.user?.id || this.credentials?.userId || 0);
        if (isSeller && Number.isFinite(currentUserId) && currentUserId > 0) {
          effectiveSellerIds = [currentUserId];
        }
      }

      // Clean up the data and only include safe, known fields
      const safeFields = {
        name: safeString(contactData.name) || '',
        FirstName: safeString((contactData as any).FirstName),
        LastName: safeString((contactData as any).LastName),
        email: safeString(contactData.email),
        phone: safeString(contactData.phone),
        // Prefer new explicit international code fields
        international_code_phone_pro: safeString((contactData as any).international_code_phone_pro),
        mobile: safeString(contactData.mobile),
        mobile_country_code: safeString((contactData as any).mobile_country_code),
        street: safeString(contactData.street),
        street2: safeString(contactData.street2),
        main_street3: safeString((contactData as any).main_street3),
        city: safeString(contactData.city),
        zip: safeString(contactData.zip),
        website: safeString(contactData.website),
        function: safeString(contactData.function),
        // New personal/partner fields
        birth_date: safeString((contactData as any).birth_date),
        birth_date_partner: safeString((contactData as any).birth_date_partner),
        partner_name: safeString((contactData as any).partner_name),
        social_link: safeString((contactData as any).social_link),
        // Language: default to English (US) if not provided, but allow override from UI
        lang: contactData.lang || 'en_US',
        // Use title_ids from custom model (Many2one-like -> send single ID)
        title_ids: Array.isArray(contactData.title_ids) && contactData.title_ids.length > 0 && typeof contactData.title_ids[0] === 'number'
          ? contactData.title_ids[0]
          : false,
        Email1: safeString(contactData.Email1),
        Email2: safeString(contactData.Email2),
        // Phone 1/2 with their international codes
        international_code_phone1: safeString((contactData as any).international_code_phone1),
        Phone1: safeString(contactData.Phone1),
        international_code_phone2: safeString((contactData as any).international_code_phone2),
        Phone2: safeString(contactData.Phone2),
        BirthDate: contactData.BirthDate || false,
        ShippingAddress: safeString(contactData.ShippingAddress),

        Shipping_stree2: safeString(contactData.Shipping_stree2),
        Shipping_stree3: safeString(contactData.Shipping_stree3),
        ShippingAddress2: safeString(contactData.ShippingAddress2),
        ShippingAddress3: safeString(contactData.ShippingAddress3),

        Country_Shipping_Address: safeString(contactData.Country_Shipping_Address),
        State_Shipping_Address: safeString(contactData.State_Shipping_Address),
        City_Shipping_Address: safeString(contactData.City_Shipping_Address),
        Zip_Shipping_Address: safeString(contactData.Zip_Shipping_Address),
        BillingAddress: safeString(contactData.BillingAddress),
        Country_Billing_Address: safeString(contactData.Country_Billing_Address),
        State_Billing_Address: safeString(contactData.State_Billing_Address),
        City_Billing_Address: safeString(contactData.City_Billing_Address),
        Zip_Billing_Address: safeString(contactData.Zip_Billing_Address),
        // Company fields
        is_company: contactData.is_company || false,
        company_type: contactData.company_type || 'person', // 'person' or 'company'
        parent_id: contactData.parent_id || false, // Company reference in Odoo
        Company: safeString(contactData.Company), // Company field in Odoo
        // Upper country (FileMaker-style text field)
        countries_Country: safeString((contactData as any).countries_Country),
        // Send only numeric country_id; otherwise false
        country_id: typeof contactData.country_id === 'number' ? contactData.country_id : false, // Country Many2One field
        // Sellers (Many2many): preserve explicit value, otherwise auto-attach current seller user.
        seller_ids: effectiveSellerIds.length > 0
          ? [[6, 0, effectiveSellerIds]]
          : undefined,
        control_galleries_ids: contactData.control_galleries_ids || false, // Gallery (Many2One to control.galleries)
        // Calculated status (Many2one)
        calculated_status_ids: (contactData as any).calculated_status_ids || false,
        // Category (legacy text) and Many2one
        category_crm: safeString((contactData as any).category_crm),
        category_crm_ids: typeof (contactData as any).category_crm_ids === 'number' ? (contactData as any).category_crm_ids : false,
        // Id field (can be number or string)
        Id: (contactData as any).Id !== undefined && (contactData as any).Id !== false && (contactData as any).Id !== ''
          ? (typeof (contactData as any).Id === 'number' ? (contactData as any).Id : (typeof (contactData as any).Id === 'string' && !isNaN(Number((contactData as any).Id)) ? Number((contactData as any).Id) : (contactData as any).Id))
          : false,

        Id_with_c: safeString((contactData as any).Id_with_c) || safeString((contactData as any).IdEntity) || false,
      };


      // Remove all false values to avoid sending unnecessary fields
      const cleanedData = Object.entries(safeFields).reduce((acc, [key, value]) => {
        if (value !== false && value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      console.log('📝 Creating contact in crm.prospect with cleaned data:', cleanedData);

      const response = await this.executeCall(
        'crm.prospect',
        'create',
        [
          cleanedData // Contact fields to create
        ]
      );

      // Odoo returns the new contact ID as a number
      if (typeof response === 'number') {
        console.log(`✅ Contact created successfully in crm.prospect with ID: ${response}`);
        return response;
      } else {
        console.error('❌ Failed to create contact - invalid response:', response);
        return null;
      }
    } catch (error) {
      console.error('❌ Error creating contact:', error);
      throw error;
    }
  }


  // 🗄️ ARCHIVE CONTACT
  async archiveContact(contactId: number, modelName?: string): Promise<boolean> {
    try {

      let model: string;

      if (modelName) {

        model = modelName;
        console.log(`📋 Using provided model for archive: ${model}`);
      } else {

        model = 'res.partner';

        try {

          const prospectCheck = await this.executeCall('crm.prospect', 'read', [
            [contactId],
            ['id']
          ]);
          if (prospectCheck && Array.isArray(prospectCheck) && prospectCheck.length > 0) {
            model = 'crm.prospect';
            console.log(`📋 Contact ${contactId} verified in crm.prospect, using that model for archive`);
          }
        } catch (e) {

        }


        if (model === 'res.partner') {
          try {
            const partnerCheck = await this.executeCall('res.partner', 'read', [
              [contactId],
              ['id']
            ]);
            if (partnerCheck && Array.isArray(partnerCheck) && partnerCheck.length > 0) {
              model = 'res.partner';
              console.log(`📋 Contact ${contactId} verified in res.partner, using that model for archive`);
            }
          } catch (e) {

            console.log(`⚠️ Could not verify contact ${contactId} in either model, defaulting to res.partner`);
          }
        }
      }

      const response = await this.executeCall(
        model,
        'write',
        [
          [contactId], // Array with the contact ID
          {
            "active": false // Set active to false to archive
          }
        ]
      );

      return response === true;
    } catch (error) {
      console.error(`❌ Error archiving contact ${contactId}:`, error);
      throw error;
    }
  }

  // ✏️ EDIT ARTWORK
  async updateArtwork(artworkId: number, updateData: Partial<OdooArtwork>): Promise<boolean> {
    try {
      const response = await this.executeCall(
        'product.template',
        'write',
        [
          [artworkId], // Array with the artwork ID
          updateData   // Fields to update
        ]
      );

      return response === true;
    } catch (error) {
      console.error(`❌ Error updating artwork ${artworkId}:`, error);
      throw error;
    }
  }

  // ➕ CREATE ARTWORK
  async createArtwork(artworkData: Partial<OdooArtwork>): Promise<number | null> {
    try {
      const response = await this.executeCall(
        'product.template',
        'create',
        [
          artworkData // Artwork fields to create
        ]
      );

      // Odoo returns the new artwork ID as a number
      if (typeof response === 'number') {
        console.log(`✅ Artwork created successfully with ID: ${response}`);
        return response;
      } else {
        console.error('❌ Failed to create artwork - invalid response:', response);
        return null;
      }
    } catch (error) {
      console.error('❌ Error creating artwork:', error);
      throw error;
    }
  }

  // 🗄️ ARCHIVE ARTWORK
  async archiveArtwork(artworkId: number): Promise<boolean> {
    try {
      const response = await this.executeCall(
        'product.template',
        'write',
        [
          [artworkId], // Array with the artwork ID
          {
            "active": false // Set active to false to archive
          }
        ]
      );

      return response === true;
    } catch (error) {
      console.error(`❌ Error archiving artwork ${artworkId}:`, error);
      throw error;
    }
  }

  // 💰 GET CONTACT SALES HISTORY
  async getContactSalesHistory(contact: any): Promise<any[]> {

    try {
      const rawId = (contact as any)?.id;
      const partnerId = (typeof rawId === 'number' ? rawId : (rawId && /^\d+$/.test(String(rawId)) ? parseInt(String(rawId), 10) : undefined));
      const idWithC = (contact as any)?.Id_with_c || (contact as any)?.IdEntity || undefined;

      if (!partnerId && !idWithC) {
        console.warn('⚠️ Contact lacks id and Id_with_c/IdEntity; cannot fetch sales history.', contact);

        return [];
      }


      let result: any[] = await this.executeCall(
        'res.partner',
        'get_sales_history',
        [partnerId || false, idWithC || false, 200, 0]
      );

      if (!Array.isArray(result) || result.length === 0) return [];

      // Enrich with artwork image URLs from product.template (fast, cached on server)
      try {
        const ids: number[] = Array.from(new Set(
          result
            .map((r: any) => r?.product_template_id)
            .map((v: any) => Array.isArray(v) ? v[0] : v)
            .map((v: any) => (typeof v === 'number' ? v : (v && /^\d+$/.test(String(v)) ? parseInt(String(v), 10) : undefined)))
            .filter((v: any): v is number => Number.isFinite(v))
        ));

        if (ids.length > 0) {
          const tmplFields = [
            'id', 'name', 'res_artist_id', 'IdName', 'artwork_price_combined', 'list_price', 'Status', 'CRMTypeLayout',
            'NewMainLR', 'NewMainHR', 'main_super_picture_hd', 'main_picture_hd', 'main_web_picture', 'main_a6_picture',
            'detail_1_url', 'detail_2_url', 'perspective_url', 'back_pictures', 'frame_picture',
            'other_url', 'signatures_pictures', 'image_1920'
          ];
          const tmplRecs: any[] = await this.executeCall('product.template', 'read', [ids], { fields: tmplFields });
          const idToUrl: Record<number, string | undefined> = {};

          for (const rec of Array.isArray(tmplRecs) ? tmplRecs : []) {
            const url = this.getBestArtworkImageUrl(rec);
            if (url) idToUrl[rec.id] = url;
          }
          result = result.map((row: any) => {
            const raw = row?.product_template_id;
            const pid = Array.isArray(raw) ? raw[0] : (typeof raw === 'number' ? raw : (raw && /^\d+$/.test(String(raw)) ? parseInt(String(raw), 10) : undefined));
            const image_url = (pid && idToUrl[pid]) ? idToUrl[pid] : row?.image_url;
            return image_url ? { ...row, image_url } : row;
          });
        }
      } catch (_) {

      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching contact sales history:', error);
      return [];
    }
  }

  // 💰 GET SALES REVENUE (REAL DATA)
  async getSalesRevenue(startDate: string): Promise<number> {
    try {
      const domain = [
        "&",
        ['SoldDate', '>=', startDate],
        ['SoldPriceGlobalEur', '>', 0]
      ];

      const sales = await this.executeCall(
        'gallery.sales.lines.items',
        'search_read',
        [domain],
{
          fields: ['SoldPriceGlobalEur', 'Currency', 'SoldDate'],
          order: 'SoldDate desc'
        }
      );

      // Calculate total revenue in EUR
      let totalRevenue = 0;
      if (sales && Array.isArray(sales)) {
        totalRevenue = sales.reduce((sum: number, sale: any) => {
          return sum + (sale.SoldPriceGlobalEur || 0);
        }, 0);
      }

      return totalRevenue;
    } catch (error) {
      console.error('Error getting sales revenue:', error);
      return 0;
    }
  }

  // 🎨 GET ALL ARTWORKS (BASE FILTER)
  async getAllArtworks(limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [

      'id', 'name', 'res_artist_id', 'IdName', 'Category', 'Medium',

      'artwork_price_combined', 'Status', 'image_1920',
      'control_gallery_combined', 'SizeH', 'SizeL', 'create_date', 'Type'
    ];

    const domain = [
      "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];


    const options: any = {
      fields,
      order: 'entry_date desc, id desc'
    };


    // Only add limit and offset if limit > 0
    if (limit > 0) {
      options.limit = limit;
      options.offset = offset;
    }

    const result = await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      options
    );
    return result;
  }

  // 🎨 GET ARTWORKS BY ARTIST (AUTOSUGGESTION) - WITH ALL IMAGE URLS
  async getArtworksByArtist(artistName: string, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];
    console.log("artistName", artistName);

    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["res_artist_id", "ilike", artistName]
    ];

    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const result = await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, offset, 'create_date desc', shouldHide ? true : undefined]
    );
    return result;
  }

  // 🎨 GET ARTWORKS BY ARTIST ID OR NAME (enhanced version for flexible filtering)
  async getArtworksByArtistIdOrName(artistIdOrName: string | number, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];
    console.log("🎨 Searching artworks by artist ID or name:", artistIdOrName);


    let domain: any[];


    if (typeof artistIdOrName === 'number' || /^\d+$/.test(String(artistIdOrName))) {
      // Search by ID
      const artistId = typeof artistIdOrName === 'number' ? artistIdOrName : parseInt(String(artistIdOrName));
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["res_artist_id", "=", artistId]
      ];
      console.log("🆔 Searching by artist ID:", artistId);
    } else {
      // Search by name
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["res_artist_id", "ilike", String(artistIdOrName)]
      ];
      console.log("📝 Searching by artist name:", artistIdOrName);
    }

    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const result = await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, offset, 'create_date desc', shouldHide ? true : undefined]
    );
    return result;
  }

  // 🎨 GET ARTWORKS BY MULTIPLE ARTISTS (IDs or Names) - Enhanced for flexible filtering
  async getArtworksByArtistsIdOrName(artistsIdOrName: (string | number)[], limit = 50, offset = 0): Promise<OdooArtwork[]> {
    if (!artistsIdOrName || artistsIdOrName.length === 0) {
      return [];
    }

    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];
    console.log("🎨 Searching artworks by multiple artists (ID/Name):", artistsIdOrName);


    // Separate IDs and names
    const artistIds: number[] = [];
    const artistNames: string[] = [];


    artistsIdOrName.forEach(artist => {
      if (typeof artist === 'number' || /^\d+$/.test(String(artist))) {
        const id = typeof artist === 'number' ? artist : parseInt(String(artist));
        artistIds.push(id);
      } else {
        artistNames.push(String(artist));
      }
    });

    console.log("🆔 Artist IDs:", artistIds);
    console.log("📝 Artist Names:", artistNames);

    // Build OR conditions for IDs and names
    const conditions: any[] = [];

    // Add ID conditions
    if (artistIds.length > 0) {
      conditions.push(["res_artist_id", "in", artistIds]);
    }

    // Add name conditions (each name as a separate ilike condition)
    artistNames.forEach(name => {
      conditions.push(["res_artist_id", "ilike", name]);
    });

    if (conditions.length === 0) {
      return [];
    }

    // Build domain with OR logic
    let domain: any[];
    if (conditions.length === 1) {
      // Single condition
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        conditions[0]
      ];
    } else {
      // Multiple conditions with OR
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["|", ...Array(conditions.length - 1).fill("|"), ...conditions]
      ];
    }

    console.log("🔍 Odoo domain:", JSON.stringify(domain, null, 2));

    const shouldHide = await this.shouldHideArtworksWithoutImage();
    const result = await this.executeCall(
      'product.template',
      'search_read_artworks',
      [domain, fields, limit, offset, 'create_date desc', shouldHide ? true : undefined]
    );
    return result;
  }

  // 🎨 GET ARTWORKS BY ARTISTS, CATEGORIES, PRICES, DATES, TYPES AND STATUS (Ultimate filtering with ALL filters)
  async getArtworksByArtistsAndCategories(

    artistsIdOrName: (string | number)[],
    categories: string[] = [],
    priceRanges: string[] = [],
    minPrice?: number,
    maxPrice?: number,
    dateInStock: string[] = [],
    types: string[] = [],
    statuses: string[] = [],
    limit = 50,

    offset = 0
  ): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'Type'];
    console.log("🎨 Searching artworks by artists + categories + prices + dates + types + status:", { artistsIdOrName, categories, priceRanges, dateInStock, types, statuses });


    // Separate IDs and names
    const artistIds: number[] = [];
    const artistNames: string[] = [];


    if (artistsIdOrName) {
      artistsIdOrName.forEach(artist => {
        if (typeof artist === 'number' || /^\d+$/.test(String(artist))) {
          const id = typeof artist === 'number' ? artist : parseInt(String(artist));
          artistIds.push(id);
        } else {
          artistNames.push(String(artist));
        }
      });
    }

    console.log("🆔 Artist IDs:", artistIds);
    console.log("📝 Artist Names:", artistNames);
    console.log("🏷️ Categories:", categories);
    console.log("💰 Price Ranges:", priceRanges);
    console.log("📅 Date in Stock:", dateInStock);
    console.log("🎭 Types:", types);
    console.log("📊 Statuses:", statuses);

    // Build base domain conditions
    let domainConditions: any[] = [
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    // Build artist OR conditions
    const artistConditions: any[] = [];

    // Add ID conditions
    if (artistIds.length > 0) {
      artistConditions.push(["res_artist_id", "in", artistIds]);
    }

    // Add name conditions (each name as a separate ilike condition)
    artistNames.forEach(name => {
      artistConditions.push(["res_artist_id", "ilike", name]);
    });

    // Add artist conditions to domain if they exist
    if (artistConditions.length > 0) {
      if (artistConditions.length === 1) {
        domainConditions.push(artistConditions[0]);
      } else {
        // Multiple artist conditions with OR logic
        const orArray = Array(artistConditions.length - 1).fill("|");
        domainConditions.push([...orArray, ...artistConditions]);
      }
    }

    // 🏷️ Add category filtering if provided
    if (categories && categories.length > 0) {
      const validCategories = categories.filter(cat => cat !== 'All' && cat.trim() !== '');
      if (validCategories.length > 0) {
        domainConditions.push(["Category", "in", validCategories]);
      }
    }

    // 🎭 Add type filtering if provided
    if (types && types.length > 0) {
      const validTypes = types.filter(type => type !== 'All' && type.trim() !== '');

      if (validTypes.length > 0) {
        domainConditions.push(["Type", "in", validTypes]);
      }
    }

    // 💰 Add price range filtering if provided
    if (priceRanges && priceRanges.length > 0) {
      const priceConditions: any[] = [];

      priceRanges.forEach(priceRange => {
        switch (priceRange) {
          case '< 100K':
            priceConditions.push(["artwork_price_combined", "<", 100000]);
            break;
          case '100K - 500K':
            priceConditions.push([
              "&",
              ["artwork_price_combined", ">=", 100000],
              ["artwork_price_combined", "<=", 500000] // Use <= for inclusive range
            ]);
            break;
          case '500K - 1M':
            priceConditions.push([
              "&",
              ["artwork_price_combined", ">=", 500000],
              ["artwork_price_combined", "<=", 1000000] // Use <= for inclusive range
            ]);
            break;
          case '> 1M':
            priceConditions.push(["artwork_price_combined", ">=", 1000000]);
            break;
        }
      });

      if (priceConditions.length > 0) {
        if (priceConditions.length === 1) {
          domainConditions.push(priceConditions[0]);
        } else {
          // Correctly build the nested OR structure for multiple price ranges
          let orGroup = ['|', priceConditions[priceConditions.length - 2], priceConditions[priceConditions.length - 1]];
          for (let i = priceConditions.length - 3; i >= 0; i--) {
            orGroup = ['|', priceConditions[i], orGroup];
          }
          domainConditions.push(orGroup);
        }
      }
    }

    // Custom price range filter
    if (minPrice) {
      domainConditions.push(["artwork_price_combined", ">=", minPrice]);
    }
    if (maxPrice) {
      domainConditions.push(["artwork_price_combined", "<=", maxPrice]);
    }

    // 📅 Add date in stock filtering if provided
    if (dateInStock && dateInStock.length > 0) {
      const dateConditions: any[] = [];
      const now = new Date();


      dateInStock.forEach(dateRange => {
        let startDate: Date;


        switch (dateRange) {
          case 'Last 7 days':
            startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
          case 'Last 15 days':
            startDate = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
          case 'Last 30 days':
            startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
          case 'Last 90 days':
            startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
        }
      });

      if (dateConditions.length > 0) {
        if (dateConditions.length === 1) {
          // Single date range
          domainConditions.push(dateConditions[0]);
        } else {
          // Multiple date ranges with OR logic
          const orArray = Array(dateConditions.length - 1).fill("|");
          domainConditions.push([...orArray, ...dateConditions]);
        }
      }
    }

    // 📊 Add status filtering if provided
    if (statuses && statuses.length > 0) {
      const validStatuses = statuses.filter(status => status.trim() !== '');
      if (validStatuses.length > 0) {
        domainConditions.push(["Status", "in", validStatuses]);
      }
    }


    // Build final domain with proper AND logic
    const domain = domainConditions;

    console.log("🔍 Artists + Categories + Prices + Dates + Types + Status Odoo domain:", JSON.stringify(domain, null, 2));

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],

      {
        fields,

        limit,
        offset,
        order: 'entry_date desc, id desc'
      }
    );
  }

  // 🚀 NEW UNIFIED METHOD - Search artworks with ALL filters (special + normal statuses)
  async searchArtworksUnified(
    statuses: string[] = [],
    artists: (string | number)[] = [],
    categories: string[] = [],
    types: string[] = [],
    priceRanges: string[] = [],
    minPrice?: number,
    maxPrice?: number,
    dateFrom?: string,
    dateTo?: string,
    sortBy: string = 'id desc',
    limit: number = 20,
    offset: number = 0,
    locations: string[] = [],
    currency: string = 'EUR',
    dimensions?: {
      unit: 'cm' | 'inch';
      minWidth?: number;
      maxWidth?: number;
      minHeight?: number;
      maxHeight?: number;
    },
    domainExtra: any[] = []
  ): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'Type'];
    const shouldHide = await this.shouldHideArtworksWithoutImage();

    console.log("🚀 [UNIFIED] Searching artworks with unified method:", {
      statuses, artists, categories, types, priceRanges, minPrice, maxPrice, dateFrom, dateTo, currency, dimensions
    });

    const normalizedLocations = (locations || [])
      .filter((l) => l && l !== "All")
      .map((l) => l.toLowerCase());
    const params: any = {
      fields,
      limit,
      offset,
      order: sortBy,
      has_image: shouldHide ? true : undefined,
      statuses,
      artists,
      categories,
      types,
      price_ranges: priceRanges,
      min_price: minPrice,
      max_price: maxPrice,
      date_from: dateFrom,
      date_to: dateTo,
      locations: normalizedLocations,
      currency,
    };


    if (dimensions) {
      params.dimension_unit = dimensions.unit; // 'cm' or 'inch'
      if (dimensions.minWidth !== undefined && dimensions.minWidth > 0) {
        params.min_width = dimensions.minWidth;
      }
      if (dimensions.maxWidth !== undefined && dimensions.maxWidth > 0) {
        params.max_width = dimensions.maxWidth;
      }
      if (dimensions.minHeight !== undefined && dimensions.minHeight > 0) {
        params.min_height = dimensions.minHeight;
      }
      if (dimensions.maxHeight !== undefined && dimensions.maxHeight > 0) {
        params.max_height = dimensions.maxHeight;
      }
    }
    const result = await this.executeCall(
      'product.template',
      'search_read_artworks_unified',
      [domainExtra],
      params
    );


    if (result && result.length > 0) {
      const firstArtwork = result[0];
      console.log("[UNIFIED] First artwork currency fields:", {
        id: firstArtwork.id,
      });
    }

    return result;
  }

  // 🚀 NEW UNIFIED METHOD - Count artworks with ALL filters (special + normal statuses)
  async countArtworksUnified(
    statuses: string[] = [],
    artists: (string | number)[] = [],
    categories: string[] = [],
    types: string[] = [],
    priceRanges: string[] = [],
    minPrice?: number,
    maxPrice?: number,
    dateFrom?: string,
    dateTo?: string,

    locations: string[] = [],
    currency: string = 'EUR',
    dimensions?: {
      unit: 'cm' | 'inch';
      minWidth?: number;
      maxWidth?: number;
      minHeight?: number;
      maxHeight?: number;
    },
    domainExtra: any[] = []
  ): Promise<number> {
    const shouldHide = await this.shouldHideArtworksWithoutImage();


    console.log("🔢 [UNIFIED] Counting artworks with unified method:", {
      statuses, artists, categories, types, priceRanges, minPrice, maxPrice, dateFrom, dateTo, currency
    });

    const params: any = {
      has_image: shouldHide ? true : undefined,
      statuses,
      artists,
      categories,
      types,
      locations,
      price_ranges: priceRanges,
      min_price: minPrice,
      max_price: maxPrice,
      date_from: dateFrom,
      date_to: dateTo,
      currency,
    };


    if (dimensions) {
      params.dimension_unit = dimensions.unit; // 'cm' or 'inch'
      if (dimensions.minWidth !== undefined && dimensions.minWidth > 0) {
        params.min_width = dimensions.minWidth;
      }
      if (dimensions.maxWidth !== undefined && dimensions.maxWidth > 0) {
        params.max_width = dimensions.maxWidth;
      }
      if (dimensions.minHeight !== undefined && dimensions.minHeight > 0) {
        params.min_height = dimensions.minHeight;
      }
      if (dimensions.maxHeight !== undefined && dimensions.maxHeight > 0) {
        params.max_height = dimensions.maxHeight;
      }
    }
    return await this.executeCall(
      'product.template',
      'search_count_artworks_unified',
      [domainExtra],
      // {
      //   has_image: shouldHide ? true : undefined,
      //   statuses,
      //   artists,
      //   categories,
      //   types,
      //   locations,
      //   price_ranges: priceRanges,
      //   min_price: minPrice,
      //   max_price: maxPrice,
      //   date_from: dateFrom,
      //   date_to: dateTo,
      //   currency,
      // }
      params
    );
  }

  // 🎨 COUNT ARTWORKS BY ARTISTS, CATEGORIES, PRICES, DATES, TYPES AND STATUS (Ultimate count API)
  async countArtworksByArtistsAndCategories(

    artistsIdOrName: (string | number)[],
    categories: string[] = [],
    priceRanges: string[] = [],
    minPrice?: number,
    maxPrice?: number,
    dateInStock: string[] = [],
    types: string[] = [],
    statuses: string[] = [],
    locations: string[] = []
  ): Promise<number> {
    console.log("🔢 Counting artworks by artists + categories + prices + dates + types + status:", { artistsIdOrName, categories, priceRanges, dateInStock, types, statuses });
    // ⚡ Cache + inflight dedupe for count
    const TTL_MS = 30000;
    const cacheKey = `aw_count:${JSON.stringify({ artistsIdOrName, categories, priceRanges, minPrice, maxPrice, dateInStock, types, statuses })}`;
    const now = Date.now();
    const cached = this.artworksCountCache.get(cacheKey);
    if (cached && now - cached.timestamp < TTL_MS) return cached.data;
    const inflight = this.artworksCountInflight.get(cacheKey);
    if (inflight) return inflight;


    // Separate IDs and names
    const artistIds: number[] = [];
    const artistNames: string[] = [];


    if (artistsIdOrName) {
      artistsIdOrName.forEach(artist => {
        if (typeof artist === 'number' || /^\d+$/.test(String(artist))) {
          const id = typeof artist === 'number' ? artist : parseInt(String(artist));
          artistIds.push(id);
        } else {
          artistNames.push(String(artist));
        }
      });
    }

    // Build base domain conditions (same logic as main method)
    let domainConditions: any[] = [
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    // Build artist OR conditions
    const artistConditions: any[] = [];

    // Add ID conditions
    if (artistIds.length > 0) {
      artistConditions.push(["res_artist_id", "in", artistIds]);
    }

    // Add name conditions (each name as a separate ilike condition)
    artistNames.forEach(name => {
      artistConditions.push(["res_artist_id", "ilike", name]);
    });

    // Add artist conditions to domain if they exist
    if (artistConditions.length > 0) {
      if (artistConditions.length === 1) {
        domainConditions.push(artistConditions[0]);
      } else {
        // Multiple artist conditions with OR logic
        const orArray = Array(artistConditions.length - 1).fill("|");
        domainConditions.push([...orArray, ...artistConditions]);
      }
    }

    // 🏷️ Add category filtering if provided
    if (categories && categories.length > 0) {
      const validCategories = categories.filter(cat => cat !== 'All' && cat.trim() !== '');
      if (validCategories.length > 0) {
        domainConditions.push(["Category", "in", validCategories]);
      }
    }

    // 🎭 Add type filtering if provided
    if (types && types.length > 0) {
      const validTypes = types.filter(type => type !== 'All' && type.trim() !== '');

      if (validTypes.length > 0) {
        domainConditions.push(["Type", "in", validTypes]);
      }
    }

    // 💰 Add price range filtering if provided
    if (priceRanges && priceRanges.length > 0) {
      const priceConditions: any[] = [];

      priceRanges.forEach(priceRange => {
        switch (priceRange) {
          case '< 100K':
            priceConditions.push(["artwork_price_combined", "<", 100000]);
            break;
          case '100K - 500K':
            priceConditions.push([
              "&",
              ["artwork_price_combined", ">=", 100000],
              ["artwork_price_combined", "<=", 500000] // Use <= for inclusive range
            ]);
            break;
          case '500K - 1M':
            priceConditions.push([
              "&",
              ["artwork_price_combined", ">=", 500000],
              ["artwork_price_combined", "<=", 1000000] // Use <= for inclusive range
            ]);
            break;
          case '> 1M':
            priceConditions.push(["artwork_price_combined", ">=", 1000000]);
            break;
        }
      });

      if (priceConditions.length > 0) {
        if (priceConditions.length === 1) {
          domainConditions.push(priceConditions[0]);
        } else {
          // Correctly build the nested OR structure for multiple price ranges
          let orGroup = ['|', priceConditions[priceConditions.length - 2], priceConditions[priceConditions.length - 1]];
          for (let i = priceConditions.length - 3; i >= 0; i--) {
            orGroup = ['|', priceConditions[i], orGroup];
          }
          domainConditions.push(orGroup);
        }
      }
    }

    // Custom price range filter
    if (minPrice) {
      domainConditions.push(["artwork_price_combined", ">=", minPrice]);
    }
    if (maxPrice) {
      domainConditions.push(["artwork_price_combined", "<=", maxPrice]);
    }

    // 📅 Add date in stock filtering if provided
    if (dateInStock && dateInStock.length > 0) {
      const dateConditions: any[] = [];
      const now = new Date();


      dateInStock.forEach(dateRange => {
        let startDate: Date;

        switch (dateRange) {
          case 'Last 7 days':
            startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
          case 'Last 15 days':
            startDate = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
          case 'Last 30 days':
            startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
          case 'Last 90 days':
            startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
            dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
            break;
        }
      });

      if (dateConditions.length > 0) {
        if (dateConditions.length === 1) {
          // Single date range
          domainConditions.push(dateConditions[0]);
        } else {
          // Multiple date ranges with OR logic
          const orArray = Array(dateConditions.length - 1).fill("|");
          domainConditions.push([...orArray, ...dateConditions]);
        }
      }
    }

    // 🎭 Add type filtering if provided
    if (types && types.length > 0) {
      const validTypes = types.filter(type => type !== 'All' && type.trim() !== '');

      if (validTypes.length > 0) {
        if (validTypes.length === 1) {
          domainConditions.push(["Type", "=", validTypes[0]]);
        } else {
          const typeConditions = validTypes.map(type => ["Type", "=", type]);
          const orArray = Array(typeConditions.length - 1).fill("|");
          domainConditions.push([...orArray, ...typeConditions]);
        }
      }
    }

    // 📊 Add status filtering if provided
    if (statuses && statuses.length > 0) {
      const validStatuses = statuses.filter(status => status.trim() !== '');
      if (validStatuses.length > 0) {
        domainConditions.push(["Status", "in", validStatuses]);
      }
    }

    // 📍 Add location filtering if provided (use same field as getArtworksWithSorting)
    if (locations && locations.length > 0) {
      const validLocations = (locations || [])
        .filter((l) => l && l !== "All")
        .map((l) => String(l).toLowerCase());
      if (validLocations.length > 0) {
        // Use "control_gallery_combined" field with "ilike" for partial matching
        // (handles prefixes like "PickUp OGBH", "Local Consign out - OGBH-SUPPL")
        if (validLocations.length === 1) {
          domainConditions.push(["control_gallery_combined", "ilike", validLocations[0]]);
        } else {
          const locConditions: any[] = validLocations.map(loc => ["control_gallery_combined", "ilike", loc]);
          let orBlock: any = ["|", locConditions[0], locConditions[1]];
          for (let i = 2; i < locConditions.length; i++) {
            orBlock = ["|", orBlock, locConditions[i]];
          }
          domainConditions.push(orBlock);
        }
      }
    }

    // Build final domain with proper AND logic
    const domain = domainConditions;

    console.log("🔢 Ultimate COUNT domain:", JSON.stringify(domain, null, 2));

    const promise = this.executeCall(
      'product.template',
      'search_count',
      [domain]
    ).then((count: number) => {
      const num = Number(count || 0);
      this.artworksCountCache.set(cacheKey, { timestamp: Date.now(), data: num });
      return num;
    }).finally(() => {
      this.artworksCountInflight.delete(cacheKey);
    });
    this.artworksCountInflight.set(cacheKey, promise);
    const count = await promise;
    console.log(`🔢 Found ${count} total matching artworks`);
    return count;
  }

  // 🎨 GET ARTWORKS BY PRICE RANGE
  async getArtworksByPrice(priceRange: string, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];

    let domain: any[];

    switch (priceRange) {
      case '< 100K':
        domain = [
          "&", "&",
          ["active", "=", true],
          ["artwork_price_combined", ">", 1],
          ["artwork_price_combined", "<", 100000]
        ];
        break;
      case '100K - 500K':
        domain = [
          "&", "&",
          ["active", "=", true],
          ["artwork_price_combined", ">=", 100000],
          ["artwork_price_combined", "<=", 500000]
        ];
        break;
      case '500K - 1M':
        domain = [
          "&", "&",
          ["active", "=", true],
          ["artwork_price_combined", ">=", 500000],
          ["artwork_price_combined", "<=", 1000000]
        ];
        break;
      case '> 1M':
        domain = [
          "&",
          ["active", "=", true],
          ["artwork_price_combined", ">", 1000000]
        ];
        break;
      default: // 'All'
        domain = [
          "&",
          ["active", "=", true],
          ["artwork_price_combined", ">", 1]
        ];
    }


    const options: any = {
      fields,
      order: 'entry_date desc, id desc'
    };


    // Only add limit and offset if limit > 0
    if (limit > 0) {
      options.limit = limit;
      options.offset = offset;
    }

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      options
    );
  }

  // 🎨 GET ARTWORKS BY DATE IN STOCK (CREATE DATE) - WITH ALL IMAGE URLS
  async getArtworksByDateInStock(dateRange: string, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];

    const now = new Date();
    let startDate: Date;

    switch (dateRange) {
      case 'Last 7 days':
        startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 15 days':
        startDate = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 30 days':
        startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 3 months':
        startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 6 months':
        startDate = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
        break;
      case 'Last year':
        startDate = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
        break;
      default:
        // All time
        startDate = new Date('1900-01-01');
    }

    const startDateStr = startDate.toISOString().split('T')[0];

    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["create_date", ">=", startDateStr]
    ];


    const options: any = {
      fields,
      order: 'entry_date desc, id desc'
    };


    // Only add limit and offset if limit > 0
    if (limit > 0) {
      options.limit = limit;
      options.offset = offset;
    }

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      options
    );
  }

  // 🎨 GET ARTWORKS BY CATEGORY - WITH ALL IMAGE URLS
  async getArtworksByCategory(category: string, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];

    let domain: any[];
    if (category === 'All') {
      domain = [
        "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1]
      ];
    } else {
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["Category", "ilike", category]
      ];
    }


    const options: any = {
      fields,
      order: 'entry_date desc, id desc'
    };


    // Only add limit and offset if limit > 0
    if (limit > 0) {
      options.limit = limit;
      options.offset = offset;
    }

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      options
    );
  }

  // 🎨 GET ARTWORKS BY STATUS - WITH ALL IMAGE URLS
  async getArtworksByStatus(status: string, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];

    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["Status", "ilike", status]
    ];


    const options: any = {
      fields,
      order: 'entry_date desc, id desc'
    };


    // Only add limit and offset if limit > 0
    if (limit > 0) {
      options.limit = limit;
      options.offset = offset;
    }

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      options
    );
  }

  // 🎨 GET ARTWORKS BY TYPE - WITH ALL IMAGE URLS
  async getArtworksByType(type: string, limit = 50, offset = 0): Promise<OdooArtwork[]> {
    const fields = [...this.STANDARD_ARTWORK_FIELDS, 'create_date', 'Type'];

    let domain: any[];
    if (type === 'All') {
      domain = [
        "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1]
      ];
    } else {
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["Type", "ilike", type]
      ];
    }


    const options: any = {
      fields,
      order: 'entry_date desc, id desc'
    };

    // Only add limit and offset if limit > 0
    if (limit > 0) {
      options.limit = limit;
      options.offset = offset;
    }

    return await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      options
    );
  }

  // 🎨 COUNT ARTWORKS BY CATEGORY
  async countArtworksByCategory(category: string): Promise<number> {
    let domain: any[];
    if (category === 'All') {
      domain = [
        "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1]
      ];
    } else {
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["Category", "ilike", category]
      ];
    }

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 COUNT ARTWORKS BY STATUS
  async countArtworksByStatus(status: string): Promise<number> {
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["Status", "ilike", status]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 COUNT ARTWORKS BY PRICE RANGE
  async countArtworksByPrice(priceRange: string): Promise<number> {
    let domain: any[];

    switch (priceRange) {
      case '< 100K':
        domain = [
          "&", "&",
          ["active", "=", true],
          ["artwork_price_combined", ">", 1],
          ["artwork_price_combined", "<", 100000]
        ];
        break;
      case '100K - 500K':
        domain = [
          "&", "&",
          ["active", "=", true],
          ["artwork_price_combined", ">=", 100000],
          ["artwork_price_combined", "<=", 500000]
        ];
        break;
      case '500K - 1M':
        domain = [
          "&", "&",
          ["active", "=", true],
          ["artwork_price_combined", ">=", 500000],
          ["artwork_price_combined", "<=", 1000000]
        ];
        break;
      case '> 1M':
        domain = [
          "&",
          ["active", "=", true],
          ["artwork_price_combined", ">", 1000000]
        ];
        break;
      default: // 'All'
        domain = [
          "&",
          ["active", "=", true],
          ["artwork_price_combined", ">", 1]
        ];
    }

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 COUNT ARTWORKS BY TYPE
  async countArtworksByType(type: string): Promise<number> {
    let domain: any[];
    if (type === 'All') {
      domain = [
        "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1]
      ];
    } else {
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["Type", "ilike", type]
      ];
    }

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 COUNT ALL ARTWORKS
  async countAllArtworks(): Promise<number> {
    const domain = [
      "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 COUNT ARTWORKS BY ARTIST
  async countArtworksByArtist(artistName: string): Promise<number> {
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["res_artist_id", "ilike", artistName]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 COUNT ARTWORKS BY DATE IN STOCK
  async countArtworksByDateInStock(dateRange: string): Promise<number> {
    const now = new Date();
    let startDate: Date;

    switch (dateRange) {
      case 'Last 7 days':
        startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 15 days':
        startDate = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 30 days':
        startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 3 months':
        startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
        break;
      case 'Last 6 months':
        startDate = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
        break;
      case 'Last year':
        startDate = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
        break;
      default:
        // All time
        startDate = new Date('1900-01-01');
    }

    const startDateStr = startDate.toISOString().split('T')[0];

    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["create_date", ">=", startDateStr]
    ];

    return await this.executeCall(
      'product.template',
      'search_count',
      [domain]
    );
  }

  // 🎨 GET ARTIST SUGGESTIONS (fast, direct artist search)
  async getArtistSuggestions(searchTerm: string, limit = 10): Promise<string[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }


    const artistDomain: any[] = [
      ["name", "ilike", searchTerm]
    ];

    const artists = await this.executeCall(
      'res.artist',
      'search_read',
      [artistDomain],
      { fields: ['name'], limit: Math.max(50, limit * 3), order: 'name asc' }
    );

    const namesFromArtist = Array.isArray(artists)
      ? artists

        .map((a: any) => String(a.name || '').trim())
        .filter((n: string) => n.length > 0)
      : [];


    if (namesFromArtist.length === 0) {
      const domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["res_artist_id", "ilike", searchTerm]
      ];

      const results = await this.executeCall(
        'product.template',
        'search_read',
        [domain],
        {
          fields: ['res_artist_id'],
          limit: Math.max(50, limit * 3),
          order: 'res_artist_id asc'
        }
      );

      const fallbackNames = (Array.isArray(results) ? results : [])
        .map((artwork: any) => Array.isArray(artwork.res_artist_id) ? artwork.res_artist_id[1] : null)
        .filter((name: string | null) => name !== null && name.toLowerCase().includes(searchTerm.toLowerCase()))

        .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index);
      return fallbackNames.sort().slice(0, limit);
    }


    const unique = Array.from(new Set(namesFromArtist));
    return unique.slice(0, limit);
  }

  // 🎨 GET ARTIST AUTOSUGGESTIONS WITH ID AND NAME (enhanced version for ID/name filtering)
  async getArtistSuggestionsWithId(searchTerm: string, limit = 10): Promise<{ id: number, name: string }[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    // Check if searchTerm is a number (ID search)
    const isIdSearch = /^\d+$/.test(searchTerm.trim());

    let domain: any[];
    if (isIdSearch) {
      // Search by ID
      const artistId = parseInt(searchTerm.trim());
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["res_artist_id", "=", artistId]
      ];
    } else {
      // Search by name (existing logic)
      domain = [
        "&", "&",
        ["active", "=", true],
        ["artwork_price_combined", ">", 1],
        ["res_artist_id", "ilike", searchTerm]
      ];
    }

    const results = await this.executeCall(
      'product.template',
      'search_read',
      [domain],
{
        fields: ['res_artist_id'],
        limit: limit * 2, // Get more to account for duplicates
        order: 'res_artist_id asc'
      }
    );

    // Extract unique artists with ID and name
    const artistMap = new Map<number, string>();

    results.forEach((artwork: any) => {
      if (Array.isArray(artwork.res_artist_id) && artwork.res_artist_id.length >= 2) {
        const [artistId, artistName] = artwork.res_artist_id;


        // For name search, filter by name matching
        if (!isIdSearch) {
          if (artistName && artistName.toLowerCase().includes(searchTerm.toLowerCase())) {
            artistMap.set(artistId, artistName);
          }
        } else {
          // For ID search, add directly
          artistMap.set(artistId, artistName);
        }
      }
    });

    // Convert to array and sort
    const artistList = Array.from(artistMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, limit);

    return artistList;
  }

  async getAllArtists(): Promise<{ id: number; name: string }[]> {
    const artistMap = new Map<number, string>();
    const limit = 500;
    let offset = 0;
    const availableArtworkDomain: any[] = [
      ["active", "=", true],
      ["Status", "in", ["In stock", "In transit", "On Order", "On order", "On order unpaid", "On order in payment"]],
      ["res_artist_id", "!=", false],
    ];

    const addArtistFromArtwork = (artwork: any) => {
      if (
        Array.isArray(artwork?.res_artist_id) &&
        artwork.res_artist_id.length >= 2
      ) {
        const [artistId, artistName] = artwork.res_artist_id;
        const id = Number(artistId);
        const name = String(artistName || "").trim();
        if (Number.isFinite(id) && id > 0 && name) {
          artistMap.set(id, name);
        }
      }
    };

    while (true) {
      const artworks = await this.executeCall(
        "product.template",
        "search_read",
        [availableArtworkDomain],
        {
          fields: ["res_artist_id"],
          limit,
          offset,
          order: "res_artist_id asc",
        },
      );

      if (!Array.isArray(artworks) || artworks.length === 0) {
        break;
      }

      artworks.forEach(addArtistFromArtwork);

      if (artworks.length < limit) {
        break;
      }

      offset += artworks.length;
    }

    return Array.from(artistMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // 🎨 BATCH LOOKUP ARTIST NAMES BY IDs - MUCH faster than individual calls
  async getArtistNamesByIds(artistIds: number[]): Promise<Map<number, string>> {
    const artistMap = new Map<number, string>();

    if (artistIds.length === 0) {
      return artistMap;
    }

    try {
      console.log(`🎨 Batch fetching names for ${artistIds.length} artists:`, artistIds.slice(0, 10));

      // Search for artworks with any of these artist IDs (single API call)
      const domain = [
        "&",
        ["active", "=", true],
        ["res_artist_id", "in", artistIds]
      ];

      const response = await this.executeCall('product.template', 'search_read', [
        domain,
        ['res_artist_id'],
        0, // offset
        1000 // Get up to 1000 artworks to cover all artists
      ]);

      // Build artist ID -> name mapping
      response.forEach((artwork: any) => {
        if (Array.isArray(artwork.res_artist_id) && artwork.res_artist_id.length >= 2) {
          const [artistId, artistName] = artwork.res_artist_id;
          if (artistIds.includes(artistId) && !artistMap.has(artistId)) {
            artistMap.set(artistId, artistName);
          }
        }
      });

      // Add fallbacks for any artist IDs not found
      artistIds.forEach(id => {
        if (!artistMap.has(id)) {
          artistMap.set(id, `Artist ID ${id}`);
        }
      });

      console.log(`✅ Resolved ${artistMap.size} artist names in single batch call`);
      return artistMap;
    } catch (error) {
      console.error('❌ Error in batch artist lookup:', error);
      // Return fallbacks for all IDs on error
      artistIds.forEach(id => {
        artistMap.set(id, `Artist ID ${id}`);
      });
      return artistMap;
    }
  }

  // 🎨 GET ARTIST BY NAME (returns ID and name)
  async getArtistByName(artistName: string): Promise<{ id: number, name: string } | null> {
    if (!artistName || artistName.trim().length < 2) {
      return null;
    }

    console.log('🎨 Searching for artist by name:', artistName);
    const trimmedName = artistName.trim();


    try {
      const artistDomain: any[] = [
        ["name", "ilike", trimmedName]
      ];

      const artists = await this.executeCall(
        'res.artist',
        'search_read',
        [artistDomain],
        { fields: ['id', 'name'], limit: 10, order: 'name asc' }
      );

      if (Array.isArray(artists) && artists.length > 0) {

        let matchedArtist = artists.find((a: any) =>
          a.name && a.name.trim().toLowerCase() === trimmedName.toLowerCase()
        );

        if (!matchedArtist) {

          matchedArtist = artists.find((a: any) => {
            const artistNameLower = (a.name || '').trim().toLowerCase();
            const searchNameLower = trimmedName.toLowerCase();
            return artistNameLower.includes(searchNameLower) ||
              searchNameLower.includes(artistNameLower);
          });
        }

        if (matchedArtist && matchedArtist.id && matchedArtist.name) {
          console.log('Found artist in res.artist:', { id: matchedArtist.id, name: matchedArtist.name });
          return { id: matchedArtist.id, name: matchedArtist.name.trim() };
        }
      }
    } catch (error) {
      console.warn('Error searching res.artist, falling back to artworks:', error);
    }


    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["res_artist_id", "ilike", trimmedName]
    ];

    const results = await this.executeCall(
      'product.template',
      'search_read',
      [domain],
      {
        fields: ['res_artist_id'],
        limit: 10,
        order: 'res_artist_id asc'
      }
    );

    console.log('Artist search results from artworks:', results);

    if (results && results.length > 0) {
      // First, try exact match
      for (const artwork of results) {

        if (Array.isArray(artwork.res_artist_id) && artwork.res_artist_id.length === 2) {
          const [artistId, artistNameFromOdoo] = artwork.res_artist_id;

          const artistNameLower = String(artistNameFromOdoo || '').trim().toLowerCase();
          const searchNameLower = trimmedName.toLowerCase();

          // Exact match
          if (artistNameLower === searchNameLower) {
            console.log('🎨 Found exact match in artworks:', { id: artistId, name: artistNameFromOdoo });
            return { id: artistId, name: String(artistNameFromOdoo).trim() };
          }
        }
      }

      // Then, try partial match
      for (const artwork of results) {
        if (Array.isArray(artwork.res_artist_id) && artwork.res_artist_id.length === 2) {
          const [artistId, artistNameFromOdoo] = artwork.res_artist_id;
          const artistNameLower = String(artistNameFromOdoo || '').trim().toLowerCase();
          const searchNameLower = trimmedName.toLowerCase();

          if (artistNameLower.includes(searchNameLower) ||
            searchNameLower.includes(artistNameLower)) {
            console.log('Found partial match in artworks:', { id: artistId, name: artistNameFromOdoo });
            return { id: artistId, name: String(artistNameFromOdoo).trim() };
          }
        }
      }
    }

    console.log('🎨 Artist not found:', artistName);
    return null;
  }

  // 🔍 GET CONTACTS WITH INTERESTS (paginated for Interests page)
  async getContactsWithInterests(

    page: number = 1,

    limit: number = 20,
    filters?: {
      searchTerm?: string;
      artistFilter?: string;
      contactFilter?: string;
      countryFilter?: string;
    }
  ): Promise<any[]> {
    console.log(`🔍 Fetching contacts with interests - page ${page}, limit ${limit}...`);
    if (filters) {
      console.log('🔍 Applied filters:', filters);
    }

    // Build domain with filters
    let domain: any[] = [
      "&",
      ["active", "=", true],
      ["interest_artist_ids", "!=", false] // Has interests
    ];

    // Add filters to domain
    if (filters?.contactFilter) {
      domain = ["&", domain, ["name", "ilike", filters.contactFilter]];
    }

    if (filters?.countryFilter) {
      domain = ["&", domain, ["Country_Shipping_Address", "ilike", filters.countryFilter]];
    }

    // Artist filter - note: we'll also filter during processing for precise matching
    if (filters?.artistFilter) {
      console.log('🎨 Adding artist filter to domain:', filters.artistFilter);
      // This is a broader filter - precise filtering happens during interest processing
    }

    // Global search term
    if (filters?.searchTerm) {

      domain = ["&", domain, ["|", "|",

        ["name", "ilike", filters.searchTerm],
        ["email", "ilike", filters.searchTerm],
        ["Country_Shipping_Address", "ilike", filters.searchTerm]
      ]];
    }

    console.log('🔍 Final domain:', JSON.stringify(domain));

    const offset = (page - 1) * limit;

    const result = await this.executeCall(
      'res.partner',
      'search_read',
      [domain],

      {
        fields: [
          'id', 'name', 'email', 'phone', 'city',

          'Country_Shipping_Address', 'client_classifications',
          'interest_artist_ids', // This should include both ID and name
          'control_galleries_ids' // Gallery information
        ],
        limit,
        offset,
        order: 'name asc'
      }
    );

    console.log(`🔍 Page ${page} returned ${result?.length || 0} contacts with interests`);
    if (result && result.length > 0) {
      console.log('🔍 First contact sample:', result[0]);
      console.log('🔍 Sample interest_artist_ids format:', result[0].interest_artist_ids);
    }

    return result || [];
  }

  // 🔍 GET TOTAL COUNT OF INTERESTS (with filters)
  async getTotalInterestsCount(filters?: {
    searchTerm?: string;
    artistFilter?: string;
    contactFilter?: string;
    countryFilter?: string;
  }): Promise<number> {
    console.log('🔍 Getting total count of interests...');
    if (filters) {
      console.log('🔍 Count with filters:', filters);
    }

    // Build domain with filters (same as getContactsWithInterests)
    let domain: any[] = [
      "&",
      ["active", "=", true],
      ["interest_artist_ids", "!=", false] // Has interests
    ];

    // Add filters to domain
    if (filters?.contactFilter) {
      domain = ["&", domain, ["name", "ilike", filters.contactFilter]];
    }

    if (filters?.countryFilter) {
      domain = ["&", domain, ["Country_Shipping_Address", "ilike", filters.countryFilter]];
    }

    // Artist filter - note: we'll also filter during processing for precise matching
    if (filters?.artistFilter) {
      console.log('🎨 Adding artist filter to count domain:', filters.artistFilter);
      // This is a broader filter - precise filtering happens during interest processing
    }

    // Global search term
    if (filters?.searchTerm) {

      domain = ["&", domain, ["|", "|",

        ["name", "ilike", filters.searchTerm],
        ["email", "ilike", filters.searchTerm],
        ["Country_Shipping_Address", "ilike", filters.searchTerm]
      ]];
    }

    const contactCount = await this.executeCall(
      'res.partner',
      'search_count',
      [domain]
    );

    console.log(`🔍 Filtered contacts with interests: ${contactCount}`);

    // Quick sample to estimate interests per contact
    const sampleContacts = await this.executeCall(
      'res.partner',
      'search_read',
      [domain],

      {

        fields: ['interest_artist_ids'],
        limit: 50, // Sample only 50 contacts for speed
        order: 'id desc' // Get recent contacts
      }
    );

    let avgInterestsPerContact = 1; // Default assumption
    if (sampleContacts && sampleContacts.length > 0) {
      let totalSampleInterests = 0;
      for (const contact of sampleContacts) {
        if (contact.interest_artist_ids && Array.isArray(contact.interest_artist_ids)) {
          totalSampleInterests += contact.interest_artist_ids.length;
        }
      }
      avgInterestsPerContact = totalSampleInterests / sampleContacts.length;
      console.log(`🔍 Sample: ${sampleContacts.length} contacts, ${totalSampleInterests} interests, avg: ${avgInterestsPerContact.toFixed(2)}`);
    }

    const estimatedTotal = Math.ceil(contactCount * avgInterestsPerContact);
    console.log(`🔍 Estimated filtered interests: ${estimatedTotal}`);
    return estimatedTotal;
  }

  // === Interests (New backend APIs) ===
  async searchPartnersInterests(filters: {
    interest_artist_ids?: number[];
    contact_ids?: number[];
    country_ids?: number[];
  }, offset = 0, limit = 20, order = 'id desc'): Promise<any[]> {
    const result = await this.executeCall(
      'res.partner',
      'search_partners_interests',
      [filters, offset, limit, order]
    );
    return Array.isArray(result) ? result : [];
  }

  async searchPartnersInterestsCount(filters: {
    interest_artist_ids?: number[];
    contact_ids?: number[];
    country_ids?: number[];
  }): Promise<number> {
    const result = await this.executeCall(
      'res.partner',
      'search_partners_interests_count',
      [filters]
    );
    return Number(result) || 0;
  }

  async interestArtistSuggestionsRich(q: string, limit = 10): Promise<Array<{ id: number; name: string }>> {
    try {
      const res = await this.executeCall('res.partner', 'interest_artist_suggestions', [q, limit]);
      return Array.isArray(res) ? res : [];
    } catch (err) {
      console.warn('interestArtistSuggestionsRich failed, falling back to legacy suggestions:', err);
      // Fallback to legacy name-only suggestions via artworks
      const names = await this.getInterestArtistSuggestions(q);
      // Use negative ids to mark as placeholders (force resolver later)
      return names.map((name: string, idx: number) => ({ id: -(idx + 1), name }));
    }
  }

  async interestContactSuggestionsRich(q: string, limit = 10): Promise<Array<{ id: number; name: string }>> {
    const res = await this.executeCall('res.partner', 'interest_contact_suggestions', [q, limit]);
    return Array.isArray(res) ? res : [];
  }

  async interestCountrySuggestionsRich(q: string, limit = 10): Promise<Array<{ id: number; name: string }>> {
    const res = await this.executeCall('res.partner', 'interest_country_suggestions', [q, limit]);
    return Array.isArray(res) ? res : [];
  }

  // Flat interests (one row per partner x artist)
  async searchInterestsFlat(
    filters: {
      interest_artist_ids?: number[];
      contact_ids?: number[];
      country_ids?: number[];
      interest_date_from?: string;
      interest_date_to?: string;
    },
    offset = 0,
    limit = 20,
    order = 'partner_id desc'
  ): Promise<Array<{
    partner_id: number;
    partner_name: string;
    partner_email?: string;
    partner_phone?: string;
    partner_city?: string;
    country_id?: number;
    country_name?: string;
    artist_id: number;
    artist_name: string;
    client_classifications?: string;
    _model?: 'crm.prospect' | 'res.partner';
    seller_names?: string;
    gallery_name?: string;
    interest_date?: string;
    interest_origin?: 'seller' | 'purchased';
    interest_added_date?: string;
    date_added?: string;
    relation_create_date?: string;
    write_date?: string;
    partner_write_date?: string;
    partner_create_date?: string;
    create_date?: string;
  }>> {
    const res = await this.executeCall('res.partner', 'search_interests_flat', [filters, offset, limit, order]);
    return Array.isArray(res) ? res : [];
  }

  async searchInterestsFlatCount(
    filters: {
      interest_artist_ids?: number[];
      contact_ids?: number[];
      country_ids?: number[];
      interest_date_from?: string;
      interest_date_to?: string;
    }
  ): Promise<number> {
    const res = await this.executeCall('res.partner', 'search_interests_flat_count', [filters]);
    return Number(res) || 0;
  }

  async getContactsInterestedInArtistDetailed(artistName: string): Promise<any[]> {
    try {
      const artistResult = await this.getArtistByName(artistName);
      if (!artistResult || !artistResult.id) {
        return [];
      }

      const rows = await this.searchInterestsFlat(
        { interest_artist_ids: [artistResult.id] },
        0,
        200,
        'interest_date desc, partner_id desc'
      );

      const seenPartnerIds = new Set<number>();
      return (Array.isArray(rows) ? rows : [])
        .filter((row: any) => row?._model === 'res.partner')
        .filter((row: any) => {
          const partnerId = Number(row?.partner_id);
          if (!Number.isFinite(partnerId) || partnerId <= 0) return false;
          if (seenPartnerIds.has(partnerId)) return false;
          seenPartnerIds.add(partnerId);
          return true;
        })
        .map((row: any) => ({
          id: Number(row.partner_id),
          name: row.partner_name || '',
          email: row.partner_email || '',
          phone: row.partner_phone || '',
          client_classifications: row.client_classifications || '',
          Country_Shipping_Address: row.country_name || '',
          interest_origin: row.interest_origin || 'seller',
        }));
    } catch (error) {
      console.error('Error fetching detailed contacts interested in artist:', error);
      return [];
    }
  }

  // Resolve artist IDs by names (case-insensitive ilike). Returns mapping name -> ids
  async findArtistIdsByNames(names: string[]): Promise<Record<string, number[]>> {
    const mapping: Record<string, number[]> = {};
    const uniqueNames = Array.from(new Set((names || []).map(n => (n || '').trim()).filter(Boolean)));
    if (uniqueNames.length === 0) return mapping;

    // Query in batches to keep payload small
    const batchSize = 10;
    for (let i = 0; i < uniqueNames.length; i += batchSize) {
      const batch = uniqueNames.slice(i, i + batchSize);
      // Build OR domain: (name ilike n1) OR (name ilike n2) ...
      const orDomain: any[] = [];
      for (const n of batch) {
        if (orDomain.length === 0) {
          orDomain.push(['name', 'ilike', n]);
        } else {
          orDomain.unshift('|');
          orDomain.push(['name', 'ilike', n]);
        }
      }
      const res = await this.executeCall('res.artist', 'search_read', [orDomain], { fields: ['id', 'name'], limit: 200 });
      const byName: Record<string, number[]> = {};
      if (Array.isArray(res)) {
        for (const row of res) {
          const rname = String(row.name || '');
          const rid = Number(row.id);
          // Associate with all batch names it matches (ilike)
          for (const queryName of batch) {
            if (rname.toLowerCase().includes(queryName.toLowerCase())) {
              if (!byName[queryName]) byName[queryName] = [];
              byName[queryName].push(rid);
            }
          }
        }
      }
      for (const n of batch) {
        if (byName[n] && byName[n].length > 0) {
          mapping[n] = Array.from(new Set(byName[n]));
        } else {
          mapping[n] = [];
        }
      }
    }
    return mapping;
  }

  // 🎨 GET ARTIST SUGGESTIONS FOR INTERESTS
  async getInterestArtistSuggestions(query: string): Promise<string[]> {
    if (!query || query.trim().length < 2) return [];


    console.log('🎨 Getting artist suggestions for interests:', query);

    // Search artworks for artists matching the query
    const domain = [
      "&", "&",
      ["active", "=", true],
      ["artwork_price_combined", ">", 1],
      ["res_artist_id", "ilike", query.trim()]
    ];

    const artworks = await this.executeCall(
      'product.template',
      'search_read',
      [domain],
{
        fields: ['res_artist_id'],
        limit: 50,
        order: 'res_artist_id asc'
      }
    );

    const artistNames = new Set<string>();
    if (artworks && artworks.length > 0) {
      for (const artwork of artworks) {
        if (Array.isArray(artwork.res_artist_id) && artwork.res_artist_id.length >= 2) {
          const artistName = artwork.res_artist_id[1];
          if (artistName.toLowerCase().includes(query.toLowerCase())) {
            artistNames.add(artistName);
          }
        }
      }
    }

    const suggestions = Array.from(artistNames)
      .sort()
      .slice(0, 5);
    console.log('🎨 Artist suggestions found:', suggestions.length);
    return suggestions;
  }

  // 👤 GET CONTACT SUGGESTIONS FOR INTERESTS  
  async getInterestContactSuggestions(query: string): Promise<string[]> {
    if (!query || query.trim().length < 2) return [];


    console.log('👤 Getting contact suggestions for interests:', query);


    const domain = [
      "&", "&",
      ["active", "=", true],
      ["interest_artist_ids", "!=", false],
      ["name", "ilike", query.trim()]
    ];

    const contacts = await this.executeCall(
      'res.partner',
      'search_read',
      [domain],

      {

        fields: ['name'],
        limit: 20,
        order: 'name asc'
      }
    );


    const suggestions = contacts
      ? contacts.map((contact: any) => contact.name).slice(0, 5)
      : [];


    console.log('👤 Contact suggestions found:', suggestions.length);
    return suggestions;
  }

  // 🌍 GET COUNTRY SUGGESTIONS FOR INTERESTS
  async getInterestCountrySuggestions(query: string): Promise<string[]> {
    if (!query || query.trim().length < 2) return [];


    console.log('🌍 Getting country suggestions for interests:', query);


    const domain = [
      "&", "&",
      ["active", "=", true],
      ["interest_artist_ids", "!=", false],
      ["Country_Shipping_Address", "ilike", query.trim()]
    ];

    const contacts = await this.executeCall(
      'res.partner',
      'search_read',
      [domain],
{
        fields: ['Country_Shipping_Address'],
        limit: 50
      }
    );

    const countries = new Set<string>();
    if (contacts && contacts.length > 0) {
      for (const contact of contacts) {

        if (contact.Country_Shipping_Address &&
          contact.Country_Shipping_Address.toLowerCase().includes(query.toLowerCase())) {

          countries.add(contact.Country_Shipping_Address);
        }
      }
    }

    const suggestions = Array.from(countries).slice(0, 5);
    console.log('🌍 Country suggestions found:', suggestions.length);
    return suggestions;
  }


  // 🔗 NEW: Generate shareable PDF link

  async generatePDFLink(artwork: any): Promise<{
    success: boolean;
    linkId?: string;
    shareableLink?: string;
    permanent?: boolean;
    size?: string;
    duration?: string;
    error?: string;
  }> {
    try {
      console.log('🔗 Generating shareable PDF link for:', artwork.title || artwork.name);

      // ✅ Send artwork data directly (consistent with downloadArtworkPDF)
      const response = await fetch(`${this.backendURL}/api/pdf/artwork/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(artwork)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔗 PDF link generated successfully:', result);

      return result;
    } catch (error) {
      console.error('❌ PDF link generation failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate PDF link'
      };
    }
  }

  // 📥 Download PDF method (existing)
  async downloadArtworkPDF(artworkData: any): Promise<void> {
    try {
      console.log(`🎨 Generating PDF for artwork ${artworkData.id || artworkData.name} with provided data...`);

      // ✅ Send artwork data directly (not wrapped in { artwork: })
      const response = await fetch(`${this.backendURL}/api/pdf/artwork`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
        body: JSON.stringify(artworkData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend PDF error:', errorText);
        throw new Error(`PDF generation failed: ${response.status} ${response.statusText}`);
      }

      // Check content type
      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('application/pdf')) {
        const text = await response.text();
        console.error('❌ Expected PDF but got:', text.substring(0, 500));
        throw new Error(`Expected PDF but received: ${contentType}`);
      }

      const blob = await response.blob();


      if (blob.size === 0) {
        throw new Error('Received empty PDF file');
      }


      console.log(`✅ PDF generated successfully, size: ${blob.size} bytes`);

      // Download the PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // ✅ Use correct field names: 'name' for Odoo data, 'title' for frontend data
      const artworkTitle = artworkData.name || artworkData.title || 'artwork';
      link.download = `artwork_${artworkData.id}_${artworkTitle.replace(/[^a-zA-Z0-9\s]/g, '_')}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ PDF downloaded successfully for artwork ${artworkData.id}`);
    } catch (error) {
      console.error('❌ PDF download error:', error);
      alert('Failed to download PDF: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  // ✨ ACTIVITY SYSTEM METHODS

  // Map legacy/alias model names to real Odoo model names
  private normalizeResModel(modelName: string): string {
    const aliases: Record<string, string> = {
      'oeuvre.oeuvre': 'product.template',
    };
    return aliases[modelName] || modelName;
  }

  // Get model ID from ir.model table
  async getModelId(modelName: string): Promise<number | null> {
    try {
      const normalized = this.normalizeResModel(modelName);
      console.log('🔍 Looking up model ID for:', normalized);
      const response = await this.executeCall(
        'ir.model',
        'search_read',
        [[['model', '=', normalized]]],
        { fields: ['id', 'model'] }
      );

      if (response && response.length > 0) {
        console.log('✅ Model ID found:', response[0].id, 'for model:', normalized);
        return response[0].id;
      }

      console.warn('⚠️ Model not found:', normalized);
      return null;
    } catch (error) {
      console.error('❌ Failed to get model ID:', error);
      return null;
    }
  }

  // Get activity types from mail.activity.type
  async getActivityTypes(): Promise<OdooActivityType[]> {
    try {
      console.log('🎯 Fetching activity types from Odoo...');
      const response = await this.executeCall(
        'mail.activity.type',
        'search_read',
        [[]],
        { fields: ['id', 'name', 'category', 'delay_count', 'icon', 'decoration_type'] }
      );

      if (response && Array.isArray(response)) {
        console.log('✅ Activity types loaded:', response.length);
        return response;
      }
      return [];
    } catch (error) {
      console.error('❌ Failed to get activity types:', error);
      return [];
    }
  }

  // Create a new activity
  async createActivity(activityData: {
    resModel: string;
    resId: number;
    activityTypeId: number;
    summary: string;
    dueDate: string;
    note?: string;
    userId?: number;
  }): Promise<number | null> {
    try {
      console.log('🎯 Creating activity:', activityData);
      console.log('🎯 Current credentials:', this.credentials);

      // STEP 1: Normalize model name and get model ID
      const resModel = this.normalizeResModel(activityData.resModel);
      const modelId = await this.getModelId(resModel);
      if (!modelId) {
        throw new Error(`Could not find model ID for: ${resModel}`);
      }

      const activityRecord = {
        res_model: resModel,
        res_model_id: modelId, // ✨ This was missing!
        res_id: activityData.resId,
        activity_type_id: activityData.activityTypeId,
        summary: activityData.summary,
        date_deadline: activityData.dueDate,
        note: activityData.note || '', // Plain text - Odoo might wrap in <p> tags automatically
        user_id: activityData.userId || this.credentials?.userId,
      };


      console.log('📝 Sending note as plain text:', activityData.note);

      console.log('🎯 Sending activity record to Odoo (with model ID):', activityRecord);


      const response = await this.executeCall(
        'mail.activity',
        'create',
        [activityRecord]
      );

      console.log('🎯 Raw response from Odoo:', response);

      if (response) {
        console.log('✅ Activity created with ID:', response);
        return response;
      }

      console.warn('⚠️ Activity creation returned falsy response:', response);
      return null;
    } catch (error) {
      console.error('❌ Failed to create activity - Full error:', error);
      console.error('❌ Error message:', error instanceof Error ? error.message : String(error));
      console.error('❌ Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      // Log the error details for debugging
      if (error && typeof error === 'object') {
        console.error('❌ Error details:', JSON.stringify(error, null, 2));
      }

      throw error; // Re-throw so we can see the actual error in the UI
    }
  }

  // Get activities for a specific record
  async getActivitiesForRecord(resModel: string, resId: number): Promise<Activity[]> {
    try {
      const normalized = this.normalizeResModel(resModel);
      console.log(`🎯 Fetching activities for ${normalized}:${resId}...`);

      const response = await this.executeCall(
        'mail.activity',
        'search_read',
        [[
          ['res_model', '=', normalized],
          ['res_id', '=', resId]
        ]],

        {
          fields: [
            'id', 'res_model', 'res_id', 'activity_type_id', 'summary',
            'date_deadline', 'user_id', 'note', 'state', 'create_date',

            'write_date', 'create_uid', 'res_name'
          ]
        }
      );

      if (response && Array.isArray(response)) {
        console.log('📋 Raw Odoo activities response:', response);
        const activeActivities = response.map(this.transformOdooActivity);


        // Get completed activities from local storage
        const completedActivities = this.getCompletedActivities(resModel, resId)
          .map(activity => this.transformOdooActivity(activity));

        // Combine active and completed activities
        const allActivities = [...activeActivities, ...completedActivities];


        console.log('✅ Activities loaded:', allActivities.length, `(${activeActivities.length} active + ${completedActivities.length} completed)`);
        return allActivities;
      }
      return [];
    } catch (error) {
      console.error('❌ Failed to get activities:', error);
      return [];
    }
  }

  // Mark activity as done (keep in history)
  async markActivityDone(activityId: number, feedback: string = ''): Promise<boolean> {
    console.log('✅ Marking activity as done (keeping in history):', activityId);


    // Get current activity data for fallback use
    let currentActivity: any[] = [];

    try {
      // First, get the current activity to check what fields are available
      currentActivity = await this.executeCall(
        'mail.activity',
        'read',
        [[activityId]],
        { fields: ['id', 'activity_type_id', 'summary', 'note', 'res_model', 'res_id', 'date_deadline', 'user_id', 'state'] }
      );


      console.log('📋 Current activity before marking done:', currentActivity);

      // Try to mark activity as done while keeping it visible as history
      console.log('🔄 Attempting to mark activity as done...');
      console.log('📝 Feedback provided:', feedback);

      // Let Odoo handle completion normally, we'll track locally in React
      try {
        const response = await this.executeCall(
          'mail.activity',
          'action_feedback',
          [[activityId], feedback || 'Completed']
        );

        if (response) {
          console.log('✅ Activity marked as done in Odoo (will become message)');
          // Store completed activity locally for React tracking
          this.storeCompletedActivity(currentActivity[0], feedback);
          return true;
        }
      } catch (actionFeedbackError) {
        console.log('❌ action_feedback failed:', actionFeedbackError);
        return false;
      }

      return false;
    } catch (error) {
      console.error('❌ Failed to mark activity as done:', error);
      return false;
    }
  }

  // Complete activity with feedback (archives/removes from active list)
  async completeActivityWithFeedback(activityId: number, feedback: string = ''): Promise<boolean> {
    try {
      console.log('🏁 Completing activity with feedback (will archive):', activityId);

      const response = await this.executeCall(
        'mail.activity',
        'action_feedback',
        [[activityId], feedback ? { feedback } : {}]
      );

      if (response) {
        console.log('🏁 Activity completed and archived');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to complete activity:', error);
      return false;
    }
  }

  // Update activity
  async updateActivity(activityId: number, updateData: Partial<{
    summary: string;
    dueDate: string;
    note: string;
    activityTypeId: number;
  }>): Promise<boolean> {
    try {
      console.log('📝 Updating activity:', activityId, updateData);

      const odooData: any = {};
      if (updateData.summary) odooData.summary = updateData.summary;
      if (updateData.dueDate) odooData.date_deadline = updateData.dueDate;
      if (updateData.note) {
        odooData.note = updateData.note; // Plain text - Odoo might wrap in <p> tags
        console.log('📝 Updating note as plain text:', updateData.note);
      }
      if (updateData.activityTypeId) odooData.activity_type_id = updateData.activityTypeId;

      const response = await this.executeCall(
        'mail.activity',
        'write',
        [[activityId], odooData]
      );

      if (response) {
        console.log('✅ Activity updated');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to update activity:', error);
      return false;
    }
  }

  // Delete activity
  async deleteActivity(activityId: number): Promise<boolean> {
    try {
      console.log('🗑️ Deleting activity:', activityId);

      const response = await this.executeCall(
        'mail.activity',
        'unlink',
        [[activityId]]
      );

      if (response) {
        console.log('✅ Activity deleted');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to delete activity:', error);
      return false;
    }
  }

  // Local storage for completed activities
  private storeCompletedActivity(activity: any, feedback: string) {
    const completedActivity = {
      ...activity,
      state: 'done',
      completedAt: new Date().toISOString(),
      feedback: feedback || '',
      id: `completed_${activity.id}_${Date.now()}` // Unique ID for completed activity
    };


    const storageKey = `completed_activities_${activity.res_model}_${activity.res_id}`;
    const existing = this.getCompletedActivities(activity.res_model, activity.res_id);
    const updated = [...existing, completedActivity];


    localStorage.setItem(storageKey, JSON.stringify(updated));
    console.log('💾 Stored completed activity locally:', completedActivity.id);
  }

  private getCompletedActivities(resModel: string, resId: number): any[] {
    const storageKey = `completed_activities_${resModel}_${resId}`;
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  }

  private clearCompletedActivities(resModel: string, resId: number) {
    const storageKey = `completed_activities_${resModel}_${resId}`;
    localStorage.removeItem(storageKey);
  }

  // Get all activities globally (for header todo count)
  async getAllActivities(): Promise<Activity[]> {
    try {
      const fields = [
        'id', 'res_model', 'res_id', 'activity_type_id', 'summary',
        'date_deadline', 'user_id', 'note', 'state', 'create_date',
        'write_date', 'create_uid', 'res_name'
      ];

      const fetchActivitiesByModels = async (models: string[]) => {
        return await this.executeCall(
          'mail.activity',
          'search_read',
          [[['res_model', 'in', models]]],
          { fields, limit: 1000 }
        );
      };

      // Always fetch safe contact/prospect activities first.
      const safeResponse = await fetchActivitiesByModels(['res.partner', 'crm.prospect']);

      // Artwork activities can fail on some Odoo databases due broken access-rule SQL fields.
      // Keep app stable: if artwork query fails, continue with safe activities.
      let artworkResponse: any[] = [];
      try {
        const artworkRows = await fetchActivitiesByModels(['product.template', 'oeuvre.oeuvre']);
        artworkResponse = Array.isArray(artworkRows) ? artworkRows : [];
      } catch (artworkError) {
        console.warn('⚠️ Skipping artwork activities due Odoo schema/access-rule error:', artworkError);
      }

      const response = [
        ...(Array.isArray(safeResponse) ? safeResponse : []),
        ...artworkResponse
      ];

      if (response && Array.isArray(response)) {

        const filteredResponse = response.filter((activity: any) =>

          activity.res_model === 'res.partner' ||
          activity.res_model === 'crm.prospect' ||
          activity.res_model === 'oeuvre.oeuvre' ||
          activity.res_model === 'product.template'
        );




        const artworkActivities = filteredResponse.filter((a: any) =>
          a.res_model === 'product.template' || a.res_model === 'oeuvre.oeuvre'
        );


        const artworkArtistMap: Record<number, string> = {};

        if (artworkActivities.length > 0) {
          const artworkIds = [...new Set(
            artworkActivities
              .filter((a: any) => a.res_model === 'product.template')
              .map((a: any) => a.res_id)
          )];
          if (artworkIds.length > 0) {
            try {
            const artworks = await this.executeCall(
              'product.template',
              'search_read',
              [[['id', 'in', artworkIds]]],
              { fields: ['id', 'res_artist_id'] }
            );

            if (artworks && Array.isArray(artworks)) {
              artworks.forEach((artwork: any) => {
                if (artwork.res_artist_id && Array.isArray(artwork.res_artist_id)) {
                  artworkArtistMap[artwork.id] = artwork.res_artist_id[1]; // Artist name
                }
              });
            }
            } catch (e) {
              console.warn('Could not fetch artist names for artworks:', e);
            }
          }
        }


        const activities = filteredResponse.map((activity: any) => {
          const transformed = this.transformOdooActivity(activity);


          if (activity.res_model === 'product.template' || activity.res_model === 'oeuvre.oeuvre') {
            (transformed as any).artistName = artworkArtistMap[activity.res_id] || null;
          }

          return transformed;
        });

        return activities;
      }

      return [];
    } catch (error) {
      console.error('❌ Failed to fetch global activities:', error);
      return [];
    }
  }

  // Global activity refresh callback (set by useGlobalActivities hook)
  private globalActivityRefreshCallback: (() => void) | null = null;

  setGlobalActivityRefreshCallback(callback: () => void) {
    this.globalActivityRefreshCallback = callback;
  }

  refreshGlobalActivities() {
    if (this.globalActivityRefreshCallback) {
      this.globalActivityRefreshCallback();
    }
  }

  // Transform Odoo activity to frontend format
  private transformOdooActivity = (odooActivity: OdooActivity): Activity => {
    const activityTypeName = Array.isArray(odooActivity.activity_type_id) ? odooActivity.activity_type_id[1] : 'Unknown';

    // Clean activity type name - remove "Done" suffix if present
    const cleanActivityTypeName = activityTypeName.replace(/ Done$/, '').replace(/ done$/, '').trim();

    return {
      id: odooActivity.id,
      resModel: odooActivity.res_model,
      resId: odooActivity.res_id,
      activityType: {
        id: Array.isArray(odooActivity.activity_type_id) ? odooActivity.activity_type_id[0] : odooActivity.activity_type_id,
        name: cleanActivityTypeName, // Use cleaned name
        category: 'todo', // Default category
        icon: 'calendar'
      },
      summary: odooActivity.summary || '',
      dueDate: odooActivity.date_deadline,
      assignedTo: {
        id: Array.isArray(odooActivity.user_id) ? odooActivity.user_id[0] : odooActivity.user_id,
        name: Array.isArray(odooActivity.user_id) ? odooActivity.user_id[1] : 'Unknown'
      },
      note: odooActivity.note || '',
      state: odooActivity.state,
      createdAt: odooActivity.create_date,
      updatedAt: odooActivity.write_date,
      feedback: (odooActivity as any).feedback || undefined, // Include feedback for completed activities
      createdBy: {
        id: Array.isArray(odooActivity.create_uid) ? odooActivity.create_uid[0] : 0,
        name: Array.isArray(odooActivity.create_uid) ? odooActivity.create_uid[1] : 'Unknown'
      },
      resName: odooActivity.res_name
    };
  };

  // 🎨 GET ARTIST NAME BY ID (for resolving artist IDs to names)
  async getArtistNameById(artistId: number): Promise<string> {
    try {
      const artists = await this.executeCall('res.artist', 'search_read', [
        [['id', '=', artistId]],
        ['id', 'name']
      ]);

      if (artists && artists.length > 0) {
        const artistName = artists[0].name;
        return artistName;
      } else {
        return `Artist ID ${artistId}`;
      }
    } catch (error) {
      console.error(`❌ Error resolving artist ID ${artistId}:`, error);
      return `Artist ID ${artistId}`;
    }
  }

  // 📝 GET CONTACT NOTES
  async getContactNotes(contactId: number, model: 'crm.prospect' | 'res.partner' = 'res.partner'): Promise<any[]> {
    try {
      const fieldName = model === 'crm.prospect' ? 'prospect_id' : 'contact_id';

      // console.log(`[getContactNotes] Searching notes for ${model} ID: ${contactId} using field: ${fieldName}`);


      const noteIds = await this.executeCall('contact.notes', 'search', [
        [[fieldName, '=', contactId], ['active', '=', true]]
      ]).catch((searchError: any) => {
        // console.error('Error searching for notes:', searchError);

        return [];
      });

      if (!noteIds || noteIds.length === 0) {

        // console.log(`[getContactNotes] No notes found for ${model} ID: ${contactId}`);
        return [];
      }

      // console.log(`[getContactNotes] Found ${noteIds.length} note(s) for ${model} ID: ${contactId}`);


      const fieldsToRead = ['id', 'date', 'description'];
      let notes = await this.executeCall('contact.notes', 'read', [
        noteIds,
        fieldsToRead
      ]).catch((readError: any) => {

        // console.error('Error reading notes:', readError);
        return [];
      });


      if (!notes) {
        notes = [];
      } else if (!Array.isArray(notes)) {
        // If read returns a single object (shouldn't happen, but just in case)
        notes = [notes];
      }

      // console.log(`[getContactNotes] Successfully loaded ${notes.length} note(s) for ${model} ID: ${contactId}`);
      return notes;
    } catch (error) {
      // console.error('❌ Error fetching contact notes:', error);

      return [];
    }
  }


  async getContactHistory(contactId: number, model: 'crm.prospect' | 'res.partner' = 'res.partner'): Promise<any[]> {
    try {

      console.log(`Fetching contact history for ${model} ID: ${contactId}`);

      let messageIds: number[] = [];
      const modelsToTry = [model];
      if (model === 'crm.prospect') {
        modelsToTry.push('res.partner');
      } else {
        modelsToTry.push('crm.prospect');
      }

      for (const tryModel of modelsToTry) {
        try {
          const contact = await this.executeCall(tryModel, 'read', [[contactId]], {
            fields: ['message_ids']
          });
          if (contact && contact.length > 0 && contact[0].message_ids) {
            messageIds = Array.isArray(contact[0].message_ids) ? contact[0].message_ids : [];
            console.log(`Found ${messageIds.length} message IDs from ${tryModel} record`);
            break;
          }
        } catch (e) {
          console.log(`Could not read message_ids from ${tryModel}:`, e);
        }
      }


      let allMessages: any[] = [];

      if (messageIds.length > 0) {
        try {

          const messages = await this.executeCall('mail.message', 'read', [messageIds], {
            fields: [
              'id',
              'date',
              'author_id',
              'subject',
              'body',
              'message_type',
              'subtype_id',
              'res_id',
              'model',
              'create_date',
              'write_date',
              'email_from',
              'partner_ids'
            ]
          });

          if (messages && Array.isArray(messages)) {
            allMessages = messages;
            console.log(`Read ${messages.length} messages by IDs`);
          }
        } catch (e) {
          console.log(`Error reading messages by IDs, trying search_read:`, e);

          try {
            for (const tryModel of modelsToTry) {
              const messages = await this.executeCall('mail.message', 'search_read', [
                [
                  ['res_id', '=', contactId],
                  ['model', '=', tryModel]
                ]
              ], {
                fields: [
                  'id',
                  'date',
                  'author_id',
                  'subject',
                  'body',
                  'message_type',
                  'subtype_id',
                  'res_id',
                  'model',
                  'create_date',
                  'write_date',
                  'email_from',
                  'partner_ids'
                ],
                limit: 500
              });

              if (messages && Array.isArray(messages) && messages.length > 0) {
                console.log(`Fetched ${messages.length} messages from mail.message for ${tryModel} ID: ${contactId}`);
                allMessages = [...allMessages, ...messages];
                break;
              }
            }
          } catch (searchErr) {
            console.log(`Error with search_read fallback:`, searchErr);
          }
        }
      } else {
        try {
          for (const tryModel of modelsToTry) {
            const messages = await this.executeCall('mail.message', 'search_read', [
              [
                ['res_id', '=', contactId],
                ['model', '=', tryModel]
              ]
            ], {
              fields: [
                'id',
                'date',
                'author_id',
                'subject',
                'body',
                'message_type',
                'subtype_id',
                'res_id',
                'model',
                'create_date',
                'write_date',
                'email_from',
                'partner_ids'
              ],
              limit: 500
            });

            if (messages && Array.isArray(messages) && messages.length > 0) {
              console.log(`Fetched ${messages.length} messages from mail.message for ${tryModel} ID: ${contactId}`);
              allMessages = [...allMessages, ...messages];
              break;
            }
          }
        } catch (searchErr) {
          console.log(`Error with search_read fallback:`, searchErr);
        }
      }


      const uniqueMessages = Array.from(
        new Map(allMessages.map((msg: any) => [msg.id, msg])).values()
      );

      console.log(`Total unique messages fetched: ${uniqueMessages.length}`);


      if (uniqueMessages.length > 0) {
        try {
          const messageIds = uniqueMessages.map((msg: any) => msg.id);
          const trackingValues = await this.executeCall('mail.tracking.value', 'search_read', [
            [['mail_message_id', 'in', messageIds]]
          ], {
            fields: ['mail_message_id', 'field', 'field_desc', 'old_value_char', 'new_value_char', 'old_value_integer', 'new_value_integer']
          });

          const trackingValuesMap: { [key: number]: any[] } = {};
          if (Array.isArray(trackingValues)) {
            trackingValues.forEach((tv: any) => {
              const msgId = tv.mail_message_id && Array.isArray(tv.mail_message_id) ? tv.mail_message_id[0] : tv.mail_message_id;
              if (msgId) {
                if (!trackingValuesMap[msgId]) {
                  trackingValuesMap[msgId] = [];
                }
                trackingValuesMap[msgId].push(tv);
              }
            });
          }

          uniqueMessages.forEach((msg: any) => {
            msg.tracking_value_ids = trackingValuesMap[msg.id]?.map((tv: any) => tv.id) || [];
            msg.tracking_values = trackingValuesMap[msg.id] || [];
          });
        } catch (e) {
          console.log('Could not fetch tracking values:', e);

          uniqueMessages.forEach((msg: any) => {
            msg.tracking_value_ids = [];
            msg.tracking_values = [];
          });
        }
      }

      let oldHistory: any[] = [];
      try {
        oldHistory = await this.executeCall('contact.history', 'search_read', [

          [['contact_id', '=', contactId], ['active', '=', true]],
          ['id', 'date', 'description', 'interaction_type', 'contact_id']
        ]) || [];
      } catch (e) {
        console.log('Old contact.history model not available, skipping');
      }


      const transformedMessages = uniqueMessages.map((msg: any) => {
        const subtype = msg.subtype_id && Array.isArray(msg.subtype_id) ? msg.subtype_id[1] : null;
        const author = msg.author_id && Array.isArray(msg.author_id) ? msg.author_id[1] : (msg.author_id === false ? 'System' : 'Unknown');
        const authorId = msg.author_id && Array.isArray(msg.author_id) ? msg.author_id[0] : null;

        const transformed = {
          id: msg.id,
          date: msg.date || msg.create_date,
          message_type: msg.message_type || 'comment',
          subtype_id: msg.subtype_id && Array.isArray(msg.subtype_id) ? msg.subtype_id[0] : null,
          subtype_name: subtype,
          author_id: authorId,
          author_name: author,
          subject: msg.subject || '',
          body: msg.body || '',
          email_from: msg.email_from || '',
          email_to: msg.partner_ids ? (Array.isArray(msg.partner_ids) ? msg.partner_ids.map((p: any) => Array.isArray(p) ? p[1] : p).join(', ') : '') : '',
          is_system: msg.author_id === false || (authorId === 1 && author === 'OdooBot'),
          tracking_value_ids: msg.tracking_value_ids || [],
          tracking_values: msg.tracking_values || [],
          _source: 'mail.message'
        };

        if (uniqueMessages.indexOf(msg) < 3) {
          console.log(`Message ${transformed.id}:`, {
            message_type: transformed.message_type,
            subtype_name: transformed.subtype_name,
            author: transformed.author_name,
            subject: transformed.subject,
            body_preview: (transformed.body || '').substring(0, 50)
          });
        }

        return transformed;
      });

      const transformedOldHistory = oldHistory.map((h: any) => ({
        id: `old_${h.id}`,
        date: h.date,
        message_type: 'comment',
        interaction_type: h.interaction_type || 'other',
        description: h.description || '',
        author_name: 'User',
        _source: 'contact.history'
      }));

      const combined = [...transformedMessages, ...transformedOldHistory].sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA;
      });

      console.log(`Total history entries (mail.message + old history): ${combined.length}`);
      return combined;

    } catch (error) {
      console.error('Error fetching contact history from chatter:', error);
      return [];
    }
  }

  // ✨ CREATE CONTACT NOTE
  async createContactNote(contactId: number, description: string, model: 'crm.prospect' | 'res.partner' = 'res.partner'): Promise<boolean> {
    try {

      // Format date for Odoo (YYYY-MM-DD HH:MM:SS)

      const now = new Date();
      const odooDate = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0') + ':' +
        String(now.getSeconds()).padStart(2, '0');


      const noteData: any = {
        description: description,
        date: odooDate,
        active: true
      };

      if (model === 'crm.prospect') {
        noteData.prospect_id = contactId;
      } else {
        noteData.contact_id = contactId;
      }

      const noteId = await this.executeCall('contact.notes', 'create', [noteData]);


      return !!noteId;
    } catch (error) {
      // console.error('❌ Error creating contact note:', error);
      // console.error('❌ Contact ID:', contactId);
      // console.error('❌ Model:', model);
      // console.error('❌ Description:', description);
      // console.error('❌ Error details:', error instanceof Error ? error.message : error);

      return false;
    }
  }

  // ✨ CREATE CONTACT HISTORY
  async createContactHistory(contactId: number, description: string, interactionType: string): Promise<boolean> {
    try {
      // Format date for Odoo (YYYY-MM-DD HH:MM:SS)
      const now = new Date();

      const odooDate = now.getFullYear() + '-' +
        String(now.getMonth() + 1).padStart(2, '0') + '-' +
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0') + ':' +
        String(now.getSeconds()).padStart(2, '0');


      const historyId = await this.executeCall('contact.history', 'create', [{
        contact_id: contactId,
        description: description,
        interaction_type: interactionType,
        date: odooDate,
        active: true
      }]);


      return !!historyId;
    } catch (error) {
      console.error('❌ Error creating contact history:', error);
      console.error('❌ Contact ID:', contactId);
      console.error('❌ Description:', description);
      console.error('❌ Interaction Type:', interactionType);
      console.error('❌ Error details:', error instanceof Error ? error.message : error);

      return false;
    }
  }

  // 🎯 GET CONTACTS INTERESTED IN SPECIFIC ARTIST (for Artwork Detail "Clients Interested" section)
  async getContactsInterestedInArtist(artistName: string): Promise<any[]> {
    try {
      // First, get the artist ID from the artist name
      const artistResult = await this.getArtistByName(artistName);
      if (!artistResult || !artistResult.id) {
        return [];
      }


      const artistId = artistResult.id;


      // Find contacts who have this artist in their interest_artist_ids
      const domain = [
        "&",
        ["active", "=", true],
        ["interest_artist_ids", "in", [artistId]]
      ];


      const contacts = await this.executeCall('res.partner', 'search_read', [
        domain,
        [
          'id',
          'name',
          'email',
          'phone',

          'client_classifications',
          'Country_Shipping_Address',
          'control_galleries_ids',
          'interest_artist_ids'
        ]
      ]);

      return contacts;
    } catch (error) {
      console.error('❌ Error fetching contacts interested in artist:', error);
      return [];
    }
  }

  // 📊 CONTACT FILTERING METHODS (similar to artwork filtering)

  // Get all contacts with filtering capability
  // async getAllContacts(limit = 200, offset = 0): Promise<OdooContact[]> {
  //   try {
  //     const domain = [
  //       ["active", "=", true]
  //     ];

  //     const result = await this.executeCall(
  //       'res.partner',
  //       'search_read',
  //       [domain],
  //       {
  //         fields: [
  //           'id', 'name', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
  //           'street', 'street2', 'city', 'zip', 'country_id', 'state_id',
  //           'BirthDate', 'client_classifications', 'is_partner_client', 'is_partner_lead',
  //           'Country_Shipping_Address', 'State_Shipping_Address', 'City_Shipping_Address', 'Zip_Shipping_Address',
  //           'Country_Billing_Address', 'State_Billing_Address', 'City_Billing_Address', 'Zip_Billing_Address',
  //           'create_date', 'write_date'
  //         ],
  //         limit: limit > 0 ? limit : undefined,
  //         offset: limit > 0 ? offset : undefined,
  //         order: 'name asc'
  //       }
  //     );

  //     return result || [];
  //   } catch (error) {
  //     console.error('❌ Error fetching all contacts:', error);
  //     return [];
  //   }
  // }

  // // Count all contacts
  // async countAllContacts(): Promise<number> {
  //   try {
  //     const domain = [
  //       ["active", "=", true]
  //     ];

  //     const count = await this.executeCall(
  //       'res.partner',
  //       'search_count',
  //       [domain]
  //     );

  //     return count || 0;
  //   } catch (error) {
  //     console.error('❌ Error counting all contacts:', error);
  //     return 0;
  //   }
  // }

  // ─────────────────────────────────────────────────────────────────────────────
// GET ALL CONTACTS (for bulk export / offline use)
// ─────────────────────────────────────────────────────────────────────────────
 

 
// 👥 GET ALL CONTACTS
async getAllContacts(limit = 200, offset = 0): Promise<OdooContact[]> {
  try {
    return await this.executeCall(
      'res.partner',
      'search_read',
      [ACTIVE_ONLY_DOMAIN],
      {
        fields: ALL_CONTACTS_FIELDS,
        ...(limit > 0 ? { limit, offset } : {}),
        order: 'name asc',
      },
    ) ?? [];
  } catch (error) {
    console.error('❌ Error fetching all contacts:', error);
    return [];
  }
}
 
// 🔢 COUNT ALL CONTACTS
async countAllContacts(): Promise<number> {
  try {
    return (await this.executeCall('res.partner', 'search_count', [ACTIVE_ONLY_DOMAIN])) ?? 0;
  } catch (error) {
    console.error('❌ Error counting all contacts:', error);
    return 0;
  }
}


  // Get contacts by country
  async getContactsByCountry(country: string, limit = 200, offset = 0): Promise<OdooContact[]> {
    try {
      const domain = [
        "&",
        ["active", "=", true],

        ["|",

          ["Country_Shipping_Address", "ilike", country],
          ["Country_Billing_Address", "ilike", country]
        ]
      ];

      const result = await this.executeCall(
        'res.partner',
        'search_read',
        [domain],

        {

          fields: [
            'id', 'name', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
            'street', 'street2', 'city', 'zip', 'country_id', 'state_id',
            'BirthDate', 'client_classifications', 'is_partner_client', 'is_partner_lead',
            'Country_Shipping_Address', 'State_Shipping_Address', 'City_Shipping_Address', 'Zip_Shipping_Address',
            'Country_Billing_Address', 'State_Billing_Address', 'City_Billing_Address', 'Zip_Billing_Address',
            'create_date', 'write_date'
          ],
          limit: limit > 0 ? limit : undefined,
          offset: limit > 0 ? offset : undefined,
          order: 'name asc'
        }
      );

      return result || [];
    } catch (error) {
      console.error('❌ Error fetching contacts by country:', error);
      return [];
    }
  }

  // Count contacts by country
  async countContactsByCountry(country: string): Promise<number> {
    try {
      const domain = [
        "&",
        ["active", "=", true],

        ["|",

          ["Country_Shipping_Address", "ilike", country],
          ["Country_Billing_Address", "ilike", country]
        ]
      ];

      const count = await this.executeCall(
        'res.partner',
        'search_count',
        [domain]
      );

      return count || 0;
    } catch (error) {
      console.error('❌ Error counting contacts by country:', error);
      return 0;
    }
  }

  // Get contacts by classification
  async getContactsByClassification(classification: string, limit = 200, offset = 0): Promise<OdooContact[]> {
    try {
      const domain = [
        "&",
        ["active", "=", true],
        ["client_classifications", "=", classification]
      ];

      const result = await this.executeCall(
        'res.partner',
        'search_read',
        [domain],

        {

          fields: [
            'id', 'name', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
            'street', 'street2', 'city', 'zip', 'country_id', 'state_id',
            'BirthDate', 'client_classifications', 'is_partner_client', 'is_partner_lead',
            'Country_Shipping_Address', 'State_Shipping_Address', 'City_Shipping_Address', 'Zip_Shipping_Address',
            'Country_Billing_Address', 'State_Billing_Address', 'City_Billing_Address', 'Zip_Billing_Address',
            'create_date', 'write_date'
          ],
          limit: limit > 0 ? limit : undefined,
          offset: limit > 0 ? offset : undefined,
          order: 'name asc'
        }
      );

      return result || [];
    } catch (error) {
      console.error('❌ Error fetching contacts by classification:', error);
      return [];
    }
  }

  // Count contacts by classification
  async countContactsByClassification(classification: string): Promise<number> {
    try {
      const domain = [
        "&",
        ["active", "=", true],
        ["client_classifications", "=", classification]
      ];

      const count = await this.executeCall(
        'res.partner',
        'search_count',
        [domain]
      );

      return count || 0;
    } catch (error) {
      console.error('❌ Error counting contacts by classification:', error);
      return 0;
    }
  }

  // Get contacts by type (client/prospect)
  async getContactsByType(type: 'client' | 'prospect', limit = 200, offset = 0): Promise<OdooContact[]> {
    try {
      const domain = [
        "&",
        ["active", "=", true],
        type === 'client' ? ["is_partner_client", "=", true] : ["is_partner_lead", "=", true]
      ];

      const result = await this.executeCall(
        'res.partner',
        'search_read',
        [domain],

        {

          fields: [
            'id', 'name', 'email', 'Email1', 'Email2', 'phone', 'Phone1', 'Phone2',
            'street', 'street2', 'city', 'zip', 'country_id', 'state_id',
            'BirthDate', 'client_classifications', 'is_partner_client', 'is_partner_lead',
            'Country_Shipping_Address', 'State_Shipping_Address', 'City_Shipping_Address', 'Zip_Shipping_Address',
            'Country_Billing_Address', 'State_Billing_Address', 'City_Billing_Address', 'Zip_Billing_Address',
            'create_date', 'write_date'
          ],
          limit: limit > 0 ? limit : undefined,
          offset: limit > 0 ? offset : undefined,
          order: 'name asc'
        }
      );

      return result || [];
    } catch (error) {
      console.error('❌ Error fetching contacts by type:', error);
      return [];
    }
  }

  // Count contacts by type
  async countContactsByType(type: 'client' | 'prospect'): Promise<number> {
    try {
      const domain = [
        "&",
        ["active", "=", true],
        type === 'client' ? ["is_partner_client", "=", true] : ["is_partner_lead", "=", true]
      ];

      const count = await this.executeCall(
        'res.partner',
        'search_count',
        [domain]
      );

      return count || 0;
    } catch (error) {
      console.error('❌ Error counting contacts by type:', error);
      return 0;
    }
  }

  // Get country suggestions for contact filtering
  async getCountrySuggestions(searchTerm: string, limit = 10): Promise<string[]> {
    try {
      if (searchTerm.length < 2) return [];

      const result = await this.executeCall(
        'res.partner',
        'search_read',
        [[
          "&",
          ["active", "=", true],
          ["|",
            ["Country_Shipping_Address", "ilike", searchTerm],
            ["Country_Billing_Address", "ilike", searchTerm]
          ]
        ]],

        {

          fields: ['Country_Shipping_Address', 'Country_Billing_Address'],
          limit: 100
        }
      );

      if (!result || !Array.isArray(result)) return [];

      // Extract unique countries
      const countries = new Set<string>();
      result.forEach((contact: any) => {
        if (contact.Country_Shipping_Address && typeof contact.Country_Shipping_Address === 'string') {
          countries.add(contact.Country_Shipping_Address);
        }
        if (contact.Country_Billing_Address && typeof contact.Country_Billing_Address === 'string') {
          countries.add(contact.Country_Billing_Address);
        }
      });

      return Array.from(countries).slice(0, limit)
        .filter(country => country.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort();
    } catch (error) {
      console.error('❌ Error fetching country suggestions:', error);
      return [];
    }
  }

  // 🎨 GET ARTWORKS WITH SORTING - Enhanced version with flexible sorting options
  async getArtworksWithSorting(

    artistsIdOrName: (string | number)[] = [],
    categories: string[] = [],
    priceRanges: string[] = [],
    minPrice?: number,
    maxPrice?: number,
    dateInStock: string[] = [],
    types: string[] = [],
    statuses: string[] = ['In stock', 'In transit', 'On Option'],
    sortBy: 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc' | 'year_asc' | 'year_desc' | 'date_asc' | 'date_desc' = 'price_desc',
    limit = 50,
    locations: string[] = [],
    offset = 0
  ): Promise<any[]> {
    console.log("🎨 Getting artworks with sorting:", { artistsIdOrName, categories, priceRanges, dateInStock, types, statuses, sortBy, limit, offset });

    // Separate IDs and names for artists
    const artistIds: number[] = [];
    const artistNames: string[] = [];


    if (artistsIdOrName) {
      artistsIdOrName.forEach(artist => {
        if (typeof artist === 'number' || /^\d+$/.test(String(artist))) {
          const id = typeof artist === 'number' ? artist : parseInt(String(artist));
          artistIds.push(id);
        } else {
          artistNames.push(String(artist));
        }
      });
    }

    // Build base domain conditions
    let domainConditions: any[] = [
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    // Build artist OR conditions
    const artistConditions: any[] = [];

    // Add ID conditions
    if (artistIds.length > 0) {
      artistConditions.push(["res_artist_id", "in", artistIds]);
    }

    // Add name conditions (each name as a separate ilike condition)
    artistNames.forEach(name => {
      artistConditions.push(["res_artist_id", "ilike", name]);
    });

    // Add artist conditions to domain if they exist
    if (artistConditions.length > 0) {
      if (artistConditions.length === 1) {
        domainConditions.push(artistConditions[0]);
      } else {
        // Multiple conditions - wrap in OR
        domainConditions.push(["|", ...artistConditions.slice(0, 2)]);
        // Add remaining conditions with OR
        for (let i = 2; i < artistConditions.length; i++) {
          domainConditions = ["|", ...domainConditions, artistConditions[i]];
        }
      }
    }

    // Add category filter
    if (categories.length > 0 && !categories.includes('All')) {
      domainConditions.push(["Category", "in", categories]);
    }

    // Add type filter (moved after category)
    if (types.length > 0 && !types.includes('All')) {
      domainConditions.push(["Type", "in", types]);
    }

    // Add status filter using 'in' operator
    if (statuses.length > 0) {
      const validStatuses = statuses.filter(status => status !== 'All');
      if (validStatuses.length > 0) {
        domainConditions.push(["Status", "in", validStatuses]);
      }
    }

    // Add price range filters - handle multiple selections with OR logic
    if (priceRanges.length > 0 && !priceRanges.includes('All')) {
      const priceConditions: any[] = [];

      priceRanges.forEach(range => {
        switch (range) {
          case '< 100K':
            priceConditions.push(["artwork_price_combined", "<", 100000]);
            break;
          case '100K - 500K':
            priceConditions.push(["&", ["artwork_price_combined", ">=", 100000], ["artwork_price_combined", "<=", 500000]]);
            break;
          case '500K - 1M':
            priceConditions.push(["&", ["artwork_price_combined", ">=", 500000], ["artwork_price_combined", "<=", 1000000]]);
            break;
          case '> 1M':
            priceConditions.push(["artwork_price_combined", ">", 1000000]);
            break;
        }
      });

      if (priceConditions.length > 0) {
        if (priceConditions.length === 1) {
          domainConditions.push(priceConditions[0]);
        } else {
          // Multiple price ranges - create nested OR structure
          let nestedOr = priceConditions[0];
          for (let i = 1; i < priceConditions.length; i++) {
            nestedOr = ["|", nestedOr, priceConditions[i]];
          }
          domainConditions.push(nestedOr);
        }
      }
    }

    // Add custom price range filters
    if (minPrice !== undefined && minPrice > 0) {
      domainConditions.push(["artwork_price_combined", ">=", minPrice]);
    }
    if (maxPrice !== undefined && maxPrice > 0) {
      domainConditions.push(["artwork_price_combined", "<=", maxPrice]);
    }

    // Add date filters (if provided)
    if (dateInStock.length > 0 && !dateInStock.includes('All')) {
      const now = new Date();
      const dateConditions: any[] = [];

      dateInStock.forEach(period => {
        let startDate: Date;
        switch (period) {
          case 'Last 7 days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 15 days':
            startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 30 days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 3 months':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 6 months':
            startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
            break;
          case 'Last year':
            startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            break;
          default:
            return; // Skip unknown periods
        }
        dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
      });

      if (dateConditions.length > 0) {
        if (dateConditions.length === 1) {
          domainConditions.push(dateConditions[0]);
        } else {
          // Multiple date ranges - create OR structure
          let dateOr = dateConditions[0];
          for (let i = 1; i < dateConditions.length; i++) {
            dateOr = ["|", dateOr, dateConditions[i]];
          }
          domainConditions.push(dateOr);
        }
      }
    }

    // Determine sort order based on sortBy parameter
    let orderClause: string;

    // 🧹 Normalize & clean locations (gallery only)
    const validLocations = (locations || [])
      .filter((l) => l && l !== "All")
      .map((l) => String(l).toLowerCase());

    switch (sortBy) {
      case 'price_asc':
        orderClause = 'artwork_price_combined ASC';
        break;
      case 'price_desc':
        orderClause = 'artwork_price_combined DESC';
        break;
      case 'name_asc':
        orderClause = 'name ASC';
        break;
      case 'name_desc':
        orderClause = 'name DESC';
        break;
      case 'year_asc':
        orderClause = 'artwork_year ASC';
        break;
      case 'year_desc':
        orderClause = 'artwork_year DESC';
        break;
      case 'date_asc':
        orderClause = 'entry_date ASC';
        break;
      case 'date_desc':
        orderClause = 'entry_date DESC';
        break;
      default:
        orderClause = 'artwork_price_combined DESC';
    }

    // 📍 Location filter (gallery only, ignore sub-location)
    if (validLocations.length > 0) {
      const locConditions = validLocations.map((loc) => [
        "control_gallery_combined",
        "ilike",
        loc,
      ]);

      if (locConditions.length === 1) {
        domainConditions.push(locConditions[0]);
      } else {
        let orBlock: any = ["|", locConditions[0], locConditions[1]];
        for (let i = 2; i < locConditions.length; i++) {
          orBlock = ["|", orBlock, locConditions[i]];
        }
        domainConditions.push(orBlock);
      }
    }



    console.log("🎨 Final domain for sorted artworks:", domainConditions);
    console.log("🎨 Sort order:", orderClause);

    // ⚡ Cache + inflight dedupe for list
    const TTL_MS = 30000;

    const cacheKey = `aw_list:${JSON.stringify({ artistsIdOrName, categories, priceRanges, minPrice, maxPrice, dateInStock, types, statuses, locations: validLocations, sortBy, limit, offset })}`;

    const now = Date.now();
    const cached = this.artworksListCache.get(cacheKey);
    if (cached && now - cached.timestamp < TTL_MS) return cached.data;
    const inflight = this.artworksListInflight.get(cacheKey);
    if (inflight) return inflight;

    try {
      const shouldHide = await this.shouldHideArtworksWithoutImage();
      const promise = this.executeCall(
        'product.template',
        'search_read_artworks',
        [domainConditions, this.STANDARD_ARTWORK_FIELDS, limit, offset, orderClause, shouldHide ? true : undefined],
        {}
      ).then((result: any) => {
        const list = Array.isArray(result) ? result : [];
        this.artworksListCache.set(cacheKey, { timestamp: Date.now(), data: list });
        console.log(`✅ Sorted artworks API returned ${list.length} results`);
        return list;
      }).finally(() => {
        this.artworksListInflight.delete(cacheKey);
      });
      this.artworksListInflight.set(cacheKey, promise);
      return await promise;
    } catch (error) {
      console.error('❌ Error fetching sorted artworks:', error);
      return [];
    }
  }

  // ⚡ Lightweight fetch: only id and IdName for filtered artworks (for client-side numeric ID sorting)
  async getArtworksIdNameWithFilters(
    artistsIdOrName: (string | number)[] = [],
    categories: string[] = [],
    priceRanges: string[] = [],
    minPrice?: number,
    maxPrice?: number,
    dateInStock: string[] = [],
    types: string[] = [],
    statuses: string[] = ['In stock', 'In transit', 'On Option'],
    limit = 2000,
    offset = 0
  ): Promise<Array<{ id: number; IdName?: string }>> {
    // Build domain using same logic as getArtworksWithSorting
    const artistIds: number[] = [];
    const artistNames: string[] = [];
    if (artistsIdOrName) {
      artistsIdOrName.forEach(artist => {
        if (typeof artist === 'number' || /^\d+$/.test(String(artist))) {
          const id = typeof artist === 'number' ? artist : parseInt(String(artist));
          artistIds.push(id);
        } else {
          artistNames.push(String(artist));
        }
      });
    }

    let domainConditions: any[] = [
      ["active", "=", true],
      ["artwork_price_combined", ">", 1]
    ];

    const artistConditions: any[] = [];
    if (artistIds.length > 0) {
      artistConditions.push(["res_artist_id", "in", artistIds]);
    }
    artistNames.forEach(name => {
      artistConditions.push(["res_artist_id", "ilike", name]);
    });
    if (artistConditions.length > 0) {
      if (artistConditions.length === 1) {
        domainConditions.push(artistConditions[0]);
      } else {
        domainConditions.push(["|", ...artistConditions.slice(0, 2)]);
        for (let i = 2; i < artistConditions.length; i++) {
          domainConditions = ["|", ...domainConditions, artistConditions[i]];
        }
      }
    }

    if (categories.length > 0 && !categories.includes('All')) {
      domainConditions.push(["Category", "in", categories]);
    }
    if (types.length > 0 && !types.includes('All')) {
      domainConditions.push(["Type", "in", types]);
    }
    if (statuses.length > 0) {
      const validStatuses = statuses.filter(status => status !== 'All');
      if (validStatuses.length > 0) {
        domainConditions.push(["Status", "in", validStatuses]);
      }
    }
    if (priceRanges.length > 0 && !priceRanges.includes('All')) {
      const priceConditions: any[] = [];
      priceRanges.forEach(range => {
        switch (range) {
          case '< 100K':
            priceConditions.push(["artwork_price_combined", "<", 100000]);
            break;
          case '100K - 500K':
            priceConditions.push(["&", ["artwork_price_combined", ">=", 100000], ["artwork_price_combined", "<=", 500000]]);
            break;
          case '500K - 1M':
            priceConditions.push(["&", ["artwork_price_combined", ">=", 500000], ["artwork_price_combined", "<=", 1000000]]);
            break;
          case '> 1M':
            priceConditions.push(["artwork_price_combined", ">", 1000000]);
            break;
        }
      });
      if (priceConditions.length > 0) {
        if (priceConditions.length === 1) {
          domainConditions.push(priceConditions[0]);
        } else {
          let nestedOr = priceConditions[0];
          for (let i = 1; i < priceConditions.length; i++) {
            nestedOr = ["|", nestedOr, priceConditions[i]];
          }
          domainConditions.push(nestedOr);
        }
      }
    }
    if (minPrice !== undefined && minPrice > 0) {
      domainConditions.push(["artwork_price_combined", ">=", minPrice]);
    }
    if (maxPrice !== undefined && maxPrice > 0) {
      domainConditions.push(["artwork_price_combined", "<=", maxPrice]);
    }
    if (dateInStock.length > 0 && !dateInStock.includes('All')) {
      const now = new Date();
      const dateConditions: any[] = [];
      dateInStock.forEach(period => {
        let startDate: Date;
        switch (period) {
          case 'Last 7 days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 15 days':
            startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 30 days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 3 months':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'Last 6 months':
            startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
            break;
          case 'Last year':
            startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            break;
          default:
            return; // Skip unknown periods
        }
        dateConditions.push(["entry_date", ">=", startDate.toISOString().split('T')[0]]);
      });
      if (dateConditions.length > 0) {
        if (dateConditions.length === 1) {
          domainConditions.push(dateConditions[0]);
        } else {
          let dateOr = dateConditions[0];
          for (let i = 1; i < dateConditions.length; i++) {
            dateOr = ["|", dateOr, dateConditions[i]];
          }
          domainConditions.push(dateOr);
        }
      }
    }

    try {
      const shouldHide = await this.shouldHideArtworksWithoutImage();
      const result = await this.executeCall(
        'product.template',
        'search_read_artworks',
        [domainConditions, ['id', 'IdName'], limit, offset, 'id desc', shouldHide ? true : undefined],
        {}
      );
      return (result || [])
        .map((it: any) => ({ id: it.id, IdName: it.IdName }));
    } catch (error) {
      console.error('❌ Error fetching id/index for artworks:', error);
      return [];
    }
  }

  // Public method: fetch full artworks by a set of ids
  async getArtworksByIdsPublic(ids: number[]): Promise<any[]> {
    if (!ids || ids.length === 0) return [];
    try {
      const shouldHide = await this.shouldHideArtworksWithoutImage();
      const domain = [["id", "in", ids]] as any;
      const result = await this.executeCall(
        'product.template',
        'search_read_artworks',
        [domain, this.STANDARD_ARTWORK_FIELDS, ids.length, 0, 'id desc', shouldHide ? true : undefined],
        {}
      );
      return result || [];
    } catch (error) {
      console.error('❌ Error fetching artworks by ids:', error);
      return [];
    }
  }

  // Special status count (Sold / On order) using order dates
  async countArtworksSpecial(
    status: 'sold' | 'on_order',
    from?: string,
    to?: string,
    domain: any[] = []
  ): Promise<number> {
    const baseDomain = [["active", "=", true], ["artwork_price_combined", ">", 1]] as any[];
    const effectiveDomain = Array.isArray(domain) ? [...baseDomain, ...domain] : baseDomain;
    const res = await this.executeCall(
      'product.template',
      'search_count_artworks_special',
      [effectiveDomain, undefined, status, from, to]
    );
    return Number(res) || 0;
  }

  // Special status list (Sold / On order) using order dates
  async getArtworksSpecial(
    status: 'sold' | 'on_order',
    fields: string[] = this.STANDARD_ARTWORK_FIELDS,
    limit = 50,
    offset = 0,
    order = 'id desc',
    from?: string,
    to?: string,
    domain: any[] = []
  ): Promise<any[]> {
    const baseDomain = [["active", "=", true], ["artwork_price_combined", ">", 1]] as any[];
    const effectiveDomain = Array.isArray(domain) ? [...baseDomain, ...domain] : baseDomain;
    const result = await this.executeCall(
      'product.template',
      'search_read_artworks_special',
      [effectiveDomain, fields, limit, offset, order, undefined, status, from, to]
    );
    return Array.isArray(result) ? result : [];
  }


  async postInternalNote(
    model: 'crm.prospect' | 'res.partner',
    recordId: number,
    noteText: string
  ): Promise<number | false> {
    try {
      console.log(`Posting internal note to ${model} ID: ${recordId}`);


      const kwargs: any = {
        body: noteText,
        message_type: 'notification',
        subtype_xmlid: 'mail.mt_note',

      };

      const messageId = await this.executeCall(
        model,
        'message_post',
        [[recordId]],
        kwargs
      );

      if (messageId) {
        console.log(`Internal note posted successfully. Message ID: ${messageId}`);
        return typeof messageId === 'number' ? messageId : false;
      }

      return false;
    } catch (error) {
      console.error(`Failed to post internal note to ${model} ID ${recordId}:`, error);
      return false;
    }
  }

  // ✉️ Send an email and log it on a contact using message_post
  async messagePostOnPartner(
    partnerId: number,
    subject: string,
    bodyHtml: string,
    attachments?: Array<{ name: string; content: string }>,
    extraKwargs?: Record<string, any>
  ): Promise<boolean> {
    try {
      const kwargs: any = {

        body: bodyHtml,
        subject,
        partner_ids: [partnerId],
        message_type: 'email',
        subtype_xmlid: 'mail.mt_comment',

      };
      if (attachments && attachments.length > 0) {
        kwargs.attachments = attachments.map(a => [a.name, a.content]);
      }
      if (extraKwargs && typeof extraKwargs === 'object') {
        Object.assign(kwargs, extraKwargs);
      }

      const res = await this.executeCall(
        'res.partner',
        'message_post',
        [[partnerId]],
        kwargs
      );
      return Boolean(res);
    } catch (error) {
      console.error('❌ message_post failed for partner', partnerId, error);
      return false;
    }
  }


  async sendEmailDirect(
    emailTo: string,
    subject: string,
    bodyHtml: string,
    attachments?: Array<{ name: string; content: string }>,
    options?: {
      recipientName?: string;
      senderName?: string;
      ccEmails?: Array<{ email: string; name?: string }>;
      /** When set, Odoo stores subject/body/recipients on `artgallery.crm.lead.proposal.email.log` for this lead. */
      crmLeadId?: number;
    }
  ): Promise<boolean> {
    try {

      let emailFrom = this.user?.email || this.credentials?.login || '';
      if (!emailFrom) {
        try {
          const prof = await this.getCurrentUserProfile();
          emailFrom = prof?.email || '';
        } catch (_) { /* ignore */ }
      }

      const senderName = (options?.senderName || this.user?.name || '').toString().trim();
      const recipientName = (options?.recipientName || '').toString().trim();

      const vals: any = {
        subject,
        body_html: bodyHtml,
        email_to: recipientName && emailTo ? `${recipientName} <${emailTo}>` : emailTo,
        auto_delete: true,
      };
      if (emailFrom) {
        vals.email_from = senderName ? `${senderName} <${emailFrom}>` : emailFrom;
        vals.reply_to = senderName ? `${senderName} <${emailFrom}>` : emailFrom;
      }


      if (options?.ccEmails && options.ccEmails.length > 0) {
        const ccList = options.ccEmails
          .map(cc => cc.name ? `${cc.name} <${cc.email}>` : cc.email)
          .join(', ');
        vals.email_cc = ccList;
      }

      if (attachments && attachments.length > 0) {
        vals.attachment_ids = attachments.map(a => [0, 0, { name: a.name, datas: a.content }]);
      }

      const crmLeadIdOpt = options?.crmLeadId;
      const crmLeadIdNum =
        crmLeadIdOpt != null && Number.isFinite(Number(crmLeadIdOpt)) && Number(crmLeadIdOpt) > 0
          ? Number(crmLeadIdOpt)
          : null;
      const bridgeVals =
        crmLeadIdNum != null ? { ...vals, crm_lead_id: crmLeadIdNum } : vals;

      try {
        const mailId = await this.executeCall('mail.mail', 'action_send_email_from_webapp', [bridgeVals], {});
        return Boolean(mailId);
      } catch (bridgeError) {

        console.warn('Bridge method not available, falling back to direct mail.mail create', bridgeError);
        // Fallback ONLY for very old Odoo deployments that do not have
        // `mail.mail.action_send_email_from_webapp`. The bridge already commits
        // the proposal email log on the lead before any failure can bubble up,
        // so duplicating that write here would produce two `artgallery.crm.lead.proposal.email.log`
        // rows on the Offers timeline whenever the bridge throws after its
        // initial commit. Just send the mail; skip log creation in this path.
        const mailId = await this.executeCall('mail.mail', 'create', [vals], {});
        if (!mailId) return false;
        await this.executeCall('mail.mail', 'send', [[mailId]]);
        return true;
      }

    } catch (error) {
      console.error('❌ sendEmailDirect failed', emailTo, error);
      return false;
    }
  }


  async ensurePartnerPrimaryEmail(partnerId: number, email: string): Promise<boolean> {
    try {
      const trimmed = (email || '').trim();
      if (!trimmed) return false;
      const current = await this.executeCall(
        'res.partner',
        'read',
        [[partnerId], ['email']]
      );
      const currEmail = Array.isArray(current) && current[0] ? (current[0].email || '') : '';
      if (String(currEmail || '').trim().toLowerCase() === trimmed.toLowerCase()) {

        return true;

      }
      const res = await this.executeCall(
        'res.partner',
        'write',
        [[partnerId], { email: trimmed }]
      );
      return Boolean(res);
    } catch (error) {
      console.error('❌ ensurePartnerPrimaryEmail failed', partnerId, email, error);
      return false;
    }
  }

  // 🔎 Public helper: find partner id by email (checks email, Email1, Email2)
  async findPartnerIdByEmail(email: string): Promise<number | null> {
    try {
      const result = await this.executeCall(
        'res.partner',
        'search_read',
        [["|", "|", ['email', '=', email], ['Email1', '=', email], ['Email2', '=', email]], ['id']],
        { limit: 1 }
      );
      if (Array.isArray(result) && result.length > 0) {
        return Number(result[0].id);
      }
      return null;
    } catch (error) {
      console.error('❌ findPartnerIdByEmail failed', email, error);
      return null;
    }
  }

  // GET PURCHASE DETAILS FOR A CONTACT
  async getContactPurchaseDetails(contactId: number): Promise<any[]> {
    const fields = [
      "id",
      "L_Customer",
      "customer_id",
      "L_Artist",
      "res_artist_id",
      "L_Artworks",
      "product_template_id",
      "L_Invoice",
      "opera_invoice_id",
      "L_Order",
      "opera_order_id",
      "SoldPriceTtcItem",
      "Currency",
      "DateInvoice"
    ];

    const customerStringId = `C${contactId}`;

    return await this.executeCall(
      'gallery.sales.lines.items',
      'search_read',
      [[["L_Customer", "=", customerStringId]]],
      { fields }
    );
  }

  async getContactVisibility(partnerId: number): Promise<boolean | null> {
    try {
      console.log(`[OdooClient] Fetching visibility from Odoo for partner: ${partnerId}`);
      // Try RPC route first
      const rpcUrl = `${this.baseURL}/rpc/v1/partner/${partnerId}/contact_visibility`;
      try {
        console.log('[OdooClient] Trying RPC visibility route...');
        const rpcResp = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params: {} })
        });
        const rpcData = await rpcResp.json().catch(() => null as any);
        if (rpcResp.ok && rpcData && rpcData.result && typeof rpcData.result.show_personal_info !== 'undefined') {
          return !!rpcData.result.show_personal_info;
        }
        // If session expired on RPC, fallback to executeCall without cookies
        if (rpcData && rpcData.error && /Session Expired|SessionExpired/i.test(String(rpcData.error.message || rpcData.error.data?.message))) {

          try {
            const prospectRes = await this.executeCall('crm.prospect', 'read', [[partnerId], ['id', 'show_personal_info']]);
            if (Array.isArray(prospectRes) && prospectRes[0] && typeof prospectRes[0].show_personal_info !== 'undefined') {
              return !!prospectRes[0].show_personal_info;
            }
          } catch (_) { }
          try {
            const partnerRes = await this.executeCall('res.partner', 'read', [[partnerId], ['id', 'show_personal_info']]);
            if (Array.isArray(partnerRes) && partnerRes[0] && typeof partnerRes[0].show_personal_info !== 'undefined') {
              return !!partnerRes[0].show_personal_info;
            }
          } catch (_) { }
        }
      } catch (_) { }

      // Fallback to HTTP route
      const url = `${this.baseURL}/api/v1/partner/${partnerId}/contact_visibility`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      console.log(`[OdooClient] Visibility response status from Odoo: ${response.status}`);
      const data = await response.json().catch(() => null as any);
      console.log('[OdooClient] Visibility response data from Odoo:', data);

      // If JSON-RPC envelope leaked through or session expired, try executeCall
      if (data && typeof data === 'object' && ('jsonrpc' in data)) {
        if ((data as any).error && /Session Expired|SessionExpired/i.test(String((data as any).error.message || (data as any).error.data?.message))) {

          try {
            const prospectRes = await this.executeCall('crm.prospect', 'read', [[partnerId], ['id', 'show_personal_info']]);
            if (Array.isArray(prospectRes) && prospectRes[0] && typeof prospectRes[0].show_personal_info !== 'undefined') {
              return !!prospectRes[0].show_personal_info;
            }
          } catch (_) { }
          try {
            const partnerRes = await this.executeCall('res.partner', 'read', [[partnerId], ['id', 'show_personal_info']]);
            if (Array.isArray(partnerRes) && partnerRes[0] && typeof partnerRes[0].show_personal_info !== 'undefined') {
              return !!partnerRes[0].show_personal_info;
            }
          } catch (_) { }
        }
        return null;
      }

      if (!response.ok) {
        console.error(`getContactVisibility failed with status: ${response.status}`);

        try {
          const prospectRes = await this.executeCall('crm.prospect', 'read', [[partnerId], ['id', 'show_personal_info']]);
          if (Array.isArray(prospectRes) && prospectRes[0] && typeof prospectRes[0].show_personal_info !== 'undefined') {
            return !!prospectRes[0].show_personal_info;
          }
        } catch (_) { }
        try {
          const partnerRes = await this.executeCall('res.partner', 'read', [[partnerId], ['id', 'show_personal_info']]);
          if (Array.isArray(partnerRes) && partnerRes[0] && typeof partnerRes[0].show_personal_info !== 'undefined') {
            return !!partnerRes[0].show_personal_info;
          }
        } catch (_) { }
        return null;
      }
      return !!data?.show_personal_info;
    } catch (e) {
      console.error('getContactVisibility error', e);

      try {
        const prospectRes = await this.executeCall('crm.prospect', 'read', [[partnerId], ['id', 'show_personal_info']]);
        if (Array.isArray(prospectRes) && prospectRes[0] && typeof prospectRes[0].show_personal_info !== 'undefined') {
          return !!prospectRes[0].show_personal_info;
        }
      } catch (_) { }
      try {
        const partnerRes = await this.executeCall('res.partner', 'read', [[partnerId], ['id', 'show_personal_info']]);
        if (Array.isArray(partnerRes) && partnerRes[0] && typeof partnerRes[0].show_personal_info !== 'undefined') {
          return !!partnerRes[0].show_personal_info;
        }
      } catch (_) { }
      return null;
    }
  }

  async getContactsVisibility(partnerIds: number[]): Promise<Record<number, boolean>> {
    try {
      const result: Record<number, boolean> = {};
      // Resolve visibility directly through ORM so both res.partner and crm.prospect
      // records use the logged-in user's permissions consistently.
      const [partnerRead, prospectRead] = await Promise.all([
        this.executeCall('res.partner', 'read', [partnerIds, ['id', 'show_personal_info']]).catch(() => []),
        this.executeCall('crm.prospect', 'read', [partnerIds, ['id', 'show_personal_info']]).catch(() => [])
      ]);


      if (Array.isArray(partnerRead)) {
        for (const r of partnerRead) {
          if (r && typeof r.id !== 'undefined') result[Number(r.id)] = !!r.show_personal_info;
        }
      }
      if (Array.isArray(prospectRead)) {
        for (const r of prospectRead) {
          if (r && typeof r.id !== 'undefined') result[Number(r.id)] = !!r.show_personal_info;
        }
      }

      return result;
    } catch (e) {
      console.error('getContactsVisibility error', e);
      return {};
    }
  }


  async sendEmailToAddress(
    emailTo: string,
    subject: string,
    bodyHtml: string,
    attachments?: Array<{ name: string; content: string }>
  ): Promise<boolean> {
    try {
      const attachmentIds = attachments && attachments.length > 0
        ? attachments.map(a => [0, 0, { name: a.name, datas: a.content, mimetype: 'application/octet-stream' }])
        : [];
      const mailId = await this.executeCall(
        'mail.mail',
        'create',
        [{
          subject,
          body_html: bodyHtml,
          email_to: emailTo,
          attachment_ids: attachmentIds
        }]
      );
      if (!mailId) return false;
      await this.executeCall('mail.mail', 'send', [[mailId]]);
      return true;
    } catch (e) {
      console.error('sendEmailToAddress failed', emailTo, e);
      return false;
    }
  }


  async getOrCreatePartnerByEmail(email: string, displayName?: string): Promise<number | null> {
    try {
      const found = await this.executeCall(
        'res.partner',
        'search_read',
        [["|", "|", ['email', '=', email], ['Email1', '=', email], ['Email2', '=', email]], ['id']],
        { limit: 1 }

      );
      if (Array.isArray(found) && found.length > 0 && typeof found[0].id !== 'undefined') {
        return Number(found[0].id);
      }


      const name = (displayName && displayName.trim()) || email.split('@')[0];
      const newId = await this.executeCall('res.partner', 'create', [{ name, email, company_type: 'person' }]);
      return typeof newId === 'number' ? newId : (typeof newId === 'string' ? parseInt(newId, 10) : null);
    } catch (e) {
      console.error('getOrCreatePartnerByEmail failed', email, e);
      return null;
    }
  }

  async createTodoList(payload: Record<string, any>): Promise<number | null> {
    try {
      const res = await this.executeCall('art.list', 'create', [payload]);
      const id = typeof res === 'number' ? res : (Array.isArray(res) ? res[0] : null);
      return Number.isFinite(id) ? Number(id) : null;
    } catch (e) {
      console.error('[OdooClient] Failed to create todo list', payload, e);
      return null;
    }
  }

  /**
   * Fetch a single art.list record with all related data pre-populated
   * for the EditListForm: reminders, documents, share rules, company shares.
   */
  async getTodoList(id: number): Promise<Record<string, any> | null> {
    try {
      // ── 1. Main record ────────────────────────────────────────────────────
      const [record] = await this.executeCall('art.list', 'read', [[id]], {
        fields: [
          'id', 'name', 'code', 'type', 'color', 'note',
          'start_date', 'end_date', 'budget', 'country',
          'partner_id', 'visibility', 'state',
          'reminder_ids', 'document_ids', 'share_ids', 'company_share_ids',
        ],
      });

      if (!record) return null;

      // ── 2. Parallel fetch of all related records ──────────────────────────
      const [reminders, documents, shareRules, companyShareRules] = await Promise.all([

        // Reminders → KeyDate[]
        record.reminder_ids?.length
          ? this.executeCall('art.list.reminder', 'read', [record.reminder_ids], {
              fields: ['id', 'name', 'description', 'date'],
            })
          : [],

        // Documents (metadata only, no binary)
        record.document_ids?.length
          ? this.executeCall('art.list.document', 'read', [record.document_ids], {
              fields: ['id', 'name', 'mimetype', 'create_date'],
            })
          : [],

        // Per-user share rules
        record.share_ids?.length
          ? this.executeCall('art.list.share', 'read', [record.share_ids], {
              fields: ['id', 'user_id', 'permission', 'expiration_date', 'state'],
            })
          : [],

        // Per-company share rules
        record.company_share_ids?.length
          ? this.executeCall('art.list.company.share', 'read', [record.company_share_ids], {
              fields: ['id', 'company_id', 'permission', 'expiration_date', 'state'],
            })
          : [],
      ]);

      // ── 3. Map reminders → KeyDate shape ──────────────────────────────────
      const mappedReminders = reminders.map((r: any) => ({
        id:       String(r.id),
        title:    r.name    || '',
        subtitle: r.description || '',
        // Odoo stores as 'YYYY-MM-DD HH:MM:SS' — keep only the date part
        date:     r.date ? String(r.date).slice(0, 10) : '',
        icon:     '📅',
      }));

      // ── 4. Map share rules → VisibilitySharingValue.colleagues shape ──────
      const mappedColleagues = shareRules.map((s: any) => {
        const userId   = Array.isArray(s.user_id) ? s.user_id[0] : s.user_id;
        const userName = Array.isArray(s.user_id) ? s.user_id[1] : '';
        const initials = userName
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();
        return {
          id:         userId,
          name:       userName,
          initials,
          email:      '',          // not stored on the share rule
          permission: s.permission,
          expires:    s.expiration_date || 'Permanent',
        };
      });

      // ── 5. Map company share rules → VisibilitySharingValue.sharedCompanies
      const mappedSharedCompanies = companyShareRules.map((c: any) => {
        const companyId   = Array.isArray(c.company_id) ? c.company_id[0] : c.company_id;
        const companyName = Array.isArray(c.company_id) ? c.company_id[1] : '';
        return {
          id:         companyId,
          name:       companyName,
          code:       '',          // not on the share rule; fill if needed
          permission: c.permission,
          expires:    c.expiration_date || 'Permanent',
        };
      });

      // ── 6. Build visibilitySharing object ─────────────────────────────────
      // For `audience` (share-all) lists we co-store the chosen permission +
      // expiry on the first `art.list.company.share` row (no new backend
      // fields needed — see CreateListForm/EditListForm). Surface those back
      // here so the form re-hydrates with the previously-saved values.
      const audienceRow =
        record.visibility === 'audience' ? companyShareRules[0] : null;

      const visibilitySharing = {
        visibility:         record.visibility || 'private',
        colleagues:         mappedColleagues,
        sharedCompanies:    mappedSharedCompanies,
        audiencePermission: audienceRow?.permission === 'write' ? 'write' : 'read',
        audienceExpires:    audienceRow?.expiration_date || '',
        owner:              { name: '' },   // filled by the component from auth store
        maintainReadOnly:   false,
      };

      // ── 7. Assemble final ArtListRecord ───────────────────────────────────
      return {
        id:                  record.id,
        name:                record.name            || '',
        code:                record.code            || '',
        type:                record.type            || 'SHW',
        color:               record.color           || '',
        note:                record.note            || '',
        start_date:          record.start_date      || '',
        end_date:            record.end_date        || '',
        budget:              record.budget          || '',
        country:             record.country         || 'PAR',
        partner_id:          Array.isArray(record.partner_id)
                               ? { id: record.partner_id[0], name: record.partner_id[1] }
                               : null,
        reminder_ids:        mappedReminders,
        visibilitySharing,
        existingDocuments:   documents.map((d: any) => ({
          id:       d.id,
          name:     d.name,
          mimetype: d.mimetype || '',
        })),
      };
    } catch (e) {
      console.error('[OdooClient] Failed to fetch todo list', { id, error: e });
      return null;
    }
  }

  async updateTodoList(id: number, payload: Record<string, any>): Promise<boolean | null> {
    try {
      const res = await this.executeCall('art.list', 'write', [[id], payload]);
      return res === true ? true : null;
    } catch (e) {
      console.error('[OdooClient] Failed to update todo list', { id, payload, error: e });
      return null;
    }
  }

  async getTodoListDocuments(id: number): Promise<ArtListDocumentAttachment[]> {
    try {
      const listId = Number(id);
      if (!Number.isFinite(listId)) return [];

      const [record] = await this.executeCall('art.list', 'read', [[listId]], {
        fields: ['document_ids'],
      });
      const documentIds = Array.isArray(record?.document_ids) ? record.document_ids : [];
      if (documentIds.length === 0) return [];

      const rows = await this.executeCall('art.list.document', 'read', [documentIds], {
        fields: ['id', 'name', 'mimetype', 'create_date'],
      });

      return (Array.isArray(rows) ? rows : []).map((row: any) => ({
        id: Number(row.id),
        name: row.name || 'Attachment',
        mimetype: row.mimetype || '',
        create_date: row.create_date || '',
      })).filter((row: ArtListDocumentAttachment) => Number.isFinite(row.id));
    } catch (e) {
      console.error('[OdooClient] Failed to fetch todo list documents', { id, error: e });
      return [];
    }
  }

  async saveTodoListDocumentToDevice(documentId: number, fallbackFileName = 'attachment'): Promise<void> {
    const id = Number(documentId);
    if (!Number.isFinite(id)) {
      throw new Error('Invalid attachment id.');
    }

    const rows = await this.executeCall('art.list.document', 'read', [[id]], {
      fields: ['name', 'mimetype', 'datas'],
    });
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) {
      throw new Error('Attachment not found.');
    }
    this.materializeIrAttachmentReadRowToDevice(row as Record<string, unknown>, id, fallbackFileName);
  }

  async deleteTodoListDocument(listId: number, documentId: number): Promise<boolean> {
    try {
      const cleanListId = Number(listId);
      const cleanDocumentId = Number(documentId);
      if (!Number.isFinite(cleanListId) || !Number.isFinite(cleanDocumentId)) return false;

      const res = await this.executeCall('art.list', 'write', [
        [cleanListId],
        { document_ids: [[2, cleanDocumentId]] },
      ]);
      return res === true;
    } catch (e) {
      console.error('[OdooClient] Failed to delete todo list document', { listId, documentId, error: e });
      return false;
    }
  }

  // This is not in used 
  // Get Art lists with search, type filter, pagination, and type counts for filters
  async getArtLists({
    search = '',
    type = '',
    limit = 10,
    offset = 0
  }: {
    search?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    data: any[];
    total: number;
    type_counts: Record<string, number>;
  }> {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (type) params.append('type', type);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const url = `${this.baseURL}/api/v1/art_lists?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (await handleMaintenanceResponse(response)) {
        return {
          data: [],
          total: 0,
          type_counts: {},
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        data: data.data || [],
        total: data.total || 0,
        type_counts: data.type_counts || {},
      };
    } catch (error) {
      // handleNetworkError();
      console.error('Failed to fetch art lists:', error);
      return {
        data: [],
        total: 0,
        type_counts: {},
      };
    }
  }

  async fetchArtLists({ search = '', type = '', country = '', page = 1, pageSize = 10, forUserId, forCompanyIds = [], visibilityRole = 'sales' }: {
    search?: string;
    type?: string;
    country?: string;
    page?: number;
    pageSize?: number;
    forUserId?: number;
    forCompanyIds?: number[];
    /** Admin: all lists. Director: own + gallery + shared. Sales: own + shared only. */
    visibilityRole?: 'admin' | 'director' | 'sales';
  }) {
    const offset = (page - 1) * pageSize;

    const searchDomain: any[] = [];
    if (search.trim()) {
      searchDomain.push('|');
      searchDomain.push(['name', 'ilike', search.trim()]);
      searchDomain.push(['code', 'ilike', search.trim()]);
    }
    if (country) {
      searchDomain.push(['country', '=', country]);
    }

    const orParts = buildArtListVisibilityOrParts({
      visibilityRole: visibilityRole ?? 'sales',
      forUserId,
      forCompanyIds,
    }) as any[][];
    const hasCompany = forCompanyIds.length > 0;

    if (orParts.length > 0) {
      for (let i = 0; i < orParts.length - 1; i++) {
        searchDomain.push('|');
      }
      orParts.forEach((p) => searchDomain.push(p));
    }

    const listDomain = [...searchDomain];
    if (type) {
      listDomain.push(['type', '=', type]);
    }

    // 1. Get list
    const listRes = await this.executeCall('art.list', 'search_read', [listDomain], {
      fields: [],
      offset,
      limit: pageSize,
      order: 'id desc'
    });

    const allShareIds = listRes.flatMap((item: any) => item.share_ids || []);
    let sharePermissions: Record<number, { userId: number; permission: string, state: string }[]> = {};
    let shareRecipientsByListId: Record<number, string[]> = {};
    if (allShareIds.length > 0) {
      const sharesRes = await this.executeCall('art.list.share', 'search_read', [
        [['id', 'in', allShareIds]]
      ], {
        fields: ['todo_list_id', 'user_id', 'permission', 'state']
      });

      sharesRes.forEach((share: any) => {
        const listId = Array.isArray(share.todo_list_id)
          ? share.todo_list_id[0]
          : share.todo_list_id;
        const userId = Array.isArray(share.user_id) ? share.user_id[0] : share.user_id;
        const userName = Array.isArray(share.user_id) ? share.user_id[1] : '';

        if (forUserId && userId === forUserId) {
          if (!sharePermissions[listId]) sharePermissions[listId] = [];
          sharePermissions[listId].push({
            userId,
            permission: share.permission,
            state: share.state
          });
        }

        if (userName && share.state === 'active') {
          if (!shareRecipientsByListId[listId]) shareRecipientsByListId[listId] = [];
          shareRecipientsByListId[listId].push(userName);
        }
      });
    }

    const allCompanyShareIds = listRes.flatMap((item: any) => item.company_share_ids || []);
    let companySharePermissions: Record<number, { companyId: number; permission: string; state: string }[]> = {};
    if (allCompanyShareIds.length > 0 && hasCompany) {
      const companySharesRes = await this.executeCall('art.list.company.share', 'search_read', [
        // 'in' on company_id to fetch rules for all user companies in one shot
        [['id', 'in', allCompanyShareIds], ['company_id', 'in', forCompanyIds]],
      ], {
        fields: ['todo_list_id', 'company_id', 'permission', 'state'],
      });

      companySharesRes.forEach((share: any) => {
        const listId = Array.isArray(share.todo_list_id)
          ? share.todo_list_id[0]
          : share.todo_list_id;
        if (!companySharePermissions[listId]) companySharePermissions[listId] = [];
        companySharePermissions[listId].push({
          companyId: Array.isArray(share.company_id) ? share.company_id[0] : share.company_id,
          permission: share.permission,
          state: share.state,
        });
      });
    }

    // 2. Get total (with same domain)
    const totalRes = await this.executeCall('art.list', 'search_count', [searchDomain]);

    // 3. Get type counts (no type filter in domain)
    const countsRes = await this.executeCall('art.list', 'read_group', [
      searchDomain,
      ['__count'],
      ['type']
    ], { lazy: false });

    const typeCounts: Record<string, number> = {};
    countsRes.forEach((group: any) => {
      const key = group.type || 'none';
      typeCounts[key] = group.__count;
    });

    return {
      data: listRes.map((item: any) => ({
        ...item,
        _sharePermissions: sharePermissions[item.id] || [],
        _shareRecipients: shareRecipientsByListId[item.id] || [],
        _companySharePermissions: companySharePermissions[item.id] || [],
      })),
      total: totalRes,
      type_counts: typeCounts
    };
  }

  /** Batch-read `company_ids` for list creators (gallery-peer delete rules). */
  async readUsersCompanyIds(userIds: number[]): Promise<Record<number, number[]>> {
    const ids = [...new Set(userIds.filter((id) => Number.isFinite(id) && id > 0))];
    if (ids.length === 0) return {};
    try {
      const rows = (await this.executeCall("res.users", "read", [ids], {
        fields: ["company_ids"],
      })) as { id: number; company_ids?: unknown }[];
      return Object.fromEntries(
        rows.map((u) => [u.id, normalizeOdooCompanyIds(u.company_ids)]),
      );
    } catch (e) {
      console.warn("readUsersCompanyIds failed:", e);
      return {};
    }
  }

  /**
   * Find the first upcoming Show / Fair list that contains the given artwork.
   *
   * Powers the "eye" button on the artwork-detail page: it should appear when
   * an artwork is part of at least one Show (`SHW`) or Fair (`FAIR`) list whose
   * `start_date` is strictly after today. The button stays visible (but
   * disabled) when such a list exists but the current user doesn't have access
   * to any of them, so users can see that the artwork is on an upcoming
   * exhibit even if the list itself is private to someone else.
   *
   * Returns:
   *   - `match`         → first list that satisfies the 3 base conditions
   *                       (artwork-in-list ∧ type∈{SHW,FAIR} ∧ start_date>today),
   *                       regardless of access. Drives the icon's *visibility*.
   *   - `accessibleMatch` → first list that ALSO satisfies user-access
   *                         (created by user, shared with user, or visibility =
   *                         'audience'). Drives the icon's *enabled* state and
   *                         the redirect target.
   *
   * Both are `null` when no list matches. We resolve both in two narrow
   * `search_read` calls rather than fetching all matches because lists can be
   * numerous and we only ever need the earliest hit.
   */
  async findFirstUpcomingShowOrFairListForArtwork(
    artworkId: number | string,
  ): Promise<{
    match: { id: number; name: string; startDate: string } | null;
    accessibleMatch: { id: number; name: string; startDate: string } | null;
  }> {
    try {
      const aid = Number(artworkId);
      if (!Number.isFinite(aid) || aid <= 0) {
        return { match: null, accessibleMatch: null };
      }

      // `today` excluded explicitly per product rule — only strictly future
      // shows / fairs surface here.
      const today = new Date().toISOString().slice(0, 10);

      const baseDomain: any[] = [
        ["artwork_ids", "in", [aid]],
        ["type", "in", ["SHW", "FAIR"]],
        ["start_date", ">", today],
      ];

      // ── Step 1: any matching list (no user filter) ──────────────────────
      // Drives `match` → controls whether the eye icon is rendered at all.
      const baseHit: any[] = await this.executeCall(
        "art.list",
        "search_read",
        [baseDomain],
        {
          fields: ["id", "name", "start_date"],
          order: "start_date asc, id asc",
          limit: 1,
        },
      ).catch(() => []);

      const match = (baseHit || [])[0]
        ? {
            id: Number(baseHit[0].id),
            name: String(baseHit[0].name || ""),
            startDate: String(baseHit[0].start_date || ""),
          }
        : null;

      if (!match) return { match: null, accessibleMatch: null };

      // ── Step 2: same role-based visibility as `fetchArtLists` ──
      const userId = this.user?.id || this.credentials?.userId;
      const userIdNumber = userId ? Number(userId) : null;
      let companyIds: number[] = [];
      let visibilityRole: "admin" | "director" | "sales" = "sales";
      if (userIdNumber) {
        try {
          const profile = await this.getCompleteUserProfile();
          companyIds = (profile?.companies || [])
            .map((c) => Number(c.id))
            .filter((id) => Number.isFinite(id) && id > 0);
          visibilityRole = mapProfileRoleToArtListRole(profile?.role);
        } catch {
          companyIds = [];
        }
      }

      const orParts = buildArtListVisibilityOrParts({
        visibilityRole,
        forUserId: userIdNumber ?? undefined,
        forCompanyIds: companyIds,
      }) as any[][];

      const accessDomain: any[] = [...baseDomain];
      if (orParts.length > 0) {
        for (let i = 0; i < orParts.length - 1; i++) accessDomain.push("|");
        orParts.forEach((p) => accessDomain.push(p));
      } else if (visibilityRole !== "admin") {
        return { match, accessibleMatch: null };
      }

      const accessHit: any[] = await this.executeCall(
        "art.list",
        "search_read",
        [accessDomain],
        {
          fields: ["id", "name", "start_date"],
          order: "start_date asc, id asc",
          limit: 1,
        },
      ).catch(() => []);

      const accessibleMatch = (accessHit || [])[0]
        ? {
            id: Number(accessHit[0].id),
            name: String(accessHit[0].name || ""),
            startDate: String(accessHit[0].start_date || ""),
          }
        : null;

      return { match, accessibleMatch };
    } catch (err) {
      console.warn(
        "findFirstUpcomingShowOrFairListForArtwork failed (non-fatal):",
        err,
      );
      return { match: null, accessibleMatch: null };
    }
  }

  /**
   * Bulk variant of `findFirstUpcomingShowOrFairListForArtwork` for list-style
   * surfaces (e.g. the artworks index page) where rendering N rows would
   * otherwise fan out into 2N RPCs. Returns a map keyed by `artwork_id` with
   * the same `match` / `accessibleMatch` shape used by the per-artwork helper.
   *
   * Strategy:
   *   1. One `search_read` for every list that satisfies the base 3 conditions
   *      (artwork in list ∧ type∈{SHW,FAIR} ∧ start_date>today) and contains
   *      ANY of the requested artwork ids. We pull `artwork_ids` so we can
   *      back-fill the per-artwork buckets.
   *   2. One more `search_read` with the same domain plus the user-access OR
   *      clause used elsewhere, so the icon's "enabled" state matches the
   *      single-artwork helper's behaviour.
   *   3. For each artwork we keep only the earliest-starting list it appears
   *      in — same ordering as the singular helper.
   */
  async findFirstUpcomingShowOrFairListsForArtworks(
    artworkIds: Array<number | string>,
  ): Promise<
    Record<
      number,
      {
        match: { id: number; name: string; startDate: string } | null;
        accessibleMatch: { id: number; name: string; startDate: string } | null;
      }
    >
  > {
    const out: Record<
      number,
      {
        match: { id: number; name: string; startDate: string } | null;
        accessibleMatch: { id: number; name: string; startDate: string } | null;
      }
    > = {};

    try {
      const ids = Array.from(
        new Set(
          (artworkIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      );
      if (ids.length === 0) return out;

      ids.forEach((id) => {
        out[id] = { match: null, accessibleMatch: null };
      });

      const today = new Date().toISOString().slice(0, 10);

      // ── Base domain (no user filter) — drives `match` per artwork ───────
      const baseDomain: any[] = [
        ["artwork_ids", "in", ids],
        ["type", "in", ["SHW", "FAIR"]],
        ["start_date", ">", today],
      ];

      // We don't need the entire artwork_ids array for matching, but we DO
      // need it to figure out which subset of `ids` each list bucket maps
      // to. Cap on `limit` is intentionally generous: the page only ever
      // shows ~20 artworks so the realistic upper bound on matching lists
      // is small even in busy galleries.
      const baseRows: any[] = await this.executeCall(
        "art.list",
        "search_read",
        [baseDomain],
        {
          fields: ["id", "name", "start_date", "artwork_ids"],
          order: "start_date asc, id asc",
          limit: 200,
        },
      ).catch(() => []);

      const requested = new Set(ids);
      const fillFrom = (
        rows: any[],
        which: "match" | "accessibleMatch",
      ): void => {
        for (const row of rows || []) {
          const listId = Number(row.id);
          const startDate = String(row.start_date || "");
          const name = String(row.name || "");
          const aids: number[] = Array.isArray(row.artwork_ids)
            ? row.artwork_ids
                .map((x: any) => Number(x))
                .filter((x: number) => Number.isFinite(x) && x > 0)
            : [];
          for (const aid of aids) {
            if (!requested.has(aid)) continue;
            // Earliest-first ordering means we can simply skip any artwork
            // that already has a hit in this bucket.
            if (!out[aid][which]) {
              out[aid][which] = { id: listId, name, startDate };
            }
          }
        }
      };
      fillFrom(baseRows, "match");

      // ── Accessible domain — drives `accessibleMatch` per artwork ────────
      // Skip the second call if there were zero base hits to begin with.
      const anyMatched = Object.values(out).some((v) => v.match !== null);
      if (anyMatched) {
        const userId = this.user?.id || this.credentials?.userId;
        const userIdNumber = userId ? Number(userId) : null;
        let companyIds: number[] = [];
        if (userIdNumber) {
          try {
            const profile = await this.getCompleteUserProfile();
            companyIds = (profile?.companies || [])
              .map((c) => Number(c.id))
              .filter((id) => Number.isFinite(id) && id > 0);
          } catch {
            companyIds = [];
          }
        }

        const orParts: any[][] = [];
        if (userIdNumber) {
          orParts.push(["create_uid", "=", userIdNumber]);
          orParts.push(["share_ids.user_id", "=", userIdNumber]);
        }
        if (companyIds.length > 0) {
          orParts.push(["company_share_ids.company_id", "in", companyIds]);
        }
        orParts.push(["visibility", "=", "audience"]);

        const accessDomain: any[] = [...baseDomain];
        for (let i = 0; i < orParts.length - 1; i++) accessDomain.push("|");
        orParts.forEach((p) => accessDomain.push(p));

        const accessRows: any[] = await this.executeCall(
          "art.list",
          "search_read",
          [accessDomain],
          {
            fields: ["id", "name", "start_date", "artwork_ids"],
            order: "start_date asc, id asc",
            limit: 200,
          },
        ).catch(() => []);
        fillFrom(accessRows, "accessibleMatch");
      }

      return out;
    } catch (err) {
      console.warn(
        "findFirstUpcomingShowOrFairListsForArtworks failed (non-fatal):",
        err,
      );
      return out;
    }
  }

  /**
   * Fetch every art list containing the given artwork(s), marking each row
   * with whether the current user can open it from the CRM UI.
   */
  async findArtworkListsForArtworks(
    artworkIds: Array<number | string>,
  ): Promise<Record<number, ArtworkListMembership[]>> {
    const out: Record<number, ArtworkListMembership[]> = {};

    try {
      const ids = Array.from(
        new Set(
          (artworkIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      );
      if (ids.length === 0) return out;

      ids.forEach((id) => {
        out[id] = [];
      });

      const baseDomain: any[] = [["artwork_ids", "in", ids]];
      const fields = ["id", "code", "name", "type", "color", "start_date", "country", "artwork_ids"];

      const baseRows: any[] = await this.executeCall(
        "art.list",
        "search_read",
        [baseDomain],
        {
          fields,
          order: "start_date desc, id desc",
          limit: 500,
        },
      ).catch(() => []);

      if (!Array.isArray(baseRows) || baseRows.length === 0) return out;

      const previewArtworkIds = Array.from(
        new Set(
          baseRows.flatMap((row: any) =>
            Array.isArray(row.artwork_ids)
              ? row.artwork_ids
                  .map((x: any) => Number(x))
                  .filter((x: number) => Number.isFinite(x) && x > 0)
                  .slice(0, 3)
              : [],
          ),
        ),
      );
      const previewImageMap = new Map<number, string>();
      if (previewArtworkIds.length > 0) {
        try {
          const previewArtworks = await this.getArtworksByIds(previewArtworkIds);
          for (const artwork of previewArtworks || []) {
            const artworkId = Number(artwork?.id);
            const imageUrl = this.getBestArtworkImageUrl(artwork);
            if (Number.isFinite(artworkId) && imageUrl) {
              previewImageMap.set(artworkId, imageUrl);
            }
          }
        } catch (err) {
          console.warn("Artwork list membership preview images failed:", err);
        }
      }

      const userId = this.user?.id || this.credentials?.userId;
      const userIdNumber = userId ? Number(userId) : null;
      let companyIds: number[] = [];
      if (userIdNumber) {
        try {
          const profile = await this.getCompleteUserProfile();
          companyIds = (profile?.companies || [])
            .map((c) => Number(c.id))
            .filter((id) => Number.isFinite(id) && id > 0);
        } catch {
          companyIds = [];
        }
      }

      const orParts: any[][] = [];
      if (userIdNumber) {
        orParts.push(["create_uid", "=", userIdNumber]);
        orParts.push(["share_ids.user_id", "=", userIdNumber]);
      }
      if (companyIds.length > 0) {
        orParts.push(["company_share_ids.company_id", "in", companyIds]);
      }
      orParts.push(["visibility", "=", "audience"]);

      const accessDomain: any[] = [...baseDomain];
      for (let i = 0; i < orParts.length - 1; i++) accessDomain.push("|");
      orParts.forEach((p) => accessDomain.push(p));

      const accessRows: any[] = await this.executeCall(
        "art.list",
        "search_read",
        [accessDomain],
        {
          fields: ["id"],
          limit: 500,
        },
      ).catch(() => []);

      const accessibleIds = new Set(
        (Array.isArray(accessRows) ? accessRows : [])
          .map((row: any) => Number(row.id))
          .filter((id: number) => Number.isFinite(id) && id > 0),
      );
      const requested = new Set(ids);

      for (const row of baseRows) {
        const listId = Number(row.id);
        if (!Number.isFinite(listId) || listId <= 0) continue;

        const rowArtworkIds: number[] = Array.isArray(row.artwork_ids)
          ? row.artwork_ids
              .map((x: any) => Number(x))
              .filter((x: number) => Number.isFinite(x) && x > 0)
          : [];
        const previewImages = rowArtworkIds
          .slice(0, 3)
          .map((artworkId) => previewImageMap.get(artworkId))
          .filter((url): url is string => !!url);

        const list: ArtworkListMembership = {
          id: listId,
          code: String(row.code || ""),
          name: String(row.name || "Untitled list"),
          type: String(row.type || ""),
          color: String(row.color || ""),
          startDate: String(row.start_date || ""),
          country: String(row.country || ""),
          accessible: accessibleIds.has(listId),
          previewImages: previewImages.length ? previewImages : undefined,
        };

        for (const aid of rowArtworkIds) {
          if (!requested.has(aid)) continue;
          out[aid].push(list);
        }
      }

      return out;
    } catch (err) {
      console.warn("findArtworkListsForArtworks failed (non-fatal):", err);
      return out;
    }
  }

  async findArtworkListsForArtwork(
    artworkId: number | string,
  ): Promise<ArtworkListMembership[]> {
    const aid = Number(artworkId);
    if (!Number.isFinite(aid) || aid <= 0) return [];
    const map = await this.findArtworkListsForArtworks([aid]);
    return map[aid] || [];
  }

  async addArtworksToList(listIds: number[], artworkIds: number[]): Promise<boolean | null> {
    try {
      // Clean and prepare artwork IDs once (outside the loop)
      const cleanArtworkIds = artworkIds
        .map(id => Number(id))
        .filter(id => Number.isFinite(id));

      if (cleanArtworkIds.length === 0) {
        return true;
      }

      // Prepare the many2many commands (array of [4, id])
      const commands = cleanArtworkIds.map(id => [4, id]);

      // Update each list
      for (const listId of listIds) {
        const res = await odooClient.executeCall("art.list", "write", [
          [listId],
          {
            artwork_ids: commands
          },
        ]);
      }

      return true;
    } catch (e) {
      console.error(
        '[OdooClient] Failed to add artworks to list',
        { listIds, artworkIds, error: e }
      );
      return false;
    }
  }

  /**
   * Removes multiple artworks from one or more art lists
   * 
   * @param listIds - Array of list IDs to remove artworks from
   * @param artworkIds - Array of artwork IDs to remove
   * @returns true if operation succeeded, false if failed, null if nothing to do (invalid/cleaned input)
   */
  async removeArtworksFromList(listIds: number[], artworkIds: number[]): Promise<boolean | null> {
    try {
      // Clean and prepare artwork IDs once (outside the loop)
      const cleanArtworkIds = artworkIds
        .map(id => Number(id))
        .filter(id => Number.isFinite(id));

      if (cleanArtworkIds.length === 0) {
        return true; // nothing to remove → consider it a success
      }

      // For removal we use command [3, id] → unlink (remove link without deleting record)
      const commands = cleanArtworkIds.map(id => [3, id]);

      // Update each list
      for (const listId of listIds) {
        const res = await odooClient.executeCall("art.list", "write", [
          [listId],
          {
            artwork_ids: commands
          },
        ]);
      }

      return true;
    } catch (e) {
      console.error(
        '[OdooClient] Failed to remove artworks from list',
        { listIds, artworkIds, error: e }
      );
      return false;
    }
  }

  async fetchUsers({ search = '', page = 1, pageSize = 10 }: {
    search?: string;
    page?: number;
    pageSize?: number;
  }) {
    const offset = (page - 1) * pageSize;

    const searchDomain: any[] = [['active', '=', true]];
    if (search.trim()) {
      searchDomain.push('|');
      searchDomain.push(['name', 'ilike', search.trim()]);
      searchDomain.push(['email', 'ilike', search.trim()]);
    }

    return await this.executeCall('res.users', 'search_read', [searchDomain], {
      fields: ['id', 'name', 'email', 'login'],
      offset,
      limit: pageSize,
      order: 'name asc'
    });
  }

  async forceDeleteArtLists(ids: number | number[]) {
    const idList = Array.isArray(ids) ? ids : [ids];
    if (idList.length === 0) return { success: false, deletedCount: 0 };

    const result = await this.executeCall('art.list', 'unlink', [idList]);
    const success = result === true || (Array.isArray(result) && result.every(r => r === true));

    return {
      success,
      deletedCount: success ? idList.length : 0,
      message: success ? undefined : "Deletion failed"
    };
  }

  /** Odoo uid for RPC (logged-in user). */
  getOdooUserId(): number | null {
    const raw = this.user?.id ?? this.credentials?.userId;
    if (raw === undefined || raw === null) return null;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Default `company_id` for portal-created CRM leads: the logged-in user’s company
   * from Odoo session (`authenticate` → `res.users` default company), not the contact’s
   * `res.partner.company_id`.
   */
  getSessionDefaultCompanyId(): number {
    const n = normalizeOdooMany2oneId(this.user?.company_id);
    return n != null ? n : 1;
  }

  /**
   * Refreshes `this.user.company_id` from `/web/session/get_session_info` (same source as
   * Odoo’s browser client). Call after login refresh and before CRM creates if `company_id`
   * might be missing or was stored as a raw many2one tuple.
   */
  async syncUserCompanyFromSession(): Promise<void> {
    try {
      const info = await this.getSessionInfo();
      const r = info?.result;
      if (r == null || r.uid == null || r.uid === false) return;
      const cid = normalizeOdooMany2oneId(r.company_id);
      if (cid == null) return;
      if (!this.user) {
        this.user = {
          id: r.uid,
          name: String(r.name ?? ""),
          email: String(r.username ?? r.email ?? ""),
          login: this.credentials?.login || "",
          partner_id: normalizeOdooMany2oneId(r.partner_id) ?? 0,
          is_admin: !!r.is_admin,
          company_id: cid,
        };
      } else {
        this.user = {
          ...this.user,
          id: r.uid,
          company_id: cid,
          name: this.user.name || String(r.name ?? ""),
          email: this.user.email || String(r.username ?? r.email ?? ""),
          is_admin: this.user.is_admin || !!r.is_admin,
        };
      }
      localStorage.setItem("odoo_user", JSON.stringify(this.user));
    } catch {
      /* ignore */
    }
  }

  /**
   * Base64 for Odoo `datas` / `message_post` attachments (no newlines, exact bytes).
   * Avoids `readAsDataURL` on large PDFs and keeps binary identical to the Blob.
   */
  private async blobToOdooBase64(blob: Blob): Promise<string> {
    const buf = await blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    const chunk = 0x4000;
    let binary = "";
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(
        null,
        bytes.subarray(i, i + chunk) as unknown as number[],
      );
    }
    return btoa(binary);
  }

  /** jsPDF often yields `type: ""`; set `application/pdf` so Odoo stores the part as a real PDF. */
  private toPdfFileForUpload(blob: Blob, fileName: string): File {
    const safeName = fileName.toLowerCase().endsWith(".pdf")
      ? fileName
      : `${fileName.replace(/\.[^.]+$/, "")}.pdf`;
    return new File([blob], safeName, { type: "application/pdf" });
  }

  /**
   * `<input type="file">` often leaves `Blob.type` empty; Odoo’s PDF viewer rejects
   * `application/octet-stream` for `.pdf` (“failed to load the document”) even when bytes are valid.
   */
  private resolveAttachmentMimetype(fileName: string, blobType: string): string {
    const hint = (blobType || "").trim().toLowerCase();
    if (hint && hint !== "application/octet-stream") {
      return blobType.trim();
    }
    const ext = (fileName.split(".").pop() || "").toLowerCase();
    const map: Record<string, string> = {
      pdf: "application/pdf",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      bmp: "image/bmp",
      tif: "image/tiff",
      tiff: "image/tiff",
      svg: "image/svg+xml",
      heic: "image/heic",
      txt: "text/plain",
      csv: "text/csv",
      json: "application/json",
      xml: "application/xml",
      zip: "application/zip",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    };
    if (ext && map[ext]) return map[ext];
    return hint || "application/octet-stream";
  }

  private async resolveMailUploadCsrfToken(): Promise<string | null> {
    const info = await this.getSessionInfo();
    const token = info?.result?.csrf_token;
    if (typeof token === "string" && token.length > 0) {
      this.csrfToken = token;
      return token;
    }
    return null;
  }

  /**
   * Opera / Odoo web chatter upload (same as Postman: multipart to /mail/attachment/upload).
   */
  private async tryMailAttachmentMultipartUpload(params: {
    threadId: number;
    threadModel: string;
    file: Blob;
    fileName: string;
    isPending?: boolean;
    /** When true, keep original filename and blob type (e.g. Offers tab uploads). */
    preserveFileIdentity?: boolean;
    /** When `preserveFileIdentity`, browser `File.type` is often ""; use extension-based MIME. */
    resolvedMime?: string;
  }): Promise<boolean> {
    const csrf = await this.resolveMailUploadCsrfToken();
    if (!csrf) {
      console.warn(
        "[CRM lead attachment] multipart skipped: no csrf_token from session (cookie/2FA session often required for /mail/attachment/upload)",
      );
      return false;
    }
    const form = new FormData();
    form.append("csrf_token", csrf);
    form.append("is_pending", String(params.isPending ?? false));
    form.append("thread_id", String(params.threadId));
    form.append("thread_model", params.threadModel);
    const ufile = params.preserveFileIdentity
      ? new File([params.file], params.fileName, {
          type:
            params.resolvedMime ||
            (params.file.type && params.file.type.trim()) ||
            "application/octet-stream",
        })
      : this.toPdfFileForUpload(params.file, params.fileName);
    form.append("ufile", ufile);

    const url = `${this.baseURL}/mail/attachment/upload`;
    const resp = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: form,
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      console.warn(
        "mail/attachment/upload failed",
        resp.status,
        text?.slice?.(0, 400),
      );
      return false;
    }

    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      try {
        const data = await resp.json();
        if (data?.error) {
          console.warn("mail/attachment/upload JSON error", data.error);
          return false;
        }
      } catch {
        /* non-JSON body treated as success */
      }
    }
    return true;
  }

  /** YYYY-MM-DD HH:mm:ss for custom `x_date` on opportunity price history lines. */
  private formatOdooLocalDateTime(d = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  /** Maps multi-currency totals to `crm.lead` `x_opp_expected_*` write values. */
  private buildOppExpectedRevenueCurrencyFields(
    totals: OpportunityRevenueByCurrency,
  ): Record<string, number> {
    const out: Record<string, number> = {};
    for (const code of PIPELINE_MULTICURRENCY_CODES) {
      out[OPP_EXPECTED_ODOO_FIELD[code]] = Number(totals[code]) || 0;
    }
    return out;
  }

  /**
   * Deduplicate + coerce artwork IDs for `x_proposal_artwork_ids` writes.
   * Drops anything non-numeric so the Many2many write never receives garbage.
   */
  private normalizeProposalArtworkIds(
    ids: Array<number | string | null | undefined> | undefined,
  ): number[] {
    if (!ids || ids.length === 0) return [];
    const out: number[] = [];
    const seen = new Set<number>();
    for (const raw of ids) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  }

  /**
   * One2many commands for `crm.lead.opportunity_price_history_ids`:
   * one line with per-currency quote snapshot (`x_hist_quote_*`) + legacy `x_amount`.
   */
  private buildProposalOpportunityPriceHistoryCommands(
    totals: OpportunityRevenueByCurrency,
    legacyAmount: number,
    userId: number,
    remark: string,
  ): [number, number, Record<string, unknown>][] {
    const line: Record<string, unknown> = {
      x_user_id: userId,
      x_date: this.formatOdooLocalDateTime(),
      x_remark: remark,
      x_amount: Number(legacyAmount) || 0,
    };
    for (const code of PIPELINE_MULTICURRENCY_CODES) {
      line[HIST_QUOTE_ODOO_FIELD[code]] = Number(totals[code]) || 0;
    }
    return [[0, 0, line]];
  }

  private static offerPipelineRowMany2oneId(field: unknown): number | null {
    if (field === false || field == null) return null;
    if (Array.isArray(field) && field.length >= 1) {
      const n = Number(field[0]);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    if (typeof field === "number" && Number.isFinite(field) && field > 0) {
      return field;
    }
    return null;
  }

  /**
   * Accepted / Declined (and Odoo won/lost-like): do not reuse for proposal sync or “new opportunity”.
   */
  private static offerPipelineRowIsTerminalOutcome(row: {
    stage_id?: unknown;
    probability?: unknown;
  }): boolean {
    const st = row.stage_id as [number, string] | false | undefined;
    const stageId =
      st && Array.isArray(st) && st.length >= 1 ? Number(st[0]) : 0;
    const stageLabel =
      st && Array.isArray(st) && st.length >= 2 ? String(st[1] ?? "") : "";
    const pRaw = row.probability;
    const prob =
      typeof pRaw === "number" && Number.isFinite(pRaw) ? pRaw : undefined;
    return opportunityStageIsTerminalAcceptedOrDeclined(
      Number.isFinite(stageId) ? stageId : 0,
      stageLabel,
      prob,
    );
  }

  /**
   * Logged-in user’s offer opportunity for this `res.partner` (same scope as Offers Kanban).
   * Newest match first (`searchReadOfferPipelineLeads` ordering).
   * Leads in Accepted / Declined are skipped so a new open opportunity can be created.
   */
  async findMyOfferPipelineLeadIdForPartner(partnerId: number): Promise<number | null> {
    const uid = this.getOdooUserId();
    const pid = Number(partnerId);
    if (!uid || !Number.isFinite(pid) || pid <= 0) return null;
    try {
      const rows = await this.searchReadOfferPipelineLeads({ limit: 400 });
      for (const row of rows) {
        const rowPartner = OdooClient.offerPipelineRowMany2oneId(row.partner_id);
        const rowUser = OdooClient.offerPipelineRowMany2oneId(row.user_id);
        if (rowPartner === pid && rowUser === uid) {
          if (OdooClient.offerPipelineRowIsTerminalOutcome(row)) continue;
          const id = Number((row as { id?: unknown }).id);
          return Number.isFinite(id) && id > 0 ? id : null;
        }
      }
    } catch (e) {
      console.warn("findMyOfferPipelineLeadIdForPartner: pipeline read failed", e);
    }
    return null;
  }

  /**
   * Logged-in user’s offer opportunity for this `crm.prospect` (prospect-only opps).
   * Terminal stages (Accepted / Declined) are skipped — see `findMyOfferPipelineLeadIdForPartner`.
   */
  async findMyOfferPipelineLeadIdForProspect(prospectId: number): Promise<number | null> {
    const uid = this.getOdooUserId();
    const prid = Number(prospectId);
    if (!uid || !Number.isFinite(prid) || prid <= 0) return null;
    try {
      const rows = await this.searchReadOfferPipelineLeads({ limit: 400 });
      for (const row of rows) {
        const rowProspect = OdooClient.offerPipelineRowMany2oneId(
          (row as { prospect_id?: unknown }).prospect_id,
        );
        const rowUser = OdooClient.offerPipelineRowMany2oneId(row.user_id);
        if (rowProspect === prid && rowUser === uid) {
          if (OdooClient.offerPipelineRowIsTerminalOutcome(row)) continue;
          const id = Number((row as { id?: unknown }).id);
          return Number.isFinite(id) && id > 0 ? id : null;
        }
      }
    } catch (e) {
      console.warn("findMyOfferPipelineLeadIdForProspect: pipeline read failed", e);
    }
    return null;
  }

  /**
   * Odoo `portal_find_visible_offer_opportunity` — may return any one visible opp, not necessarily
   * the logged-in salesperson’s row when several exist for the same client.
   */
  async findExistingOpportunityLeadId(partnerId: number): Promise<number | null> {
    const pid = Number(partnerId);
    if (!Number.isFinite(pid) || pid <= 0) return null;
    const raw = await this.executeCall(
      "crm.lead",
      "portal_find_visible_offer_opportunity",
      [],
      { partner_id: pid },
    );
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Same as `findExistingOpportunityLeadId` for `crm.lead.prospect_id` (crm.prospect).
   */
  async findExistingOpportunityLeadIdForProspect(
    prospectId: number,
  ): Promise<number | null> {
    const pid = Number(prospectId);
    if (!Number.isFinite(pid) || pid <= 0) return null;
    const raw = await this.executeCall(
      "crm.lead",
      "portal_find_visible_offer_opportunity",
      [],
      { prospect_id: pid },
    );
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Offers app: if **this user** already has an active offer opportunity for the client, return it;
   * otherwise create a minimal opportunity (expected revenue 0, first stage).
   */
  async ensureOfferPipelineLeadForPartner(params: {
    /** Linked `res.partner` when present; omit or `false` for prospect-only opportunities. */
    partnerId?: number | false;
    /** Set when the contact source is `crm.prospect` (never put the prospect id in `partner_id`). */
    prospectId?: number | false;
    partnerName: string;
    leadName: string;
    emailFrom?: string | false;
    phone?: string | false;
  }): Promise<{ leadId: number; created: boolean }> {
    const rawPartner = params.partnerId;
    const partnerIdNum =
      rawPartner !== false &&
      rawPartner != null &&
      Number.isFinite(Number(rawPartner)) &&
      Number(rawPartner) > 0
        ? Number(rawPartner)
        : null;
    const rawProspect = params.prospectId;
    const prospectIdNum =
      rawProspect !== false &&
      rawProspect != null &&
      Number.isFinite(Number(rawProspect)) &&
      Number(rawProspect) > 0
        ? Number(rawProspect)
        : null;

    if (prospectIdNum != null) {
      const existing =
        await this.findMyOfferPipelineLeadIdForProspect(prospectIdNum);
      if (existing != null) {
        return { leadId: existing, created: false };
      }
    } else if (partnerIdNum != null) {
      const existing = await this.findMyOfferPipelineLeadIdForPartner(partnerIdNum);
      if (existing != null) {
        return { leadId: existing, created: false };
      }
    } else {
      throw new Error(
        "ensureOfferPipelineLeadForPartner: need a valid partner or prospect id",
      );
    }

    const userId = this.getOdooUserId();
    if (!userId) {
      throw new Error("Not authenticated");
    }
    await this.syncUserCompanyFromSession();
    const partnerName = params.partnerName.trim() || "Client";
    const name =
      params.leadName.trim() || `${partnerName}'s opportunity`;
    const zeroTotals = emptyOpportunityRevenueByCurrency();
    const vals: Record<string, any> = {
      partner_id: partnerIdNum ?? false,
      prospect_id: prospectIdNum ?? false,
      name,
      email_from: params.emailFrom || false,
      phone: params.phone || false,
      expected_revenue: 0,
      ...this.buildOppExpectedRevenueCurrencyFields(zeroTotals),
      opportunity_price_history_ids: this.buildProposalOpportunityPriceHistoryCommands(
        zeroTotals,
        0,
        userId,
        "Offers — new opportunity",
      ),
      priority: "0",
      company_id: this.getSessionDefaultCompanyId(),
      user_id: userId,
      team_id: 1,
      type: "opportunity",
      stage_id: 1,
      partner_name: false,
      contact_name: partnerName,
      country_id: false,
      state_id: false,
      city: false,
      street: false,
      street2: false,
      zip: false,
      mobile: false,
      website: false,
      function: false,
      title: false,
    };
    const kwargs = {
      context: {
        default_type: "opportunity",
        default_stage_id: 1,
      },
    };
    const finalCompanyIdOnLead = this.getSessionDefaultCompanyId();
    const leadCompanyName =
      await this.readResCompanyNameForOffersLog(finalCompanyIdOnLead);
    const sessionRaw = this.user?.company_id;
    console.info("[Offers CRM company] crm.lead create payload", {
      partnerId: partnerIdNum,
      prospectId: prospectIdNum,
      companyIdOnLead: finalCompanyIdOnLead,
      companyNameOnLead: leadCompanyName,
      source:
        "Logged-in user’s default company (Odoo session / res.users), not the client’s company_id",
      sessionUserCompanyIdRaw: sessionRaw,
      usedFallbackCompanyId1: finalCompanyIdOnLead === 1 && (!sessionRaw || Number(sessionRaw) <= 0),
    });
    const newId = await this.executeCall("crm.lead", "create", [vals], kwargs);
    const n = typeof newId === "number" ? newId : Number(newId);
    if (!Number.isFinite(n)) {
      throw new Error("crm.lead create returned invalid id");
    }
    return { leadId: n, created: true };
  }

  /**
   * Create a CRM opportunity on stage 1 (via default_stage_id + stage_id).
   */
  async createProposalOpportunity(params: {
    partnerId?: number | false;
    prospectId?: number | false;
    partnerName: string;
    emailFrom?: string | false;
    phone?: string | false;
    expectedRevenue: number;
    revenueByCurrency: OpportunityRevenueByCurrency;
    userId: number;
    teamId?: number;
    priceHistoryRemark?: string;
    /**
     * Create the lead without appending an `opportunity_price_history_ids`
     * line. Used by the PDF / link preview flows so only the actual send
     * button produces a history row on the Offers card.
     */
    skipPriceHistory?: boolean;
    artworkIds?: Array<number | string | null | undefined>;
  }): Promise<number> {
    const rawP = params.partnerId;
    const partnerIdForLead =
      rawP !== false &&
      rawP != null &&
      Number.isFinite(Number(rawP)) &&
      Number(rawP) > 0
        ? Number(rawP)
        : null;
    const rawPr = params.prospectId;
    const prospectIdForLead =
      rawPr !== false &&
      rawPr != null &&
      Number.isFinite(Number(rawPr)) &&
      Number(rawPr) > 0
        ? Number(rawPr)
        : null;
    if (partnerIdForLead == null && prospectIdForLead == null) {
      throw new Error(
        "createProposalOpportunity: need a valid partner or prospect id",
      );
    }
    await this.syncUserCompanyFromSession();
    const name = `${params.partnerName}'s opportunity`;
    const historyRemark = params.priceHistoryRemark ?? "Proposal sync";
    const totals = params.revenueByCurrency;
    const normalizedArtworkIds = this.normalizeProposalArtworkIds(
      params.artworkIds,
    );
    const vals: Record<string, any> = {
      partner_id: partnerIdForLead ?? false,
      prospect_id: prospectIdForLead ?? false,
      name,
      email_from: params.emailFrom || false,
      phone: params.phone || false,
      expected_revenue: params.expectedRevenue,
      ...this.buildOppExpectedRevenueCurrencyFields(totals),
      ...(normalizedArtworkIds.length > 0
        ? { x_proposal_artwork_ids: [[6, 0, normalizedArtworkIds]] }
        : {}),
      ...(params.skipPriceHistory
        ? {}
        : {
            opportunity_price_history_ids:
              this.buildProposalOpportunityPriceHistoryCommands(
                totals,
                params.expectedRevenue,
                params.userId,
                historyRemark,
              ),
          }),
      priority: "0",
      company_id: this.getSessionDefaultCompanyId(),
      user_id: params.userId,
      team_id: params.teamId ?? 1,
      type: "opportunity",
      stage_id: 1,
      partner_name: false,
      contact_name: params.partnerName,
      country_id: false,
      state_id: false,
      city: false,
      street: false,
      street2: false,
      zip: false,
      mobile: false,
      website: false,
      function: false,
      title: false,
    };
    const kwargs = {
      context: {
        default_type: "opportunity",
        default_stage_id: 1,
      },
    };
    const proposalFinalCompanyId = this.getSessionDefaultCompanyId();
    const proposalLeadName =
      await this.readResCompanyNameForOffersLog(proposalFinalCompanyId);
    const proposalSessionRaw = this.user?.company_id;
    console.info("[Proposal CRM company] crm.lead create payload", {
      partnerId: partnerIdForLead,
      prospectId: prospectIdForLead,
      companyIdOnLead: proposalFinalCompanyId,
      companyNameOnLead: proposalLeadName,
      source:
        "Logged-in user’s default company (Odoo session / res.users), not the client’s company_id",
      sessionUserCompanyIdRaw: proposalSessionRaw,
      usedFallbackCompanyId1:
        proposalFinalCompanyId === 1 &&
        (!proposalSessionRaw || Number(proposalSessionRaw) <= 0),
    });
    const newId = await this.executeCall("crm.lead", "create", [vals], kwargs);
    const n = typeof newId === "number" ? newId : Number(newId);
    if (!Number.isFinite(n)) {
      throw new Error("crm.lead create returned invalid id");
    }
    return n;
  }

  /**
   * Set expected revenue on an existing opportunity and append a price history line
   * (`opportunity_price_history_ids`), matching Opera CRM behaviour.
   */
  async updateProposalOpportunityExpectedRevenue(
    leadId: number,
    expectedRevenue: number,
    revenueByCurrency: OpportunityRevenueByCurrency,
    userId: number,
    priceHistoryRemark?: string,
    /**
     * Refresh `expected_revenue` without appending a new price-history row.
     * Used by the PDF / link preview flows so only the actual send produces
     * one `Proposal sync` entry on the Offers card.
     */
    skipPriceHistory?: boolean,
    artworkIds?: Array<number | string | null | undefined>,
  ): Promise<void> {
    const id = Number(leadId);
    if (!Number.isFinite(id)) {
      throw new Error("updateProposalOpportunityExpectedRevenue: invalid lead id");
    }
    const uid = Number(userId);
    if (!Number.isFinite(uid)) {
      throw new Error("updateProposalOpportunityExpectedRevenue: invalid user id");
    }
    const historyRemark = priceHistoryRemark ?? "Proposal sync";
    const totals = revenueByCurrency;
    const normalizedArtworkIds = this.normalizeProposalArtworkIds(artworkIds);
    await this.executeCall("crm.lead", "portal_offer_lead_write_proposal_values", [
      id,
      {
        expected_revenue: expectedRevenue,
        ...this.buildOppExpectedRevenueCurrencyFields(totals),
        ...(normalizedArtworkIds.length > 0
          ? {
              x_proposal_artwork_ids: normalizedArtworkIds.map(
                (aid) => [4, aid] as [number, number],
              ),
            }
          : {}),
        ...(skipPriceHistory
          ? {}
          : {
              opportunity_price_history_ids:
                this.buildProposalOpportunityPriceHistoryCommands(
                  totals,
                  expectedRevenue,
                  uid,
                  historyRemark,
                ),
            }),
      },
    ]);
  }

  /**
   * Attach a file to a record.
   * `crm.lead`: `ir.attachment` via JSON-RPC first (same auth as lead create; works without CSRF),
   * then Postman-style multipart, then `message_post` if still needed.
   * Other models: multipart, then `ir.attachment`.
   */
  async uploadMailAttachmentToThread(params: {
    threadId: number;
    threadModel?: string;
    file: Blob;
    fileName: string;
    isPending?: boolean;
    /** When true, do not coerce filename to `.pdf` or force PDF mime (non-proposal uploads). */
    preserveFileIdentity?: boolean;
  }): Promise<boolean> {
    const model = params.threadModel ?? "crm.lead";
    const attachmentName = params.preserveFileIdentity
      ? params.fileName
      : params.fileName.toLowerCase().endsWith(".pdf")
        ? params.fileName
        : `${params.fileName.replace(/\.[^.]+$/, "")}.pdf`;
    const mimetype = this.resolveAttachmentMimetype(attachmentName, params.file.type);

    const tryMultipart = () =>
      this.tryMailAttachmentMultipartUpload({
        threadId: params.threadId,
        threadModel: model,
        file: params.file,
        fileName: attachmentName,
        isPending: params.isPending,
        preserveFileIdentity: params.preserveFileIdentity,
        resolvedMime: params.preserveFileIdentity ? mimetype : undefined,
      });

    const tryMessagePostLead = async (): Promise<boolean> => {
      if (model !== "crm.lead") return false;
      try {
        const datas = await this.blobToOdooBase64(params.file);
        await this.executeCall("crm.lead", "message_post", [[params.threadId]], {
          body: params.preserveFileIdentity
            ? "<p>File attached.</p>"
            : "<p>Proposal PDF attached.</p>",
          message_type: "comment",
          subtype_xmlid: "mail.mt_comment",
          attachments: [[attachmentName, datas]],
        });
        return true;
      } catch (e) {
        console.warn(
          "[CRM lead attachment] message_post failed:",
          e instanceof Error ? e.message : e,
        );
        return false;
      }
    };

    /** Same visibility as Offers pipeline + correct ``company_id`` (Odoo ACL-safe for directors). */
    const tryPortalOfferLeadCreateAttachment = async (): Promise<boolean> => {
      if (model !== "crm.lead") return false;
      try {
        const datas = await this.blobToOdooBase64(params.file);
        const rid = await this.executeCall("crm.lead", "portal_offer_lead_create_attachment", [
          params.threadId,
          attachmentName,
          datas,
          mimetype || false,
        ]);
        const n = typeof rid === "number" ? rid : Number(rid);
        return Number.isFinite(n) && n > 0;
      } catch (e) {
        console.warn(
          "[CRM lead attachment] portal_offer_lead_create_attachment failed:",
          e instanceof Error ? e.message : e,
        );
        return false;
      }
    };

    const tryIrAttachment = async (): Promise<boolean> => {
      try {
        const datas = await this.blobToOdooBase64(params.file);
        const baseVals: Record<string, unknown> = {
          name: attachmentName,
          datas,
          res_model: model,
          res_id: params.threadId,
          mimetype,
        };
        try {
          await this.executeCall("ir.attachment", "create", [
            { ...baseVals, type: "binary" },
          ]);
        } catch (e1) {
          console.warn(
            "[CRM lead attachment] ir.attachment create (type=binary) failed, retrying without type:",
            e1 instanceof Error ? e1.message : e1,
          );
          await this.executeCall("ir.attachment", "create", [baseVals]);
        }
        return true;
      } catch (e) {
        console.warn(
          "[CRM lead attachment] ir.attachment create failed:",
          e instanceof Error ? e.message : e,
        );
        return false;
      }
    };

    const tryMultipartLogged = async (): Promise<boolean> => {
      try {
        const ok = await tryMultipart();
        if (!ok) {
          console.warn(
            "[CRM lead attachment] multipart /mail/attachment/upload returned false (see tryMailAttachmentMultipartUpload logs above)",
          );
        }
        return ok;
      } catch (e) {
        console.warn(
          "[CRM lead attachment] multipart threw (network/CORS?):",
          e instanceof Error ? e.message : e,
        );
        return false;
      }
    };

    if (model === "crm.lead") {
      /**
       * 1) Portal helper: same visibility as Offers + lead ``company_id`` on attachment (directors
       *    on colleagues' opps often fail direct ``ir.attachment`` / chatter due to ACL).
       * 2) ``ir.attachment`` JSON-RPC: good mimetype/binary when ACL allows.
       * 3) Multipart chatter upload (needs CSRF session).
       * 4) ``message_post`` last fallback.
       */
      const steps = [
        { name: "portal_offer_lead_create_attachment", run: tryPortalOfferLeadCreateAttachment },
        { name: "ir.attachment", run: tryIrAttachment },
        { name: "multipart", run: tryMultipartLogged },
        { name: "message_post", run: tryMessagePostLead },
      ];

      console.info("[CRM lead attachment] start", {
        threadId: params.threadId,
        fileName: attachmentName,
        bytes: params.file.size,
        preserveFileIdentity: !!params.preserveFileIdentity,
        tryOrder: steps.map((s) => s.name).join(" → "),
      });

      for (const step of steps) {
        const ok = await step.run();
        if (ok) {
          console.info(`[CRM lead attachment] success via ${step.name}`);
          return true;
        }
      }
      console.error("[CRM lead attachment] all methods failed for", {
        threadId: params.threadId,
        fileName: attachmentName,
      });
      return false;
    }

    if (await tryMultipart()) return true;
    return tryIrAttachment();
  }

  /** How Odoo `res.partner.company_id` maps to the numeric id used on `crm.lead.company_id`. */
  private resolvePartnerCompanyPickFromOdooField(cid: unknown): {
    companyId: number;
    pick: "contact_company" | "default_fallback_id_1";
  } {
    if (cid === false || cid == null) {
      return { companyId: 1, pick: "default_fallback_id_1" };
    }
    if (Array.isArray(cid)) {
      const id0 = Number(cid[0]);
      if (Number.isFinite(id0) && id0 > 0) {
        return { companyId: id0, pick: "contact_company" };
      }
      return { companyId: 1, pick: "default_fallback_id_1" };
    }
    const n = Number(cid);
    if (Number.isFinite(n) && n > 0) {
      return { companyId: n, pick: "contact_company" };
    }
    return { companyId: 1, pick: "default_fallback_id_1" };
  }

  private async readResCompanyNameForOffersLog(companyId: number): Promise<string> {
    const id = Number(companyId);
    if (!Number.isFinite(id) || id <= 0) return "(invalid company id)";
    try {
      const rows = await this.executeCall("res.company", "read", [[id], ["name"]]);
      const row = Array.isArray(rows) ? rows[0] : null;
      if (row && row.name != null && row.name !== false) {
        return String(row.name);
      }
    } catch {
      /* ignore */
    }
    return "(name unavailable)";
  }

  async readPartnerBasics(partnerId: number): Promise<{
    name: string;
    email: string | false;
    phone: string | false;
    mobile: string | false;
    companyId: number;
  } | null> {
    const rows = await this.executeCall("res.partner", "read", [
      [partnerId],
      [
        "name",
        "email",
        "Email1",
        "Email2",
        "email_pro",
        "phone",
        "mobile",
        "Phone1",
        "Phone2",
        "phone_pro",
        "international_code_phone1",
        "international_code_phone2",
        "international_code_phone_pro",
        "mobile_country_code",
        "company_id",
      ],
    ]);
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) return null;
    const cid = row.company_id;
    const meta = this.resolvePartnerCompanyPickFromOdooField(cid);
    const companyId =
      Number.isFinite(meta.companyId) && meta.companyId > 0 ? meta.companyId : 1;
    const contactHasCompany = meta.pick === "contact_company";
    const companyNameForLog = contactHasCompany
      ? await this.readResCompanyNameForOffersLog(companyId)
      : "(no company on partner — not fetching name for placeholder id)";
    console.info(
      "[Offers CRM company] CONTACT only: res.partner.company_id — does NOT set crm.lead.company_id",
      {
        partnerId,
        contactHasCompanyOnPartner: contactHasCompany,
        rawPartnerCompanyField: cid,
        ...(contactHasCompany
          ? {
              contactCompanyId: companyId,
              contactCompanyName: companyNameForLog,
            }
          : {
              contactCompanyIdOnPartner: null,
              basicsReturnPlaceholderCompanyId: 1,
              basicsPlaceholderNote:
                "Internal default when partner.company_id is empty; Offers lead create uses logged-in user company instead.",
            }),
        newLeadCompanySource: "getSessionDefaultCompanyId() — see ‘crm.lead create payload’ log next",
      },
    );
    return {
      name: String(row.name || ""),
      email: coalesceEmailFromOdooRow(row),
      phone: buildFullPhoneForCrmLead(row),
      mobile: row.mobile != null && row.mobile !== false ? odooStrTrim(row.mobile) || false : false,
      companyId,
    };
  }

  /**
   * Load a `crm.prospect` row for pipeline / lead contact fields.
   * Uses the same `search_read` + domain pattern as `getContactById` (works when `read` or
   * non-existent fields such as `partner_id` / `company_id` on prospect would fail).
   */
  private async readProspectRowForCrmLead(prospectId: number): Promise<any | null> {
    const pid = Number(prospectId);
    if (!Number.isFinite(pid) || pid <= 0) return null;

    const prospectFields = [
      "name",
      "email",
      "Email1",
      "Email2",
      "email_pro",
      "phone",
      "mobile",
      "Phone1",
      "Phone2",
      "phone_pro",
      "international_code_phone1",
      "international_code_phone2",
      "international_code_phone_pro",
      "mobile_country_code",
      "Id_with_c",
      "IdEntity",
      "Id",
      /** `crm.prospect` links to `res.partner` here (no `partner_id` on prospect in Opera). */
      "parent_id",
    ];

    try {
      const rows = await this.executeCall("crm.prospect", "search_read", [
        [["id", "=", pid]],
        prospectFields,
      ]);
      if (Array.isArray(rows) && rows.length > 0) return rows[0];
      return null;
    } catch {
      const minimal = [
        "name",
        "email",
        "Email1",
        "Email2",
        "email_pro",
        "phone",
        "mobile",
        "Phone1",
        "Phone2",
        "phone_pro",
        "Id_with_c",
        "IdEntity",
        "parent_id",
      ];
      try {
        const rows = await this.executeCall("crm.prospect", "search_read", [
          [["id", "=", pid]],
          minimal,
        ]);
        if (Array.isArray(rows) && rows.length > 0) return rows[0];
      } catch {
        return null;
      }
      return null;
    }
  }

  /**
   * Build CRM lead contact source from a `crm.prospect` row (by prospect id).
   */
  private async contactSourceFromProspectRecord(
    recordId: number,
  ): Promise<{
    /** Linked `res.partner` id for `crm.lead.partner_id`, or `false` if none. */
    partnerId: number | false;
    /** Always the `crm.prospect` id — write to `crm.lead.prospect_id`, never to `partner_id`. */
    prospectId: number;
    partnerName: string;
    emailFrom: string | false;
    phone: string | false;
    companyId: number;
  } | null> {
    const row = await this.readProspectRowForCrmLead(recordId);
    if (!row) return null;
    const emailFrom = coalesceEmailFromOdooRow(row);
    const phone = buildFullPhoneForCrmLead(row);
    const partnerName = odooStrTrim(row.name) || "Contact";

    let partnerId: number | false = false;
    const rawPartnerM2o = row.partner_id;
    if (
      Array.isArray(rawPartnerM2o) &&
      typeof rawPartnerM2o[0] === "number" &&
      rawPartnerM2o[0] > 0
    ) {
      partnerId = rawPartnerM2o[0];
    }
    const rawParent = row.parent_id;
    if (
      partnerId === false &&
      Array.isArray(rawParent) &&
      typeof rawParent[0] === "number" &&
      rawParent[0] > 0
    ) {
      partnerId = rawParent[0];
    }
    if (partnerId === false && odooStrTrim(row.Id_with_c)) {
      const ids = await this.executeCall("res.partner", "search", [
        [["Id_with_c", "=", String(row.Id_with_c).trim()]],
        0,
        1,
      ]);
      if (Array.isArray(ids) && ids.length > 0) {
        const n = Number(ids[0]);
        if (Number.isFinite(n) && n > 0) partnerId = n;
      }
    }
    if (partnerId === false && odooStrTrim(row.IdEntity)) {
      const ids = await this.executeCall("res.partner", "search", [
        [["IdEntity", "=", String(row.IdEntity).trim()]],
        0,
        1,
      ]);
      if (Array.isArray(ids) && ids.length > 0) {
        const n = Number(ids[0]);
        if (Number.isFinite(n) && n > 0) partnerId = n;
      }
    }

    let companyId = 1;
    if (partnerId !== false) {
      const pb = await this.readPartnerBasics(partnerId);
      if (pb?.companyId && Number.isFinite(pb.companyId) && pb.companyId > 0) {
        companyId = pb.companyId;
      }
    }

    const finalCompanyId =
      Number.isFinite(companyId) && companyId > 0 ? companyId : 1;
    console.info(
      "[Offers CRM company] CONTACT only (crm.prospect): summary — does NOT set crm.lead.company_id",
      {
        prospectId: recordId,
        linkedPartnerId: partnerId === false ? null : partnerId,
        derivedBasicsCompanyId_forContactSourceOnly: finalCompanyId,
        note:
          partnerId !== false
            ? "Company on contact side came from linked partner (see prior ‘CONTACT only: res.partner’ log)."
            : "No linked partner — placeholder id in contact-source payload only.",
        newLeadCompanySource:
          "getSessionDefaultCompanyId() — see ‘crm.lead create payload’ log",
      },
    );

    return {
      partnerId,
      prospectId: recordId,
      partnerName,
      emailFrom,
      phone,
      companyId: finalCompanyId,
    };
  }

  /**
   * Resolve `partner_id` + email/phone for `crm.lead` from the contact picker.
   * Uses Email1/Phone1-style fields; supports `crm.prospect` vs `res.partner`.
   * When `sourceModel` is omitted, tries `res.partner` first, then `crm.prospect` (fixes missing `_model` from search).
   */
  async readCrmLeadContactSource(
    recordId: number,
    sourceModel?: "crm.prospect" | "res.partner",
  ): Promise<{
    partnerId: number | false;
    /** Present when the row is a `crm.prospect` (pipeline should set `crm.lead.prospect_id`). */
    prospectId?: number;
    partnerName: string;
    emailFrom: string | false;
    phone: string | false;
    companyId: number;
  } | null> {
    if (sourceModel === "crm.prospect") {
      console.info("[Offers CRM company] readCrmLeadContactSource branch=crm.prospect", {
        recordId,
      });
      return this.contactSourceFromProspectRecord(recordId);
    }

    if (sourceModel === "res.partner") {
      console.info("[Offers CRM company] readCrmLeadContactSource branch=res.partner (explicit)", {
        recordId,
      });
      const basics = await this.readPartnerBasics(recordId);
      if (!basics) return null;
      return {
        partnerId: recordId,
        partnerName: basics.name,
        emailFrom: basics.email,
        phone: basics.phone,
        companyId: basics.companyId,
      };
    }

    const basics = await this.readPartnerBasics(recordId);
    if (basics) {
      console.info(
        "[Offers CRM company] readCrmLeadContactSource branch=res.partner (auto-detected)",
        { recordId },
      );
      return {
        partnerId: recordId,
        partnerName: basics.name,
        emailFrom: basics.email,
        phone: basics.phone,
        companyId: basics.companyId,
      };
    }

    console.info(
      "[Offers CRM company] readCrmLeadContactSource branch=crm.prospect (fallback after partner miss)",
      { recordId },
    );
    return this.contactSourceFromProspectRecord(recordId);
  }

  /**
   * CRM stages for a sales team. Omit `teamId` to load stages across teams (fallback when team 1 is empty).
   */
  async getCrmStagesForTeam(teamId?: number, limit = 80): Promise<
    { id: number; name: string; sequence: number; probability?: number }[]
  > {
    const domain =
      teamId != null && Number.isFinite(teamId) && teamId > 0
        ? [["team_id", "=", teamId]]
        : [];
    try {
      const rows = await this.executeCall("crm.stage", "search_read", [domain], {
        fields: ["id", "name", "sequence", "probability"],
        limit,
        order: "sequence asc, id asc",
      });
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  }

  /** `crm.team` opportunity stage sequence (fills gaps e.g. empty “Declined” column). */
  private async getCrmTeamOpportunityStageIds(teamId: number): Promise<number[]> {
    if (!Number.isFinite(teamId) || teamId <= 0) return [];
    try {
      const rows = await this.executeCall("crm.team", "read", [[teamId], ["stage_ids"]], {
        context: { active_test: false },
      });
      const row = Array.isArray(rows) ? rows[0] : null;
      const raw = row?.stage_ids;
      if (!Array.isArray(raw)) return [];
      return raw
        .map((x: any) => (Array.isArray(x) ? Number(x[0]) : Number(x)))
        .filter((n: number) => Number.isFinite(n));
    } catch {
      return [];
    }
  }

  private sortCrmStageRows(rows: any[]): any[] {
    return [...rows].sort(
      (a: any, b: any) =>
        (Number(a.sequence) || 0) - (Number(b.sequence) || 0) || Number(a.id) - Number(b.id),
    );
  }

  /**
   * Append stages listed on `crm.team` but missing from `search_read` (inactive / edge cases).
   */
  private async mergeCrmStagesWithTeamStageList(teamId: number, stages: any[]): Promise<any[]> {
    const byId = new Map<number, any>();
    for (const s of stages) {
      const id = Number(s?.id);
      if (Number.isFinite(id)) byId.set(id, s);
    }
    const fromTeam = await this.getCrmTeamOpportunityStageIds(teamId);
    const missing = fromTeam.filter((id) => !byId.has(id));
    if (missing.length === 0) {
      return this.sortCrmStageRows([...byId.values()]);
    }
    const readFields = [
      "id",
      "name",
      "display_name",
      "sequence",
      "probability",
      "fold",
      "requirements",
    ];
    const readKwargs = { context: { active_test: false } };
    let more: any[] = [];
    try {
      more = await this.executeCall("crm.stage", "read", [missing, readFields], readKwargs);
    } catch {
      try {
        more = await this.executeCall(
          "crm.stage",
          "read",
          [missing, ["id", "name", "sequence", "probability", "fold"]],
          readKwargs,
        );
      } catch {
        more = [];
      }
    }
    if (Array.isArray(more)) {
      for (const m of more) {
        const id = Number(m?.id);
        if (Number.isFinite(id) && !byId.has(id)) byId.set(id, m);
      }
    }
    return this.sortCrmStageRows([...byId.values()]);
  }

  /**
   * Stages for the Offers kanban: `search_read` with **name** (always), then `read` for
   * `display_name` / `requirements` merged by id — so titles work even when `read` fails.
   * Uses `active_test: false` so inactive stages (e.g. Declined) still appear as empty columns.
   */
  async getCrmStagesForOfferPipeline(teamId?: number, limit = 400): Promise<any[]> {
    const searchDomain =
      teamId != null && Number.isFinite(teamId) && teamId > 0
        ? (["|", ["team_id", "=", teamId], ["team_id", "=", false]] as any[])
        : [];

    const baseSearchFields = [
      "id",
      "name",
      "sequence",
      "probability",
      "fold",
      "team_id",
    ];

    const inactiveSafeContext = { active_test: false };

    const searchRead = async (domain: any[], fields: string[]) => {
      return await this.executeCall("crm.stage", "search_read", [domain], {
        fields,
        limit,
        order: "sequence asc, id asc",
        context: inactiveSafeContext,
      });
    };

    const loadBrief = async (domain: any[]): Promise<any[]> => {
      try {
        const rows = await searchRead(domain, [...baseSearchFields, "display_name"]);
        return Array.isArray(rows) ? rows : [];
      } catch {
        try {
          const rows = await searchRead(domain, baseSearchFields);
          return Array.isArray(rows) ? rows : [];
        } catch {
          return [];
        }
      }
    };

    let brief: any[] = [];
    if (searchDomain.length) {
      brief = await loadBrief(searchDomain);
    }
    if (!brief.length) {
      brief = await loadBrief([]);
    }
    if (!brief.length) return [];

    const ids = brief
      .map((r: any) => Number(r.id))
      .filter((id: number) => Number.isFinite(id));
    if (ids.length === 0) return [];

    const readFieldsFull = [
      "id",
      "name",
      "display_name",
      "sequence",
      "probability",
      "fold",
      "requirements",
    ];
    const readFieldsSlim = ["id", "name", "sequence", "probability", "fold"];

    const mergeReadIntoBrief = (readRows: any[] | null | undefined): any[] => {
      const readMap = new Map<number, any>();
      if (Array.isArray(readRows)) {
        for (const r of readRows) {
          readMap.set(Number(r.id), r);
        }
      }
      const strip = (v: any) => (v === false || v == null ? "" : String(v).trim());
      return brief
        .map((b) => {
          const id = Number(b.id);
          const r = readMap.get(id);
          if (!r) return { ...b };
          const merged = { ...b, ...r };
          if (!strip(r.name) && strip(b.name)) {
            merged.name = b.name;
          }
          if (!strip(r.display_name) && strip(b.display_name)) {
            merged.display_name = b.display_name;
          }
          return merged;
        })
        .sort(
          (a: any, b: any) =>
            (Number(a.sequence) || 0) - (Number(b.sequence) || 0) || Number(a.id) - Number(b.id),
        );
    };

    const readKwargs = { context: inactiveSafeContext };

    let merged: any[];
    try {
      const readRows = await this.executeCall("crm.stage", "read", [ids, readFieldsFull], readKwargs);
      merged = mergeReadIntoBrief(readRows);
    } catch {
      try {
        const readRows = await this.executeCall("crm.stage", "read", [ids, readFieldsSlim], readKwargs);
        merged = mergeReadIntoBrief(readRows);
      } catch {
        merged = mergeReadIntoBrief(null);
      }
    }

    const tid =
      teamId != null && Number.isFinite(teamId) && teamId > 0
        ? teamId
        : Number(merged[0]?.team_id?.[0] ?? merged[0]?.team_id) || 1;
    if (Number.isFinite(tid) && tid > 0) {
      merged = await this.mergeCrmStagesWithTeamStageList(tid, merged);
    }
    return merged;
  }

  /**
   * Files on opportunities (`ir.attachment` on `crm.lead`).
   * Uses Opera CRM `crm.lead` portal method so visibility matches Offers pipeline (not raw ir.attachment ACL).
   */
  async searchReadAttachmentsForCrmLeads(leadIds: number[]): Promise<any[]> {
    const ids = [...new Set(leadIds.map((n) => Number(n)).filter((n) => Number.isFinite(n)))];
    if (ids.length === 0) return [];
    try {
      const rows = await this.executeCall("crm.lead", "portal_offer_lead_attachment_rows", [ids], {});
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  }

  /**
   * Chatter-linked files (`mail.message` → `ir.attachment`), `res_id` remapped to lead id.
   * Same portal visibility as Offers pipeline.
   */
  async searchReadChatterAttachmentsForCrmLeads(leadIds: number[]): Promise<any[]> {
    const ids = [...new Set(leadIds.map((n) => Number(n)).filter((n) => Number.isFinite(n)))];
    if (ids.length === 0) return [];
    try {
      const rows = await this.executeCall("crm.lead", "portal_offer_lead_chatter_attachment_rows", [ids], {});
      return Array.isArray(rows) ? rows : [];
    } catch {
      return [];
    }
  }

  /**
   * Active opportunities for the Offers pipeline (proposal-synced `crm.lead` records).
   */
  async searchReadOfferPipelineLeads(params?: {
    createAfter?: string;
    teamId?: number;
    limit?: number;
  }): Promise<any[]> {
    const limit = params?.limit ?? 300;
    const domain: any[] = [
      ["type", "=", "opportunity"],
      ["active", "=", true],
    ];
    if (params?.createAfter) {
      domain.push(["create_date", ">=", params.createAfter]);
    }
    if (params?.teamId != null && Number.isFinite(params.teamId)) {
      domain.push(["team_id", "=", params.teamId]);
    }

    const fields = [
      "id",
      "name",
      "partner_id",
      "prospect_id",
      "contact_name",
      "email_from",
      "phone",
      "expected_revenue",
      "probability",
      "stage_id",
      "user_id",
      "company_id",
      "team_id",
      "create_date",
      "write_date",
      "date_closed",
      "opportunity_note_ids",
      "opportunity_price_history_ids",
      "x_proposal_artwork_ids",
      ...PIPELINE_MULTICURRENCY_CODES.map((c) => OPP_EXPECTED_ODOO_FIELD[c]),
    ];

    const rows = await this.executeCall("crm.lead", "search_read_offers_pipeline", [domain], {
      fields,
      limit,
      offset: 0,
      order: "create_date desc, id desc",
    });
    return Array.isArray(rows) ? rows : [];
  }

  /**
   * Opera CRM: one2many `opportunity_note_ids` lines (`x_user_id`, `x_date`, `x_note`).
   * Opera Art Gallery: comodel for `crm.lead.opportunity_note_ids`.
   */
  private static readonly CRM_LEAD_OPPORTUNITY_NOTE_MODEL = "artgallery.opportunity.notes";

  private static readonly CRM_LEAD_PROPOSAL_EMAIL_LOG_MODEL =
    "artgallery.crm.lead.proposal.email.log";

  private static readonly CRM_LEAD_OPPORTUNITY_PRICE_HISTORY_MODEL =
    "artgallery.opportunity.price.history";

  /**
   * `crm.lead.proposal_email_log_ids` (One2many) — avoids relying only on `search_read` on the log model.
   * Safe when the field is missing on older DBs: returns an empty map.
   */
  async readCrmLeadsProposalEmailLinkIds(leadIds: number[]): Promise<Map<number, number[]>> {
    const out = new Map<number, number[]>();
    const uniq = [...new Set(leadIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    if (uniq.length === 0) return out;
    const chunkSize = 80;
    try {
      for (let i = 0; i < uniq.length; i += chunkSize) {
        const chunk = uniq.slice(i, i + chunkSize);
        const rows = await this.executeCall("crm.lead", "read", [chunk, ["id", "proposal_email_log_ids"]]);
        if (!Array.isArray(rows)) continue;
        for (const row of rows as Record<string, unknown>[]) {
          const lid = Number(row.id);
          const raw = row.proposal_email_log_ids;
          const ids: number[] = Array.isArray(raw)
            ? raw
                .map((x) => (Array.isArray(x) ? Number(x[0]) : Number(x)))
                .filter((n) => Number.isFinite(n) && n > 0)
            : [];
          if (Number.isFinite(lid)) out.set(lid, ids);
        }
      }
    } catch (e) {
      console.warn("readCrmLeadsProposalEmailLinkIds: proposal_email_log_ids unavailable?", e);
    }
    return out;
  }

  async readCrmLeadProposalEmailLogLines(ids: number[]): Promise<Record<string, unknown>[]> {
    const uniq = [...new Set(ids.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    if (uniq.length === 0) return [];
    const model = OdooClient.CRM_LEAD_PROPOSAL_EMAIL_LOG_MODEL;
    const fields = [
      "id",
      "x_lead_id",
      "x_subject",
      "x_body_html",
      "x_email_from",
      "x_email_to",
      "x_email_cc",
      "x_sent_at",
      "x_user_id",
    ];
    try {
      const rows = await this.executeCall(model, "read", [uniq, fields]);
      return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
    } catch (e) {
      console.warn("readCrmLeadProposalEmailLogLines: failed for model", model, e);
      return [];
    }
  }

  /**
   * Read the chatter email thread for one or more opportunities.
   * This is what we use to surface replies / received messages in Offers.
   */
  async readCrmLeadMailMessages(leadIds: number[]): Promise<Record<string, unknown>[]> {
    const uniq = [...new Set(leadIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    if (uniq.length === 0) return [];
    try {
      const rows = await this.executeCall(
        "mail.message",
        "search_read",
        [[["model", "=", "crm.lead"], ["res_id", "in", uniq]]],
        {
          fields: [
            "id",
            "res_id",
            "date",
            "author_id",
            "subject",
            "body",
            "message_type",
            "email_from",
            "email_to",
            "create_date",
            "write_date",
          ],
          limit: 2000,
          order: "date desc, id desc",
        },
      );
      return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
    } catch (e) {
      console.warn("readCrmLeadMailMessages failed:", e);
      return [];
    }
  }

  /**
   * Proposal emails logged from the web app (`crm_lead_id` on send). Not the same as `ir.attachment`.
   * Use with {@link readCrmLeadsProposalEmailLinkIds} + {@link readCrmLeadProposalEmailLogLines} for the same data path as Notes / price history.
   */
  async searchReadCrmLeadProposalEmailLogs(leadIds: number[]): Promise<{
    records: Record<string, unknown>[];
    error?: string;
  }> {
    const uniq = [...new Set(leadIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    if (uniq.length === 0) return { records: [] };
    const model = OdooClient.CRM_LEAD_PROPOSAL_EMAIL_LOG_MODEL;
    const fields = [
      "id",
      "x_lead_id",
      "x_subject",
      "x_body_html",
      "x_email_from",
      "x_email_to",
      "x_email_cc",
      "x_sent_at",
      "x_user_id",
    ];
    try {
      const rows = await this.executeCall(model, "search_read", [[["x_lead_id", "in", uniq]]], {
        fields,
        limit: 2000,
        order: "x_sent_at desc, id desc",
      });
      return { records: Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [] };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn("searchReadCrmLeadProposalEmailLogs failed (module not upgraded?)", e);
      return { records: [], error: msg };
    }
  }

  async readCrmLeadOpportunityPriceHistoryLines(
    ids: number[],
  ): Promise<Record<string, unknown>[]> {
    const uniq = [...new Set(ids.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    if (uniq.length === 0) return [];
    const model = OdooClient.CRM_LEAD_OPPORTUNITY_PRICE_HISTORY_MODEL;
    const histFields = [
      "id",
      "x_user_id",
      "x_date",
      "x_remark",
      "x_amount",
      ...PIPELINE_MULTICURRENCY_CODES.map((c) => HIST_QUOTE_ODOO_FIELD[c]),
    ];
    try {
      const rows = await this.executeCall(model, "read", [uniq, histFields]);
      return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
    } catch (e) {
      console.warn("readCrmLeadOpportunityPriceHistoryLines: failed for model", model, e);
      return [];
    }
  }

  async readCrmLeadOpportunityNoteLines(ids: number[]): Promise<Record<string, unknown>[]> {
    const uniq = [...new Set(ids.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    if (uniq.length === 0) return [];
    const model = OdooClient.CRM_LEAD_OPPORTUNITY_NOTE_MODEL;
    try {
      const rows = await this.executeCall(model, "read", [
        uniq,
        ["id", "x_user_id", "x_date", "x_note"],
      ]);
      return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
    } catch (e) {
      console.warn("readCrmLeadOpportunityNoteLines: failed for model", model, e);
      return [];
    }
  }

  /** Read full note rows for one lead (after append, without reloading the whole pipeline). */
  async readCrmLeadOpportunityNotesForLead(leadId: number): Promise<Record<string, unknown>[]> {
    const id = Number(leadId);
    if (!Number.isFinite(id) || id <= 0) return [];
    const rawIds = await this.executeCall(
      "crm.lead",
      "portal_offer_lead_read_opportunity_note_ids",
      [id],
      {},
    );
    const noteIds: number[] = Array.isArray(rawIds)
      ? rawIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    return this.readCrmLeadOpportunityNoteLines(noteIds);
  }

  /** Append an internal opportunity note line (same shape as Opera web `crm.lead/write`). */
  async appendCrmLeadOpportunityNote(leadId: number, noteText: string): Promise<void> {
    const id = Number(leadId);
    if (!Number.isFinite(id)) {
      throw new Error("appendCrmLeadOpportunityNote: invalid lead id");
    }
    const text = String(noteText ?? "").trim();
    if (!text) {
      throw new Error("Note text is empty.");
    }
    const userId = this.getOdooUserId();
    if (!userId) {
      throw new Error("Not authenticated.");
    }
    await this.executeCall(
      "crm.lead",
      "portal_offer_lead_append_opportunity_note",
      [id],
      {
        x_date: this.formatOdooLocalDateTime(),
        x_note: text,
      },
    );
  }

  /**
   * Read the full activity timeline for one opportunity (Offers > Timeline tab).
   * Rows come back newest-first as `{ id, x_date, x_message, x_user_id, create_date }`
   * (`x_user_id` is the standard Odoo many2one tuple `[id, name]`).
   */
  async readCrmLeadOpportunityTimeline(
    leadId: number,
  ): Promise<Record<string, unknown>[]> {
    const id = Number(leadId);
    if (!Number.isFinite(id) || id <= 0) return [];
    try {
      const rows = await this.executeCall(
        "crm.lead",
        "portal_offer_lead_read_opportunity_timeline",
        [id],
        {},
      );
      return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
    } catch (err) {
      console.warn("readCrmLeadOpportunityTimeline failed:", err);
      return [];
    }
  }

  /**
   * Append one ``artgallery.opportunity.timeline`` row on the opportunity.
   * `x_user_id` is stamped server-side from the session, so the frontend only
   * needs to supply the short message (e.g. "Offer created", "Email sent").
   * Returns the new row id, or `null` if the call failed (non-fatal — used in
   * fire-and-forget logging spots).
   */
  async appendCrmLeadOpportunityTimeline(
    leadId: number,
    message: string,
  ): Promise<number | null> {
    const id = Number(leadId);
    if (!Number.isFinite(id) || id <= 0) return null;
    const text = String(message ?? "").trim();
    if (!text) return null;
    if (!this.getOdooUserId()) return null;
    try {
      const newId = await this.executeCall(
        "crm.lead",
        "portal_offer_lead_append_opportunity_timeline",
        [id],
        {
          x_message: text,
          x_date: this.formatOdooLocalDateTime(),
        },
      );
      const n = Number(newId);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch (err) {
      console.warn("appendCrmLeadOpportunityTimeline failed:", err);
      return null;
    }
  }

  /** Opera / Odoo web root (deep links, `/web/content/` downloads). */
  getOdooWebBaseUrl(): string {
    return this.baseURL;
  }

  /** Open opportunity in the native CRM form. */
  getCrmLeadFormUrl(leadId: number): string {
    const id = Number(leadId);
    if (!Number.isFinite(id)) return `${this.baseURL}/web`;
    return `${this.baseURL}/web#id=${id}&model=crm.lead&view_type=form`;
  }

  getIrAttachmentDownloadUrl(attachmentId: number): string {
    const id = Number(attachmentId);
    if (!Number.isFinite(id)) return this.baseURL;
    return `${this.baseURL}/web/content/${id}?download=true`;
  }

  private safeDownloadBasename(name: string, fallback: string): string {
    const raw = (name || fallback || "attachment").trim() || "attachment";
    return raw.replace(/[/\\?%*:|"<>]/g, "_").slice(0, 200) || "attachment";
  }

  /** Build a file from one ``ir.attachment`` read row (or portal Offers download row). */
  private materializeIrAttachmentReadRowToDevice(
    row: Record<string, unknown>,
    attachmentIdForOpenFallback: number,
    fallbackFileName: string,
  ): void {
    const fileName = this.safeDownloadBasename(
      typeof row.name === "string" ? row.name : "",
      fallbackFileName,
    );
    const mime =
      typeof row.mimetype === "string" && row.mimetype.trim()
        ? row.mimetype.trim()
        : "application/octet-stream";
    const type = typeof row.type === "string" ? row.type : "";
    const urlField = typeof row.url === "string" ? row.url.trim() : "";

    if (type === "url" && /^https?:\/\//i.test(urlField)) {
      window.open(urlField, "_blank", "noopener,noreferrer");
      return;
    }

    const datas = row.datas;
    if (datas == null || datas === false) {
      window.open(
        this.getIrAttachmentDownloadUrl(attachmentIdForOpenFallback),
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    const b64 = String(datas).replace(/\s/g, "");
    let binary: string;
    try {
      binary = atob(b64);
    } catch {
      throw new Error("Could not decode attachment data.");
    }
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mime });
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  /**
   * Download `ir.attachment` using the same JSON-RPC / `call_kw` auth as the rest of the app.
   * A plain browser open of `/web/content/<id>` usually 404s because that route expects an Odoo
   * session cookie, while password-mode API calls do not establish one.
   *
   * When `crmLeadId` is set (Offers pipeline), uses `portal_offer_lead_read_attachment_for_download`
   * first so directors can fetch files on opportunities whose `company_id` differs from the session.
   */
  async saveIrAttachmentToDevice(
    attachmentId: number,
    fallbackFileName = "attachment",
    crmLeadId?: number,
  ): Promise<void> {
    const id = Number(attachmentId);
    if (!Number.isFinite(id)) {
      throw new Error("Invalid attachment id.");
    }

    const leadNum = crmLeadId == null ? NaN : Number(crmLeadId);
    if (Number.isFinite(leadNum) && leadNum > 0) {
      try {
        const portalRow = await this.executeCall(
          "crm.lead",
          "portal_offer_lead_read_attachment_for_download",
          [leadNum, id],
        );
        if (portalRow && typeof portalRow === "object" && !Array.isArray(portalRow)) {
          this.materializeIrAttachmentReadRowToDevice(
            portalRow as Record<string, unknown>,
            id,
            fallbackFileName,
          );
          return;
        }
      } catch (e) {
        console.warn(
          "[CRM lead attachment download] portal_offer_lead_read_attachment_for_download failed, falling back to ir.attachment read:",
          e instanceof Error ? e.message : e,
        );
      }
    }

    const rows = await this.executeCall("ir.attachment", "read", [
      [id],
      ["name", "mimetype", "datas", "type", "url"],
    ]);
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) {
      throw new Error("Attachment not found.");
    }
    this.materializeIrAttachmentReadRowToDevice(row as Record<string, unknown>, id, fallbackFileName);
  }

  /**
   * Stages for any listed team **or** with no team (`team_id` = false).
   * Declined/Lost columns are often `team_id: false` in Opera/Odoo, so they were missing from `team_id = 1`-only queries.
   */
  private buildDomainTeamIdIn(teamIds: number[]): any[] {
    const ids = [...new Set(teamIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))];
    const noTeam: any[] = ["team_id", "=", false];
    if (ids.length === 0) return [noTeam];
    const clauses: any[] = [noTeam];
    for (const t of ids) clauses.push(["team_id", "=", t]);
    let d: any = clauses[clauses.length - 1];
    for (let i = clauses.length - 2; i >= 0; i--) {
      d = ["|", clauses[i], d];
    }
    return d;
  }

  /**
   * All stages for any of the given sales teams (inactive included). Fills gaps when Declined lives on another team.
   */
  async searchReadCrmStagesForTeamIds(teamIds: number[], limit = 500): Promise<any[]> {
    const domain = this.buildDomainTeamIdIn(teamIds);
    if (domain.length === 0) return [];
    const ctx = { active_test: false };
    const fieldsFull = [
      "id",
      "name",
      "display_name",
      "sequence",
      "probability",
      "fold",
      "team_id",
      "requirements",
    ];
    const fieldsSlim = ["id", "name", "sequence", "probability", "fold", "team_id"];
    try {
      const rows = await this.executeCall("crm.stage", "search_read", [domain], {
        fields: fieldsFull,
        limit,
        order: "sequence asc, id asc",
        context: ctx,
      });
      return Array.isArray(rows) ? rows : [];
    } catch {
      try {
        const rows = await this.executeCall("crm.stage", "search_read", [domain], {
          fields: fieldsSlim,
          limit,
          order: "sequence asc, id asc",
          context: ctx,
        });
        return Array.isArray(rows) ? rows : [];
      } catch {
        return [];
      }
    }
  }

  /**
   * Stages whose name or display_name matches keywords (inactive included). Helps surface “Declined” / “Lost” from any team.
   */
  async searchReadCrmStagesByNameKeywords(substrings: string[], limit = 150): Promise<any[]> {
    const parts = substrings.map((s) => String(s).trim()).filter(Boolean);
    if (parts.length === 0) return [];
    const ctx = { active_test: false };
    const fields = ["id", "name", "display_name", "sequence", "probability", "fold", "team_id"];
    const clauses: any[] = [];
    for (const p of parts) {
      clauses.push(["name", "ilike", p]);
      clauses.push(["display_name", "ilike", p]);
    }
    let d: any = clauses[clauses.length - 1];
    for (let i = clauses.length - 2; i >= 0; i--) {
      d = ["|", clauses[i], d];
    }
    try {
      const rows = await this.executeCall("crm.stage", "search_read", [d], {
        fields,
        limit,
        order: "sequence asc, id asc",
        context: ctx,
      });
      return Array.isArray(rows) ? rows : [];
    } catch {
      let d2: any = ["name", "ilike", parts[parts.length - 1]];
      for (let i = parts.length - 2; i >= 0; i--) {
        d2 = ["|", ["name", "ilike", parts[i]], d2];
      }
      try {
        const rows = await this.executeCall("crm.stage", "search_read", [d2], {
          fields: ["id", "name", "sequence", "probability", "fold", "team_id"],
          limit,
          order: "sequence asc, id asc",
          context: ctx,
        });
        return Array.isArray(rows) ? rows : [];
      } catch {
        return [];
      }
    }
  }

  /**
   * Opera Offers board: same as web `crm.stage/read` on ids `[1,2,3,4,9]` (Sent → Declined).
   * Columns follow `orderedStageIds` order; titles use `display_name` from the response.
   * Tries several read contexts plus `search_read` — some installs reject certain kwargs on `crm.stage`.
   */
  async readCrmOfferKanbanStages(orderedStageIds: number[] = [1, 2, 3, 4, 9]): Promise<any[]> {
    const ids = orderedStageIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) return [];

    const fieldsFull = [
      "id",
      "name",
      "display_name",
      "sequence",
      "probability",
      "fold",
      "requirements",
      "team_id",
    ];
    const fieldsSlim = ["id", "name", "display_name", "sequence", "probability", "fold", "team_id"];
    const fieldsMinimal = ["id", "name", "display_name", "sequence", "probability", "fold"];

    const readKwargsVariants = [
      { context: { active_test: false, default_type: "opportunity" } },
      { context: { active_test: false } },
      {},
    ];

    let rows: any[] = [];
    for (const kwargs of readKwargsVariants) {
      if (rows.length > 0) break;
      for (const fields of [fieldsFull, fieldsSlim, fieldsMinimal]) {
        try {
          const res = await this.executeCall("crm.stage", "read", [ids, fields], kwargs);
          if (Array.isArray(res) && res.length > 0) {
            rows = res;
            break;
          }
        } catch {
          /* try next */
        }
      }
    }

    if (rows.length === 0) {
      const domain: any[] = [["id", "in", ids]];
      try {
        const res = await this.executeCall("crm.stage", "search_read", [domain], {
          fields: fieldsSlim,
          limit: Math.max(ids.length, 20),
          order: "sequence asc, id asc",
          context: { active_test: false },
        });
        rows = Array.isArray(res) ? res : [];
      } catch {
        try {
          const res = await this.executeCall("crm.stage", "search_read", [domain], {
            fields: fieldsMinimal,
            limit: Math.max(ids.length, 20),
            order: "id asc",
          });
          rows = Array.isArray(res) ? res : [];
        } catch {
          rows = [];
        }
      }
    }

    const byId = new Map<number, any>();
    for (const r of rows) {
      const id = Number(r?.id);
      if (Number.isFinite(id)) byId.set(id, r);
    }
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }

  async writeCrmLeadStage(leadId: number, stageId: number): Promise<void> {
    const lid = Number(leadId);
    const sid = Number(stageId);
    if (!Number.isFinite(lid) || !Number.isFinite(sid)) {
      throw new Error("writeCrmLeadStage: invalid id");
    }
    await this.executeCall("crm.lead", "portal_offer_lead_write_stage", [lid, sid]);
  }

  /**
   * Upsert one row per artwork on `artgallery.artwork.proposal.count` for the
   * current seller + given client. The backend keeps a single row per
   * `(seller, client, artwork)` tuple and bumps `hit_count` (→ `write_date`)
   * on repeats, so all four Proposal modal entry points (Web View, Copy PDF
   * link, Download PDF, Send Mail) collapse to a single row.
   *
   * `client._model` tells us which table the client lives on: prospect-first
   * lookup happens in `getContactById`; email-only recipients (no record id
   * yet) are silently skipped because there is nothing to attribute the
   * count to.
   *
   * Fire-and-forget — proposal actions never block on this.
   */
  async recordArtworkProposalAction(
    artworkIds: Array<number | string | null | undefined>,
    client: {
      id?: number | string | null;
      _model?: "crm.prospect" | "res.partner";
    } | null | undefined,
  ): Promise<void> {
    try {
      if (!client || !client._model) return;
      const clientId = Number(client.id);
      if (!Number.isFinite(clientId) || clientId <= 0) return;
      const ids: number[] = [];
      const seen = new Set<number>();
      for (const raw of artworkIds || []) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
          seen.add(n);
          ids.push(n);
        }
      }
      if (ids.length === 0) return;
      if (!this.getOdooUserId()) return;
      await this.executeCall(
        "artgallery.artwork.proposal.count",
        "record_proposal_action",
        [ids, clientId, client._model],
      );
    } catch (err) {
      // Never surface counter-tracking failures to the user — the proposal
      // action itself has already succeeded by the time we get here.
      console.warn("recordArtworkProposalAction failed (non-fatal):", err);
    }
  }

  /**
   * Read `product.template` rows for the artwork IDs saved on a lead's
   * `x_proposal_artwork_ids`. Returns the lightweight subset needed by the
   * "Browse artworks" modal (id, name, artist, cover image, list price).
   */
  async getProposalArtworksByIds(
    artworkIds: Array<number | string | null | undefined>,
  ): Promise<
    Array<{
      id: number;
      name: string;
      artist: string;
      year: string;
      coverUrl: string;
      priceEur: number;
    }>
  > {
    try {
      if (!this.getOdooUserId()) return [];
      const ids: number[] = [];
      const seen = new Set<number>();
      for (const raw of artworkIds || []) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
          seen.add(n);
          ids.push(n);
        }
      }
      if (ids.length === 0) return [];
      const rows: any[] = await this.executeCall(
        "product.template",
        "read",
        [ids],
        {
          fields: [
            "id",
            "name",
            "res_artist_id",
            "artwork_year",
            "PriceEUREnt",
            "NewMainLR",
            "NewMainHR",
            "main_super_picture_hd",
            "main_picture_hd",
            "main_web_picture",
            "main_a6_picture",
            "image_1920",
          ],
        },
      );
      if (!Array.isArray(rows)) return [];
      const orderIndex = new Map<number, number>();
      ids.forEach((id, idx) => orderIndex.set(id, idx));
      const safeUrl = (v: unknown): string => {
        if (!v || v === false || v === "false") return "";
        return typeof v === "string" ? v : "";
      };
      return rows
        .map((r) => {
          const idNum = Number(r?.id);
          const artistField = r?.res_artist_id;
          const artist = Array.isArray(artistField)
            ? String(artistField[1] || "")
            : String(artistField || "");
          let coverUrl =
            safeUrl(r?.NewMainLR) ||
            safeUrl(r?.NewMainHR) ||
            safeUrl(r?.main_super_picture_hd) ||
            safeUrl(r?.main_web_picture) ||
            safeUrl(r?.main_picture_hd) ||
            safeUrl(r?.main_a6_picture);
          if (!coverUrl && r?.image_1920 && r.image_1920 !== false && r.image_1920 !== "false") {
            coverUrl = `data:image/png;base64,${r.image_1920}`;
          }
          const priceEur = Number(r?.PriceEUREnt);
          return {
            id: idNum,
            name: String(r?.name || "").trim() || "Untitled",
            artist: artist.trim() || "Unknown Artist",
            year: r?.artwork_year ? String(r.artwork_year) : "",
            coverUrl,
            priceEur: Number.isFinite(priceEur) ? priceEur : 0,
          };
        })
        .sort(
          (a, b) =>
            (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0),
        );
    } catch (err) {
      console.warn("getProposalArtworksByIds failed (non-fatal):", err);
      return [];
    }
  }

  /**
   * Fetch `{artworkId: count}` for the given artworks using the rolling
   * `days`-day window (default 10) backed by `write_date`.
   *
   * Returns an empty map if the user isn't authenticated or the backend call
   * fails — callers treat missing keys as 0 so the UI never flickers.
   */
  async getArtworkProposalCountsRecent(
    artworkIds: Array<number | string | null | undefined>,
    days: number = 10,
  ): Promise<Record<number, number>> {
    try {
      if (!this.getOdooUserId()) return {};
      const ids: number[] = [];
      const seen = new Set<number>();
      for (const raw of artworkIds || []) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
          seen.add(n);
          ids.push(n);
        }
      }
      if (ids.length === 0) return {};
      const safeDays =
        Number.isFinite(Number(days)) && Number(days) > 0 ? Number(days) : 10;
      const raw = await this.executeCall(
        "artgallery.artwork.proposal.count",
        "get_recent_counts",
        [ids, safeDays],
      );
      const out: Record<number, number> = {};
      if (raw && typeof raw === "object") {
        for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
          const key = Number(k);
          const val = Number(v);
          if (Number.isFinite(key) && key > 0 && Number.isFinite(val)) {
            out[key] = val;
          }
        }
      }
      return out;
    } catch (err) {
      console.warn("getArtworkProposalCountsRecent failed (non-fatal):", err);
      return {};
    }
  }

  /**
   * Fetch the full proposal-counter rows for a given artwork, enriched with
   * seller + client display name + email so the "Proposals sent" modal can
   * render rows with avatars and one-click Email buttons.
   *
   * Pure frontend orchestration — uses the existing model + standard CRUD
   * (`search_read` / `read`), no new backend method required.
   *
   * Returns the rows already sorted by `last_action_at` desc.
   */
  async getArtworkProposalDetails(
    artworkId: number | string,
    days: number = 10,
  ): Promise<
    Array<{
      id: number;
      artworkId: number;
      sellerId: number;
      sellerName: string;
      sellerEmail: string;
      clientId: number;
      clientModel: "res.partner" | "crm.prospect";
      clientName: string;
      clientEmail: string;
      lastActionAt: string; // raw Odoo datetime "YYYY-MM-DD HH:MM:SS"
    }>
  > {
    try {
      if (!this.getOdooUserId()) return [];
      const aid = Number(artworkId);
      if (!Number.isFinite(aid) || aid <= 0) return [];

      const safeDays =
        Number.isFinite(Number(days)) && Number(days) > 0 ? Number(days) : 10;
      // Mirror `get_recent_counts` window so the modal list matches the `$`
      // count exactly (any row outside the window is invisible to both).
      const cutoff = new Date(Date.now() - safeDays * 24 * 60 * 60 * 1000);
      const cutoffOdoo = cutoff
        .toISOString()
        .replace("T", " ")
        .slice(0, 19);

      const rows: any[] = await this.executeCall(
        "artgallery.artwork.proposal.count",
        "search_read",
        [
          [
            ["artwork_id", "=", aid],
            ["last_action_at", ">", cutoffOdoo],
          ],
        ],
        {
          fields: [
            "id",
            "artwork_id",
            "seller_id",
            "partner_id",
            "prospect_id",
            "last_action_at",
          ],
          order: "last_action_at desc",
        },
      );

      if (!Array.isArray(rows) || rows.length === 0) return [];

      const sellerIds = new Set<number>();
      const partnerIds = new Set<number>();
      const prospectIds = new Set<number>();
      for (const r of rows) {
        const sid = Array.isArray(r.seller_id) ? r.seller_id[0] : 0;
        if (Number.isFinite(sid) && sid > 0) sellerIds.add(Number(sid));
        const pid = Array.isArray(r.partner_id) ? r.partner_id[0] : 0;
        if (Number.isFinite(pid) && pid > 0) partnerIds.add(Number(pid));
        const prid = Array.isArray(r.prospect_id) ? r.prospect_id[0] : 0;
        if (Number.isFinite(prid) && prid > 0) prospectIds.add(Number(prid));
      }

      const [sellersRaw, partnersRaw, prospectsRaw] = await Promise.all([
        sellerIds.size > 0
          ? this.executeCall("res.users", "read", [Array.from(sellerIds)], {
              fields: ["id", "name", "email"],
            }).catch(() => [])
          : Promise.resolve([]),
        partnerIds.size > 0
          ? this.executeCall(
              "res.partner",
              "read",
              [Array.from(partnerIds)],
              { fields: ["id", "name", "email"] },
            ).catch(() => [])
          : Promise.resolve([]),
        prospectIds.size > 0
          ? this.executeCall(
              "crm.prospect",
              "read",
              [Array.from(prospectIds)],
              { fields: ["id", "name", "email"] },
            ).catch(() => [])
          : Promise.resolve([]),
      ]);

      const sellersById = new Map<number, { name: string; email: string }>();
      for (const u of sellersRaw as any[]) {
        sellersById.set(Number(u.id), {
          name: typeof u.name === "string" ? u.name : "",
          email: typeof u.email === "string" ? u.email : "",
        });
      }
      const partnersById = new Map<number, { name: string; email: string }>();
      for (const p of partnersRaw as any[]) {
        partnersById.set(Number(p.id), {
          name: typeof p.name === "string" ? p.name : "",
          email: typeof p.email === "string" ? p.email : "",
        });
      }
      const prospectsById = new Map<number, { name: string; email: string }>();
      for (const p of prospectsRaw as any[]) {
        prospectsById.set(Number(p.id), {
          name: typeof p.name === "string" ? p.name : "",
          email: typeof p.email === "string" ? p.email : "",
        });
      }

      const out = rows
        .map((r) => {
          const sellerId = Array.isArray(r.seller_id) ? r.seller_id[0] : 0;
          const sellerNameFallback = Array.isArray(r.seller_id)
            ? r.seller_id[1] || ""
            : "";
          const seller = sellersById.get(Number(sellerId)) || {
            name: sellerNameFallback,
            email: "",
          };

          const partnerId = Array.isArray(r.partner_id) ? r.partner_id[0] : 0;
          const prospectId = Array.isArray(r.prospect_id)
            ? r.prospect_id[0]
            : 0;
          let clientId = 0;
          let clientModel: "res.partner" | "crm.prospect" = "res.partner";
          let clientName = "";
          let clientEmail = "";
          if (Number.isFinite(partnerId) && partnerId > 0) {
            clientId = Number(partnerId);
            clientModel = "res.partner";
            const fallback = Array.isArray(r.partner_id)
              ? r.partner_id[1] || ""
              : "";
            const hit = partnersById.get(clientId);
            clientName = hit?.name || fallback;
            clientEmail = hit?.email || "";
          } else if (Number.isFinite(prospectId) && prospectId > 0) {
            clientId = Number(prospectId);
            clientModel = "crm.prospect";
            const fallback = Array.isArray(r.prospect_id)
              ? r.prospect_id[1] || ""
              : "";
            const hit = prospectsById.get(clientId);
            clientName = hit?.name || fallback;
            clientEmail = hit?.email || "";
          }

          return {
            id: Number(r.id),
            artworkId: Array.isArray(r.artwork_id)
              ? Number(r.artwork_id[0])
              : Number(r.artwork_id),
            sellerId: Number(sellerId) || 0,
            sellerName: seller.name,
            sellerEmail: seller.email,
            clientId,
            clientModel,
            clientName,
            clientEmail,
            lastActionAt:
              typeof r.last_action_at === "string" ? r.last_action_at : "",
          };
        })
        .filter((row) => row.clientId > 0);

      // Defensive resort in case backend `order` was honored differently.
      out.sort((a, b) =>
        a.lastActionAt > b.lastActionAt
          ? -1
          : a.lastActionAt < b.lastActionAt
            ? 1
            : 0,
      );

      return out;
    } catch (err) {
      console.warn("getArtworkProposalDetails failed (non-fatal):", err);
      return [];
    }
  }

  async getVisibleDashboardNotification(): Promise<DashboardNotification> {
    const res = await this.executeCall(
      "artgallery.dashboard.notification",
      "get_visible_notification",
      [],
      {},
    );

    return parseDashboardNotificationPayload(res as Record<string, unknown>);
  }

  async getDashboardNotification(): Promise<DashboardNotification> {
    const res = await this.executeCall(
      "artgallery.dashboard.notification",
      "get_current_notification",
      [],
      {},
    );

    if (res?.code === 403) {
      throw new Error("Forbidden");
    }

    return parseDashboardNotificationPayload(res as Record<string, unknown>);
  }

  async saveDashboardNotification(input: DashboardNotificationInput): Promise<DashboardNotification> {
    const res = await this.executeCall(
      "artgallery.dashboard.notification",
      "save_current_notification",
      [
        {
        message: input.message || "",
        start_date: input.start_date || false,
        end_date: input.end_date || false,
        },
      ],
      {},
    );

    if (res?.code === 403) {
      throw new Error("Forbidden");
    }

    const notification = res?.notification || res || {};
    return parseDashboardNotificationPayload(notification as Record<string, unknown>);
  }

  async getCacheClearButtonEnabled(): Promise<boolean> {
    try {
      const value = await this.executeCall(
        "ir.config_parameter",
        "get_param",
        [CACHE_CLEAR_BUTTON_PARAM_KEY, "False"],
        {},
      );
      return parseConfigFlag(value);
    } catch {
      return false;
    }
  }

  async setCacheClearButtonEnabled(enabled: boolean): Promise<void> {
    await this.executeCall(
      "ir.config_parameter",
      "set_param",
      [CACHE_CLEAR_BUTTON_PARAM_KEY, enabled ? "True" : "False"],
      {},
    );
  }
}


export const odooClient = new OdooClient();
export default odooClient;
